import React, { useState, useEffect, useRef, useMemo } from 'react';
import ScrollX from '../components/ScrollX';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import DocumentsBubble from './DocumentsBubble';
import { loadLookupOptions } from '../utils/lookups';
import { APPEAL_STATUS, PARCEL_STATUS } from '../constants/statusCodes';
import { pctToInput, pctParse } from '../constants/refFields';
import JurisdictionBreakdown from '../components/JurisdictionBreakdown';
import ReferenceBillScan from '../components/ReferenceBillScan';
import AppealSupportingDocs from '../components/AppealSupportingDocs';
import MoneyInput, { cleanMoney } from '../components/MoneyInput';
import { detectRateUnit, pickYearBill, printedBillRate, effectiveBillRate, yearAdValorem, isSupplementalBill } from '../reports/taxRate';
import useAssignments from '../hooks/useAssignments';
import { ParcelTsDisplay } from '../components/AssignmentPopover';
import { roleNames } from '../utils/assignments';
import { countyFromAssessor } from '../utils/format';
import { resolveVersion } from '../utils/resolveVersion';
import EntityNotes from '../components/EntityNotes';
import { representedInYear, retirementLabel, retiredAsOf, yearState, yearChip, isBaselineYear } from '../utils/retirement';
import { resolveContactsForParcel } from '../utils/contactResolve';
import { computeTaxBill, navShares } from '../utils/taxBill';
import { primaryAppealRow, taxableTotalOf, ratePercentOf, jurisdictionTaxLines } from '../utils/appealRows';
import { parseSigned, changedYearFields, recomputedMarketTotal, recomputedTaxableSubtotal, marketTotalIsStated, levelsToPersist, levelHasDerived, DERIVED_FLAGS } from '../utils/parcelSave';
import { suggestJurisdictionRates, suggestionLabel } from '../utils/rateSuggestion';
import { resolveFinalLevel } from '../utils/finalLevel';
import { blankLevelRow, levelColumnsLike, copyLevelRows, copyDeductionLines, levelIsEmpty } from '../utils/newAppealLevel';
import { jurisdictionLabel, SCHOOL_JURISDICTION } from '../utils/jurisdictionLabel';
import { ORIGINAL_NOTICE, sectionKey, parseSection, isOriginalSection, originalOnly } from '../utils/notices';
import NoticeTypePicker from '../components/NoticeTypePicker';
import YearSwitchWindow from '../components/YearSwitchWindow';
import { parcelYearFigures, PARCEL_YEAR_BILL_COLS } from '../reports/parcelYearRows';
import { fetchAll, fetchByIds } from '../reports/pagination';
import { prorataSet, prorataShares, prorataProblem, pctOf, saveProrataSet, sameAssessorProperties } from '../utils/prorata';
import { syncRows } from '../utils/syncRows';
import { toNumber } from '../reports/format';
import { submitTaxBill } from '../utils/submitTaxBill';
import { cleanEnteredLines, lineRateAndBase } from '../utils/billLines';
import { checkBill } from '../utils/billChecks';
import { TAX_BILL_STATUS, TAX_BILL_CHIP_LABEL, billWorkflowStatus } from '../constants/statuses';
import ParcelFirmAssignment from '../components/ParcelFirmAssignment';
import { fmtPerSf, figuresFor, marketTotalOf } from '../utils/perSf';
import ParcelRoleEditor from '../components/ParcelRoleEditor';
import { DOC_KEYS } from '../utils/docVocab';
import { taxesFooter, deltaMatters } from '../utils/taxesFooter';
import { deriveBillPayment } from '../utils/billPayment';
import { BillPaymentPanel, MarkAllPaid } from '../components/BillPayment';
import { PAID_PROOFS } from '../utils/paymentRecord';
import { resolveFirmAssignment, resolveRoleRoster, firmPrimaryContact } from '../utils/resolveAssignment';

// RE/PP Parcel Type, Exemption, Appeal Status, Parcel Status, Services now load from lookup_lists
// (managed in Table Management). Appeal/Parcel Status logic keys off the codes in ../constants/statusCodes.

const fmt = v => v == null || v === '' ? '' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtInt = v => v == null || v === '' ? '' : Number(v).toLocaleString('en-US');   // a taxable value, no $
// Cents-precise currency — for the tax section, where amounts are verified against real bills to the penny.
const fmtc = v => v == null || v === '' ? '' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
// The computed-vs-billed check on the Taxes tab: an amber ⚠ beside Billed, figures on hover. d is billed − calculated.
const deltaWarn = { color: '#b45309', fontSize: '11px', fontWeight: 700, cursor: 'help' };
const deltaTitle = (calc, d) => `Calculated ${fmtc(calc)}, difference ${d > 0 ? '+' : '−'}${fmtc(Math.abs(d))}`;
// A bare 'YYYY-MM-DD' as M/D/YYYY, from its parts — never new Date(str), which reads it as UTC midnight.
const fmtYmd = (d) => { if (!d) return ''; const [y, m, day] = String(d).slice(0, 10).split('-').map(Number); return `${m}/${day}/${y}`; };
// The Paid tag's tooltip: who marked it, and the date / amount only when known (blank on the bill → not shown).
const paidTitle = (pd) => [
  'Paid' + (pd.at ? ` ${fmtYmd(pd.at)}` : '') + (pd.amount != null ? ` — ${fmtc(pd.amount)}` : ''),
  pd.source === 'staff' ? 'Marked paid by staff' : 'Detected on the scanned bill',
  (!pd.at || pd.amount == null) ? 'Paid date / amount not shown on the bill' : null,
].filter(Boolean).join('\n');

// E2b: which appeal-value keys count as "entered" (used by createParcel to skip empty rows).
const APPEAL_VALUE_KEYS = ['market_land','market_imp','market_pp','cap_deduction','less_exemption_amount','taxable_land','taxable_imp','taxable_pp','less_exemptions_amount','market_total','taxable_subtotal','taxable_total'];
// Parcel-info field split (the RE/PP-immutability model). PER-YEAR fields are state/events that change year to year
// → sourced from parcel_year_data (overlaid onto the base parcels row for the selected year). STABLE fields are
// physical/identity facts fixed for the life of the parcel → ALWAYS read from base parcels, never overlaid/nulled.
// ⛔ appeal_status is DELIBERATELY NOT IN THIS LIST — do not "complete" it by adding it back.
// Every field here falls back to the base `parcels` column when the year row is empty (see the merge below);
// that fallback is what stopped account_number/owner_name/services blanking on parcels with no year row.
// appeal_status must NOT fall back: parcels.appeal_status is RETIRED and holds stale values (pre-2026-08-17,
// plus whatever the base-row mirror kept writing until 2026-08-19 — see PARCEL_BASE_MIRROR), so
// falling back to it re-creates the split-brain dc693be removed (the modal showed 'Under Review' from the dead
// column while the grid showed Value Pending). A year with no status IS Value Pending — the same rule
// StaffPortal.js:768 applies to the Properties grid. It is handled explicitly right after this loop.
const PARCEL_YEAR_FIELDS = ['owner_name','account_number','assessor_bldg_sqft','client_bldg_sqft','nla_sqft','units','parcel_status','exemption_type','exemption_exp_year','appeal_deadline','reappraisal_year','services'];   // nla_sqft/units: divisors for the per-SF figures — year-grained, so they load from the year row like the other measurements
// ⛔ THE ONLY year-grained columns copied down to the base `parcels` row on a current-year save (the compat
// cache for pages that read parcels directly — Properties, Calendar, list rows, scan matching).
//
// THIS IS AN ALLOW-LIST ON PURPOSE. saveParcelFields used to do `Object.assign(baseUpdate, yf)` — a spread of
// the WHOLE year payload — which implicitly asserted that every field in yearFieldsFrom() also exists on
// parcels. It doesn't: nla_sqft and units were added to parcel_year_data alone on 2026-08-16, so from that
// day every current-year save 400'd with "Could not find the 'nla_sqft' column of 'parcels'". Not just NLA
// edits — the payload is built from the whole form, so saving an appeal deadline failed too, on 2026 only
// (older years skip the mirror). A keyed pick inverts the failure mode: a field added to yearFieldsFrom is
// now ABSENT from the base update by default — a stale cache someone notices — rather than a 400 that blocks
// every save.
//
// ⛔ appeal_status IS DELIBERATELY NOT HERE even though the column exists. It is RETIRED (bf573b5): the year
// row is the single source. The spread had been writing it on every current-year save, so that retirement was
// never actually true until this list replaced the spread. Nothing reads it — the modal resolves the year row,
// StaffPortal.js:768 reads parcel_year_data. Do not add it back.
//
// The four with no reader found (client_bldg_sqft, exemption_exp_year, reappraisal_year, exemption_type) are
// KEPT: the survey covered StaffPortal/propertyRollup/Calendar/cascadeMatch/DocumentScanner, not Reports,
// PropertyExecutiveSummary or the CSV exports. Mirroring a column that exists is harmless; dropping one that
// something reads is a stale cache that reads as a data bug. Pruning belongs in its own change.
const PARCEL_BASE_MIRROR = [
  'account_number',       // ⛔ the scan-matching cache — cascadeMatch / DocumentScanner resolve a scanned bill through it
  'appeal_deadline',      // Calendar events + the Properties "Earliest Deadline" roll-up read parcels directly
  'owner_name',           // property/parcel list rows and exports
  'parcel_status',        // Active/Retired filtering and roll-ups
  'assessor_bldg_sqft',   // list columns and per-SF displays outside the modal
  'services',             // list / report columns
  'exemption_type',       // read in a parcels-reading module
  'client_bldg_sqft',     // no reader found — kept, see above
  'exemption_exp_year',   // no reader found — kept
  'reappraisal_year',     // no reader found — kept
];

const PARCEL_STABLE_FIELDS = ['parcel_name','re_pp','legal_description','land_area_sqft','year_built','parcel_type','parcel_type_detail','sigertax_prop_id','sigertax_parcel_id','address','address2','city','state','postal_code','location_code','assessor','assessor_ref_id','consultant_firm'];

// Numeric parcel fields → numeric DB columns. ACCEPT human comma-grouping ("2,829,222"), REJECT malformed grouping
// ("2,829,22" — 2-digit final group), and NEVER silently coerce: blindly stripping commas would turn the typo
// "2,829,22" into 282922 (10× wrong) and write a bad land area. parseNumericField → { ok, value }: `value` is a clean
// Number (or null for blank); `ok:false` when the token isn't a well-formed number → the caller rejects with a message.
function parseNumericField(v) {
  if (v == null || String(v).trim() === '') return { ok: true, value: null };
  const s = String(v).trim();
  const wellFormed = /^\d+(\.\d+)?$/.test(s) || /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s);  // plain OR US thousands-grouped
  if (!wellFormed) return { ok: false, value: null };
  const n = Number(s.replace(/,/g, ''));
  return isNaN(n) ? { ok: false, value: null } : { ok: true, value: n };
}
const NUMERIC_PARCEL_FIELDS = [
  ['land_area_sqft', 'Land Area (sq ft)'], ['year_built', 'Year Built'], ['reappraisal_year', 'Reappraisal Year'],
  ['assessor_bldg_sqft', 'Assessor Bldg Sqft'], ['client_bldg_sqft', 'Client Bldg Sqft'], ['exemption_exp_year', 'Exemption Exp. Year'],
];
// First malformed numeric field's label (for a visible reject message), or null when all are valid/blank.
function validateNumericParcelFields(d) {
  for (const [k, label] of NUMERIC_PARCEL_FIELDS) if (!parseNumericField(d[k]).ok) return label;
  return null;
}

// An appeal level's dates (parcel_appeals, appeal levels only) — kept beside the values, never counted as values.
const APPEAL_DATE_KEYS = ['filed_date', 'hearing_date', 'evidence_due_date'];
function appealRowHasValues(r) { return APPEAL_VALUE_KEYS.some(k => r[k] != null); }

// ── DecisionBar (PER-SECTION): one dropdown — — / Accepted / Appealed / Withdrawn / Denied (Philip, 2026-10-21; Denied 2026-10-26).
//    Accepted / Withdrawn / Denied are stored (parcel_appeal_decisions); Appealed is DERIVED (the next level exists), so
//    while the next level exists the dropdown shows Appealed and is locked — removing that level is the way back.
//    — clears a stored decision. err: the last write was refused → "Not saved".
// The Decision dropdown — in a section's bar, or in a notice group's header when a level has additional notices.
// fit: fill the cell (a notice card's header) instead of the natural width.
function DecisionSelect({ value, locked, lockTitle, onChange, s, c, fit = false }) {
  return (
    <select value={value} disabled={locked} title={locked ? lockTitle : undefined} onChange={e => onChange(e.target.value)}
      style={{ ...s.select, fontSize: '12px', fontWeight: 700,
               ...(fit ? { width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '4px 4px' } : { width: 'auto', padding: '5px 8px' }),
               color: value === 'appealed' ? 'var(--red)' : (value === 'withdrawn' || value === 'denied') ? c.muted : c.text, cursor: locked ? 'not-allowed' : 'pointer' }}>
      <option value="">—</option>
      <option value="accepted">Accepted</option>
      <option value="appealed">Appealed</option>
      <option value="withdrawn">Withdrawn</option>
      <option value="denied">Denied</option>
    </select>
  );
}
const NotSaved = ({ title }) => <span title={title} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--red)' }}>Not saved</span>;
function DecisionBar({ value, locked, lockTitle, onChange, err, s, c }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 18px' }}>
      <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Decision</span>
      <DecisionSelect value={value} locked={locked} lockTitle={lockTitle} onChange={onChange} s={s} c={c} />
      {err && <NotSaved />}
    </div>
  );
}

// Appeal level identity by index (1-based). Keys appeal_one…appeal_four match existing DB rows; appeal_five+
// are generated (uncapped). Titles: 1–4 spelled out, 5+ use the digit ("Appeal 5") per spec.
const APPEAL_KEY_WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const appealKeyFor = (n) => `appeal_${APPEAL_KEY_WORDS[n - 1] || n}`;
const appealTitleFor = (n) => `Appeal ${n <= 4 ? ['One', 'Two', 'Three', 'Four'][n - 1] : n}`;
const appealIndexOf = (key) => { const w = key.replace('appeal_', ''); const i = APPEAL_KEY_WORDS.indexOf(w); return i >= 0 ? i + 1 : (parseInt(w, 10) || 0); };

// ── ValueCell: stable input component for appeal value fields ──
function ValueCell({ level, j, rowKey, initialValue, inputStyle, onCommit }) {
  const fmtNum = (v) => {
    const n = parseSigned(v);
    return n == null ? '' : n.toLocaleString('en-US');
  };
  const [localVal, setLocalVal] = React.useState(() => fmtNum(initialValue));
  React.useEffect(() => { setLocalVal(fmtNum(initialValue)); }, [initialValue]);
  return (
    <input
      value={localVal}
      onChange={e => {
        // Allow digits and commas while typing, and ONE leading minus — a negative value is real data
        // (PTMS carries a −830,093 improvement), not a typo.
        const v = e.target.value;
        const raw = (v.trim().startsWith('-') ? '-' : '') + v.replace(/[^0-9,]/g, '');
        setLocalVal(raw);
      }}
      onBlur={e => {
        const formatted = fmtNum(e.target.value);
        setLocalVal(formatted);
        // Commit the raw numeric value (no commas) to parent
        const numeric = parseSigned(e.target.value) || null;
        onCommit(numeric);
      }}
      placeholder="—"
      style={inputStyle}
    />
  );
}

// The Values tab's notice cards (renderAppealBlock, Philip 2026-10-23): one table per level, each notice's group of
// cells drawn as its own card — s.card's background, border and 12px corners, padded by its own padding cells. Cells
// carry data-b (in a card), data-p (a card's padding), data-l / data-r (a card's left / right edge); the first and last
// rows are the cards' top and bottom padding. A row passes its tint (--tint) and separator colour (--line, with
// data-line) down to its cells, which the cards need instead of the row's own background and border — padding cells
// take neither. Scoped to table.pm-nb. !important where the cells' inline styles set a border of their own.
const NOTICE_BOX_CSS = (c) => `
.pm-nb [data-b] { background: linear-gradient(var(--tint, transparent), var(--tint, transparent)), linear-gradient(${c.card}, ${c.card}); }
.pm-nb [data-b][data-p] { background: linear-gradient(${c.card}, ${c.card}); }
.pm-nb tr[data-hov]:hover > [data-b] { --tint: ${c.rowHover}; }
.pm-nb tr[data-line] > [data-b]:not([data-p]) { border-bottom: 1px solid var(--line) !important; }
.pm-nb [data-l] { border-left: 1px solid ${c.border} !important; }
.pm-nb [data-r] { border-right: 1px solid ${c.border} !important; }
.pm-nb thead tr:first-child > [data-b] { border-top: 1px solid ${c.border} !important; border-top-left-radius: 12px; border-top-right-radius: 12px; }
.pm-nb input[type="date"]::-webkit-calendar-picker-indicator { margin: 0; padding: 0 0 0 2px; }
.pm-nb input[type="date"]::-webkit-datetime-edit { padding: 0; }
.pm-nb tbody tr:last-child > [data-b] { border-bottom: 1px solid ${c.border} !important; border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; }
`;

// `onYearChange(yr)` tells the host the value year moved. sessionStorage['wh_valueYear'] is the shared
// store but it is NOT reactive — no event fires in the same tab — so the host cannot observe a write.
// This is that channel, and doSwitchYear is the one funnel both of this modal's selectors go through.
export default function ParcelModal({ parcel, mode = 'view', property, darkMode, staffUser, onClose, onParcelUpdated, onParcelCreated, onYearChange = null, focusSection = null, focusBill = null }) {
  const isDark = darkMode;
  const asg = useAssignments(); // read-only CM/CS/TS resolution for the Key Contacts panel

  // ── Colours (match StaffPortal) ──────────────────────────────
  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', subtext: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', gold: 'var(--red)',
    rowHover: 'rgba(255,255,255,0.03)', th: 'rgba(26,34,56,0.5)',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: '#565e73', subtext: '#3a4058',
    input: '#ffffff', inputBorder: '#c8ccd8', gold: 'var(--red-dark)',
    rowHover: 'rgba(0,0,0,0.02)', th: 'var(--line-soft)',
  };

  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'visible', marginBottom: '16px' },
    cardHeader: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardTitle: { fontSize: '13px', fontWeight: '700', color: c.text },
    label: { fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' },
    input: { width: '100%', padding: '7px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    inputMono: { width: '100%', maxWidth: '160px', padding: '6px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Mono',monospace", textAlign: 'right' },
    select: { width: '100%', padding: '7px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btn: { padding: '7px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '7px 14px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.subtext, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    badge: (color) => ({ background: `rgba(${color},0.12)`, color: `rgb(${color})`, borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }),
  };

  // ── State ────────────────────────────────────────────────────
  // E2a: create-mode blank seed — linkage from `property` prop (source unresolved; null-safe), parcel_status self-defaults.
  const createSeed = () => ({
    parcel_name: '', re_pp: 'Real Estate', parcel_status: PARCEL_STATUS.active, appeal_status: APPEAL_STATUS.value_pending,
    master_property_id: property?.master_property_id ?? null,
    property_id: property?.property_id ?? property?.id ?? null,
    state: property?.state ?? null,
  });
  const [parcelData, setParcelData] = useState(() => mode === 'create' ? createSeed() : { ...parcel });
  const [savedParcelData, setSavedParcelData] = useState(() => mode === 'create' ? createSeed() : { ...parcel }); // snapshot for diff/revert
  const [editMode, setEditMode] = useState(mode === 'create'); // create = all-editable from open
  // ⛔ LAND AREA IS ENTERED IN EITHER UNIT AND STORED IN ONE. parcels.land_area_sqft is SQUARE FEET and
  // perSf.js:73 divides by it for Land/LSF — so acres typed into a sq-ft field yields a Land/LSF 43,560x
  // too high. That is worse than a blank: perSf.js:13-16 deliberately returns null (rendered "-") for a
  // MISSING divisor precisely so nobody reads a number they cannot reproduce, and a wrong-unit divisor
  // defeats that by looking confident. Assessor sites usually publish acres, so the field has to accept
  // them.
  //
  // ⛔ NO MAGNITUDE HEURISTIC. The unit is DECLARED, never guessed. (DocumentScanner.js:767 guesses for
  // assessor_bldg_sqft — "under 100 and has a decimal means acres" — which silently mis-stores 85 acres
  // as 85 sq ft and a 45.5 sq ft structure as 1,981,980. Not fixed here; see the commit message.)
  //
  // Only the UNIT is state. The value stays in parcelData.land_area_sqft as SQUARE FEET at all times, so
  // every save path (:1106, :1266) and every consumer is untouched. landInput is a typing buffer: null
  // means "not mid-edit, derive the display from the stored value", which is what lets a unit switch
  // CONVERT what is shown rather than reinterpret it — flipping to ACRES on a stored 101,930 shows 2.34,
  // it does not read 101,930 as acres.
  const [landUnit, setLandUnit] = useState('sqft');   // 'sqft' | 'acres' — display only, never persisted
  const [landInput, setLandInput] = useState(null);   // null = show the stored value
  const SQFT_PER_ACRE = 43560;
  const landNum = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')); return isNaN(n) ? null : n; };
  const landStoredSqft = landNum(parcelData.land_area_sqft);
  const landDisplay = landInput !== null ? landInput
    : landStoredSqft == null ? ''
    : landUnit === 'acres' ? String(Math.round((landStoredSqft / SQFT_PER_ACRE) * 10000) / 10000)
    : String(parcelData.land_area_sqft ?? '');
  const setLandFromInput = (raw) => {
    setLandInput(raw);
    const n = landNum(raw);
    setParcelData(p => ({ ...p, land_area_sqft: n == null ? raw : (landUnit === 'acres' ? String(Math.round(n * SQFT_PER_ACRE)) : raw) }));
  };
  const [createError, setCreateError] = useState('');
  const [pendingByYear, setPendingByYear] = useState({}); // E2b: create-mode per-year Proposed Values held in memory until Create Parcel
  const [reviewMode, setReviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // Which installment's bill-fields card is open, as { colId, inst }. The tax grid is now a NAVIGATION
  // surface: clicking an installment row opens its fields here instead of rendering every card inline.
  const [billModal, setBillModal] = useState(null);
  // Reopen: which bill's reason box is open, the typed reason, and the in-flight guard. Keyed by tax_bill_id
  // so two installments cannot share one box.
  const [reopenFor, setReopenFor] = useState(null);
  const [reopenWhy, setReopenWhy] = useState('');
  const [reopenBusy, setReopenBusy] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [kcRoleOpts, setKcRoleOpts] = useState([]);   // Contact Roles lookup (label/code/category) for the Key Contacts panel
  const [kcContacts, setKcContacts] = useState([]);   // resolveContactsForParcel result — scope-matched to this parcel, per role
  // EXPERT roles no longer come from contact_properties: they are per-parcel, per-value-year rows in
  // parcel_contact_roles, resolved as a SET (all rows at max(value_year) <= the viewed year). Attorney
  // Contact is NOT firm personnel and stays in kcContacts with the client/delivery roles.
  const [roleRoster, setRoleRoster] = useState({});      // { role: { year, people[] } }
  const [roleFirm, setRoleFirm] = useState(null);        // the parcel's resolved firm { id, company, year, primary } — Key Contacts' Consultant; defaults the picker
  const [editingRole, setEditingRole] = useState(null);  // role code currently open in the editor
  useEffect(() => {
    if (!contactsOpen) return;
    (async () => {
      if (!kcRoleOpts.length) {
        const lid = (await supabase.from('lookup_lists').select('id').eq('name', 'Contact Roles').maybeSingle()).data?.id;
        if (lid) setKcRoleOpts((await supabase.from('lookup_options').select('label,code,category,sort_order').eq('list_id', lid).eq('active', true).order('sort_order')).data || []);
      }
      const pid = parcel?.property_id || parcelData?.property_id;
      setKcContacts(pid ? await resolveContactsForParcel({ property_id: pid, re_pp: parcelData?.re_pp }) : []);
    })();
    // eslint-disable-next-line
  }, [contactsOpen, parcel?.id, parcelData?.re_pp]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [parcelTypeSearch, setParcelTypeSearch] = useState('');
  const [parcelTypeOpen, setParcelTypeOpen] = useState(false);
  const parcelTypeRef = useRef(null);
  // Close the Parcel Type dropdown on a mousedown OUTSIDE the field (mirrors the ratio popover). Escape is handled
  // on the input. Not onBlur — it fires before an option's onClick and would close the list before the pick registers.
  useEffect(() => {
    if (!parcelTypeOpen) return;
    const onDown = (e) => { if (parcelTypeRef.current && !parcelTypeRef.current.contains(e.target)) { setParcelTypeOpen(false); setParcelTypeSearch(''); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [parcelTypeOpen]);

  // Appeals
  const [appeals, setAppeals] = useState({}); // { proposed: {County: {...}, City: {...}}, appeal_one: {...} }
  // Per-jurisdiction tax rates for (parcel, valueYear) — { [jurisdiction]: { tax_rate, rate_unit, comment } }.
  // ⛔ LEVEL-INDEPENDENT, hence its own table and its own state: a rate belongs to a jurisdiction in a year,
  // not to an appeal level, and holding it per level would let proposed and final diverge — which
  // aggregateSavings would then report as a reduction we had won rather than the assessment cap.
  const [jurRates, setJurRates] = useState({});
  const [savingsBills, setSavingsBills] = useState(new Map());   // this parcel's tax_bills by year, for parcelYearFigures
  const [savingsRefs, setSavingsRefs] = useState(new Map());     // pre-appeal reference bills by year (tax_bill_references)
  const [refScanOpen, setRefScanOpen] = useState(false);
  // The scanned bill's jurisdiction lines for this parcel-year, pooled across collectors, for the rate
  // suggestion on the Proposed panel. Each carries _collectorId so the suggestion can say where it came from.
  const [billLines, setBillLines] = useState([]);
  // The YEAR'S NOTICE rate lines (parcel_year_data.rate_lines), for a year with a notice and no bill — which is
  // every pre-engagement year. Already arrives on the year row's select('*'); it was simply unread.
  const [noticeLines, setNoticeLines] = useState([]);
  const [jurRatesDirty, setJurRatesDirty] = useState(false);
  const [appealsLoading, setAppealsLoading] = useState(true);
  // Open to the app-wide selected Value Year (persisted across parcel opens); else the parcel's sys_year / current year.
  const [valueYear, setValueYear] = useState(() => sessionStorage.getItem('wh_valueYear') || (parcel?.sys_year ? String(parcel.sys_year) : String(new Date().getFullYear())));
  // ⛔ MUST SIT BELOW the valueYear declaration above. This effect's dependency array is evaluated during
  // RENDER, so referencing valueYear from an effect written higher up is a temporal-dead-zone
  // ReferenceError that blanks the whole modal — it compiles, lints as a warning, and throws only in the
  // browser. That is exactly how 6d48353 shipped broken; see also 92be788, same mistake, same shape.
  useEffect(() => {
    if (!contactsOpen || !parcel?.id) return;
    (async () => {
      try {
        const [roster, firm] = await Promise.all([
          resolveRoleRoster(parcel.id, valueYear),
          resolveFirmAssignment(parcel.id, valueYear),
        ]);
        // A row with consultant_id null is a tombstone (the engagement ended) — no firm, as Parcel Details shows.
        const fr = firm.row?.consultant_id ? firm.row : null;
        const primary = fr ? await firmPrimaryContact(fr.consultant_id) : null;
        setRoleRoster(roster);
        setRoleFirm(fr ? { id: fr.consultant_id, company: fr.consultant?.company || null, year: firm.year, primary } : null);
      } catch (e) { console.warn('[ParcelModal] role roster load failed:', e.message); setRoleRoster({}); setRoleFirm(null); }
    })();
  }, [contactsOpen, parcel?.id, valueYear]);
  const [jbCollector, setJbCollector] = useState(null);  // Taxes → which collector's Jurisdiction Breakdown is open
  const [jbBill, setJbBill] = useState(null);            // the tax_bills row backing that breakdown (undefined=loading, null=none) + liveIds
  const [jbEdit, setJbEdit] = useState(null);            // lines being edited by hand (null = not editing)
  const [jbMsg, setJbMsg] = useState('');
  const [jbSaving, setJbSaving] = useState(false);
  // ⛔ THE PANEL BELONGS TO ONE PARCEL-YEAR. It stayed open across a year change showing the previous year's lines, and
  // with editing that would put one year's lines in front of a TS looking at another. Close it instead.
  useEffect(() => { setJbCollector(null); setJbEdit(null); setJbMsg(''); }, [parcel?.id, valueYear]);
  async function openJurisdictionBreakdown(collectorId) {
    if (jbCollector === collectorId) { setJbCollector(null); setJbEdit(null); setJbMsg(''); return; }   // toggle closed
    if (!parcel?.id) return;
    setJbCollector(collectorId); setJbEdit(null); setJbMsg('');
    await loadJurisdictionBreakdown(collectorId);
  }
  async function loadJurisdictionBreakdown(collectorId) {
    setJbBill(undefined);                                                 // loading
    // ⛔ NEWEST LIVE ROW THAT ACTUALLY HAS RATES — not the newest row outright.
    // This used to take `.order('submitted_at' desc).limit(1)` across ALL live rows for the collector, with no
    // regard for whether that row carried a breakdown. A MANUALLY processed bill has none by construction
    // (processBill submits jurisdiction_rates: null / extracted_fields: null), so the moment a second
    // installment was processed it became the newest row and displaced the scanned one holding the real
    // breakdown — the panel read "No jurisdiction breakdown stored" while eleven jurisdictions sat unread on
    // the sibling row. Verified on 4343-019-027/2021: inst 2 (transmitted, manual) 0 jurisdictions, inst 1
    // (filed, scanned) 11, neither superseded.
    //
    // LATENT UNTIL 4509372. The lookup has ALWAYS been wrong for a multi-installment collector; it simply
    // never had a second row to pick until per-card processing made installment 2 processable from the modal.
    //
    // NOT scoped per installment, deliberately: the breakdown is a property of the BILL DOCUMENT, not of an
    // installment — one CA secured bill covers both installments and carries ONE set of rates. Per-installment
    // scoping would correctly show nothing while you are looking at installment 2, which is worse than this.
    //
    // ⛔ WHAT THIS PAPERS OVER, STATED SO IT IS NOT MISTAKEN FOR EXACT: if a collector's installments genuinely
    // came from SEPARATE documents with DIFFERENT rates, this shows whichever was submitted most recently —
    // possibly the wrong one. Same limitation as scanBill's fileExchangeId ("View Tax Bill" opens one
    // installment's PDF) and for the same reason: the panel is per-COLLECTOR while the data is per-BILL.
    // Fixing it properly means making both per-bill, which is a bigger change than either warrants today.
    const { data, error } = await supabase.from('tax_bills')
      .select('id,jurisdiction_rates,total_ad_valorem,taxable_value,installment_number,bill_type,supplemental_seq,submitted_at')
      .eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('collector_id', collectorId)
      .is('superseded_at', null)
      .order('submitted_at', { ascending: false }).limit(50);
    // A swallowed read error renders as "no breakdown stored" — indistinguishable from genuinely having none,
    // which is exactly the ambiguity that made this take a round to diagnose. Say so in the console at least.
    if (error) console.warn('[jurisdictionBreakdown]', error.message);
    // The collector-year's breakdown is the REGULAR bill's: a supplemental (its own prorated lines, its own window) is
    // neither shown here nor overwritten by a save here (Philip, 2026-10-29).
    const regular = (data || []).filter(b => !isSupplementalBill(b));
    const withRates = regular.find(b => Array.isArray(b.jurisdiction_rates) && b.jurisdiction_rates.length > 0);
    const pick = withRates || regular[0] || null;
    // liveIds: every live regular row of the collector-year — hand-entered lines are written to all of them (saveJurisdictionLines).
    setJbBill(pick ? { ...pick, liveIds: regular.map(b => b.id) } : null);
  }
  // ── Jurisdiction lines entered by hand (Philip, 2026-10-16; docs/clark-county-entry-plan.md item 1) ──
  // ⛔ STORED WHERE A SCAN STORES THEM: tax_bills.jurisdiction_rates, on EVERY live installment row of the collector-year,
  // because the lines are ANNUAL and the scan copies one set onto each installment. So the panel, the rate suggestion
  // (billLines), savings method B and the reports read them exactly as they read scanned lines — no second source to
  // union. The bill's totals (billed, ad valorem, non-ad valorem) are NOT touched; the panel's Σ rows show whether the
  // lines reconcile with them. A year with no bill row has nowhere to put lines: the panel says to enter the bill first.
  const startJurisdictionEdit = () => {
    setJbMsg('');
    setJbEdit((jbBill?.jurisdiction_rates || []).map(l => ({ ...l })));
  };
  const editJurisdictionLine = (i, field, value) => setJbEdit(prev => prev.map((l, j) => (j === i ? { ...l, [field]: value } : l)));
  async function saveJurisdictionLines(collectorId) {
    const ids = jbBill?.liveIds || [];
    if (!ids.length || !jbEdit) return;
    const lines = cleanEnteredLines(jbEdit);
    setJbSaving(true); setJbMsg('');
    // ⛔ .select('id') FOR THE AFFECTED ROW COUNT — a policy-blocked write returns 0 rows and NO error.
    const { data, error } = await supabase.from('tax_bills')
      .update({ jurisdiction_rates: lines }).in('id', ids).select('id');
    setJbSaving(false);
    if (error || (data || []).length !== ids.length) {
      setJbMsg(`Couldn't save the jurisdiction lines: ${error ? error.message : `saved on ${(data || []).length} of ${ids.length} installment rows (permission?)`}`);
      return;
    }
    setJbEdit(null);
    setBillsReload(n => n + 1);   // re-read billLines (rate suggestion) from the rows just written
    await loadJurisdictionBreakdown(collectorId);
  }
  // Phase 5 — "View Tax Bill": resolve the scanned PDF via the bill's file_exchange_id (loaded into scanBill)
  // -> file_exchange.storage_path -> a signed URL, and open it. The link is written at scan time but had no
  // reader until now. No bill on file -> a brief inline note (handled at the call site by disabling the button).
  const [billOpenErr, setBillOpenErr] = useState('');
  async function openTaxBillPdf(collectorId) {
    setBillOpenErr('');
    const feId = scanBill[collectorId]?.fileExchangeId;
    if (!feId) { setBillOpenErr('No scanned tax bill on file for this collector/year.'); return; }
    const { data: fe } = await supabase.from('file_exchange').select('storage_path').eq('id', feId).single();
    if (!fe?.storage_path) { setBillOpenErr('Bill record found but its file is missing.'); return; }
    let url;
    try { url = await fileUrl(fe.storage_path, 'open'); }   // R2: server-signed via /api/download-sign (staff-gated + audited)
    catch { setBillOpenErr('Could not open the bill file.'); return; }
    window.open(url, '_blank', 'noopener');
  }
  // Value Year selector range ← the GLOBAL Year Management setting (folder_year_config). Fallback range until it loads.
  const [valueYears, setValueYears] = useState(() => Array.from({ length: 8 }, (_, i) => String(2021 + i))); // 2021-2028 fallback until folder_year_config loads
  const [compareMode, setCompareMode] = useState(false);
  const [compareYears, setCompareYears] = useState([]);
  const [compareData, setCompareData] = useState({}); // { year: { proposed: {County: {...}} } }
  const [proposedOpen, setProposedOpen] = useState(false);
  const [appealCount, setAppealCount] = useState(0); // # of open appeal levels — level n is open iff n <= appealCount (dynamic, uncapped)
  // Additional notices of the year (parcel_notices, step 6): [{ notice_no, type_key, notice_date, _new, _dirty }], by
  // notice_no. Their sections live in the per-level maps under section keys ('proposed@1' — utils/notices).
  const [notices, setNotices] = useState([]);
  const [noticeDepth, setNoticeDepth] = useState({}); // { [notice_no]: # of its open appeal levels } — appealCount's twin
  const [noticeTypes, setNoticeTypes] = useState([]); // notice_types, retired ones too (an existing notice keeps its name)
  const [noticePicker, setNoticePicker] = useState(false); // the Notice Type window is open
  const [nonNtrg, setNonNtrg] = useState({});        // { [notice_no]: true } — that notice's Appeal One was a Non-NTRG appeal (step 7)
  const [nonNtrgErr, setNonNtrgErr] = useState({});  // { [notice_no]: true } — the last tick was refused (Not saved)
  const [removeLevelConfirm, setRemoveLevelConfirm] = useState(null); // appeal-level index pending remove-confirm
  const [collapsed, setCollapsed] = useState({}); // { [level]: true } — UI-only collapsed sections (default expanded)
  // E1a: anchor-nav section refs
  const parcelDetailsRef = useRef(null);
  const appealsRef = useRef(null);
  const taxesRef = useRef(null);
  const complianceRef = useRef(null);
  const notesRef = useRef(null);
  const [notesCount, setNotesCount] = useState(0);      // rolled-up note count → nav badge "Notes (N)"
  const [notesPropName, setNotesPropName] = useState(''); // property name for the "Property note — 18 East" label
  const [propRetire, setPropRetire] = useState(null);     // { first_year, retired_year, reactivated_year } — property side of the retirement cascade
  // ⛔ THE ONE EDITABILITY PREDICATE — declared here so saveParcelFields (:1046) and the header banner/buttons
  // use the SAME rule. They used to compute it separately; a button that enables while the save refuses is
  // two implementations of one question.
  //
  // ⛔ EVALUATED AGAINST THE SAVED **OR** THE PENDING representation, never the pending alone. Gating on the
  // pending value is SELF-DEFEATING: set First Year to 2023 while viewing 2021 and the parcel instantly reads
  // as not-represented, so every save button disables and you can never save the change you just made.
  // Gating on SAVED alone has the mirror flaw — you could not CLEAR a first_year from an excluded year.
  // Either side being represented means "you may save", which is exactly the case where the edit IS the
  // representation. Both sides false = genuinely a year we do not hold; read-only, correctly.
  // ⛔ DATA ENTRY, NOT REPRESENTATION — and renamed to say so. This was `representedInYear(...)`,
  // which coupled "may this year hold data" to "were we engaged" and made a pre-engagement BASELINE
  // year unenterable: on a parcel starting 2026 there was no way to record the 2025 figures the
  // savings are measured against. yearState separates them; a retirement GAP is still closed, because
  // that is a year we had and gave up and writing into one produced the eight stray rows the gates
  // below exist for. Work is still decided by representedInYear, in SQL, which never sees 'baseline'.
  const yearAcceptsData = (pd) => yearState(pd, propRetire, valueYear) !== 'closed';
  // ⛔ ONE DERIVATION FOR "IS THIS YEAR BASELINE", used by the banner AND by both bill-writing paths
  // (processBill, addOffScheduleInstallment). A bill written on a baseline year must land in 'recorded',
  // not 'submitted': expected_bills gates on is_represented, so a submitted one reaches no queue and
  // strands — which is exactly what happened to the September bill this change also repairs. Reads
  // parcelData, the same source the banner reads, so what the screen says and what gets written agree.
  const yearIsBaseline = () => isBaselineYear(parcelData, propRetire, valueYear);
  const scrollToSection = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const [docsOpen, setDocsOpen] = useState(false); // E1a: Documents shortcut
  const [appealSaving, setAppealSaving] = useState(false);
  const [appealSaveMsg, setAppealSaveMsg] = useState('');

  // Resize handle
  const [modalHeight, setModalHeight] = useState(94);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartH = useRef(0);

  function onDragStart(e) {
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartH.current = modalHeight;
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
    e.preventDefault();
  }
  function onDragMove(e) {
    if (!isDragging.current) return;
    const delta = dragStartY.current - e.clientY;
    const newH = Math.min(98, Math.max(30, dragStartH.current + (delta / window.innerHeight * 100)));
    setModalHeight(newH);
  }
  function onDragEnd() {
    isDragging.current = false;
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
  }

  // Tax rate for savings calc
  const [lookups, setLookups] = useState({}); // { 'RE Parcel Type':[{label,code}], 'Exemption':[...], ... }
  const [assessorRatio, setAssessorRatio] = useState(null); // linked assessor's ratio (decimal) — Assessment Ratio default
  const proKey = useRef(0);
  const [proLines, setProLines] = useState(() => [{ key: 0, id: null, property_id: null, other: false, prop_tenant: '', id_code: '', pro_rata_pct: 100 }]); // allocation line items (sum must = 100%)
  const dedKey = useRef(0);
  const [deductions, setDeductions] = useState({}); // { [level]: [ { key, type, amounts: { [jurisdiction]: amount } } ] } — multi-line deductions per appeal level
  const [sectionDeadlines, setSectionDeadlines] = useState({}); // { [level]: 'YYYY-MM-DD' } — per-section deadline (parcel_appeal_deadlines)
  const [levelDates, setLevelDates] = useState({}); // { [level]: { filed_date, hearing_date, evidence_due_date } } — appeal levels only; not on screen (2026-10-21), loaded and written back unchanged
  const [decisions, setDecisions] = useState({}); // { [level]: 'accepted' } — per-section terminal decision (parcel_appeal_decisions); 'appealed' is derived
  const [parcelTaxes, setParcelTaxes] = useState({});      // { [collector_id]: { tax_rate, nonad:{ [installment_number]:[{amount}] } } } — per-collector rate + entered Non-Ad lines
  const [savedParcelTaxes, setSavedParcelTaxes] = useState({}); // snapshot of the loaded/last-saved tax state — drives the tax-section dirty check
  const [taxCollectors, setTaxCollectors] = useState([]);  // full collector records (name, discount_offered) for the parcel's collectors — Taxes blocks
  // Manual bill entry ("Process bill → Approval"): enter a bill by hand in the tax section and drop it into the
  // SAME pipeline a scan does (via submitTaxBill). procForm holds only EDITS; unedited fields fall back to defaults
  // sourced from tax-section state (billed / computed ad-valorem / nav / installment schedule / collector pay-to).
  const [procForm, setProcForm] = useState({});            // { [`${valueYear}:${collectorId}:${instNum}`]: { field: value } } — edits only
  // ⛔ KEYED PER CARD (`${collectorId}:${instNum}`), not per modal. A single procMsg was overwritten by each
  // call of the old collector-level loop, so installment 1 failing and installment 2 succeeding reported only
  // the success. Every outcome now belongs to the card it happened on.
  const [procBusy, setProcBusy] = useState('');            // the card key currently submitting, '' = idle
  const [procMsg, setProcMsg] = useState({});              // { [cardKey]: message }
  const [taxInstallments, setTaxInstallments] = useState({}); // { [collector_id]: [ {installment_number, kind, bill_type, tax_type, due_date, split_pct, adjustment_type, adjustment_pct, ratio} ] } — collector_installments schedule
  const [priorYearTax, setPriorYearTax] = useState({});    // { [collector_id]: total } — prior year's tax section total (parcel_taxes N-1), for the Cook 55% prepay
  const [scanBill, setScanBill] = useState({});            // { [collector_id]: { rate, nav, fileExchangeId } } — scanned tax_bills row for auto-populate + View Tax Bill
  const [billsReload, setBillsReload] = useState(0);
  // Add-an-installment to an OFF-SCHEDULE bill (a CA supplemental's second stub). Keyed by the card's
  // billedScan key so two supplemental cards can't share one form. { key, seq, dueDate, amountDue,
  // adValorem, nonAdValorem, busy, msg }
  const [addOff, setAddOff] = useState(null);       // bumped after a successful submit → re-read tax_bills so the chips update in place
  const [billedScan, setBilledScan] = useState({});        // { [collector_id]: { [installment_number]: { total, adv, nav, discount, penalty } } } — the AUTHORITATIVE billed amounts from the scanned bill (what the client owes → TBT); computeTaxBill is the verification cross-check
  const [propInfo, setPropInfo] = useState({ name: property?.name ?? null, mpid: property?.master_property_id ?? parcel?.master_property_id ?? null }); // property name + MPID → default pro-rata line autofill
  const [propCounty, setPropCounty] = useState(property?.county ?? null); // property's county → prioritizes the assessor dropdown (slice 3)
  const [proShown, setProShown] = useState(false); // pro-rata section revealed? (hidden until [+], or true if saved lines exist)
  const [proDelKey, setProDelKey] = useState(null); // pro-rata line pending confirm-delete
  const [savedProLines, setSavedProLines] = useState([]); // last-loaded/saved allocation baseline (pro-rata ✕ discard target)
  // Pro-rata in reports (Philip 2026-10-28, utils/prorata): every saved line of the parcel (the every-year set and any
  // year's override), whether the lines on screen are this year's override, and the client's properties to pick from.
  const [proRows, setProRows] = useState([]);
  const [proYearOnly, setProYearOnly] = useState(false);
  const [proReload, setProReload] = useState(0);      // bumped after a save → the saved lines are read back
  const [clientProps, setClientProps] = useState([]);
  const [proClientName, setProClientName] = useState('');   // the client's short name, for the Taxes tab's share lines
  const [dirtyLevels, setDirtyLevels] = useState({}); // per-appeal-level unsaved edits (grid + deductions): { [level]: true }
  const [proDirty, setProDirty] = useState(false);    // unsaved pro-rata edits (parcel-scoped; saved via saveParcelFields)
  const [showExitWarn, setShowExitWarn] = useState(false); // unsaved-changes-on-exit dialog
  const [pendingYearSwitch, setPendingYearSwitch] = useState(null); // target year awaiting the Save / Discard / Cancel window
  const markLevelDirty = (level) => setDirtyLevels(prev => prev[level] ? prev : { ...prev, [level]: true });
  // Set a section's deadline; marks the level dirty and (last-write-wins) overwrites the top parcel Appeal
  // Deadline with the entered date. Clearing a section leaves the top field untouched. An additional notice's
  // deadline never touches the top field — that is the original notice's (step 6, decision 3).
  const setSectionDeadline = (level, date) => {
    setSectionDeadlines(prev => ({ ...prev, [level]: date }));
    markLevelDirty(level);
    if (date && isOriginalSection(level)) setParcelData(p => ({ ...p, appeal_deadline: date }));
  };
  const [ratioAudit, setRatioAudit] = useState({}); // append-only ratio-override audit: { 'level|jur': [rows newest-first] }
  const [ratioPop, setRatioPop] = useState(null); // floating Assessment-Ratio reason popover: { level, jur, x, top, via } | null
  const [newComment, setNewComment] = useState(''); // transient new-comment box in the edit popover (appended on Save)
  const [ratioSnapshot, setRatioSnapshot] = useState(null); // prior ratio state to restore if the edit is dismissed w/o a comment
  const ratioPopRef = useRef(null); // popover DOM node — for click-outside containment checks
  const ratioTimer = useRef(null);  // hover-view auto-dismiss timer (3s)

  // Assessor/collector reference link (M2 Phase 1). assessor_ref_id lives in parcelData; these drive the UI.
  const [stateAssessors, setStateAssessors] = useState([]);   // assessors loaded for this parcel's state (picker source)
  const [cascadeCollectors, setCascadeCollectors] = useState([]); // collectors linked to the picked assessor
  const [selCollectorIds, setSelCollectorIds] = useState(() => new Set()); // checked → parcel_collectors (selected year)
  const [savedCollectorIds, setSavedCollectorIds] = useState(() => new Set()); // baseline for the loaded year — collectors dirty-check
  const [collectorAccts, setCollectorAccts] = useState({}); // { [collector_id]: account_number } — the collector's acct # for this parcel/year
  // Where the header's account came from: the viewed year's row, or (no year value) the base parcels row.
  const [acctSource, setAcctSource] = useState({ base: null, year: null });
  const [savedCollectorAccts, setSavedCollectorAccts] = useState({}); // baseline for the loaded year — acct-# dirty-check
  const collectorsRef = useRef(null);   // deep-link scroll target (focusSection === 'collectors', e.g. from the pull worklist)
  // Bill-fields cards, keyed `${collectorId}:${installmentNumber}` — the deep-link target for a Tax Bill
  // Processing row, which is about ONE installment, not the section.
  const billCardRefs = useRef({});

  // Deep-link: scroll a named section into view once, after the modal paints.
  //   'collectors' — from the pull worklist's "Assign collector". Forces edit mode: assigning a collector IS an edit.
  //   'taxes'      — from a Tax Bill Processing row. Does NOT force edit mode: the Taxes section's inputs (billed,
  //                  due, adj) and "Process bill → Approval" are live without it, and flipping the whole parcel
  //                  form into edit mode would be a side effect the TS never asked for.
  useEffect(() => {
    if (focusSection !== 'collectors' && focusSection !== 'taxes') return;
    if (focusSection === 'collectors') setEditMode(true);
    const ref = focusSection === 'collectors' ? collectorsRef : taxesRef;
    // 'taxes' uses block:'start' — the Taxes card is taller than the viewport, so centring it would land
    // mid-table. 'start' puts its header at the top with the collector table immediately below.
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: focusSection === 'taxes' ? 'start' : 'center' }), 350);
    return () => clearTimeout(t);
  }, [focusSection]);

  // ⛔ RE-ANCHOR ONCE AFTER THE TAX CONTENT LOADS. The scroll above fires on a fixed 350ms timer, but the
  // Taxes card is empty until FOUR async loads land (collector versions via resolveVersion,
  // collector_installments, parcel_taxes, tax_bills). Scrolling to a card that is still ~0px tall lands
  // correctly for that instant and then drifts upward — to Proposed Values — as the content mounts and
  // pushes the page down. Timing-dependent, so it looks intermittent rather than broken.
  // Fires once (guarded by the ref, not by state, so it can't loop) when the collectors first arrive.
  // ⛔ RE-ANCHOR ONCE, AND ONLY WHEN THE TARGET ACTUALLY EXISTS. Three independent chains feed this region and
  // the earlier version watched only the first:
  //   A  parcel_collectors → resolveVersion (up to 3 queries/collector) → taxCollectors   [renders the block]
  //   B  parcel_collectors → collector_installments → taxInstallments                     [decides WHICH cards exist]
  //   C  loadAll (parcels, year row, appeals, deductions, deadlines, decisions)            [how tall the content ABOVE is]
  // Keying on A alone scrolled while B and C were still in flight, and C is what pushes the page down — hence
  // landing on an appeal section instead of the card.
  // ⛔ AND THE TARGET MAY NOT EXIST YET: computeTaxBill synthesises a SINGLE {installmentNumber:1} row when
  // installments haven't arrived (taxBill.js:66/131/142), so before B lands the ref map holds only `<col>:1`.
  // A deep link to installment 2 found nothing and the old code quietly fell back to the section — which is
  // what turned "target not ready" into "drifted somewhere plausible". Waiting for the card is the fix; the
  // fallback is now BOUNDED rather than immediate, so an installment with genuinely no card still lands.
  const taxFocusDoneRef = useRef(false);
  useEffect(() => {
    if (focusSection !== 'taxes' || taxFocusDoneRef.current) return;
    if (appealsLoading || !taxCollectors.length) return;         // C still growing, or the block isn't rendered
    const key = focusBill ? `${focusBill.collectorId}:${focusBill.installmentNumber}` : null;
    const go = (el, block) => { taxFocusDoneRef.current = true; el?.scrollIntoView({ behavior: 'smooth', block }); };
    const card = key ? billCardRefs.current[key] : null;
    if (card)       { const t = setTimeout(() => go(card, 'center'), 120); return () => clearTimeout(t); }
    if (!focusBill) { const t = setTimeout(() => go(taxesRef.current, 'start'), 120); return () => clearTimeout(t); }
    // Ready, but this installment's card hasn't rendered. Re-runs (and cancels this timer) the moment B lands;
    // if it never does — the installment isn't in the schedule — fall back to the section rather than nowhere.
    const t = setTimeout(() => {
      const late = billCardRefs.current[key];
      go(late || taxesRef.current, late ? 'center' : 'start');
    }, 1200);
    return () => clearTimeout(t);
  }, [focusSection, focusBill, appealsLoading, taxCollectors.length, taxInstallments]);

  // Lookup-sourced option lists (fall back to the system-managed constants for the two status lists
  // so the dropdowns are never momentarily empty before the query resolves).
  const optLabels = (name) => (lookups[name] || []).map(o => o.label);
  const reTypeOpts = optLabels('RE Parcel Type');
  const ppTypeOpts = optLabels('PP Parcel Type');
  const exemptionOpts = optLabels('Exemption');
  const serviceOpts = optLabels('Services');
  const appealStatusOpts = optLabels('Appeal Status').length ? optLabels('Appeal Status') : Object.values(APPEAL_STATUS);
  const parcelStatusOpts = optLabels('Parcel Status').length ? optLabels('Parcel Status') : Object.values(PARCEL_STATUS);
  const selectedServices = String(parcelData.services || '').split(',').map(s => s.trim()).filter(Boolean);

  // re_pp reads from base (stable). Normalize null/variant to the same default the RE/PP dropdown shows, so the
  // header badge, the Parcel Type (RE)/(PP) list, and the tax math all agree with the dropdown.
  const rePp = parcelData.re_pp || 'Real Estate';
  const isRE = rePp === 'Real Estate';
  const parcelTypes = isRE ? reTypeOpts : ppTypeOpts;
  const filteredParcelTypes = parcelTypes.filter(t => t.toLowerCase().includes(parcelTypeSearch.toLowerCase()));

  // ── Load data ────────────────────────────────────────────────
  useEffect(() => { loadLookupOptions().then(setLookups); }, []); // option lists for the parcel dropdowns

  // Linked assessor's ratio → the Assessment Ratio row's default (before per-cell overrides).
  // assessor_ref_id is the ENTITY anchor; resolve the version for the selected year (Q3 fallback if that year has none).
  useEffect(() => {
    const aid = parcelData.assessor_ref_id;
    if (!aid) { setAssessorRatio(null); return; }
    resolveVersion('assessors', aid, valueYear)
      .then(({ row }) => setAssessorRatio(row?.assessment_ratio ?? null))
      .catch(() => setAssessorRatio(null));
  }, [parcelData.assessor_ref_id, valueYear]);

  // Pro-rata allocation line items for this parcel-year: the year's override, else the every-year set (utils/prorata);
  // one line at 100% to the parcel's own property when there are none.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_prorata').select('*').eq('parcel_id', parcel.id).order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (error) { console.warn('[pro-rata] read failed:', error.message); return; }
        const { lines, yearOnly } = prorataSet(data || [], valueYear);
        const initial = lines.length ? lines.map(toProLine) : [blankProLine(100, true)];
        setProRows(data || []);
        setProYearOnly(yearOnly);
        setProLines(initial);
        setSavedProLines(initial); // baseline for the pro-rata ✕ discard-unsaved
        setProShown(lines.length > 0); // reveal only when the year already has saved allocations
      });
  }, [parcel?.id, mode, valueYear, proReload]);

  // A pro-rata line's pick-list: the client's properties with a parcel under this parcel's assessor, and always the
  // parcel's own property (utils/prorata sameAssessorProperties). Paged on the id (unique), sorted here by name.
  const homePropId = parcel?.property_id || parcelData.property_id || property?.id || null;
  useEffect(() => {
    if (!homePropId) { setClientProps([]); return undefined; }
    let alive = true;
    (async () => {
      const { data: home, error } = await supabase.from('properties').select('client_id').eq('id', homePropId).maybeSingle();
      if (error) throw error;
      if (!home?.client_id) return;
      const { data: cl, error: clErr } = await supabase.from('clients').select('name,short_name').eq('id', home.client_id).maybeSingle();
      if (clErr) throw clErr;
      if (alive) setProClientName(cl?.short_name || cl?.name || '');
      const rows = await fetchAll('properties', 'id,name,master_property_id', (q) => q.eq('client_id', home.client_id).order('id'));
      const theirs = await fetchByIds('parcels', 'property_id,assessor,assessor_ref_id', 'property_id', rows.map(p => p.id));
      const picked = sameAssessorProperties(rows, theirs, { assessor: parcelData.assessor, assessor_ref_id: parcelData.assessor_ref_id }, homePropId);
      if (alive) setClientProps(picked.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    })().catch((e) => console.warn('[pro-rata] client properties read failed:', e?.message || e));
    return () => { alive = false; };
  }, [homePropId, parcelData.assessor, parcelData.assessor_ref_id]);

  // Append-only Assessment-Ratio override audit — grouped by 'level|jurisdiction', newest first, for popover history.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_ratio_audit').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear).order('created_at', { ascending: false })
      .then(({ data }) => {
        const g = {};
        (data || []).forEach(r => { const k = `${sectionKey(r.appeal_level, r.notice_no)}|${r.jurisdiction}`; (g[k] = g[k] || []).push(r); });
        setRatioAudit(g);
      });
  }, [parcel?.id, valueYear, mode]);

  // Multi-line deductions per section (every notice). Rows share a line_index across jurisdictions (same deduction,
  // one amount per jurisdiction column). Grouped into { level: [ { type, amounts:{jur:amt} } ] }.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_appeal_deductions').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear)
      .then(({ data }) => {
        const byLevel = {};
        (data || []).forEach(r => {
          const lv = sectionKey(r.appeal_level, r.notice_no), li = r.line_index ?? 0;
          byLevel[lv] = byLevel[lv] || {};
          byLevel[lv][li] = byLevel[lv][li] || { key: ++dedKey.current, type: '', amounts: {} };
          if (r.deduction_type) byLevel[lv][li].type = r.deduction_type;
          if (r.amount != null) byLevel[lv][li].amounts[r.jurisdiction] = r.amount;
        });
        const out = {};
        Object.keys(byLevel).forEach(lv => {
          out[lv] = Object.keys(byLevel[lv]).sort((a, b) => Number(a) - Number(b)).map(li => byLevel[lv][li]);
        });
        setDeductions(out);
      });
  }, [parcel?.id, valueYear, mode]);

  // Per-section deadlines for this parcel/year, keyed by section — every notice (does NOT touch the top field on load).
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_appeal_deadlines').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(r => { if (r.deadline) m[sectionKey(r.appeal_level, r.notice_no)] = r.deadline; });
        setSectionDeadlines(m);
      });
  }, [parcel?.id, valueYear, mode]);

  // Notice types, retired ones too: a notice keeps its type's name; only active ones are offered in the window.
  useEffect(() => {
    if (mode === 'create') return;
    supabase.from('notice_types').select('key, name, active, sort').then(({ data, error }) => {
      if (error) console.warn('[notice_types] read:', error.message);
      setNoticeTypes([...(data || [])].sort((a, b) => (a.sort - b.sort) || a.name.localeCompare(b.name)));
    });
  }, [mode]);

  // Per-section terminal decisions, keyed by section — every notice ('appealed' is derived from the next level existing).
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_appeal_decisions').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(r => { if (r.decision) m[sectionKey(r.appeal_level, r.notice_no)] = r.decision; });
        setDecisions(m);
      });
  }, [parcel?.id, valueYear, mode]);

  // Per-collector tax rows for this parcel/year (parcel_taxes). Keyed by collector_id.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_taxes').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(r => {
          // Reconstruct EVERY override the save writes into `installments`, or the field round-trips to empty:
          // NAV (single line), billed-per-installment (record of truth), and the per-installment due date.
          // ⛔ OVERRIDES LIVE IN THEIR OWN LINE, AND ONLY A DELIBERATE EDIT PUTS THEM THERE.
          // tax_rate, non-ad and ad-valorem are TRANSCRIBED FACTS: tax_bills is authoritative for them, and
          // parcel_taxes holds a value only where a TS overrode it. Before this, the card read
          // `pt.X ?? scan.X` with pt holding whatever the last Save All snapshotted off the scan — so a
          // stale cache and a deliberate override were THE SAME SHAPE and neither the code nor a person
          // could tell them apart. 8940-436-128/2022 showed rate "1" and non-ad 4,921.98 from a 09-22 save
          // while the bill said 1.16552 and 1,146.36, and a rescan could never dislodge it.
          //
          // ⛔ THE LEGACY r.tax_rate COLUMN AND 'nonad' LINE ARE DELIBERATELY NOT READ. They cannot have
          // been overrides — overrides did not live there — so under the ruling they are stale by
          // definition. That is what retires the existing rows without a migration: nothing reads them,
          // and the next Save All stops writing them. Values stay on disk as inert residue.
          // ⛔ NOR IS AN OVERRIDE'S tax_rate (Philip 2026-10-27): a rate typed on this tab is the Tax Rate row's now
          // (parcel_jurisdiction_rates) — one stored rate for the cell and the row. The two typed before that were moved
          // there by 2026-10-27_typed_rates_to_tax_rate_row.
          let ovr = {}; const billed = {}, dueOv = {}, draft = {}, advOv = {}, adj = {};
          (Array.isArray(r.installments) ? r.installments : []).forEach(line => {
            if (line && line.type === 'override') ovr = { ...line };
            if (line && line.type === 'advalorem' && line.installment_number != null) {
              // ⛔ line.amount IS NOT AN OVERRIDE AND MUST NOT BE HYDRATED AS ONE. It is effB() — the
              // RESOLVED figure, override-or-scan collapsed into one number — and reading it back into
              // bMan promoted a scan-derived amount to an override that then outranked the corrected
              // bill. One Save All plus one reload was enough. Slower to show than the rate (invisible
              // until the modal is reopened), and worse in consequence: this is the figure the TBT
              // transmits and the base next year's Cook 55% prepay reads.
              // It is still WRITTEN, for TaxBillWorklist's has-this-been-billed signal — which is
              // load-bearing for a multi-stub CA bill, where tax_bills has one row and this has two.
              if (line.override && line.override.amount != null) billed[line.installment_number] = String(line.override.amount);
              // DRAFT: the Bill-fields card values that have no other home. Kept as its own sub-object so the
              // line's long-standing keys (amount/computed/due_date/split_pct/adj_type) keep their meaning.
              if (line.draft && typeof line.draft === 'object') draft[line.installment_number] = line.draft;
              // Only a REAL date becomes an override. collectorTaxModel treats any `!== undefined` entry as an
              // override, so seeding '' here would MASK collector_installments' scheduled date instead of
              // falling back to it. Stored as a `date` → already 'YYYY-MM-DD', the shape the input wants.
              if (line.due_date) dueOv[line.installment_number] = line.due_date;
              // Per-installment ad-valorem override (the annual figure for THAT bill), same rule as above.
              if (line.override && line.override.ad_valorem != null) advOv[line.installment_number] = line.override.ad_valorem;
              // Adj Type / value typed on this card (an Abatement's dollars, a Discount's %). Present only where
              // someone set one; line.adj_type is the RESOLVED type and is never read back as an override.
              if (line.override && ('adj_type' in line.override || 'adj_value' in line.override)) {
                const a = {};
                if ('adj_type' in line.override) a.type = line.override.adj_type ?? '';
                if ('adj_value' in line.override) a.pct = line.override.adj_value == null ? '' : String(line.override.adj_value);
                adj[line.installment_number] = a;
              }
            }
          });
          m[r.collector_id] = { nav: ovr.nav ?? undefined, billed, dueOv, draft, advOv, adj, proposed_tax: r.proposed_tax, final_tax: r.final_tax };
        });
        setParcelTaxes(m); setSavedParcelTaxes(JSON.parse(JSON.stringify(m)));
      });
  }, [parcel?.id, valueYear, mode]);

  // Property name + MPID for the default pro-rata line. In create mode they arrive via the `property` prop;
  // in view/edit only the parcel's master_property_id is present, so fetch the name by MPID.
  useEffect(() => {
    const mpid = property?.master_property_id ?? parcel?.master_property_id ?? null;
    if (property?.name) { setPropInfo({ name: property.name, mpid }); setPropCounty(property.county ?? null); return; }
    if (!mpid) { setPropInfo({ name: null, mpid: null }); setPropCounty(null); return; }
    supabase.from('properties').select('name, master_property_id, county').eq('master_property_id', mpid).limit(1)
      .then(({ data }) => { const p = (data || [])[0]; setPropInfo({ name: p?.name ?? null, mpid }); setPropCounty(p?.county ?? null); });
  }, [property, parcel?.master_property_id]);

  // Autofill ONLY the pristine default allocation line (single, unsaved, empty) from the property — never
  // clobber a value already typed or a saved allocation (those load with id != null or non-empty fields).
  useEffect(() => {
    if (propInfo.name == null && propInfo.mpid == null) return;
    setProLines(prev => {
      if (prev.length !== 1) return prev;
      const l = prev[0];
      if (l.id != null) return prev;                                              // saved line — leave as-is
      if (l.property_id || l.other || (l.prop_tenant || '').trim() || (l.id_code || '').trim()) return prev;  // already has input
      return [{ ...l, property_id: homePropId, prop_tenant: propInfo.name || '', id_code: propInfo.mpid != null ? String(propInfo.mpid) : '' }];
    });
  }, [propInfo, homePropId]);

  useEffect(() => {
    loadAll();
  }, [parcel?.id, valueYear]);

  // The Value Year selectors list the GLOBAL year range (Year Management → folder_year_config), so rolled years
  // (e.g. 2027) appear automatically. Years without data for this parcel just show blank — that's expected.
  useEffect(() => {
    supabase.from('folder_year_config').select('min_year,current_max_year').single()
      .then(({ data }) => {
        if (data && data.min_year && data.current_max_year && data.current_max_year >= data.min_year) {
          setValueYears(Array.from({ length: data.current_max_year - data.min_year + 1 }, (_, i) => String(data.min_year + i)));
        }
      });
  }, []);

  // Assessors loaded for this parcel's state → drives picker-vs-free-text. Empty = no reference data yet.
  useEffect(() => {
    const st = parcelData.state;
    if (!st) { setStateAssessors([]); return; }
    // Load entity_id and DEDUPE by entity — after the year-version roll each assessor has many rows (one per
    // value_year), but the picker selects an ENTITY ("which assessor"), so show one option per entity anchor.
    // Paginate: one state's active version-rows already approach the 1000-row cap (IL ~816), so a single
    // response would silently drop the tail (assessors past ~"M") once a state crosses it.
    (async () => {
      const rows = [];
      for (let from = 0; ; from += 1000) {
        const { data } = await supabase.from('assessors').select('id,entity_id,name,state,county')
          .eq('state', st).eq('active', true).order('name').range(from, from + 999);
        rows.push(...(data || []));
        if (!data || data.length < 1000) break;
      }
      const seen = new Set();
      setStateAssessors(rows.filter(a => {
        if (!a.entity_id || seen.has(a.entity_id)) return false;
        seen.add(a.entity_id); return true;
      }));
    })();
  }, [parcelData.state]);

  // Slice 3 — split the state's assessors into the property's-county matches (PINNED at the top of the dropdown) and
  // the rest (listed once, below). Never hard-filter: every assessor is reachable, just not listed twice. Blank/no
  // match → matched:[] → dropdown renders the flat list exactly as before (no regression).
  const assessorGroups = useMemo(() => {
    const pc = (propCounty || '').trim().toLowerCase();
    if (!pc) return { matched: [], rest: stateAssessors };
    const matched = [], rest = [];
    for (const a of stateAssessors) {
      ((countyFromAssessor(a) || '').trim().toLowerCase() === pc ? matched : rest).push(a);
    }
    return { matched, rest };
  }, [stateAssessors, propCounty]);

  // Collectors linked to the picked assessor (cascade source) via assessor_collectors.
  // Both sides are ENTITY anchors now (the FK re-point killed the collectors(*) embed → PGRST200), so read the
  // junction's collector_id (an entity_id) then resolve each collector's version for the selected year. id := entity_id
  // so the option values / selCollectorIds comparisons keep working against parcel_collectors.collector_id.
  useEffect(() => {
    const aid = parcelData.assessor_ref_id;
    if (!aid) { setCascadeCollectors([]); return; }
    let cancelled = false;
    (async () => {
      const { data: links } = await supabase.from('assessor_collectors').select('collector_id').eq('assessor_id', aid);
      const eids = [...new Set((links || []).map(l => l.collector_id).filter(Boolean))];
      const rows = await Promise.all(eids.map(eid => resolveVersion('collectors', eid, valueYear).then(r => r.row).catch(() => null)));
      if (cancelled) return;
      setCascadeCollectors(rows.filter(Boolean).map(r => ({ ...r, id: r.entity_id }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    })();
    return () => { cancelled = true; };
  }, [parcelData.assessor_ref_id, valueYear]);

  // Existing parcel_collectors for the SELECTED year (edit mode) → pre-check the cascade.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) return;
    supabase.from('parcel_collectors').select('collector_id,account_number').eq('parcel_id', parcel.id).eq('active', true).eq('tax_year', valueYear)
      .then(({ data }) => {
        const ids = new Set((data || []).map(r => r.collector_id)); setSelCollectorIds(ids); setSavedCollectorIds(new Set(ids));
        const accts = Object.fromEntries((data || []).map(r => [r.collector_id, r.account_number || '']));
        setCollectorAccts(accts); setSavedCollectorAccts(accts);
      });
  }, [parcel?.id, mode, valueYear]);

  // Full collector records + installment schedules for the parcel's collectors (drives the Taxes blocks).
  // selCollectorIds are ENTITY anchors (parcel_collectors.collector_id); resolve each to its version for the selected
  // year (id := entity_id so taxInstallments/parcelTaxes keying holds), and scope installments to that tax_year.
  useEffect(() => {
    const ids = [...selCollectorIds];
    if (!ids.length) { setTaxCollectors([]); setTaxInstallments({}); return; }
    let cancelled = false;
    (async () => {
      const rows = await Promise.all(ids.map(eid => resolveVersion('collectors', eid, valueYear).then(r => r.row).catch(() => null)));
      if (cancelled) return;
      setTaxCollectors(rows.filter(Boolean).map(r => ({ id: r.entity_id, name: r.name, discount_offered: r.discount_offered, nav_billed_on: r.nav_billed_on || 'split',
        mailing_address_1: r.mailing_address_1, mailing_address_2: r.mailing_address_2, mailing_city: r.mailing_city, mailing_state: r.mailing_state, mailing_zip: r.mailing_zip }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    })();
    // ASSUMPTION (not a DB guarantee): keying installments off valueYear presumes the value year and the billing
    // year coincide. collector_installments is the one table here this modal never writes — its tax_year was
    // backfilled from the DUE DATE (2026-07-10_taxbill_stageA_workflow.sql: extract(year from due_date)), so it is a
    // billing-calendar year owned elsewhere, while every other read/write below keys off the value year this modal
    // wrote. They are equal today (all installments are 2026) and the schema does model them separately —
    // tax_bills carries both tax_year and value_year. Revisit this line if a jurisdiction ever bills a value year in
    // a different calendar year; the fix is to key installments off the bill year, not the value year.
    supabase.from('collector_installments').select('collector_id,installment_number,kind,bill_type,tax_type,due_date,split_pct,adjustment_type,adjustment_pct,ratio').in('collector_id', ids).eq('tax_year', valueYear).order('installment_number')
      .then(({ data }) => {
        if (cancelled) return;
        const m = {};
        (data || []).forEach(r => { (m[r.collector_id] = m[r.collector_id] || []).push(r); });
        setTaxInstallments(m);
      });
    return () => { cancelled = true; };
  }, [selCollectorIds, valueYear]);

  // Prior year's tax-section total per collector (parcel_taxes for year N-1) — the base for Cook's 55%
  // first-installment prepay. Summed over that collector's prior rows. Absent => the calculator shows
  // "prior year not entered" rather than computing from null.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) { setPriorYearTax({}); return; }
    const prevYear = String(Number(valueYear) - 1);
    let cancelled = false;
    // ⛔ REBUILT PER INSTALLMENT, NOT READ FROM THE STORED SCALAR. parcel_taxes.amount is Σ effBilled
    // frozen at the last Save All, so a prior-year bill corrected afterwards never reached this number —
    // and this number is multiplied by 0.55 to produce a Cook prepay a client pays.
    //
    // "Was it an override" is UNANSWERABLE for the total: it is a sum, not something anyone typed. So the
    // total is not asked. Override-ness exists only at installment grain, where a person typed it, and the
    // base is rebuilt as Σ (override.amount ?? the bill) — the same resolution the current year uses.
    //
    // ⛔ SCHEDULED INSTALLMENTS ONLY. A supplemental is a mid-year reassessment, not part of the scheduled
    // annual the prepay is computed off, and the old scalar excluded them by construction (offScheduleKey
    // kept them out of model.rows). Summing tax_bills naively would let one inflate next year's prepay.
    //
    // Rejected bills DO count: a rejected bill is still what the county billed, and excluding it would drop
    // the base to zero mid-correction — precisely when someone is most likely to be looking at it.
    (async () => {
      const [{ data: bills }, { data: pts }] = await Promise.all([
        supabase.from('tax_bills')
          .select('collector_id,installment_number,supplemental_seq,total_bill_amount')
          .eq('parcel_id', parcel.id).eq('tax_year', prevYear).is('superseded_at', null),
        supabase.from('parcel_taxes')
          .select('collector_id,installments').eq('parcel_id', parcel.id).eq('tax_year', prevYear),
      ]);
      if (cancelled) return;
      const ovr = {};
      (pts || []).forEach(r => {
        (Array.isArray(r.installments) ? r.installments : []).forEach(line => {
          if (line && line.type === 'advalorem' && line.installment_number != null
              && line.override && line.override.amount != null) {
            (ovr[r.collector_id] = ovr[r.collector_id] || {})[line.installment_number] = Number(line.override.amount);
          }
        });
      });
      const billed = {};
      (bills || []).forEach(b => {
        if (b.installment_number == null || b.supplemental_seq != null || b.total_bill_amount == null) return;
        (billed[b.collector_id] = billed[b.collector_id] || {})[b.installment_number] = Number(b.total_bill_amount);
      });
      const m = {};
      new Set([...Object.keys(billed), ...Object.keys(ovr)]).forEach(cid => {
        const insts = new Set([...Object.keys(billed[cid] || {}), ...Object.keys(ovr[cid] || {})]);
        let sum = 0, any = false;
        insts.forEach(i => {
          const v = (ovr[cid] || {})[i] ?? (billed[cid] || {})[i];
          if (v != null && Number.isFinite(v)) { sum += v; any = true; }
        });
        if (any) m[cid] = Number(sum.toFixed(2));
      });
      setPriorYearTax(m);
    })();
    return () => { cancelled = true; };
  }, [parcel?.id, valueYear, mode]);

  // Scanned tax_bills for this parcel+year → (a) collector-level auto-populate for rate / non-ad-valorem + the
  // View-Tax-Bill link, and (b) the AUTHORITATIVE billed amounts keyed per installment (tax_bills is one row per
  // installment). Billed is the number of record that flows to the TBT; the computed figure is the cross-check.
  useEffect(() => {
    if (mode === 'create' || !parcel?.id) { setScanBill({}); setBilledScan({}); return; }
    let cancelled = false;
    // ⛔ due_date AND delinquent_date ARE IN THIS LIST ON PURPOSE. submitTaxBill writes both, and this select
    // is the ONLY thing that brings them back to the form. due_date was written by submitTaxBill:72 and
    // omitted here, so a processed bill's due date never returned — a field that saves and comes back blank,
    // the same write-list/read-list split as the NLA bug and the installments due-date bug. Anything added to
    // the write must be added here too.
    // billed_amount / paid_* / discount_available / discount_derived / needs_review / review_note (migration
    // 2026-10-08_tax_bills_paid_and_billed; paid_proof / paid_note / paid_recorded_by from 2026-10-13_tax_bills_payment_proof,
    // read through the same fallback) are read when the columns exist. ⛔ IF THEY DON'T YET (the migration not
    // applied), the read retries with the original list and the tab behaves exactly as before — so the code and the
    // migration need not land at the same moment.
    const BASE_COLS = 'id,collector_id,installment_number,bill_type,supplemental_seq,status,due_date,delinquent_date,account_number,discount_date,total_tax_rate,taxable_value,jurisdiction_rates,total_ad_valorem,total_non_ad_valorem,discount_amount,penalty_fees,total_bill_amount,file_exchange_id';
    const PAID_COLS = ',billed_amount,paid_at,paid_amount,paid_source,paid_proof,paid_note,paid_recorded_by,discount_available,discount_derived,needs_review,review_note';
    const readBills = (cols) => supabase.from('tax_bills').select(cols)
      .eq('parcel_id', parcel.id).eq('tax_year', String(valueYear)).is('superseded_at', null)
      .order('installment_number');
    readBills(BASE_COLS + PAID_COLS)
      .then((res) => (res.error && res.error.code === '42703') ? readBills(BASE_COLS) : res)
      .then(({ data }) => {
        if (cancelled) return;
        const m = {}, b = {};
        // ⛔ ONE ARRAY PER COLLECTOR, NOT PER ROW. jurisdiction_rates is ANNUAL and duplicated onto every
        // installment of the same bill, so pooling every row would double or triple the line count and the
        // suggested rate with it. The query is ordered by installment_number, so the first row carrying a
        // non-empty array is the deterministic pick — the same rule the rate/nav fold below uses, and the
        // same one pickRateLines applies in the report layer.
        const jl = [];
        const linesSeen = new Set();
        (data || []).forEach(r => {
          if (!r.collector_id) return;
          // ⛔ PER-COLLECTOR FOLD — FIRST ROW WINS, and that is only defensible because of the .order() above.
          // Without it PostgREST row order is arbitrary, so `rate`, `nav` and `fileExchangeId` came from a
          // RANDOM installment: "View Tax Bill" (:258) opened whichever one won the race, and rate/nav flowed
          // into procValues → submitTaxBill, i.e. into written data. The ordering makes the pick deterministic
          // (lowest installment number).
          // ⛔ IT DOES NOT MAKE THE FOLD CORRECT, and moving `status` out of it below does NOT close this.
          // rate/nav are genuinely per-collector-year so one installment's value is fine; fileExchangeId is
          // NOT — a collector whose installments were scanned from SEPARATE documents will still open the
          // wrong PDF. That needs fileExchangeId keyed per installment, which is its own change.
          // The year's rate comes from yearBill (reports/taxRate pickYearBill — the lowest regular installment that isn't
          // rejected), the same bill the reports read, set once the fold is done.
          if (!m[r.collector_id]) m[r.collector_id] = { nav: r.total_non_ad_valorem, fileExchangeId: r.file_exchange_id };
          // A supplemental's lines are prorated, on its own year's rate — never the collector-year's (Philip, 2026-10-29).
          if (!linesSeen.has(r.collector_id) && !isSupplementalBill(r) && Array.isArray(r.jurisdiction_rates) && r.jurisdiction_rates.length) {
            linesSeen.add(r.collector_id);
            r.jurisdiction_rates.forEach(l => { if (l) jl.push({ ...l, _collectorId: r.collector_id }); });
          }
          // ⛔ NEVER COERCE A NULL INSTALLMENT TO 1. This was `r.installment_number ?? 1`, and because the
          // .order() above puts NULLs LAST (Postgres ASC default), an off-schedule bill was written to key 1
          // AFTER the real installment 1 — overwriting it. On 8940-436-116/2022 the installment-1 card was
          // showing a SUPPLEMENTAL's figures (1,421.93, due 2023-05-31, account …22 010 33) instead of the
          // annual bill's. Not a missing number on screen: a wrong one, which is the kind people act on.
          // Off-schedule bills now live in their own key namespace — the '#' is what marks them, since JS
          // object keys are strings and a bare 1 and '1' are the same key.
          const inst = offScheduleKey(r);
          (b[r.collector_id] = b[r.collector_id] || {})[inst] = {
            // ⛔ total = BILLED (the full tax, before discount); amountDue = what the bill PRINTS as due. They differ on
            // a paid bill (due $0) and on a Florida bill (due = the discounted first tier). billed_amount is new
            // (2026-10-08); a bill without it falls back to total_bill_amount — exactly the old behaviour.
            total: r.billed_amount != null ? r.billed_amount : r.total_bill_amount,
            amountDue: r.total_bill_amount,
            billedIsFull: r.billed_amount != null,   // false = an older bill whose "billed" is really its Amount Due
            adv: r.total_ad_valorem, nav: r.total_non_ad_valorem,
            discount: r.discount_amount, penalty: r.penalty_fees,
            discountAvailable: r.discount_available ?? null, discountDerived: !!r.discount_derived,
            paid: r.paid_source ? { source: r.paid_source, at: r.paid_at || null, amount: r.paid_amount ?? null,
                                    proof: r.paid_proof || null, note: r.paid_note || null, recordedBy: r.paid_recorded_by || null } : null,
            needsReview: !!r.needs_review, reviewNote: r.review_note || null,
            // Per-installment workflow state. THE status lives here, never on the per-collector fold above:
            // one collector can hold installment 1 'filed' and installment 2 not yet processed at all.
            status: r.status, taxBillId: r.id,
            // ⛔ PER BILL, not the per-collector fold above. Adding an installment to an off-schedule bill
            // copies this onto the new row so BOTH halves point at the ONE scanned document — a CA
            // supplemental prints both stubs on a single PDF. (The per-collector fold's fileExchangeId is
            // still "first installment wins"; the View-Tax-Bill gap noted at :812 is now one field away from
            // being fixable, but changing that control is its own change.)
            fileExchangeId: r.file_exchange_id,
            dueDate: r.due_date, delinquentDate: r.delinquent_date, discountDate: r.discount_date,
            accountNumber: r.account_number,
          };
        });
        Object.keys(m).forEach((cid) => { m[cid].yearBill = pickYearBill((data || []).filter((r) => r.collector_id === cid)); });
        setScanBill(m); setBilledScan(b); setBillLines(jl);
      });
    return () => { cancelled = true; };
  }, [parcel?.id, valueYear, mode, billsReload]);   // billsReload: re-read after a submit so the chips update in place

  // Key for a tax_bills row inside billedScan. A SCHEDULED bill keys on its collector installment number, so
  // every existing `bScan[installmentNumber]` lookup is untouched. An OFF-SCHEDULE bill (Supplemental /
  // Delinquent — installment_number NULL) gets its own namespace, disambiguated by its own sequence and
  // finally by row id so two of them can never collide the way they used to.
  const offScheduleKey = (r) => r.installment_number != null
    ? r.installment_number
    : `${r.bill_type || 'Off-schedule'}#${r.supplemental_seq ?? r.id}`;
  const isOffScheduleKey = (k) => String(k).includes('#');

  // Property name for the Notes rollup label ("Property note — 18 East").
  useEffect(() => {
    const pid = parcel?.property_id || parcelData?.property_id;
    if (!pid) { setNotesPropName(''); setPropRetire(null); return; }
    supabase.from('properties').select('name,retired_year,reactivated_year').eq('id', pid).maybeSingle().then(({ data }) => {
      setNotesPropName(data?.name || '');
      setPropRetire(data ? { retired_year: data.retired_year, reactivated_year: data.reactivated_year } : null);
    });
    // eslint-disable-next-line
  }, [parcel?.property_id, parcelData?.property_id]);

  async function loadAll() {
    if (mode === 'create') { setAppealsLoading(false); return; } // E2a: no DB reads on a nonexistent id
    setAppealsLoading(true);

    // Identity/links/value-caches from parcels; the year-varying info is overlaid from parcel_year_data
    // for the SELECTED year. If this year has no snapshot yet, those fields render BLANK (Stage-4 copy-forward
    // will create the row). Re-runs on valueYear change (this effect's deps), so switching years reloads the year.
    const { data: freshParcel } = await supabase.from('parcels').select('*').eq('id', parcel.id).single();
    const { data: yearRow } = await supabase.from('parcel_year_data').select('*').eq('parcel_id', parcel.id).eq('tax_year', valueYear).maybeSingle();
    // ⛔ ALREADY FETCHED, NEVER READ. This row is loaded with select('*') for the parcel-field overlay, so
    // rate_lines has been arriving since the capture shipped and nothing consumed it. No new query, and no risk
    // of the read-but-not-selected failure: select('*') cannot omit a column.
    setNoticeLines(Array.isArray(yearRow?.rate_lines) ? yearRow.rate_lines : []);
    if (freshParcel) {
      // Year-varying info comes from the year's snapshot; if this year has NO row yet, blank those fields
      // (they'll fill it) — identity/links/value-caches stay from parcels. Stage-4 copy-forward creates the row.
      const merged = { ...freshParcel };
      // per-year ← the year's snapshot; FALL BACK to the base parcels value when this year has no row OR the
      // row's field is empty — never blank a populated base field (this caused the account_number/owner_name/
      // services data-loss on parcels that have no parcel_year_data row yet).
      PARCEL_YEAR_FIELDS.forEach(k => {
        const yv = yearRow ? yearRow[k] : null;
        merged[k] = (yv === null || yv === undefined || yv === '') ? freshParcel[k] : yv;
      });
      // appeal_status: THE YEAR ROW OR NOTHING (never freshParcel — that column is retired). No row for this
      // year, or a null in it, means the year hasn't been worked yet → Value Pending. Matches StaffPortal.js:768.
      merged.appeal_status = (yearRow && yearRow.appeal_status) || APPEAL_STATUS.value_pending;
      PARCEL_STABLE_FIELDS.forEach(k => { merged[k] = freshParcel[k]; });               // stable ← base parcels, always (immutable)
      setParcelData(merged); setSavedParcelData(merged);
      setAcctSource({ base: freshParcel.account_number || null, year: (yearRow && yearRow.account_number) || null });
    }

    // Load portal users for this client
    if (parcel.client_name) {
      const { data: pu } = await supabase.from('client_portal_users').select('id,name,email').eq('client_id', parcel.client_name).order('name');
      setPortalUsers(pu || []);
    }

    // Load appeals
    // Every notice of the year: the original under its plain level keys, an additional notice under 'level@n'.
    const { data: ap } = await supabase.from('parcel_appeals')
      .select('*')
      .eq('parcel_id', parcel.id)
      .eq('tax_year', valueYear)
      .order('appeal_level')
      .order('is_primary', { ascending: false });

    // Group by level → jurisdiction
    const grouped = {};
    (ap || []).forEach(row => {
      const sk = sectionKey(row.appeal_level, row.notice_no);
      if (!grouped[sk]) grouped[sk] = {};
      grouped[sk][row.jurisdiction] = row;
    });

    // If no proposed in parcel_appeals, seed the screen from parcel_values FOR THIS YEAR (scanner output).
    // ⛔ THIS YEAR ONLY. There used to be a fallback here: when the viewed year had no parcel_values, it
    // took the most recent year that did and seeded from that. Two things were wrong with it.
    //   1. ON SCREEN — a 2022 tab rendered 2021's figures. Values come from the notice; a year with no
    //      notice shows nothing. Same "misleading and confusing" outcome already ruled out for the roll.
    //   2. ON DISK — saveAppealLevel writes tax_year: valueYear (:1481), discarding the seed's own
    //      tax_year (set at :925). So ANY save, or any switch away (doSwitchYear:1586 auto-saves
    //      proposed), persisted the earlier year's values as the viewed year's. Observed 2026-08-23:
    //      parcel_appeals rows at 2022/2023 carrying 2021/2022 figures, created 1-2s before a
    //      retirement stamp — the retirement was incidental, ANY save on ANY switched-to year did it.
    // Removing the fallback fixed both at the source. The wider shape — saveAll and doSwitchYear persisting a
    // level nobody touched — was closed 2026-10-06 after the audit of every setAppeals site: every real edit
    // marks its level dirty, so saves now write edited levels only (levelsToPersist). Rows seeded here are
    // flagged _seeded_from_scanner and stay display-only until "Use derived values" or an edit to the level.
    let pvAll = null;
    if (!grouped.proposed || Object.keys(grouped.proposed).length === 0) {
      const { data: pvCurrent } = await supabase.from('parcel_values')
        .select('*')
        .eq('parcel_id', parcel.id)
        .eq('tax_year', valueYear)
        .eq('notice_no', ORIGINAL_NOTICE)
        .order('is_primary', { ascending: false });
      if (pvCurrent && pvCurrent.length > 0) pvAll = pvCurrent;

      if (pvAll && pvAll.length > 0) {
        grouped.proposed = {};
        const pn = parseSigned;
        pvAll.forEach((pv, ji) => {
          const juris = pv.jurisdiction || 'County';
          grouped.proposed[juris] = {
            parcel_id: parcel.id,
            tax_year: pv.tax_year,
            appeal_level: 'proposed',
            jurisdiction: juris,
            is_primary: ji === 0,
            market_land: pn(pv.land_value),
            market_imp: pn(pv.improvement_value),
            market_pp: null,
            market_total: pn(pv.market_value),
            // ⛔ NOT SEEDED FROM capped_value, WHICH IS A VALUE AND NOT A DEDUCTION. DocumentScanner:407
            // builds capped_value as pick(...,'assessed_value') — the capped assessed FIGURE — while this
            // column is the amount the cap took OFF (cap_deduction = uncapped − capped). Writing one into
            // the other is a unit error: it put 4,901,000 into cap_deduction on 8940-436-171, an LA County
            // parcel where no cap applies, equal to its own taxable_subtotal.
            // NOT DERIVED EITHER: which figure is the uncapped one differs by state (market − capped in
            // Texas, school taxable − non-school in Florida), so a derived value would be a guess in a
            // column the taxable total subtracts. Entered by hand; see the Assessment Cap row.
            cap_deduction: null,
            less_exemption_type: null,
            less_exemption_amount: pn(pv.absolute_exemption),
            taxable_land: null,
            taxable_imp: null,
            taxable_pp: null,
            taxable_subtotal: pn(pv.taxable_value),
            less_exemptions_type: null,
            less_exemptions_amount: pn(pv.exemption_subtotal),
            taxable_total: pn(pv.taxable_value),
            _seeded_from_scanner: true,
          };
        });
      }
    }

    // An appeal level's filed / hearing / evidence-due dates — the same on every jurisdiction row of the level, so
    // the earliest non-null wins if they ever differ (2026-10-14_parcel_appeals_dates.sql).
    const dates = {};
    (ap || []).forEach(row => {
      if (row.appeal_level === 'proposed') return;
      const sk = sectionKey(row.appeal_level, row.notice_no);
      APPEAL_DATE_KEYS.forEach(k => {
        const d = dates[sk] || (dates[sk] = {});
        if (row[k] && (!d[k] || row[k] < d[k])) d[k] = row[k];
      });
    });

    // Set all state together before clearing loading flag
    setAppeals(grouped);
    setLevelDates(dates);
    if (grouped.proposed && Object.keys(grouped.proposed).length > 0) setProposedOpen(true);
    // Open as many appeal levels as have saved data (consecutive from 1 — the only shape the flow produces).
    let ac = 0;
    while (grouped[appealKeyFor(ac + 1)] && Object.keys(grouped[appealKeyFor(ac + 1)]).length > 0) ac++;
    setAppealCount(ac);

    // The year's additional notices, and how deep each one's appeal goes (counted the same way).
    const { data: pn, error: pnErr } = await supabase.from('parcel_notices')
      .select('notice_no, type_key, notice_date').eq('parcel_id', parcel.id).eq('tax_year', String(valueYear)).order('notice_no');
    if (pnErr) console.warn('[parcel_notices] read:', pnErr.message);
    const depth = {};
    (pn || []).forEach(n => {
      let d = 0;
      while (Object.keys(grouped[sectionKey(appealKeyFor(d + 1), n.notice_no)] || {}).length > 0) d++;
      depth[n.notice_no] = d;
    });
    setNotices(pn || []);
    setNoticeDepth(depth);
    const { data: nn, error: nnErr } = await supabase.from('parcel_non_ntrg')
      .select('notice_no').eq('parcel_id', parcel.id).eq('tax_year', String(valueYear));
    if (nnErr) console.warn('[parcel_non_ntrg] read:', nnErr.message);
    setNonNtrg(Object.fromEntries((nn || []).map(r => [r.notice_no, true])));
    setNonNtrgErr({});

    // This parcel's bills, every year — the same select the reports make. Savings use this year's bill rate, else
    // the latest prior year's (parcelYearFigures), so prior years are needed too.
    const { data: sb, error: sbErr } = await supabase.from('tax_bills')
      .select(PARCEL_YEAR_BILL_COLS)
      .eq('parcel_id', parcel.id);
    if (sbErr) console.warn('[tax_bills] savings read:', sbErr.message);
    const byYear = new Map();
    (sb || []).forEach((b) => { const y = String(b.tax_year); byYear.set(y, [...(byYear.get(y) || []), b]); });
    setSavingsBills(byYear);
    await loadSavingsRefs();

    // Per-jurisdiction rates for this parcel-year. Empty for every parcel today, which is why the fallback in
    // jurisdictionTaxLines (one jurisdiction -> the collector rate) keeps the arithmetic unchanged.
    const { data: jr, error: jrErr } = await supabase.from('parcel_jurisdiction_rates')
      .select('jurisdiction,tax_rate,rate_unit,comment')
      .eq('parcel_id', parcel.id).eq('tax_year', valueYear);
    // A swallowed read error would render as "no rates entered" — indistinguishable from genuinely having
    // none, and the tax would silently fall back. Say so at least in the console.
    if (jrErr) console.warn('[parcel_jurisdiction_rates] read:', jrErr.message);
    setJurRates(Object.fromEntries((jr || []).map(r => [r.jurisdiction, r])));
    setJurRatesDirty(false);

    setAppealsLoading(false); // clear AFTER all state is set
    setDirtyLevels({}); setProDirty(false); // fresh load = clean baseline (parcel fields still tracked by comparison)
  }

  // Reconcile the parcel's collector links for the SELECTED year. Other years are untouched.
  //
  // ⛔ NO BLANKET DELETE. This used to delete EVERY row for (parcel, year) and re-insert the selection —
  // while the load that fills the form (:736) filters `.eq('active', true)`. So an INACTIVE link was
  // deleted and never re-inserted: the row and its account_number vanished on an unrelated parcel save,
  // silently. No inactive rows exist today (checked: 0), which made it latent, not fixed.
  //
  // ⛔ AND THE ONE-LINE FIX IS WORSE THAN THE BUG. Adding `.eq('active', true)` to the delete leaves the
  // inactive row in place — and parcel_collectors has unique (parcel_id, tax_year, collector_id)
  // (2026-07-07_parcel_year_data.sql:78). If that collector is in the selection, the insert violates the
  // constraint, and because the delete has ALREADY run, every collector link for the year is gone. A
  // latent bug becomes a live one.
  //
  // So: delete only what is genuinely leaving (active rows whose collector is no longer selected, by id),
  // and UPSERT the selection — which reactivates a previously-inactive row instead of colliding with it.
  //
  // ⛔ BOTH WRITES ARE CHECKED. parcel_collectors is RLS-enabled (2026-08-07_rls_batch1), so a blocked
  // write returns 0 rows and NO error — the links would simply be absent from the form on next load with
  // nothing said. Returns false on failure; the caller surfaces it the way the tax save does.
  // Returns null on success, or a reason string. ⛔ It does NOT choose where the message goes: the edit path
  // surfaces on setSaveMsg and the create path on setCreateError, and a function that picked one would write
  // to a control the user is not looking at.
  async function syncParcelCollectors(parcelId) {
    if (!parcelId) return null;
    const fail = (what, msg) => `Collector links failed (${what}): ${msg}`;

    const { data: existing, error: exErr } = await supabase.from('parcel_collectors')
      .select('id,collector_id,active').eq('parcel_id', parcelId).eq('tax_year', valueYear);
    if (exErr) return fail('read', exErr.message);

    // `active !== false` so a null reads as active — the column is nullable and older rows predate it.
    const leaving = (existing || []).filter(r => r.active !== false && !selCollectorIds.has(r.collector_id)).map(r => r.id);
    if (leaving.length) {
      const { data: del, error: delErr } = await supabase.from('parcel_collectors').delete().in('id', leaving).select('id');
      if (delErr || (del || []).length !== leaving.length) {
        return fail('remove', delErr ? delErr.message : `removed ${(del || []).length} of ${leaving.length} — blocked, not saved.`);
      }
    }

    const rows = [...selCollectorIds].map(cid => ({
      parcel_id: parcelId, collector_id: cid, tax_year: valueYear, active: true,
      account_number: (collectorAccts[cid] || '').trim() || null,
    }));
    if (rows.length) {
      const { data: up, error: upErr } = await supabase.from('parcel_collectors')
        .upsert(rows, { onConflict: 'parcel_id,tax_year,collector_id' }).select('id');
      if (upErr || (up || []).length !== rows.length) {
        return fail('save', upErr ? upErr.message : `wrote ${(up || []).length} of ${rows.length} — blocked, not saved.`);
      }
    }
    return null;
  }

  // Auto-derive the property's county from its parcels' (county-named) assessors. Fires after a parcel save.
  // Rules: NEVER overwrite a non-blank county (manual/prior value is frozen); conflict-aware — set only when the
  // property's non-retired parcels agree on EXACTLY ONE county (0 or 2+ distinct → leave blank; the manual county
  // input is the human's escape hatch for multi-county properties). Non-blocking: any failure is logged, never
  // blocks the parcel save.
  async function maybeDeriveCounty(propertyId) {
    if (!propertyId) return;
    try {
      const { data: prop } = await supabase.from('properties').select('county').eq('id', propertyId).single();
      if (!prop || (prop.county && String(prop.county).trim())) return;   // missing prop, or county already set → never overwrite
      const { data: sibs } = await supabase.from('parcels').select('assessor, assessor_ref_id, retired_year, reactivated_year').eq('property_id', propertyId);
      const active = (sibs || []).filter(p => representedInYear(p, null, valueYear));
      const refIds = [...new Set(active.map(p => p.assessor_ref_id).filter(Boolean))];
      let aByEntity = {};
      if (refIds.length) {
        // assessor_ref_id is the ENTITY anchor → look up by entity_id (multiple year-versions share it; county/name are entity-stable).
        const { data: asr } = await supabase.from('assessors').select('entity_id, county, name').in('entity_id', refIds);
        aByEntity = Object.fromEntries((asr || []).map(a => [a.entity_id, a]));
      }
      const counties = [...new Set(active
        .map(p => countyFromAssessor(p.assessor_ref_id && aByEntity[p.assessor_ref_id] ? aByEntity[p.assessor_ref_id] : p.assessor))
        .filter(Boolean))];
      if (counties.length === 1) {
        await supabase.from('properties').update({ county: counties[0] }).eq('id', propertyId);
      }
    } catch (e) {
      console.warn('[maybeDeriveCounty] skipped:', e?.message || e);
    }
  }

  // PER-YEAR parcel-info fields → a parcel_year_data row payload (state/events that change year to year).
  function yearFieldsFrom(d) {
    return {
      owner_name: d.owner_name ?? null,
      account_number: d.account_number ?? null,   // per-year: the assessor's account # can change / be inconsistent year to year
      assessor_bldg_sqft: parseNumericField(d.assessor_bldg_sqft).value,
      client_bldg_sqft: parseNumericField(d.client_bldg_sqft).value,
      parcel_status: d.parcel_status || PARCEL_STATUS.active,
      exemption_type: d.exemption_type ?? null,
      exemption_exp_year: parseNumericField(d.exemption_exp_year).value,
      appeal_status: d.appeal_status ?? null,
      appeal_deadline: d.appeal_deadline || null,
      reappraisal_year: parseNumericField(d.reappraisal_year).value,   // per-year: the reappraisal cycle differs by year (moved from stable 2026-07-24)
      services: d.services || null,
      // Year-grained measurements — the divisors for the per-SF figures. Written on the SELECTED year's
      // row only (the upsert keys on parcel_id+tax_year), so a remeasure in 2027 leaves 2021 untouched.
      nla_sqft: parseNumericField(d.nla_sqft).value,
      units: parseNumericField(d.units).value,
    };
  }
  // STABLE parcel-info fields → the base parcels row (immutable per parcel; never year-scoped).
  function stableFieldsFrom(d) {
    return {
      // ⛔ parcel_name IS WRITTEN BEFORE THERE IS AN INPUT FOR IT, deliberately. Until this line the field
      // existed only in insertRow, so a rename had no channel at all — an input added first would have
      // taken the edit, reported success and dropped it. Blank is blocked in saveParcelFields, not here.
      parcel_name: d.parcel_name ?? null,
      re_pp: d.re_pp ?? null,
      consultant_firm: d.consultant_firm ?? null,   // per-parcel consultant (TEXT company name — see known-limitation note; UI writes this, importer also does)
      legal_description: d.legal_description ?? null,
      land_area_sqft: parseNumericField(d.land_area_sqft).value,
      year_built: parseNumericField(d.year_built).value,
      parcel_type: d.parcel_type ?? null,
      parcel_type_detail: d.parcel_type_detail ?? null,
      sigertax_prop_id: d.sigertax_prop_id ? parseInt(d.sigertax_prop_id, 10) : null,
      sigertax_parcel_id: d.sigertax_parcel_id ? parseInt(d.sigertax_parcel_id, 10) : null,
      address: d.address ?? null,
      address2: d.address2 ?? null,
      city: d.city ?? null,
      state: d.state ?? null,
      postal_code: d.postal_code ?? null,
      location_code: d.location_code ?? null,
      assessor: d.assessor ?? null,
      assessor_ref_id: d.assessor_ref_id || null,
    };
  }

  // ── Save parcel header fields (YEAR-SCOPED) ──────────────────
  async function saveParcelFields() {
    const wasRetired = savedParcelData.retired_year != null;   // pre-save state → detect the null→retired transition for the property prompt
    const proErr = proShown && prorataProblem(proLines);
    if (proErr) { setSaveMsg(proErr); setTimeout(() => setSaveMsg(''), 4000); return false; }
    // The name is required on create and must stay required on edit — a parcel whose name is blanked renders
    // as 'Unnamed Parcel' everywhere and is unfindable in the Parcels search (StaffPortal:1116 matches on it).
    if (!String(parcelData.parcel_name || '').trim()) { setSaveMsg('Parcel Name cannot be empty.'); setTimeout(() => setSaveMsg(''), 5000); return false; }
    // Reject malformed numbers BEFORE the write (a bad value like "2,829,22" 400s the numeric column and would
    // silently abort the save). Visible message, no write — the user fixes it and re-saves.
    const numErr = validateNumericParcelFields(parcelData);
    if (numErr) { setSaveMsg(`${numErr} must be a valid number (e.g. 2,829,222 or 2829222)`); setTimeout(() => setSaveMsg(''), 5000); return false; }
    // Retirement save-guard: never create a parcel_year_data row for a year the parcel isn't represented in (the exact
    // write that produced the spurious Pen Factory 2026 row). Belt-and-suspenders with the year-tab cap. No write.
    if (!yearAcceptsData(savedParcelData) && !yearAcceptsData(parcelData)) {
      setSaveMsg(`Not represented in ${valueYear} — this parcel is retired for that year. Saving here would create a stray ${valueYear} row. Switch to a represented year.`);
      setTimeout(() => setSaveMsg(''), 6000); return false;
    }
    setSaving(true);
    // PER-YEAR → the year row, CHANGED FIELDS ONLY. The load snapshot (savedParcelData) already holds what the
    // screen displayed — the Value Pending default for a year with no status, and the base-row fallbacks for
    // empty year fields — so comparing against it drops everything the user did not edit. Writing the whole
    // payload stamped those display values into the year: one Save on DTAG 5550 2021 wrote 'Value Pending'
    // into a year that had no status, along with any base-row values the year row lacked.
    const yf = changedYearFields(yearFieldsFrom(parcelData), yearFieldsFrom(savedParcelData));
    const sf = stableFieldsFrom(parcelData);   // STABLE → base parcels (parcel-wide)
    const now = new Date().toISOString();
    // ⛔ THE YEAR-SCOPED WRITES ARE GATED ON THE POST-SAVE STATE, not the guard above.
    // :1066 tests saved-OR-pending on purpose: that || is what lets you record a representation
    // boundary FROM the year it takes effect. This is the narrower question — once this save lands,
    // does valueYear still belong to us? Answered by representation generally, so it covers BOTH
    // ends: a first_year of 2022 set while viewing 2021 excludes 2021 exactly as a retirement does.
    // Observed 2026-08-23: eight stray rows, five from retirement stamps and three from first_year.
    // NOTE this stops CREATION only — a year row that already existed before the boundary moved
    // survives, invisible behind is_represented. Those are a data cleanup, not a code path.
    const yearRowAllowed = yearAcceptsData(parcelData);
    // PER-YEAR: upsert ONLY the selected year's row (never touches other years).
    // Nothing per-year edited → no write at all, so a Save on a year never worked creates no row.
    let yErr = null;
    if (yearRowAllowed && Object.keys(yf).length) {
      ({ error: yErr } = await supabase.from('parcel_year_data')
        .upsert({ parcel_id: parcel.id, tax_year: valueYear, ...yf, updated_at: now }, { onConflict: 'parcel_id,tax_year' }));
    }
    // BASE parcels: STABLE fields ALWAYS (immutable, so an edit on any year applies parcel-wide). PER-YEAR fields
    // mirror to base only for the current year (compat cache for pages that read parcels directly — Properties,
    // Calendar, list rows). Editing a past year still persists stable edits but leaves the base's per-year cache alone.
    const currentYear = savedParcelData.sys_year ? String(savedParcelData.sys_year) : '2026';
    const baseUpdate = { ...sf, pro_rata_pct: parcelData.pro_rata_pct || null,
      // first_year sits beside retired_year: parcel-wide, written explicitly rather than through
      // stableFieldsFrom, and never mirrored into parcel_year_data — it is not a per-year fact.
      first_year: parcelData.first_year ?? null,
      retired_year: parcelData.retired_year ?? null, reactivated_year: parcelData.reactivated_year ?? null, updated_at: now };
    // Keyed pick from the allow-list above — NEVER a spread of yf (see PARCEL_BASE_MIRROR for why).
    if (valueYear === currentYear) PARCEL_BASE_MIRROR.forEach(k => { if (k in yf) baseUpdate[k] = yf[k]; });
    // NEVER null the scan-matching cache: if account_number is empty in form state, drop it from the base
    // update so the existing parcels.account_number is preserved (cascadeMatch / DocumentScanner depend on it).
    if (baseUpdate.account_number == null || baseUpdate.account_number === '') delete baseUpdate.account_number;
    let pErr = null;
    if (!yErr) ({ error: pErr } = await supabase.from('parcels').update(baseUpdate).eq('id', parcel.id));
    const error = yErr || pErr;
    // syncParcelCollectors is ALSO year-scoped (deletes then re-inserts parcel_collectors at valueYear),
    // so it needs the same gate — otherwise the save that skipped the year row still stamps collector
    // rows onto a year it just declared out, and the fix would be half a fix. syncParcelProrata is
    // parcel-wide (parcel_prorata has no tax_year) and maybeDeriveCounty writes properties, so both run.
    // ⛔ A FAILED COLLECTOR SYNC STOPS THE REST. It reports its own reason via setSaveMsg; continuing would
    // run the prorata and county derivations over links that were not saved, and the save would look clean.
    if (!error) {
      // Collector links only when they changed — the same rule; an unchanged set used to be rewritten on every save.
      if (yearRowAllowed && collectorsChanged()) {
        const cErr = await syncParcelCollectors(parcel.id);
        if (cErr) { setSaveMsg(cErr); setTimeout(() => setSaveMsg(''), 8000); setSaving(false); return; }
      }
      const pErr = await syncParcelProrata(parcel.id);
      if (pErr) { setSaveMsg(pErr); setTimeout(() => setSaveMsg(''), 8000); setSaving(false); return; }
      await maybeDeriveCounty(parcel?.property_id || parcelData.property_id);
    }
    // Property confirm-prompt: ONLY when THIS save newly retired the parcel (null→retired transition) AND it was the last
    // live one AND the property isn't already retired → no nag on re-edits or after a prior Yes. Property-level write only.
    if (!error && !wasRetired && parcelData.retired_year) {
      const pid = parcel?.property_id || parcelData.property_id;
      if (pid) {
        const [{ data: sibs }, { data: propRow }] = await Promise.all([
          supabase.from('parcels').select('retired_year').eq('property_id', pid),
          supabase.from('properties').select('retired_year').eq('id', pid).maybeSingle(),
        ]);
        if (!propRow?.retired_year && sibs?.length && sibs.every(s => s.retired_year != null)) {
          // max of the parcels' first-years-out — the property is represented while ANY parcel still is,
          // so the property's own first year out is the latest of them. Value unchanged by the 2026-08-23
          // exclusive-end flip; only the wording was ("after" claimed capYear was still ours).
          const capYear = sibs.map(s => s.retired_year).sort().at(-1);
          if (window.confirm(`All parcels in this property are now retired. Retire the property too (from ${capYear})?`)) {
            await supabase.from('properties').update({ retired_year: capYear }).eq('id', pid);
            setPropRetire(pr => ({ ...(pr || {}), retired_year: capYear }));
          }
        }
      }
    }
    setSaving(false);
    // Surface ANY write failure visibly — a silent revert here is exactly what caused the "saves but loops" bug.
    if (error) { setSaveMsg(`Save failed: ${error.message || error}`); setTimeout(() => setSaveMsg(''), 6000); }
    if (!error) {
      setSavedParcelData({ ...parcelData }); // update snapshot
      setSavedProLines(proLines);            // pro-rata baseline now matches what was just synced
      setSavedCollectorIds(new Set(selCollectorIds)); // collectors baseline now matches what was just synced
      setSavedCollectorAccts({ ...collectorAccts });  // acct-# baseline now matches what was just synced
      setProDirty(false);                    // parcel fields + pro-rata persisted (per-level grid dirtiness untouched)
      setEditMode(false);
      setReviewMode(false);
      // Say it when the year row was skipped — the boundary and the base fields DID save, but any
      // per-year edits staged in the same click did not, and reporting a bare "Saved" would hide that.
      setSaveMsg(yearRowAllowed ? 'Saved' : `Saved — ${valueYear} is outside representation, so no ${valueYear} year row was written.`);
      setTimeout(() => setSaveMsg(''), yearRowAllowed ? 3000 : 6000);
      if (onParcelUpdated) onParcelUpdated({ ...parcelData });
    }
    return !error;
  }

  // ── E2a: create a new parcel (insert half only; multi-year values come in E2b) ──
  async function createParcel() {
    const missing = [];
    if (!parcelData.parcel_name || !String(parcelData.parcel_name).trim()) missing.push('Parcel Name');
    if (!parcelData.account_number || !String(parcelData.account_number).trim()) missing.push('Assessor ID');
    if (!parcelData.parcel_type_detail || !String(parcelData.parcel_type_detail).trim()) missing.push('Parcel Type');
    if (!parcelData.assessor || !String(parcelData.assessor).trim()) missing.push('Assessor');
    if (missing.length) { setCreateError('Required: ' + missing.join(', ')); return; }
    const proErr = proShown && prorataProblem(proLines);
    if (proErr) { setCreateError(proErr); return; }
    const numErr = validateNumericParcelFields(parcelData);
    if (numErr) { setCreateError(`${numErr} must be a valid number (e.g. 2,829,222 or 2829222)`); return; }
    // Gap A is surfaced non-blockingly (inline ⚠ on the Collectors card) — creation NEVER aborts on a missing collector.
    setCreateError('');
    setSaving(true);
    const insertRow = {
      parcel_name: parcelData.parcel_name,
      master_property_id: parcelData.master_property_id ?? null,
      property_id: parcelData.property_id ?? null,
      // ⛔ RESOLVED HERE, NOT IN createSeed. properties has no client_name column, so the old seed copied
      // undefined; and useAssignments loads async while createSeed runs synchronously at mount, so seeding
      // from the map would read {}. By save time it has resolved. Display-only — there is no input for it.
      //
      // client_id is the REAL link (the clients.id slug, what assignments and every other resolver key on);
      // client_name is the denormalised display label beside it. Writing only one of the two is what left
      // 023300-0010 barely attached to RecNation, so both land together or the parcel is not linked.
      client_id: property?.client_id ?? null,
      client_name: asg.clientNameById[property?.client_id] ?? null,
      state: parcelData.state ?? null,
      parcel_status: parcelData.parcel_status || PARCEL_STATUS.active,
      // ⛔ appeal_status is NOT written here — parcels.appeal_status is RETIRED. The status lands in the year
      // row via yearFieldsFrom() in the parcel_year_data upsert below. Writing it here would resurrect the
      // dead column and give the next reader something plausible-but-stale to fall back to.
      // ⛔ CORRECTION 2026-08-19: removing it from THIS payload was not sufficient, and bf573b5's claim that
      // the column was "not written" was wrong when made. saveParcelFields' base-row mirror was still
      // spreading the whole year payload — appeal_status included — into parcels on every current-year save.
      // PARCEL_BASE_MIRROR now excludes it, which is what actually made the retirement true.
      re_pp: parcelData.re_pp || 'Real Estate',
      owner_name: parcelData.owner_name || null,
      address: parcelData.address || null,
      address2: parcelData.address2 || null,
      city: parcelData.city || null,
      postal_code: parcelData.postal_code || null,
      location_code: parcelData.location_code || null,
      legal_description: parcelData.legal_description || null,
      account_number: parcelData.account_number || null,
      sigertax_prop_id: parcelData.sigertax_prop_id ? parseInt(parcelData.sigertax_prop_id, 10) : null,
      sigertax_parcel_id: parcelData.sigertax_parcel_id ? parseInt(parcelData.sigertax_parcel_id, 10) : null,
      land_area_sqft: parseNumericField(parcelData.land_area_sqft).value,
      assessor_bldg_sqft: parseNumericField(parcelData.assessor_bldg_sqft).value,
      client_bldg_sqft: parseNumericField(parcelData.client_bldg_sqft).value,
      year_built: parseNumericField(parcelData.year_built).value,
      parcel_type: parcelData.parcel_type || null,
      parcel_type_detail: parcelData.parcel_type_detail || null,
      pro_rata_pct: parcelData.pro_rata_pct || null,
      services: parcelData.services || null,
      exemption_type: parcelData.exemption_type || null,
      exemption_exp_year: parseNumericField(parcelData.exemption_exp_year).value,
      appeal_deadline: parcelData.appeal_deadline || null,
      assessor: parcelData.assessor || null,
      assessor_ref_id: parcelData.assessor_ref_id || null,
    };
    const { data, error } = await supabase.from('parcels').insert(insertRow).select().single();
    if (error) { setSaving(false); setCreateError('Save failed: ' + error.message); return; }
    const newId = data.id;
    // Year-scoped info snapshot for the created year (matches saveParcelFields' year-scoped write).
    await supabase.from('parcel_year_data').upsert({ parcel_id: newId, tax_year: valueYear, ...yearFieldsFrom(parcelData), updated_at: new Date().toISOString() }, { onConflict: 'parcel_id,tax_year' });
    const cErr = await syncParcelCollectors(newId);
    if (cErr) setCreateError(`${cErr} — the parcel WAS created; re-save its collectors from the parcel.`);
    await maybeDeriveCounty(parcelData.property_id ?? property?.id ?? null);
    const pErr = await syncParcelProrata(newId, true);
    if (pErr) setCreateError(`${pErr} — the parcel WAS created; re-save its pro-rata from the parcel.`);
    // E2b: persist accumulated Proposed Values per year (stash the displayed year first so it isn't lost).
    // Row builder inline-duplicated from saveAppealLevel intentionally — keeps that shipped fn untouched.
    const pn = parseSigned;   // keeps the minus sign (a negative value is real data)
    const allYears = { ...pendingByYear, [valueYear]: appeals };
    const appealRows = [];
    for (const [yr, levels] of Object.entries(allYears)) {
      for (const [lvl, jurMap] of Object.entries(levels || {})) {
        for (const [jurisdiction, row] of Object.entries(jurMap || {})) {
          const cleanRow = {
            parcel_id: newId,
            tax_year: yr,
            notice_no: ORIGINAL_NOTICE,
            appeal_level: lvl,
            jurisdiction,
            is_primary: row.is_primary,
            market_land: pn(row.market_land),
            market_imp: pn(row.market_imp),
            market_pp: pn(row.market_pp),
            market_total: pn(row.market_total),
            cap_deduction: pn(row.cap_deduction),
            less_exemption_type: row.less_exemption_type || null,
            less_exemption_amount: pn(row.less_exemption_amount),
            taxable_land: pn(row.taxable_land),
            taxable_imp: pn(row.taxable_imp),
            taxable_pp: pn(row.taxable_pp),
            taxable_subtotal: pn(row.taxable_subtotal),
            less_exemptions_type: row.less_exemptions_type || null,
            less_exemptions_amount: pn(row.less_exemptions_amount),
            taxable_total: pn(row.taxable_total),
            updated_at: new Date().toISOString(),
          };
          if (appealRowHasValues(cleanRow)) appealRows.push(cleanRow);
        }
      }
    }
    if (appealRows.length > 0) {
      const { error: aerr } = await supabase.from('parcel_appeals').insert(appealRows);
      if (aerr) {
        await supabase.from('parcels').delete().eq('id', newId); // ON DELETE CASCADE ('c') wipes any partial appeals
        setSaving(false);
        setCreateError('Save failed (values): ' + aerr.message);
        return;
      }
    }
    setSaving(false);
    if (onParcelCreated) onParcelCreated(data);
  }

  // ── Appeal helpers ───────────────────────────────────────────
  // `above` = the level above (an appeal level only): the new level takes its columns — names, order, primary —
  // with every value blank (utils/newAppealLevel). Proposed, or nothing above, gets one primary 'County' column.
  // `level` here and below is a SECTION KEY (utils/notices): 'appeal_one' for the original, 'appeal_one@1' for notice 1.
  const levelBase = (level) => { const { level: l, notice } = parseSection(level); return { parcel_id: parcel?.id, tax_year: valueYear, notice_no: notice, appeal_level: l }; };
  const levelAbove = (level) => {
    const { level: l, notice } = parseSection(level);
    return sectionKey(appealIndexOf(l) <= 1 ? 'proposed' : appealKeyFor(appealIndexOf(l) - 1), notice);
  };
  function initAppealLevel(level, above = null) {
    setAppeals(prev => {
      // Don't overwrite if data already exists
      if (prev[level] && Object.keys(prev[level]).length > 0) return prev;
      return { ...prev, [level]: levelColumnsLike(above ? prev[above] : null, levelBase(level)) };
    });
  }
  // "Copy values from <level above>" — a withdrawn or no-change outcome. Every column's values and the deduction
  // lines, never the savings-baseline marker or a derived flag. Marks the level edited; Save All stores it.
  function copyValuesFromAbove(level) {
    const above = levelAbove(level);
    setAppeals(prev => ({ ...prev, [level]: copyLevelRows(prev[above], levelBase(level)) }));
    setDeductions(prev => ({ ...prev, [level]: copyDeductionLines(prev[above], () => ++dedKey.current) }));
    markLevelDirty(level);
  }

  // ── Decision handlers (parameterized — collapse the per-site inlined copies) ──
  // ⛔ WRITES THE YEAR ROW, NOT parcels. appeal_status is PER VALUE YEAR: parcel_year_data is the single
  // source and parcels.appeal_status is retired (see the note on that column). This used to update
  // `parcels` while the modal READ parcel_year_data — so appealing set "Appeal Open" into a column nothing
  // displays, the badge flipped in memory, and on reload the year row's stale value won the merge and was
  // then written back down by the next save. 20 rows had already drifted apart for 2026.
  //
  // Single-year upsert on (parcel_id, tax_year): one statement, one year, so the cross-year isolation
  // triggers pass and an earlier year can never be touched by a decision taken in this one.
  const setAppealStatus = async (status) => {
    if (!parcel?.id) { setParcelData(p => ({ ...p, appeal_status: status })); return; }
    const { data, error } = await supabase.from('parcel_year_data')
      .upsert({ parcel_id: parcel.id, tax_year: String(valueYear), appeal_status: status, updated_at: new Date().toISOString() },
              { onConflict: 'parcel_id,tax_year' })
      .select('parcel_id');
    // TWO-PART CHECK, not one. parcel_year_data is RLS-enabled (2026-08-07_rls_batch3_parcel_data.sql:40-48)
    // with is_active_staff() on the UPDATE policy's USING as well as its WITH CHECK. An upsert that conflicts
    // onto an existing row takes the UPDATE branch, and a USING failure there returns 0 ROWS AND NO ERROR —
    // the DocumentIntake:751 shape. An { error }-only check would show the badge flipping over a write that
    // did nothing, which is precisely how the status and the decision drift apart again.
    const failed = error ? error.message : (!data?.length ? 'the write affected 0 rows (permission or missing row)' : null);
    if (failed) { console.warn('[setAppealStatus]', failed); setSaveMsg(`Couldn't save the appeal status: ${failed}`); setTimeout(() => setSaveMsg(''), 6000); return; }
    setParcelData(p => ({ ...p, appeal_status: status }));
  };
  // A section's stored decision: written FIRST, shown only once the database took it. parcel_appeal_decisions is
  // RLS-enabled, and a refused write returns 0 ROWS AND NO ERROR — so the row count is checked as well as { error },
  // and a refused write leaves the screen as it was with "Not saved" beside the dropdown. → true when saved.
  const [decisionErr, setDecisionErr] = useState({}); // { [level]: true } — that section's last decision write was refused
  const [levelSaveErr, setLevelSaveErr] = useState({}); // { [section]: what failed } — that section's last values save failed
  const writeDecision = async (level, decision) => {
    if (!parcel?.id) return false;
    const { level: lvl, notice } = parseSection(level);
    const q = decision
      ? supabase.from('parcel_appeal_decisions').upsert({ parcel_id: parcel.id, tax_year: valueYear, notice_no: notice, appeal_level: lvl, decision }, { onConflict: 'parcel_id,tax_year,notice_no,appeal_level' }).select('id')
      : supabase.from('parcel_appeal_decisions').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', notice).eq('appeal_level', lvl).select('id');
    const { data, error } = await q;
    const ok = !error && data?.length > 0;
    if (!ok) console.warn('[writeDecision]', level, decision, error?.message || 'the write affected 0 rows');
    setDecisionErr(prev => ({ ...prev, [level]: !ok }));
    if (!ok) return false;
    setDecisions(prev => { const n = { ...prev }; if (decision) n[level] = decision; else delete n[level]; return n; });
    return true;
  };
  // Non-NTRG appeal (step 7): a row in parcel_non_ntrg = that notice's Appeal One was not NTRG's appeal. Written at once,
  // like the decision; shown only once the database took it (row count checked — a refused write returns 0 rows).
  const writeNonNtrg = async (notice, on) => {
    if (!parcel?.id) return;
    const q = on
      ? supabase.from('parcel_non_ntrg').insert({ parcel_id: parcel.id, tax_year: String(valueYear), notice_no: notice, created_by: staffUser?.name || staffUser?.email || null }).select('id')
      : supabase.from('parcel_non_ntrg').delete().eq('parcel_id', parcel.id).eq('tax_year', String(valueYear)).eq('notice_no', notice).select('id');
    const { data, error } = await q;
    const ok = !error && data?.length > 0;
    if (!ok) console.warn('[writeNonNtrg]', notice, on, error?.message || 'the write affected 0 rows');
    setNonNtrgErr(prev => ({ ...prev, [notice]: !ok }));
    if (ok) setNonNtrg(prev => { const n = { ...prev }; if (on) n[notice] = true; else delete n[notice]; return n; });
  };
  // ⛔ ONLY THE ORIGINAL NOTICE MOVES THE YEAR'S APPEAL STATUS (step 6, decision 3). An additional notice's decision
  // is stored and shown, nothing more. An appealed notice still has to read as an open appeal — that is step 8's.
  const statusFrom = async (level, status) => { if (isOriginalSection(level)) await setAppealStatus(status); };
  // Accept a section: record its terminal decision + set the parcel status. Per-section.
  const acceptDecision = async (level) => {
    if (await writeDecision(level, 'accepted')) await statusFrom(level, APPEAL_STATUS.value_accepted);
  };
  // Clear a section's decision (—) + revert the parcel status to under_review. → true when cleared.
  const recallDecision = async (level) => {
    if (!(await writeDecision(level, null))) return false;
    await statusFrom(level, APPEAL_STATUS.under_review);
    return true;
  };
  // Withdraw a section: we appealed then pulled it — value stands, but a real event (distinct from Accepted,
  // where we never appealed). Records the terminal decision + sets the parcel status to Withdrawn. Per-section.
  const withdrawDecision = async (level) => {
    if (await writeDecision(level, 'withdrawn')) await statusFrom(level, APPEAL_STATUS.withdrawn);
  };
  // Deny a section: the appeal was decided against us — the value it appealed from stands, as for Withdrawn.
  // Records the terminal decision + sets the parcel status to Denied. Per-section.
  const denyDecision = async (level) => {
    if (await writeDecision(level, 'denied')) await statusFrom(level, APPEAL_STATUS.denied);
  };
  // The Decision dropdown. Appealed from a stored decision clears it first; a refused clear stops there.
  const changeDecision = async (level, idx, v) => {
    if (v === 'accepted') return acceptDecision(level);
    if (v === 'withdrawn') return withdrawDecision(level);
    if (v === 'denied') return denyDecision(level);
    if (v === '') return decisions[level] ? recallDecision(level) : undefined;
    if (v === 'appealed') {
      if (decisions[level] && !(await recallDecision(level))) return;
      const { notice } = parseSection(level);
      return notice ? appealNoticeToNext(notice, idx) : appealToNext(idx);
    }
  };
  // Open the next appeal level (n = current level index; 0 = proposed → opens appeal_one). Uncapped.
  const appealToNext = async (n) => {
    await setAppealStatus(APPEAL_STATUS.appeal_open);
    const next = n + 1;
    setAppealCount(c => Math.max(c, next));
    initAppealLevel(appealKeyFor(next), levelAbove(appealKeyFor(next)));
    // Opening an appeal is a deliberate act, so the new level counts as edited: Save stores it, and it is still
    // there on reload (the level count is rebuilt from stored rows). Without this, edited-only saving would
    // drop an opened-but-empty level while the status says Appeal Open.
    markLevelDirty(appealKeyFor(next));
  };
  // The same for an additional notice: opens ITS next level, beside the original's. The year's status is not touched.
  const appealNoticeToNext = (notice, n) => {
    const next = sectionKey(appealKeyFor(n + 1), notice);
    setNoticeDepth(d => ({ ...d, [notice]: Math.max(d[notice] || 0, n + 1) }));
    initAppealLevel(next, levelAbove(next));
    markLevelDirty(next);
  };

  const gNum = (row, f) => (typeof row[f] === 'number' ? row[f] : parseSigned(row[f]) || 0);
  const dedLines = (level) => deductions[level] || [];
  const dedSumFor = (level, jur) => dedLines(level).reduce((s, l) => s + gNum(l.amounts, jur), 0); // total deductions for one jurisdiction column
  // Re-derive taxable_* = effective ratio × market_* (override value if set, else the linked assessor's ratio).
  // Auto-filled but still editable; skipped when no usable numeric ratio. Mutates row in place.
  function fillTaxableFromRatio(row) {
    const eff = row.assessment_ratio_override ? parseFloat(row.assessment_ratio) : parseFloat(assessorRatio);
    if (!Number.isFinite(eff)) return;
    // ⛔ A GENUINE ZERO IS A VALUE; AN ABSENT MARKET IS NOT. These three lines ended `|| null`, which flattened
    // a computed 0 to null — so "no improvement value" and "improvement value of zero" became the same stored
    // thing, and no later test could tell them apart. That ambiguity is what made "fill only what is absent"
    // hard to define.
    // ⛔ The guard could NOT simply be deleted: gNum maps an absent market AND a zero market both to 0, so a
    // bare `Math.round(eff * gNum(...))` would store a derived 0 for a component with no market data at all —
    // inventing a value rather than preserving one. Presence is therefore tested on the RAW market field
    // (null/''/non-numeric → absent), and only a real number derives.
    const mv = (f) => parseSigned(row[f]);
    const derive = (f) => { const m = mv(f); return m == null ? null : Math.round(eff * m); };
    row.taxable_land = derive('market_land');
    row.taxable_imp = derive('market_imp');
    row.taxable_pp = derive('market_pp');
  }
  // `prev` = the row before this edit, so a total that was only the sum of components just cleared is blanked
  // rather than kept as if it had been typed (utils/parcelSave).
  function recomputeTotals(row, prev) {
    // Sum the components when present; otherwise PRESERVE a stated total — a capped FL row carries the just value
    // here with blank components, and the old `sum || null` wiped it on any edit in the row.
    row.market_total = recomputedMarketTotal(row, prev);
    // Sum the components when present; otherwise PRESERVE a manually- or scan-entered subtotal (total-only
    // jurisdictions like the FL school column, whose TRIM notice gives only a taxable total, no land/imp split).
    row.taxable_subtotal = recomputedTaxableSubtotal(row, prev);
    // ⛔ THE CAP COMES OFF BEFORE EXEMPTIONS, WHICH IS WHY IT IS ITS OWN COLUMN AND NOT A DEDUCTION LINE.
    // Florida's non-homestead assessment cap limits NON-SCHOOL assessed value to +10% a year; schools are
    // exempt from it. It reduces assessed value, and exemptions then apply to what is left — so it is not an
    // exemption type and cannot live in the Deductions picker beside Homestead or Freeport.
    // Boynton Beach 435200IN 2026: prior 9,927,259 x 1.10 = 10,919,985, a reduction of 858,438 from the
    // 11,778,423 the schools tax. Every Florida parcel rising more than 10% produces this.
    // Order is immaterial to the arithmetic (both are subtractions) but material to meaning; see the panel row.
    row.taxable_total = taxableTotalOf(row, dedSumFor(row.appeal_level, row.jurisdiction));
  }

  // ── FILL AN EMPTY TAXABLE FROM market × ratio ────────────────────────────────────────────────────────────
  // fillTaxableFromRatio had exactly two callers (:1317 editing a market cell, :1342 changing the ratio /
  // toggling Override), so it fired only on a keystroke. A scanner-seeded proposed row arrives with market_land
  // and market_imp populated and taxable_land/imp/pp set explicitly to null (see the seeding block in
  // loadAll) — no keystroke, no derivation, so every CA parcel had to be hand-copied.
  //
  // It ran at 20% purely because reaching 20% REQUIRES ticking Override and typing, which is the :1342
  // trigger. Nothing about 100% is special: `1` passes Number.isFinite exactly as 0.2 does.
  //
  // ⛔ FILLS ONLY WHAT IS ABSENT, per component. A stated or hand-entered taxable is never touched, whatever
  // its provenance. Presence is `== null`, NOT falsy — a stored 0 is a real taxable value and stays.
  // ⛔ AND A STATED SUBTOTAL IS NEVER RECOMPUTED. A scanner-seeded row carries taxable_subtotal/taxable_total
  // from the notice's stated taxable_value while its components are null; recomputeTotals would replace that
  // stated figure with the sum of whatever we just derived. Totals are recomputed ONLY when they were null.
  //
  // ⛔ PLACED HERE, BELOW dedSumFor and recomputeTotals, on purpose: it calls dedSumFor, and a const arrow
  // declared further down would be a temporal-dead-zone hazard if this ever moved into render.
  // Runs as its own effect, not inside loadAll, because assessorRatio resolves in a LATER effect — filling
  // inside loadAll would always see a null ratio and skip. Guarded by a ref keyed on parcel|year|ratio so it
  // runs once per load; the fill is idempotent regardless (it only writes nulls).
  const taxableFilledRef = useRef(null);
  useEffect(() => {
    if (mode === 'create' || appealsLoading || !parcel?.id) return;
    const key = `${parcel.id}|${valueYear}|${assessorRatio}`;
    if (taxableFilledRef.current === key) return;
    taxableFilledRef.current = key;
    setAppeals(prev => {
      let touched = false;
      const next = {};
      Object.entries(prev).forEach(([level, jurs]) => {
        next[level] = {};
        Object.entries(jurs || {}).forEach(([jur, r]) => {
          const row = { ...r };
          const eff = row.assessment_ratio_override ? parseFloat(row.assessment_ratio) : parseFloat(assessorRatio);
          if (Number.isFinite(eff)) {
            const subtotalWasStated = row.taxable_subtotal != null;
            [['taxable_land', 'market_land'], ['taxable_imp', 'market_imp'], ['taxable_pp', 'market_pp']].forEach(([t, m]) => {
              const mv = parseSigned(row[m]);
              if (row[t] == null && Number.isFinite(mv) && mv !== 0) { row[t] = Math.round(eff * mv); row._derived_taxable = true; touched = true; }
            });
            if (!subtotalWasStated) {
              const sum = ['taxable_land', 'taxable_imp', 'taxable_pp'].reduce((a, k) => a + (Number(row[k]) || 0), 0);
              // ⛔ THROUGH THE SAME HELPER AS recomputeTotals, or the cap is honoured on an edit and
              // dropped on load — the two formulas have to be one formula.
              if (sum) { row.taxable_subtotal = sum; row.taxable_total = taxableTotalOf({ ...row, taxable_subtotal: sum }, dedSumFor(level, jur)); }
            }
          }
          next[level][jur] = row;
        });
      });
      return touched ? next : prev;   // never re-render for nothing
    });
    // ⛔ dirtyLevels is deliberately NOT set, and the filled rows are flagged _derived_taxable: the figure is
    // DISPLAY-ONLY. Saves write edited levels only (levelsToPersist), so it is stored solely through the
    // "Use derived values" button or once the user edits that level. Until 2026-10-06 it persisted on the next
    // save of any kind — which is how a deliberately blank taxable (DTAG 18111, exemption amount unknown) got
    // overwritten with market × ratio just by someone viewing the year and switching away.
  }, [mode, appealsLoading, parcel?.id, valueYear, assessorRatio]);   // eslint-disable-line react-hooks/exhaustive-deps

  function updateAppealField(level, jurisdiction, field, value) {
    // value arrives as numeric (from ValueCell onCommit) or string (from selects)
    markLevelDirty(level);
    const numVal = (typeof value === 'number') ? value : (parseSigned(value) || null);
    setAppeals(prev => {
      const levelData = { ...(prev[level] || {}) };
      const prevRow = levelData[jurisdiction];
      const row = { ...(prevRow || { ...levelBase(level), jurisdiction, is_primary: jurisdiction === 'County' }) };
      // Store numeric for numeric fields, raw for type fields
      const isTypeField = field.endsWith('_type');
      row[field] = isTypeField ? value : numVal;
      // Editing a market value re-derives taxable from the effective assessment ratio.
      if (field === 'market_land' || field === 'market_imp' || field === 'market_pp') fillTaxableFromRatio(row);
      recomputeTotals(row, prevRow);
      levelData[jurisdiction] = row;
      return { ...prev, [level]: levelData };
    });
    // Stage F #4: entering a real value in an APPEAL level that's still appeal_open flips status → under_review
    // LIVE (badge updates immediately; persisted with the next save via parcelData.appeal_status). Value-entry
    // is the trigger — NOT merely opening the level (which stays appeal_open until values are actually entered).
    const ENTERED_VALUE_FIELDS = ['market_land', 'market_imp', 'market_pp', 'market_total', 'taxable_land', 'taxable_imp', 'taxable_pp', 'taxable_subtotal'];
    // The original notice's levels only — an additional notice never moves the year's status (step 6, decision 3).
    if (level !== 'proposed' && isOriginalSection(level) && ENTERED_VALUE_FIELDS.includes(field) && numVal != null && parcelData.appeal_status === APPEAL_STATUS.appeal_open) {
      setParcelData(p => ({ ...p, appeal_status: APPEAL_STATUS.under_review }));
    }
  }

  // Set a non-numeric appeal field (ratio override flag / ratio value / comment) — no coercion.
  function setAppealRaw(level, jurisdiction, field, value) {
    markLevelDirty(level);
    setAppeals(prev => {
      const levelData = { ...(prev[level] || {}) };
      const row = { ...(levelData[jurisdiction] || { ...levelBase(level), jurisdiction, is_primary: jurisdiction === 'County' }) };
      row[field] = value;
      // Changing the ratio value or toggling override re-derives taxable — or REVERTS it to empty when there's
      // no usable ratio (override off / ratio cleared), so unchecking Override blanks the auto-calculated taxable.
      if (field === 'assessment_ratio' || field === 'assessment_ratio_override') {
        const eff = row.assessment_ratio_override ? parseFloat(row.assessment_ratio) : parseFloat(assessorRatio);
        if (Number.isFinite(eff)) fillTaxableFromRatio(row);
        else { row.taxable_land = null; row.taxable_imp = null; row.taxable_pp = null; }
        recomputeTotals(row);
      }
      levelData[jurisdiction] = row;
      return { ...prev, [level]: levelData };
    });
  }

  // ⛔ COLUMNS ARE PROPOSED'S; EVERY APPEAL LEVEL FOLLOWS (Philip 2026-10-21). A column is added and removed on
  // Proposed only, and the change goes to every open level, so a level never carries a column Proposed doesn't.
  // Both mark each level edited, so Save All stores an added column even while it's blank, and deletes a removed
  // one (saveAppealLevel) — before this, a removed column was only dropped from the screen and came back on reload.
  // Every open section with columns: the original's levels, then each additional notice's (step 6, decision 2 —
  // a jurisdiction added or removed on Proposed applies to every group).
  const openLevels = () => [
    'proposed', ...Array.from({ length: appealCount }, (_, i) => appealKeyFor(i + 1)),
    ...notices.flatMap(n => [0, ...Array.from({ length: noticeDepth[n.notice_no] || 0 }, (_, i) => i + 1)]
      .map(i => sectionKey(i ? appealKeyFor(i) : 'proposed', n.notice_no))),
  ].filter(l => Object.keys(appeals[l] || {}).length > 0);
  const noticeTypeName = (notice) => {
    const key = notices.find(n => n.notice_no === notice)?.type_key;
    return noticeTypes.find(t => t.key === key)?.name || key || 'Notice';
  };
  const levelTitle = (key) => {
    const { level: l, notice } = parseSection(key);
    const t = l === 'proposed' ? 'Proposed Notice' : appealTitleFor(appealIndexOf(l));
    return notice ? `${noticeTypeName(notice)} · ${t}` : t;
  };

  // The choices on Proposed's "+ Add Jurisdiction". School is stored under the name Florida's scanner writes.
  const ADD_JURISDICTIONS = ['City', SCHOOL_JURISDICTION];
  function addJurisdiction(jurisName) {
    if ((appeals.proposed || {})[jurisName]) return;
    const levels = openLevels();
    setAppeals(prev => {
      const next = { ...prev };
      levels.forEach(l => { if (!(prev[l] || {})[jurisName]) next[l] = { ...prev[l], [jurisName]: blankLevelRow(levelBase(l), jurisName, false) }; });
      return next;
    });
    levels.forEach(markLevelDirty);
  }

  // ⛔ THE MAIN COLUMN IS NEVER REMOVED, judged by its ROLE (is_primary), not its name: DocumentScanner names columns
  // from the notice, so a parcel can hold a primary "PALM BEACH COUNTY" and no "County" at all. Legacy rows carry no
  // flag, so "County" is also kept when no row is flagged. A column with figures on any level asks first, naming them.
  function removeJurisdiction(jurisName) {
    const proposed = appeals.proposed || {};
    if (Object.keys(proposed).length <= 1) return;                                   // never leave Proposed with no base
    if (proposed[jurisName]?.is_primary === true) return;
    if (jurisName === 'County' && !Object.values(proposed).some(r => r?.is_primary === true)) return;
    const levels = openLevels();
    const withFigures = levels.filter(l => (appeals[l] || {})[jurisName] && !levelIsEmpty(
      { [jurisName]: appeals[l][jurisName] },
      (deductions[l] || []).map(d => ({ amounts: { [jurisName]: (d.amounts || {})[jurisName] } }))));
    const label = jurisdictionLabel(jurisName);
    if (withFigures.length && !window.confirm(`${label} has figures on ${withFigures.map(levelTitle).join(', ')}.\n\nRemove ${label} from every level? It's deleted when you Save All.`)) return;
    setAppeals(prev => {
      const next = { ...prev };
      levels.forEach(l => { if ((prev[l] || {})[jurisName]) { const ld = { ...prev[l] }; delete ld[jurisName]; next[l] = ld; } });
      return next;
    });
    levels.forEach(markLevelDirty);
  }

  // ── Per-jurisdiction tax rates ───────────────────────────────────────────────────────────────────────
  // ⛔ THE VALUE IS STORED AS ENTERED, WITH ITS UNIT. Normalising to percent on save would turn a mis-set
  // toggle into a stored number silently 10x wrong, with nothing left on the row to show what was typed.
  // Florida TRIM notices quote millage, California quotes percent; ratePercentOf() converts on read.
  // ⛔ THE TAX RATE ROW IS ON EVERY YEAR, AND IT IS THE ONE STORED RATE (Philip 2026-10-27, reversing 10-08's
  // two-base-only row). A single-base year's rate is its one column's row entry, and the Taxes tab's rate cell reads
  // and writes that same entry (enteredRate / setEnteredRate), so a rate typed from a notice counts on the card and in
  // the reports alike. It is used only where the year's bill gives none (taxInputs: the bill wins).
  // ⛔ TWO TAXABLE BASES = 2+ COLUMNS ON THE YEAR'S PROPOSED LEVEL, and nothing cleverer (Philip, 2026-10-08: no rules
  // about "Adjustment" or duplicate school columns). There each column is taxed at its own row rate (ratesInEffect →
  // jurisdictionTaxLines), since one bill rate can't be split across bases; on one column the resolved rate applies.
  const twoBase = Object.keys(appeals.proposed || {}).length >= 2;
  const ratesInEffect = twoBase ? jurRates : {};
  const soleColumn = () => { const k = Object.keys(appeals.proposed || {}); return k.length === 1 ? k[0] : null; };
  const enteredRate = () => { const j = soleColumn(); return j ? ratePercentOf(jurRates[j]) : null; };
  const setJurRate = (jurisdiction, patch) => {
    setJurRatesDirty(true);
    setJurRates(prev => ({ ...prev, [jurisdiction]: { rate_unit: 'percent', ...(prev[jurisdiction] || {}), ...patch } }));
  };
  // The Taxes tab's rate cell: a percent, into the sole column's row entry (no-op when the year has no single column).
  const setEnteredRate = (val) => { const j = soleColumn(); if (j) setJurRate(j, { tax_rate: val, rate_unit: 'percent' }); };

  async function saveJurRates() {
    if (!jurRatesDirty || !parcel?.id) return true;
    const hasRate = (r) => r && r.tax_rate != null && String(r.tax_rate).trim() !== '';
    const fail = (what, error) => {
      setSaveMsg(`Couldn't ${what}: ${error ? error.message : 'no rows affected (permission?)'}`);
      setTimeout(() => setSaveMsg(''), 8000);
      return false;
    };
    // A STORED rate that has been cleared is deleted (by its id, so the count proves it) — the Taxes tab's rate cell
    // writes here, and clearing it has to take the rate away, as clearing the old typed override did.
    const cleared = Object.entries(jurRates).filter(([, r]) => r && r.id && !hasRate(r));
    if (cleared.length) {
      const { data, error } = await supabase.from('parcel_jurisdiction_rates').delete()
        .in('id', cleared.map(([, r]) => r.id)).select('id');
      if (error || (data || []).length !== cleared.length) return fail('clear the tax rate', error);
      setJurRates(prev => { const n = { ...prev }; cleared.forEach(([j]) => { delete n[j]; }); return n; });
    }
    const rows = Object.entries(jurRates)
      // A row with no rate is not written: absent means no entered rate — on a single base the bill's rate (or
      // nothing) decides, and on a multi-base year that column has no rate (see jurisdictionTaxLines).
      .filter(([, r]) => hasRate(r))
      .map(([jurisdiction, r]) => ({
        parcel_id: parcel.id, tax_year: String(valueYear), jurisdiction,
        tax_rate: (() => { const n = parseFloat(String(r.tax_rate).replace(/[^0-9.]/g, '')); return Number.isFinite(n) ? n : null; })(),
        rate_unit: r.rate_unit === 'mills' ? 'mills' : 'percent',
        comment: r.comment || null, updated_at: new Date().toISOString(),
      }));
    if (!rows.length) { setJurRatesDirty(false); return true; }
    // ⛔ .select FOR THE AFFECTED ROW COUNT — a policy-blocked write returns 0 rows and NO error. The ids come back so
    // a rate saved now can be cleared later in the same session.
    const { data, error } = await supabase.from('parcel_jurisdiction_rates')
      .upsert(rows, { onConflict: 'parcel_id,tax_year,jurisdiction' }).select('id,jurisdiction');
    if (error || (data || []).length !== rows.length) return fail(`save the tax rate${rows.length === 1 ? '' : 's'}`, error);
    setJurRates(prev => { const n = { ...prev }; data.forEach((d) => { if (n[d.jurisdiction]) n[d.jurisdiction] = { ...n[d.jurisdiction], id: d.id }; }); return n; });
    setJurRatesDirty(false);
    return true;
  }

  // `level` is a section key (utils/notices) — the original's level, or an additional notice's 'level@n'.
  // → false when anything failed: the level stays edited, "Not saved" names it, and Save All stops there.
  async function saveAppealLevel(level) {
    const { level: lvl, notice } = parseSection(level);
    const failures = [];
    setAppealSaving(true);
    // Force blur all inputs first to commit any in-progress edits
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
      await new Promise(r => setTimeout(r, 50));
    }
    const pn = parseSigned;   // keeps the minus sign (a negative value is real data)
    const levelData = appeals[level] || {};
    for (const [jurisdiction, row] of Object.entries(levelData)) {
      const cleanRow = {
        parcel_id: parcel.id,
        tax_year: valueYear,
        notice_no: notice,
        appeal_level: lvl,
        jurisdiction,
        is_primary: row.is_primary,
        // The savings-baseline marker (utils/baselineSavings) — the original's Proposed only; the database refuses
        // it on any other level, and an additional notice has no baseline picker.
        savings_baseline_for: level === 'proposed' ? (row.savings_baseline_for || null) : null,
        market_land: pn(row.market_land),
        market_imp: pn(row.market_imp),
        market_pp: pn(row.market_pp),
        market_total: pn(row.market_total),
        cap_deduction: pn(row.cap_deduction),
        less_exemption_type: row.less_exemption_type || null,
        less_exemption_amount: pn(row.less_exemption_amount),
        taxable_land: pn(row.taxable_land),
        taxable_imp: pn(row.taxable_imp),
        taxable_pp: pn(row.taxable_pp),
        taxable_subtotal: pn(row.taxable_subtotal),
        less_exemptions_type: row.less_exemptions_type || null,
        less_exemptions_amount: pn(row.less_exemptions_amount),
        taxable_total: pn(row.taxable_total),
        assessment_ratio: row.assessment_ratio_override ? pn(row.assessment_ratio) : null,
        assessment_ratio_override: !!row.assessment_ratio_override,
        assessment_ratio_comment: row.assessment_ratio_override ? (row.assessment_ratio_comment || null) : null,
        // Appeal levels only; the same dates on every jurisdiction row of the level. Proposed is the notice, not an appeal.
        // ⛔ THE STORED DATES GO BACK UNCHANGED. Filed / Hearing / Evidence due are no longer on the card (Philip,
        // 2026-10-21: appeals are run by consultants), but this upsert writes all three keys, so they must still be
        // loaded (levelDates). Dropping the load and keeping this line would blank every date on the next save.
        ...(lvl === 'proposed' ? {} : Object.fromEntries(APPEAL_DATE_KEYS.map(k => [k, levelDates[level]?.[k] || null]))),
        updated_at: new Date().toISOString(),
      };
      // ⛔ CHECKED. This used to log a failure to the console only, so a refused save looked saved (Philip 2026-10-21:
      // show "Not saved", as decisions do). .select for the row count — an RLS-refused write returns 0 rows, no error.
      const { data: up, error } = await supabase.from('parcel_appeals')
        .upsert(cleanRow, { onConflict: 'parcel_id,tax_year,notice_no,appeal_level,jurisdiction' }).select('id');
      if (error || !up?.length) failures.push(`${jurisdictionLabel(jurisdiction)} values (${error ? error.message : '0 rows saved'})`);
    }
    // This level's deduction lines: one row per (line_index, jurisdiction) for every non-empty line — the type repeats
    // across a line's jurisdiction rows. ⛔ WRITTEN WITH syncRows, NOT DELETE-ALL-THEN-INSERT (Philip 2026-10-23): only
    // a changed, new or removed line is written, so a Save with nothing changed writes nothing (and logs nothing).
    const dedJurs = Object.keys(levelData);
    const dedRows = [];
    (deductions[level] || []).forEach((line, li) => {
      const hasAmt = dedJurs.some(j => pn(line.amounts[j]) != null);
      if (!line.type && !hasAmt) return; // skip fully-empty line
      dedJurs.forEach(jur => dedRows.push({ parcel_id: parcel.id, tax_year: valueYear, notice_no: notice, appeal_level: lvl, jurisdiction: jur, line_index: li, deduction_type: line.type || null, amount: pn(line.amounts[jur]) }));
    });
    const dedSync = await syncRows(supabase, { table: 'parcel_appeal_deductions', rows: dedRows, key: ['jurisdiction', 'line_index'],
      match: { parcel_id: parcel.id, tax_year: valueYear, notice_no: notice, appeal_level: lvl } });
    if (dedSync.error) failures.push(`deductions (${dedSync.error})`);
    // Section deadline: upsert when set, delete the row when cleared (one per parcel/year/level).
    const secDl = sectionDeadlines[level];
    if (secDl) {
      const { error: dlErr } = await supabase.from('parcel_appeal_deadlines')
        .upsert({ parcel_id: parcel.id, tax_year: valueYear, notice_no: notice, appeal_level: lvl, deadline: secDl }, { onConflict: 'parcel_id,tax_year,notice_no,appeal_level' });
      if (dlErr) failures.push(`deadline (${dlErr.message})`);
    } else {
      await supabase.from('parcel_appeal_deadlines').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', notice).eq('appeal_level', lvl);
    }
    // Columns removed on the card (removeJurisdiction): the upsert above never touches a stored row that's no longer
    // on screen, so it is deleted here. ⛔ The affected-row count is checked — a policy-blocked delete returns 0 rows
    // and NO error — and a failure leaves the level edited, so the next Save All tries again.
    const { data: storedCols, error: colErr } = await supabase.from('parcel_appeals').select('jurisdiction')
      .eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', notice).eq('appeal_level', lvl);
    const leavingCols = colErr ? [] : (storedCols || []).map(r => r.jurisdiction).filter(j => !(j in levelData));
    let colFail = colErr ? `couldn't read the stored columns: ${colErr.message}` : null;
    if (leavingCols.length) {
      const { data: del, error: delErr } = await supabase.from('parcel_appeals').delete()
        .eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', notice).eq('appeal_level', lvl).in('jurisdiction', leavingCols).select('id');
      if (delErr || (del || []).length !== leavingCols.length) colFail = delErr ? delErr.message : `removed ${(del || []).length} of ${leavingCols.length} — blocked`;
    }
    if (colFail) failures.push(`removing ${leavingCols.length ? leavingCols.map(jurisdictionLabel).join(', ') : 'columns'} (${colFail})`);
    if (failures.length) {
      console.warn('[saveAppealLevel]', level, failures);
      setLevelSaveErr(prev => ({ ...prev, [level]: failures.join('; ') }));   // red "Not saved" on the section
      setAppealSaving(false);
      return false;
    }
    setLevelSaveErr(prev => { if (!prev[level]) return prev; const n = { ...prev }; delete n[level]; return n; });
    // ⛔ NO AUTO-SET OF appeal_status HERE — do not restore it as a convenience. Saving values is NOT
    // "working the notice". Values arrive from imports, scans and year rolls with nobody having reviewed
    // anything, so a save must never move the status. The lifecycle is deliberate and human:
    //   roll → Value Pending (waiting on the notice)  →  someone RECEIVES AND WORKS the notice, and picks
    //   Under Review from the dropdown  →  the decision handlers drive it from there.
    // This block used to force under_review unconditionally on every proposed-level save, which silently
    // overwrote a terminal decision already recorded for the year — accept on 2021 at 19:06, then any
    // Save All / save-on-exit / year-switch wrote Under Review back over 'Value Accepted', leaving
    // parcel_appeal_decisions and parcel_year_data.appeal_status disagreeing. Three routes reached it:
    // saveAll's level loop, saveAllAndExit, and doSwitchYear's proposed autosave.
    setAppealSaving(false);
    setDirtyLevels(prev => { const n = { ...prev }; delete n[level]; return n; }); // clear THIS level only
    // Saved → its figures are stored, no longer derived: drop the display flags so the marker goes away.
    setAppeals(prev => {
      if (!levelHasDerived(prev[level])) return prev;
      const ld = {};
      Object.entries(prev[level]).forEach(([jur, r]) => { const row = { ...r }; DERIVED_FLAGS.forEach(f => delete row[f]); ld[jur] = row; });
      return { ...prev, [level]: ld };
    });
    setAppealSaveMsg('Saved');
    setTimeout(() => setAppealSaveMsg(''), 3000);
    return true;
  }

  // ── Unsaved-changes guard on exit ─────────────────────────────
  // Dirty = any per-level grid/deduction edit (dirtyLevels) OR pro-rata edit (proDirty) OR any parcel-field change
  // (compared against the loaded savedParcelData snapshot). Volatile React keys never enter the comparison.
  // Dirty if ANY appeal level has unsaved grid/deduction edits, OR pro-rata changed, OR a parcel field changed.
  const dirtyLevelList = () => Object.keys(dirtyLevels).filter(l => dirtyLevels[l]);
  const parcelFieldsChanged = () => Object.keys(savedParcelData || {}).some(k => String(parcelData?.[k] ?? '') !== String(savedParcelData[k] ?? ''));
  // SINGLE source of truth for collector edits (selection + per-collector Collector ID) vs the loaded baseline.
  // Both the dirty check AND the Review Changes diff read this — no parallel path. Returns one row per change:
  //   {type:'added'|'removed'|'changed', old, new} where old/new are the collector's Collector ID (account_number).
  const collectorChangeList = () => {
    const ids = new Set([...savedCollectorIds, ...selCollectorIds]);
    const out = [];
    ids.forEach(id => {
      const inSaved = savedCollectorIds.has(id), inSel = selCollectorIds.has(id);
      if (inSel && !inSaved) out.push({ id, type: 'added', old: '', new: collectorAccts[id] || '' });
      else if (!inSel && inSaved) out.push({ id, type: 'removed', old: savedCollectorAccts[id] || '', new: '' });
      else { const o = savedCollectorAccts[id] || '', n = collectorAccts[id] || ''; if (o !== n) out.push({ id, type: 'changed', old: o, new: n }); }
    });
    return out;
  };
  const collectorsChanged = () => collectorChangeList().length > 0;
  // Parcel-info dirty = year_data fields (incl. services) OR the year's collector selection/IDs.
  const parcelInfoChanged = () => parcelFieldsChanged() || collectorsChanged();
  const taxesChanged = () => JSON.stringify(parcelTaxes) !== JSON.stringify(savedParcelTaxes);
  // Bill-fields card edits, for THIS year only. procForm holds edits and nothing else, so any field present on a
  // card belonging to the displayed year is unsaved work. Scoped by the year prefix so the exit warning can never
  // fire for edits on a year that isn't on screen.
  const procCardsChanged = () => Object.entries(procForm)
    .some(([k, v]) => k.startsWith(`${valueYear}:`) && v && Object.keys(v).length > 0);
  // ⛔ procCardsChanged() IS PART OF THIS. Until it was, Save All reported success having written none of the
  // card, and closing the modal discarded every card edit with NO warning — Bill-fields values persist only via
  // Process (submitTaxBill). Counting them here does not make Save All store them; it stops them disappearing
  // without the TS being told. Remove this only when the cards actually persist.
  const isDirty = () => dirtyLevelList().length > 0 || notices.some(n => n._dirty) || proDirty || jurRatesDirty || parcelInfoChanged() || taxesChanged() || procCardsChanged();
  const requestClose = () => { if (isDirty()) setShowExitWarn(true); else onClose(); };
  // Single source of truth for the selected Value Year — BOTH year selectors (top bar + above-values) call this,
  // so they always agree. Mirrors the original in-line switch: auto-saves EDITED proposed values, then resets the
  // appeal state for the new year (loadAll re-fetches on the valueYear change). No-op when the year is unchanged.
  // The actual switch: persist the app-wide year, auto-save edited proposed values, reset appeal state for the new year.
  // ⛔ NOTHING IS SAVED HERE. A switch with unsaved edits goes through the Save / Discard / Cancel window first
  // (switchValueYear); this used to auto-save Proposed edits silently — even after Discard — and drop the rest.
  async function doSwitchYear(yr) {
    sessionStorage.setItem('wh_valueYear', yr);
    if (onYearChange) onYearChange(yr);
    setValueYear(yr); setProposedOpen(false); setAppealCount(0); setAppeals({});
    setNotices([]); setNoticeDepth({}); setLevelSaveErr({}); setNonNtrg({}); setNonNtrgErr({});
  }
  async function switchValueYear(yr) {
    if (yr === valueYear) return;
    if (isDirty()) { setPendingYearSwitch(yr); return; }   // anything unsaved → Save / Discard / Cancel (Philip 2026-10-21)
    await doSwitchYear(yr);
  }
  // Discard before a year switch. Year-scoped state reloads with the new year; the two pieces that don't are reset
  // here: Pro-Rata (parcel-wide, back to its saved lines) and the Taxes tab's bill-field cards (keyed by year).
  function discardUnsaved() {
    revertProrata();
    setProShown(savedProLines.length > 0);
    setProcForm(p => Object.fromEntries(Object.entries(p).filter(([k]) => !k.startsWith(`${valueYear}:`))));
  }
  // Whether the VIEWED year is a represented (writable) year. Non-represented years stay navigable (dimmed tabs) so a
  // give-back can be recorded, but value-saves are gated off and the save-guard blocks stray rows. Fail-open while
  // retirement is null/loading (representedInYear → true).
  const canEditYear = yearAcceptsData(savedParcelData) || yearAcceptsData(parcelData);
  // Land on the last represented year ONCE per parcel, when it OPENS on a non-represented year (e.g. a retired parcel
  // opened at the current year). Fires on parcel/ retirement load only — never fights a manual click to a dimmed tab.
  const landedRef = useRef(null);
  useEffect(() => {
    if (mode === 'create' || landedRef.current === parcel?.id) return;
    const rep = valueYears.filter(yr => representedInYear(parcelData, propRetire, yr));
    if (rep.length && !rep.includes(valueYear)) {
      const landing = [...rep].reverse().find(yr => yr <= valueYear) || rep[rep.length - 1];
      if (landing && landing !== valueYear) doSwitchYear(landing);
    }
    landedRef.current = parcel?.id;
    // eslint-disable-next-line
  }, [parcel?.id, propRetire]);
  // Current level in effect for taxes: the FINAL level (utils/finalLevel — the bottom-most level with values, a
  // withdrawn level skipped), else proposed.
  //
  // ⛔ THE PRIMARY JURISDICTION'S FIGURES, NOT A SUM ACROSS THEM. These two numbers are written straight onto
  // the tax_bills row by processBill (market_value / taxable_value), and market_value is a PRINTED COLUMN ON
  // THE TRANSMITTAL — so a sum across two Florida bases would have put a property that does not exist in
  // front of a client. Jurisdiction rows are alternative valuations of one property; see
  // src/utils/appealRows.js. jurisdictionCount is unchanged and still drives computeTaxBill's refusal.
  function currentTaxTotals() {
    const key = cardFinal().level || 'proposed';
    const juris = appeals[key] || {};
    const row = primaryAppealRow(Object.values(juris), `${parcel?.id || 'parcel'} ${valueYear} ${key}`);
    // 0 not null: every caller of these two does arithmetic or a `> 0` test on them, and the pre-existing
    // contract was a number. An ambiguous or absent row therefore reads as "no figure" exactly as an empty
    // level always did — noMarket already says so on screen, and submitTaxBill stores null for a falsy value.
    return {
      marketTotal: Number(row?.market_total) || 0,
      taxableTotal: Number(row?.taxable_total) || 0,
      jurisdictionCount: Object.keys(juris).length,
      // The level these figures came from, so collectorTaxModel builds its per-jurisdiction lines from the
      // SAME level rather than re-deriving it and risking the two disagreeing.
      levelKey: key,
    };
  }

  // Per-collector tax model — assembles the computeTaxBill input from loaded state and returns the pure
  // calculator's per-state result (CA / FL / Cook / IL). All per-state behaviour lives in taxBill.js, driven
  // by the installment encoding, not a state flag. Sources: taxable = current appeal level (summed
  // jurisdictions); rate = manual override else scanned tax_bills.total_tax_rate; nav = manual else scanned
  // total_non_ad_valorem; priorYearTotal = prior year's tax section (Cook 55%); Adj type/pct editable per row
  // (override in parcelTaxes[cid].adj). deductions = 0 for now (parcel_appeals exemptions: follow-up).
  // The collector-year's rate and where it came from — ONE resolver for the model, the Tax Rate cell and processing.
  // ⛔ THE BILL WINS (Philip 2026-10-27): the year's bill's printed total_tax_rate (band-checked), else its jurisdiction
  // lines (lineRateAndBase), else its EFFECTIVE rate (ad valorem ÷ taxable — reports/taxRate effectiveBillRate), and
  // only then a rate entered in the Tax Rate row, which is where this tab's rate cell writes too (enteredRate).
  // The lines are the collector-year's, not an installment's, so an installment processed before OR after they were
  // entered reads the same figures — nothing needs re-processing. linesTaxable: the lines' single base, the taxable
  // fallback when the appeal level carries none (collectorTaxModel).
  function taxInputs(collectorId) {
    const yb = (scanBill[collectorId] || {}).yearBill;
    const state = parcel?.state || property?.state || null;
    const fromLines = lineRateAndBase(billLines.filter(l => l._collectorId === collectorId));
    const printed = yb ? printedBillRate(yb, { state, context: 'parcel card' }) : null;
    // The collector-year's billed total when every scheduled installment is in — the reports' billedYearTotal rule.
    const billedYear = (() => {
      const kind = rePp === 'Real Estate' ? 'RE' : 'PP';
      const insts = (taxInstallments[collectorId] || []).filter(i => i.kind === kind).map(i => i.installment_number);
      const b = billedScan[collectorId] || {};
      if (!insts.length || insts.some(n => b[n]?.total == null || b[n]?.status === TAX_BILL_STATUS.REJECTED)) return null;
      return insts.reduce((s, n) => s + Number(b[n].total), 0);
    })();
    const effective = yb && printed == null && fromLines.ratePercent == null
      ? effectiveBillRate({ adValorem: yearAdValorem(billedYear, yb), taxable: yb.taxable_value ?? fromLines.taxable ?? currentTaxTotals().taxableTotal, state, context: 'parcel card' })
      : null;
    const entered = enteredRate();
    const [rate, rateSource] = printed != null ? [printed, 'bill'] : fromLines.ratePercent != null ? [fromLines.ratePercent, 'lines']
      : effective != null ? [effective, 'effective'] : entered != null ? [entered, 'entered'] : [null, null];
    return { rate, rateSource, linesTaxable: fromLines.taxable };
  }

  function collectorTaxModel(collectorId, isREp) {
    const t = parcelTaxes[collectorId] || {};
    const scan = scanBill[collectorId] || {};
    const { rate, linesTaxable } = taxInputs(collectorId);
    const nav = t.nav != null && t.nav !== '' ? Number(t.nav)
      : (scan.nav != null && scan.nav !== '' ? Number(scan.nav) : 0);
    const { taxableTotal, jurisdictionCount, levelKey } = currentTaxTotals();
    const adjOv = t.adj || {}; // { [installment_number]: { type, pct } } — editable Adj-column overrides
    const installments = (taxInstallments[collectorId] || [])
      .filter(i => i.kind === (isREp ? 'RE' : 'PP'))
      .map(i => {
        // PER-FIELD override: editing only the pct must keep the stored type (and vice-versa). An
        // all-or-nothing `ov ? ...` nulled adjType when only pct was edited, so Equalization/Discount
        // stopped being detected and the factor dropped to 1. Fall back field-by-field, matching the render.
        const ov = adjOv[i.installment_number] || {};
        const typeRaw = ov.type !== undefined ? ov.type : i.adjustment_type;
        const pctRaw  = ov.pct  !== undefined ? ov.pct  : i.adjustment_pct;
        return {
          installmentNumber: i.installment_number,
          billType: i.bill_type,
          taxType: i.tax_type,
          billPct: i.split_pct,
          adjType: typeRaw || null,
          adjPct: (pctRaw === '' || pctRaw == null) ? null : Number(pctRaw),
          dueDate: (t.dueOv && t.dueOv[i.installment_number] !== undefined) ? (t.dueOv[i.installment_number] || null) : (i.due_date || null),
        };
      });
    // ⛔ ONE LINE PER JURISDICTION ROW, EACH WITH ITS OWN BASE AND RATE. This used to pass a single synthetic
    // 'County' line carrying the PRIMARY base and the collector's rate, with multiJurisdiction flipping
    // computeTaxBill into a refusal. Now the rows themselves are the lines: a Florida parcel is taxed
    // Σ(base × that base's rate), and a single-jurisdiction parcel yields exactly one line at the collector
    // rate — the number it has always produced.
    const lvlRows = Object.values(appeals[levelKey] || {});
    // ⛔ THE LINES' BASE ONLY WHERE THE LEVEL STATES NONE: at most one column, and no taxable_total on it at all. A level
    // that says 0 (a fully exempt parcel) is a stated figure and wins; two or more columns stay the multi-base path.
    const levelTaxable = lvlRows.some(r => r && r.taxable_total != null && r.taxable_total !== '');
    const baseRows = (!levelTaxable && jurisdictionCount <= 1 && linesTaxable != null)
      ? [{ jurisdiction: lvlRows[0]?.jurisdiction || 'County', taxable_total: linesTaxable }]
      : (lvlRows.length ? lvlRows : [{ jurisdiction: 'County', taxable_total: taxableTotal }]);
    const lines = jurisdictionTaxLines(baseRows, ratesInEffect, rate,
      Object.values(appeals.proposed || {}));
    return computeTaxBill({
      jurisdictions: lines.map(l => ({ name: l.jurisdiction, taxableValue: l.taxableValue, rate: l.rate, rateSource: l.rateSource })),
      rate, nav, deductions: 0,
      priorYearTotal: priorYearTax[collectorId] ?? null,
      installments,
      multiJurisdiction: jurisdictionCount > 1,   // >1 jurisdiction column → Σ(taxable) is non-additive; refuse to compute (honest blank)
    });
  }

  // ── Manual bill entry: effective process-form values (defaults from tax-section state, overlaid with edits) ──
  const setProc = (key, field, val) => setProcForm(p => ({ ...p, [key]: { ...(p[key] || {}), [field]: val } }));
  // Currency display for the money inputs — commas + 2 decimals, no $ (so the field stays editable). moneyNum on
  // save strips commas either way; defaults render formatted, and onBlur reformats what the user typed.
  const fmtMoneyIn = (x) => { const n = parseFloat(String(x ?? '').replace(/[$,]/g, '')); return isNaN(n) ? (x == null ? '' : String(x)) : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
  // Record ANOTHER installment of an off-schedule bill — a CA supplemental's second stub. Not a computed
  // bill: there is no schedule row and nothing to derive, so the operator types the stub's four numbers.
  // ⛔ THE DOCUMENT IS REUSED, NOT RE-UPLOADED. fileExchangeId is copied from the sibling half, so both
  // rows point at the one PDF the county issued — submitTaxBill:119 writes it onto the new bill and its
  // parcel_documents upsert is keyed on file_exchange_id, so the link row is re-upserted, never duplicated.
  // Re-scanning the same PDF to record the second stub would instead create a second file_exchange row and
  // a second copy in R2, and require correcting four extracted fields (the extractor reads the FIRST stub).
  // sib = null → a NEW supplemental typed from the Taxes tab (Philip, 2026-10-21): no document, and the account
  // number is the one typed in the form (a CA supplemental carries its own, not the annual bill's).
  async function addOffScheduleInstallment(col, billType, sib) {
    const f = addOff || {};
    const numf = (x) => { const n = parseFloat(String(x ?? '').replace(/[$,]/g, '')); return isNaN(n) ? null : n; };
    const seq = parseInt(f.seq, 10);
    if (!Number.isFinite(seq) || seq < 1) { setAddOff(p => ({ ...p, msg: 'Enter which installment this is.' })); return; }
    // ⛔ NEVER OVER AN INSTALLMENT ALREADY RECORDED. submitTaxBill keys an off-schedule bill on (bill_type,
    // supplemental_seq) and SUPERSEDES a live one still awaiting approval — no error, the earlier bill is just gone.
    // A returned (rejected) one may be redone: replacing it is what returning it asked for.
    const same = (billedScan[col.id] || {})[`${billType}#${seq}`];
    if (same && same.status !== TAX_BILL_STATUS.REJECTED) { setAddOff(p => ({ ...p, msg: `${billType} installment ${seq} is already recorded.` })); return; }
    const amountDue = numf(f.amountDue);
    if (amountDue == null) { setAddOff(p => ({ ...p, msg: 'Enter the amount due for this stub.' })); return; }
    // ⛔ EVERY BILL ENDS UP WITH A RATE (Philip 2026-10-27) — Bill Check's no_rate. A warning: a second click adds it anyway.
    const noRate = checkBill({ total_ad_valorem: numf(f.adValorem), total_tax_rate: numf(f.taxRate), taxable_value: numf(f.taxable),
      state: parcel.state || property?.state || '', bill_type: billType, supplemental_seq: seq }, []).some((x) => x.code === 'no_rate');
    if (noRate && !f.noRateOk) {
      setAddOff(p => ({ ...p, noRateOk: true, msg: '\u26a0 No tax rate: enter the bill\'s rate, or its taxable value with the ad valorem. Click again to add it without one.' }));
      return;
    }
    setAddOff(p => ({ ...p, busy: true, msg: '' }));
    try {
      await submitTaxBill({
        status: yearIsBaseline() ? TAX_BILL_STATUS.RECORDED : TAX_BILL_STATUS.SUBMITTED, note: null,
        propertyId: property?.id || parcel.property_id, parcelId: parcel.id, collectorId: col.id,
        taxYear: valueYear, billType, installmentNumber: null, supplementalSeq: seq,
        submittedById: staffUser?.id || null,
        bill: {
          account_number: (sib ? sib.accountNumber : (f.accountNumber || '').trim()) || null, due_date: f.dueDate || null, value_year: valueYear,
          total_ad_valorem: numf(f.adValorem), total_non_ad_valorem: numf(f.nonAdValorem),
          total_tax_rate: numf(f.taxRate), taxable_value: numf(f.taxable),
          total_bill_amount: amountDue,
          jurisdiction_rates: null, extracted_fields: null,
        },
        fileExchangeId: sib?.fileExchangeId || null,
        docLink: sib?.fileExchangeId ? { type_key: DOC_KEYS.taxBill, folder_key: DOC_KEYS.taxBills, year_subfolder: valueYear } : null,
        tbp: { market_value: currentTaxTotals().marketTotal || null, total_ad_valorem: numf(f.adValorem), total_non_ad_valorem: numf(f.nonAdValorem), amount_due: amountDue },
      });
      setAddOff(null);
      setBillsReload(n => n + 1);
    } catch (e) {
      setAddOff(p => ({ ...p, busy: false, msg: 'Could not add it: ' + (e.message || e) }));
    }
  }

  // ⛔ OFF-SCHEDULE BILLS (a CA supplemental — a mid-year reassessment, prorated, its own sequence and installments)
  // are not on the collector's annual schedule (collector_installments is keyed (collector_id, tax_year) and shared by
  // EVERY parcel under that collector, so a row there would manufacture an expected bill for all of them). They are
  // listed under the collector's installments on the Taxes tab and open in their own bill window, which shows this
  // card: the bill as stored, its payment, and "+ Add another installment" (Philip 2026-10-23 — they used to render
  // only inside a REGULAR installment's window, so after a refresh nothing on the tab showed them). Read-only: there is
  // no schedule row to process one FROM. They stay out of the collector's annual Billed total.
  const renderOffScheduleCard = (col, bScan, k, sb) => {
    const chipKey = sb.status ? billWorkflowStatus(sb.status) : null;
    const seq = k.split('#')[1];
    const label = `${k.split('#')[0]}${/^\d+$/.test(seq) ? ` — installment ${seq}` : ''}`;
    const fld = (lbl, val) => (
      <div key={lbl}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lbl}</div>
        <div style={{ fontSize: '12px', fontFamily: 'monospace', color: c.text }}>{val ?? '—'}</div>
      </div>
    );
    return (
      <div key={k} style={{ border: `1px solid ${c.border}`, borderRadius: '8px', padding: '10px 14px', marginTop: '10px', background: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(100,116,139,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 800, color: c.text }}>{label}</span>
          {chipKey && (
            <span style={{ fontSize: '10px', fontWeight: 700, color: c.muted, background: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.1)', borderRadius: '6px', padding: '2px 7px' }}>
              {TAX_BILL_CHIP_LABEL[chipKey] || sb.status}
            </span>
          )}
          <span style={{ fontSize: '11px', color: c.muted }}>Not on the collector's annual schedule — read-only here (except its payment).</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '10px' }}>
          {fld('Account #', sb.accountNumber)}
          {fld('Due', sb.dueDate)}
          {fld('Ad Valorem', sb.adv != null ? fmtMoneyIn(sb.adv) : null)}
          {fld('Non Ad Valorem', sb.nav != null ? fmtMoneyIn(sb.nav) : null)}
          {fld('Billed', sb.total != null ? fmtMoneyIn(sb.total) : null)}
          {fld('Amount Due', sb.amountDue != null ? fmtMoneyIn(sb.amountDue) : null)}
        </div>
        {/* Payment is recordable here too — it is not a billed figure (utils/paymentRecord). */}
        {sb.taxBillId && sb.status !== TAX_BILL_STATUS.REJECTED && (
          <BillPaymentPanel bill={{ id: sb.taxBillId, status: sb.status, billed: sb.total ?? null, paid: sb.paid || null }}
            entityName={`${parcelData.parcel_name || parcel?.id} · ${col.name} · ${valueYear} ${label}`}
            staffName={staffUser?.name} onChanged={() => setBillsReload(n => n + 1)} s={s} c={c} />
        )}
        {(() => {
          // Next sequence for THIS bill type on this parcel-collector-year. An
          // unlabelled sibling keys on its row id, not a number, so it can't be counted
          // — the field stays editable for exactly that case.
          const billType = k.split('#')[0];
          const seqs = Object.keys(bScan).filter(isOffScheduleKey)
            .filter(kk => kk.split('#')[0] === billType)
            .map(kk => Number(kk.split('#')[1])).filter(nn => Number.isFinite(nn));
          const nextSeq = seqs.length ? Math.max(...seqs) + 1 : 2;
          // The form key carries the collector: two collectors can each hold a 'Supplemental#1'.
          const formKey = `${col.id}|${k}`;
          if (!(addOff && addOff.key === formKey)) return (
            <button type="button" onClick={() => setAddOff({ key: formKey, seq: String(nextSeq), dueDate: '', amountDue: '', adValorem: '', nonAdValorem: '', taxRate: '', taxable: '', busy: false, msg: '' })}
              style={{ ...s.btnGhost, marginTop: '10px', padding: '4px 10px', fontSize: '11px' }}>
              + Add another installment of this {billType.toLowerCase()}
            </button>
          );
          return renderAddOffForm(col, billType, sb, nextSeq);
        })()}
      </div>
    );
  };

  // The installment form for an off-schedule bill. sib = the bill whose next installment this is (its document
  // and account number are reused); null = a new supplemental, which takes an account number instead.
  const renderAddOffForm = (col, billType, sib, nextSeq) => {
    const setF = (field, val) => setAddOff(p2 => ({ ...p2, [field]: val, msg: '', noRateOk: false }));
    const inp = (lbl, field, ph) => (
      <div key={field}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{lbl}</div>
        <input style={{ ...s.input, fontFamily: 'monospace', fontSize: '12px', padding: '4px 7px' }}
          value={(addOff && addOff[field]) || ''} placeholder={ph}
          onChange={(e) => setF(field, e.target.value)} />
      </div>
    );
    return (
      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${c.border}` }}>
        {sib && (
          <div style={{ fontSize: '11px', color: c.muted, marginBottom: '8px' }}>
            Type the stub's figures. {sib.fileExchangeId
              ? 'The scanned bill document is reused — the county prints both stubs on one PDF, so there is nothing to upload.'
              : '⚠ The sibling bill has no document attached, so this one will have none either.'}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: '8px' }}>
          {inp('Installment #', 'seq', String(nextSeq))}
          {!sib && inp('Account #', 'accountNumber', '')}
          <div key="dd">
            <div style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Due Date</div>
            <input type="date" style={{ ...s.input, fontSize: '12px', padding: '4px 7px' }}
              value={(addOff && addOff.dueDate) || ''} onChange={(e) => setF('dueDate', e.target.value)} />
          </div>
          {inp('Amount Due', 'amountDue', '0.00')}
          {inp('Ad Valorem', 'adValorem', '0.00')}
          {inp('Non Ad Valorem', 'nonAdValorem', '0.00')}
          {inp('Tax Rate %', 'taxRate', '0.0000')}
          {inp('Taxable Value', 'taxable', '0')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <button type="button" disabled={addOff.busy}
            onClick={() => addOffScheduleInstallment(col, billType, sib)}
            style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', opacity: addOff.busy ? 0.5 : 1, cursor: addOff.busy ? 'wait' : 'pointer' }}>
            {addOff.busy ? 'Adding…' : yearIsBaseline() ? 'Record (baseline)' : 'Add → Approval'}</button>
          <button type="button" onClick={() => setAddOff(null)} style={{ ...s.btnGhost, padding: '4px 12px', fontSize: '11px' }}>Cancel</button>
          {addOff.msg && <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--red)' }}>{addOff.msg}</span>}
        </div>
      </div>
    );
  };

  function procValues(col, r) {
    // ⛔ THE YEAR IS PART OF THE KEY. Without it the key was `${collectorId}:${instNum}` and doSwitchYear does
    // not clear procForm — so 2021's typed card values reappeared on 2022's cards, where Process would submit
    // them against the wrong year. A wrong-DATA path, not a lost-data one. Keying by year (rather than clearing
    // on switch) also means switching away and back does not silently discard what was typed.
    const key = `${valueYear}:${col.id}:${r.installmentNumber}`;
    const f = procForm[key] || {};
    const bScan = billedScan[col.id] || {}; const bMan = (parcelTaxes[col.id] || {}).billed || {};
    // The card's Amount Due: a typed figure (the override) else what the BILL says is due — not the billed total, which
    // on a Florida bill is the undiscounted figure and on a paid bill is not $0. Submitting from the card writes this
    // into tax_bills.total_bill_amount, the figure the TBT asks the client to pay.
    const billedDflt = (bMan[r.installmentNumber] !== undefined && bMan[r.installmentNumber] !== '') ? bMan[r.installmentNumber] : (bScan[r.installmentNumber]?.amountDue ?? null);
    // ⛔ SCAN FIRST UNLESS OVERRIDDEN. The expression is unchanged in SHAPE — what changed is what
    // parcelTaxes.nav MEANS: it is now populated only from the override line, never snapshotted off a
    // scan, so `pt ?? scan` finally reads as 'a TS's figure, else the bill' rather than 'whatever was
    // cached last, else the bill'.
    const navDflt = (parcelTaxes[col.id] || {}).nav ?? (scanBill[col.id] || {}).nav ?? null;
    const advOvDflt = ((parcelTaxes[col.id] || {}).advOv || {})[r.installmentNumber] ?? null;
    const sb = bScan[r.installmentNumber] || {};   // THIS installment's submitted tax_bills row
    const dr = ((parcelTaxes[col.id] || {}).draft || {})[r.installmentNumber] || {};   // Save All's persisted draft
    const g = (field, dflt) => (f[field] !== undefined ? f[field] : dflt);
    return {
      key,
      billType: g('billType', dr.bill_type || r.billType || `Installment ${r.installmentNumber}`),
      // What the bill says is due (→ total_bill_amount → the TBT). The card's input writes the override (setBilled).
      amountDue: billedDflt != null ? fmtMoneyIn(billedDflt) : '',
      // Paid (catch-up bills): from this installment's bill when it is already marked, else what the TS ticks/types.
      paid: g('paid', !!(sb.paid)),
      paidDate: g('paidDate', (sb.paid && sb.paid.at) || ''),
      amountPaid: g('amountPaid', sb.paid && sb.paid.amount != null ? fmtMoneyIn(sb.paid.amount) : ''),
      paidProof: g('paidProof', (sb.paid && sb.paid.proof) || ''),
      paidNote: g('paidNote', (sb.paid && sb.paid.note) || ''),
      // ⛔ THE ANNUAL, NOT THIS INSTALLMENT'S SHARE. This defaulted to r.subTotal — the per-installment figure —
      // so a card-processed bill wrote a HALF-YEAR into tax_bills.total_ad_valorem while a scanned bill wrote
      // the ANNUAL. One column, two grains, no way to tell which you held: the TBT prints it as the year's tax
      // (tbtPdf.js:31), generate-tbt.js:93 states it is ANNUAL, detectRateUnit divides by it to choose mills vs
      // percent, and reports/taxRate.js:167 gave up on the column entirely and derives from the lines instead.
      // A draft typed BEFORE this change may still hold a per-installment figure — the label says annual so the
      // mismatch is visible rather than silent.
      // ⛔ THE BILL IS THE SOURCE, AND IT WAS NOT EVEN CONSULTED BEFORE. This read was `draft ?? annualAdv`
      // — a card-only draft, then a COMPUTED figure — while `sb` (this installment's tax_bills row) sat
      // in scope at :1848 holding total_ad_valorem the whole time. So correcting a bill could not move
      // this field by any route. Order is: deliberate override → the bill → BLANK.
      // ⛔ NEVER THE COMPUTED TAX (Philip, 2026-10-09). It used to fall back to computeTaxBill's annual figure, which
      // prints in the transmittal's Ad Valorem column and, on a discount bill, sets the row's Amount Due and the
      // discount line. A figure a client is billed from must be the bill's. The computed one is shown beside the field
      // as a hint only (the card's Bill fields, "computed: $X").
      adValorem: g('adValorem', advOvDflt != null ? fmtMoneyIn(advOvDflt) : (sb.adv != null ? fmtMoneyIn(sb.adv) : '')),
      // Per-collector-year (parcelTaxes.nav) — the card reads it and writes back to the same place.
      nonAdValorem: navDflt != null && navDflt !== '' ? fmtMoneyIn(navDflt) : '',
      // ⛔ THREE RUNGS, IN THIS ORDER: submitted tax_bills row → SAVED DRAFT (parcel_taxes) → schedule/computed
      // default. `sb` is THIS installment's tax_bills row; `dr` is the draft Save All persisted. Same shape
      // dueDate already used. A submitted bill outranks a draft because it is what was actually sent.
      discount: g('discount', dr.discount ?? ''),
      discountDate: g('discountDate', sb.discountDate || dr.discount_date || ''),
      penalty: g('penalty', dr.penalty ?? ''),
      dueDate: g('dueDate', sb.dueDate || r.dueDate || ''),
      // Manually entered — the extractor never supplies it, so the ONLY source is a person plus the draft.
      delinquentDate: g('delinquentDate', sb.delinquentDate || dr.delinquent_date || ''),
      // ⛔ READ-ONLY SOURCES — the card does NOT keep its own copy of these two.
      //   accountNumber: parcel_collectors.account_number is the single owner (same for a parcel; no
      //     per-installment account number exists). The card's input writes THERE, via setCollectorAccts.
      //   nonAdValorem (above): per-collector-year on parcelTaxes.nav. The card's input writes THERE, via setNav.
      // Keeping a per-installment copy of either would re-create the two-stores-one-value split.
      accountNumber: sb.accountNumber || collectorAccts[col.id] || parcelData.account_number || '',
      payToName: g('payToName', dr.pay_to_name || col.name || ''), payToAddr1: g('payToAddr1', dr.pay_to_address1 || col.mailing_address_1 || ''), payToAddr2: g('payToAddr2', dr.pay_to_address2 || col.mailing_address_2 || ''),
      payToCity: g('payToCity', dr.pay_to_city || col.mailing_city || ''), payToState: g('payToState', dr.pay_to_state || col.mailing_state || ''), payToZip: g('payToZip', dr.pay_to_zip || col.mailing_zip || ''),
    };
  }
  // ── Reopen a FINISHED bill for correction ────────────────────────────────────────────────────────────
  //
  // ⛔ THIS REPLACES SIXTEEN HAND-WRITTEN UPDATE STATEMENTS. There was no UI path: the card said "Already
  // Filed — resolve that bill first" and offered no means of resolving it, the same dead-end shape as the
  // consultant panel's "edit that one instead" on a screen with no edit control.
  //
  // 'rejected' is the right target: statuses.js:138 says it leaves Approvals and reappears on the TS
  // Processing worklist, which is exactly what a bill needing correction should do. reopened_at is what
  // separates THIS from a CS bounce — see the migration.
  //
  // ⛔ transmittal_id IS NULLED. It is the one field on the row that makes a claim about the outside world:
  // build-tbt-eml.js:62 gathers a transmittal's bills BY transmittal_id, so leaving it would let a rejected
  // bill be pulled into a regenerated email the client had never been sent.
  //
  // ⛔ NOTHING ELSE IS UNWOUND, deliberately. tax_bill_parcels is an audit line no reader consults; the
  // parcel_documents link is still that parcel's document; parcel_collectors.account_number is still the
  // account. And the bills THIS one superseded stay superseded — reviving them would put two live rows on
  // one key, the duplicate check-tax-bills.mjs check 3 exists to catch. Re-Processing inserts a fresh row
  // and supersedes this one, so the history stays correct without any undo.
  async function reopenBill(col, r, taxBillId) {
    const note = reopenWhy.trim();
    if (reopenBusy || !taxBillId || !note) return;
    setReopenBusy(true);
    const now = new Date().toISOString();
    // ⛔ .select('id') FOR THE AFFECTED ROW COUNT. tax_bills is RLS-enabled; a blocked update returns 0 rows
    // and NO error, so "no error" is not proof this happened.
    const { data, error } = await supabase.from('tax_bills')
      .update({ status: TAX_BILL_STATUS.REJECTED, rejected_at: now, rejected_by: staffUser?.id || null,
                rejection_note: note, reopened_at: now, transmittal_id: null })
      .eq('id', taxBillId).select('id');
    if (error || !data?.length) {
      setReopenBusy(false);
      setProcMsg(p => ({ ...p, [`${col.id}:${r.installmentNumber}`]: `Reopen failed — ${error ? error.message : 'no rows affected (permission?)'}` }));
      return;
    }
    // This is the one action that undoes a finished state; nothing else would record it.
    await supabase.from('activity_log').insert({
      staff_user: staffUser?.name || 'NTRG Staff', action: 'reopen', entity_type: 'tax_bill', entity_id: taxBillId,
      entity_name: `${parcelData.parcel_name || parcel?.id} · ${col.name} · ${valueYear} inst ${r.installmentNumber ?? '—'}`,
      details: { reason: note, tax_year: valueYear, collector_id: col.id },
    }).then(() => {}, (e) => console.warn('[reopen] activity_log:', e));
    setReopenBusy(false); setReopenFor(null); setReopenWhy('');
    setBillsReload(n => n + 1);   // re-read tax_bills so the chip flips to Returned in place
  }

  // Process ONE installment → create the tax_bills row via the SHARED write path (submitTaxBill) → it lands in
  // Approvals for valueYear, identical to a scan from that point on. file_exchange stays null; attach the doc later.
  async function processBill(col, r) {
    const v = procValues(col, r);
    if (procBusy) return;                                   // one submit at a time across the modal
    const say = (m) => setProcMsg(p => ({ ...p, [v.key]: m }));
    const num = (x) => { const n = parseFloat(String(x ?? '').replace(/[$,]/g, '')); return isNaN(n) ? null : n; };
    const amountDue = num(v.amountDue);
    if (amountDue == null) { say(`Enter an Amount Due for ${v.billType} before processing.`); return; }
    // A payment recorded by hand says how it is known (tax_bills.paid_proof, docs/payment-status-gap.md).
    if (v.paid && !v.paidProof) { say(`Pick the proof of payment for ${v.billType} before processing.`); return; }
    // ⛔ NEVER PROCESS ON UNSAVED TAX DATA (the Bug-1 trap). This guard used to live in the deleted
    // processAllInstallments; it belongs to processing, not to the loop, so it moved here rather than going
    // with it. submitTaxBill snapshots the tax section, so an unsaved edit would be submitted-but-not-stored.
    if (taxesChanged()) {
      const ok = await saveParcelTaxes();
      if (ok === false) { say('Process blocked — tax edits failed to save. Fix and retry.'); return; }
    }
    const { marketTotal, taxableTotal, levelKey } = currentTaxTotals();
    // ⛔ NEVER PROCESS ON DERIVED VALUES. submitTaxBill copies market/taxable into tax_bills, so a figure the
    // screen only derived (taxable = market × ratio, a Proposed level from the scanned notice) would be stored
    // through the back door. Derived figures are stored ONLY via "Use derived values" (or an edit to the level).
    if (!dirtyLevels[levelKey] && levelHasDerived(appeals[levelKey])) {
      say(`Process blocked — the ${levelKey === 'proposed' ? 'Proposed' : appealTitleFor(appealIndexOf(levelKey))} values are derived and not saved. Click "Use derived values" on that section first.`);
      return;
    }
    // Billed / paid / discount by the same rules as the scanner (utils/billPayment): billed = the full tax on a
    // single-payment collector, else the installment's amount due; paid only when the TS ticks Paid on the card.
    // ⛔ THE TYPED AMOUNT DUE IS THE PRINTED INSTALLMENT AMOUNT (Philip, 2026-10-11). On the card it is the Billed
    // figure (setBilled), typed off the bill or a payment summary — never a paid bill's printed $0. Without it a
    // paid installment had no billed figure: billed_amount stayed empty and the bill was flagged "printed amount
    // not found" though the TS had entered it (IL 12-08-100-006-8531 / 2021). The scanner passes its own.
    const isRE = rePp === 'Real Estate';
    const singlePayment = (taxInstallments[col.id] || []).filter(i => i.kind === (isRE ? 'RE' : 'PP')).length === 1;
    const pay = deriveBillPayment({
      state: parcel.state || property?.state || '', taxYear: valueYear, singlePayment,
      gross: (num(v.adValorem) || 0) + (num(v.nonAdValorem) || 0) + (num(v.penalty) || 0),
      amountDue, installmentAmount: amountDue, paidMarker: v.paid ? 'Y' : 'N', paidOverride: !!v.paid,
      paidDate: v.paidDate || null, amountPaid: num(v.amountPaid),
      discountAmount: num(v.discount), discountDate: v.discountDate || null,
    });
    const flSingle = String(parcel.state || property?.state || '').toUpperCase() === 'FL' && singlePayment;
    // ⛔ ONLY A BILL'S OWN RATE IS WRITTEN ONTO THE NEW BILL (Philip 2026-10-27: rates come only from bills) — its printed
    // rate or its lines'. An effective or entered rate is not the bill's; the bill gets its effective rate from its own
    // ad valorem ÷ taxable when it is read.
    const { rate: rateIn, rateSource, linesTaxable } = taxInputs(col.id);
    const rate = rateSource === 'bill' || rateSource === 'lines'
      ? (() => { const n = parseFloat(String(rateIn ?? '').replace(/[^0-9.]/g, '')); return isNaN(n) ? null : n; })() : null;
    // ⛔ THE YEAR'S LINES RIDE ALONG. This wrote jurisdiction_rates: null and then superseded the prior row, so processing
    // an installment again dropped the lines it carried; on a one-installment bill that erased the breakdown.
    const yearLines = billLines.filter(l => l._collectorId === col.id).map(({ _collectorId, ...l }) => l);
    setProcBusy(v.key); say('');
    try {
      await submitTaxBill({
        status: yearIsBaseline() ? TAX_BILL_STATUS.RECORDED : TAX_BILL_STATUS.SUBMITTED, note: null,
        propertyId: property?.id || parcel.property_id, parcelId: parcel.id, collectorId: col.id,
        taxYear: valueYear, billType: v.billType, installmentNumber: r.installmentNumber, submittedById: staffUser?.id || null,
        bill: {
          account_number: v.accountNumber || null, due_date: v.dueDate || null,
          delinquent_date: v.delinquentDate || null, value_year: valueYear,
          discount_available: flSingle ? pay.discount.available : (col.discount_offered || null),
          discount_amount: flSingle ? pay.discount.amount : num(v.discount),
          discount_date: flSingle ? pay.discount.date : (v.discountDate || null),
          discount_derived: flSingle ? pay.discount.derived : false,
          billed_amount: pay.billed,
          paid_source: pay.paid ? 'staff' : null, paid_at: pay.paidAt, paid_amount: pay.paidAmount,
          paid_proof: pay.paid ? v.paidProof : null, paid_note: pay.paid ? (v.paidNote.trim() || null) : null,
          paid_recorded_by: pay.paid ? (staffUser?.name || 'NTRG Staff') : null,
          needs_review: pay.needsReview, review_note: pay.reviewNote,
          market_value: marketTotal || null, taxable_value: taxableTotal || linesTaxable || null, total_tax_rate: rate,
          total_ad_valorem: num(v.adValorem), total_non_ad_valorem: num(v.nonAdValorem), penalty_fees: num(v.penalty),
          total_bill_amount: amountDue,
          pay_to_name: v.payToName || col.name || null, pay_to_address1: v.payToAddr1 || null, pay_to_address2: v.payToAddr2 || null,
          pay_to_city: v.payToCity || null, pay_to_state: v.payToState || null, pay_to_zip: v.payToZip || null,
          jurisdiction_rates: yearLines.length ? yearLines : null, extracted_fields: null,
        },
        fileExchangeId: null,
        tbp: { market_value: marketTotal || null, total_ad_valorem: num(v.adValorem), total_non_ad_valorem: num(v.nonAdValorem), amount_due: amountDue },
        docLink: null,
      });
      setProcForm(p => { const n = { ...p }; delete n[v.key]; return n; });   // clear this installment's edits after success
      // ⛔ EVERY BILL ENDS UP WITH A RATE (Philip 2026-10-27) — Bill Check's no_rate, the scanner's rule. A warning, not a block.
      const noRate = checkBill({
        total_ad_valorem: num(v.adValorem), total_tax_rate: rate, taxable_value: taxableTotal || linesTaxable || null,
        state: parcel.state || property?.state || '', bill_type: v.billType,
      }, yearLines).some((f) => f.code === 'no_rate');
      say((yearIsBaseline()
        ? `\u2713 ${v.billType} recorded as a baseline bill for ${valueYear}. Not sent for approval — it is the client's figure from before we were engaged.`
        : `\u2713 ${v.billType} sent to Approvals (year ${valueYear}). Attach the county bill document any time before transmit.`)
        + (noRate ? ' \u26a0 This bill has no tax rate: no rate on it or its lines, and no ad valorem and taxable value to compute an effective one.' : ''));
      setBillsReload(n => n + 1);   // re-read tax_bills so THIS card's chip flips to At Approval immediately
    } catch (e) { say('Process failed: ' + (e.message || e)); }
    finally { setProcBusy(''); }
  }

  // Persist the parcel's per-collector tax rows. Clean-replace on (parcel_id, tax_year). tax_rate + amount (full
  // ad-valorem) on the row; installments jsonb holds one 'advalorem' line per installment (computed amount + split
  // + due) plus any entered 'nonad' flat lines. Collectors with neither a rate nor any Non-Ad line are dropped.
  async function saveParcelTaxes() {
    if (!parcel?.id) return;
    const isREp = rePp === 'Real Estate';
    const rows = [...selCollectorIds].map(cid => {
      const model = collectorTaxModel(cid, isREp);
      // BILLED (authoritative) per installment: manual/override in parcelTaxes[cid].billed wins, else the scan.
      const bScan = billedScan[cid] || {};
      const bMan = (parcelTaxes[cid] || {}).billed || {};
      const effB = (inst) => (bMan[inst] !== undefined && bMan[inst] !== '') ? Number(bMan[inst])
        : (bScan[inst]?.total != null ? Number(bScan[inst].total) : null);
      const instNums = model.rows.map(r => r.installmentNumber);
      const billedTotal = instNums.some(nn => effB(nn) == null) ? null // partial → no misleading total
        : Number(instNums.reduce((s, nn) => s + effB(nn), 0).toFixed(2));
      // amount = the BILLED total (the record of truth → TBT, and the base next year's Cook 55% prepay reads).
      // computed lives alongside for audit; a partial bill leaves amount null so the prior-year base isn't wrong.
      // ⛔ pnum IS DECLARED HERE, ABOVE draftFor. It used to sit below the installments map; draftFor calls it,
      // and the map invokes draftFor, so leaving it there was a runtime TDZ ReferenceError on every save.
      const pnum = (x) => { const n = parseFloat(String(x ?? '').replace(/[^0-9.]/g, '')); return isNaN(n) ? null : n; };
      // DRAFT: the Bill-fields card values that have no other owner. Persisted so Save All keeps them — the
      // card used to be Process-only, so every payee/penalty/discount edit was silently discarded on save.
      // ⛔ NOT stored here: amountDue (it IS `amount` above), dueDate (it IS `due_date`), nonAdValorem
      // (per-collector, the 'nonad' line) and accountNumber (parcel_collectors owns it). Storing those would
      // re-create the two-stores-one-value split this change exists to remove.
      const colObj = taxCollectors.find(tc => tc.id === cid) || { id: cid };
      const draftFor = (r) => {
        const v = procValues(colObj, r);
        const d = {
          // ⛔ ad_valorem LEFT THE DRAFT. It is a bill fact (tax_bills.total_ad_valorem), not a card-only
          // value, so it is stored as an override or not at all — see the override line above.
          bill_type: v.billType || null, penalty: pnum(v.penalty),
          discount: pnum(v.discount), discount_date: v.discountDate || null, delinquent_date: v.delinquentDate || null,
          pay_to_name: v.payToName || null, pay_to_address1: v.payToAddr1 || null, pay_to_address2: v.payToAddr2 || null,
          pay_to_city: v.payToCity || null, pay_to_state: v.payToState || null, pay_to_zip: v.payToZip || null,
        };
        return Object.values(d).some(x => x != null && x !== '') ? d : null;   // don't store an all-empty draft
      };
      const installments = model.rows.map(r => ({
        type: 'advalorem', installment_number: r.installmentNumber,
        amount: effB(r.installmentNumber), computed: r.subTotal,
        due_date: r.dueDate || null, split_pct: r.billPct, adj_type: r.adjType,
        draft: draftFor(r),
        // Per-installment overrides, present only where a TS typed one. `amount` joins ad_valorem here:
        // the sibling `amount:` field above stays the RESOLVED value for the worklist, and this is the only
        // thing that outranks the bill.
        override: (() => {
          const ptc = parcelTaxes[cid] || {};
          const ov = {};
          const adv = (ptc.advOv || {})[r.installmentNumber];
          if (adv != null && adv !== '') ov.ad_valorem = pnum(adv);
          const amt = (ptc.billed || {})[r.installmentNumber];
          if (amt !== undefined && amt !== '') ov.amount = pnum(amt);
          const a = (ptc.adj || {})[r.installmentNumber] || {};
          if (a.type !== undefined) ov.adj_type = a.type || '';
          if (a.pct !== undefined) ov.adj_value = a.pct === '' || a.pct == null ? null : Number(a.pct);
          return Object.keys(ov).length ? ov : undefined;
        })(),
      }));
      // ⛔ THE 'nonad' LINE IS NO LONGER WRITTEN, AND NEITHER IS THE tax_rate COLUMN. Both duplicated a
      // tax_bills fact into a second table that nothing kept in step, which is the whole defect: every Save
      // All re-snapshotted the scan, and the next correction to the bill left the copy behind. Re-ranking
      // the reads alone would have hidden that; not writing them ends it. Only a deliberate override is
      // stored now, in its own line, so a stale cache can no longer be mistaken for a judgement.
      // The override line holds the non-ad valorem only; a typed rate is the Tax Rate row's (saveJurRates).
      const ovLine = { type: 'override', installment_number: null };
      const ptOv = parcelTaxes[cid] || {};
      if (ptOv.nav != null && ptOv.nav !== '') ovLine.nav = ptOv.nav;
      if (ovLine.nav !== undefined) installments.push(ovLine);
      installments.push({ type: 'meta', path: model.path, computed_total: model.total, billed_total: billedTotal, discount: model.discount || 0, ...model.meta });
      const pt2 = parcelTaxes[cid] || {};
      return { parcel_id: parcel.id, tax_year: valueYear, collector_id: cid, tax_rate: null, amount: billedTotal, installments,
               proposed_tax: pnum(pt2.proposed_tax), final_tax: pnum(pt2.final_tax) };
    // ⛔ THE SURVIVAL TEST MOVED OFF tax_rate, which is now always null. A collector-year is worth a row
    // when it carries a billed amount, a savings figure, or ANY override (a typed rate is no longer one of them:
    // it lives in the Tax Rate row).
    }).filter(r => r.amount != null || r.proposed_tax != null || r.final_tax != null
      || (r.installments || []).some(l => l && (l.type === 'override' || (l.override && (l.override.ad_valorem != null || l.override.amount != null)))));
    // Surface errors — this insert silently failed for months (collector_id FK targeted the pre-rebuild
    // collectors table; see 2026-07-21_parcel_taxes_entity_fk migration). Never fail a tax save silently again.
    // ⛔ ONE ROW PER COLLECTOR, WRITTEN WITH syncRows (Philip 2026-10-23): only a collector whose figures changed is
    // updated, a new one inserted, one no longer kept deleted — never delete-all-then-insert. Every write's row count is
    // checked inside (an RLS-refused write returns 0 rows, no error).
    const taxSync = await syncRows(supabase, { table: 'parcel_taxes', rows, key: ['collector_id'], match: { parcel_id: parcel.id, tax_year: valueYear } });
    if (taxSync.error) { setSaveMsg(`Tax save failed: ${taxSync.error}`); setTimeout(() => setSaveMsg(''), 8000); return false; }
    // ⛔ FOLD THE SAVED DRAFT BACK INTO LOCAL STATE, THEN clear this year's card edits. Order matters: procForm
    // is the edit layer and parcelTaxes[cid].draft is the persisted layer, so clearing procForm without seeding
    // the draft would blank every card field until the next reload — a save that visibly loses the values it
    // just stored. Both are updated from the SAME rows we confirmed landed.
    const savedDraft = {};
    rows.forEach(rw => {
      const per = {};
      (rw.installments || []).forEach(line => { if (line.type === 'advalorem' && line.draft) per[line.installment_number] = line.draft; });
      savedDraft[rw.collector_id] = per;
    });
    const merged = Object.fromEntries(Object.entries(parcelTaxes).map(([cid, v]) => [cid, { ...v, draft: savedDraft[cid] || {} }]));
    setParcelTaxes(merged);
    setProcForm(p => Object.fromEntries(Object.entries(p).filter(([k]) => !k.startsWith(`${valueYear}:`))));
    setSavedParcelTaxes(JSON.parse(JSON.stringify(merged))); // refresh the dirty baseline on a confirmed save
    return true;
  }

  // THE unified save — persists everything at once: parcel fields (+ pro-rata, validated) via saveParcelFields,
  // then every populated appeal level (grid + deductions + section deadline + status) via saveAppealLevel,
  // then the per-collector tax rows via saveParcelTaxes (after collectors are synced).
  // Returns false if pro-rata validation blocked (nothing else runs; message shown). Wired to BOTH Save buttons.
  async function saveAll() {
    // Gap A: a parcel with an assessor but no collectors won't generate tax bills — surfaced NON-BLOCKINGLY as an
    // inline ⚠ on the Collectors card (see collectorLess below). The save NEVER aborts on it (a blocking window.confirm
    // here could return false — reflexive Cancel, or Chrome's "prevent additional dialogs" suppression — and silently
    // kill the save, leaving it perpetually dirty). The pull screen's coverage banner is the downstream backstop.
    const ok = await saveParcelFields();
    if (ok === false) return false; // invalid pro-rata → abort, message shown
    // Same post-save representation gate saveParcelFields applies to its year-scoped writes.
    // parcel_appeals and parcel_taxes are both (parcel_id, tax_year)-grained like parcel_year_data,
    // so ungated they write into a year this very click declared out. Every year-scoped write in the
    // save path is now behind one predicate. The general duplicate (a represented year saving levels nobody
    // touched) is closed by writing edited levels only — see levelsToPersist below.
    if (yearAcceptsData(parcelData)) {
      // EDITED levels only. A level that is merely displayed — including derived figures (taxable = market ×
      // ratio, a Proposed level built from a scanned notice) — is not saved; those go through "Use derived values".
      const noticesOk = await saveNotices();
      if (noticesOk === false) return false;
      for (const lvl of levelsToPersist(appeals, dirtyLevels)) if ((await saveAppealLevel(lvl)) === false) return false;
      const rateOk = await saveJurRates();
      if (rateOk === false) return false;
      const taxOk = await saveParcelTaxes();
      if (taxOk === false) return false; // tax insert error — message shown, don't report success
    }
    return true;
  }
  // Exit dialog "Save": save everything, then close (create mode uses its own createParcel flow).
  async function saveAllAndExit() {
    setShowExitWarn(false);
    if (mode === 'create') { await createParcel(); return; }
    const ok = await saveAll();
    if (ok === false) return; // stay open on validation block
    onClose();
  }
  // Remove appeal level n AND every level after it (confirm-gated). Discards their data (local + DB) and
  // reverts appeal_status to the prior level's state: back to proposed (under_review if valued, else value_pending)
  // when no appeals remain, or appeal_open while a deeper appeal still stands. Handles "clicked Appealed by mistake".
  async function removeAppealLevel(n) {
    const removedKeys = [];
    for (let i = n; i <= appealCount; i++) removedKeys.push(appealKeyFor(i));
    const dropKeys = (obj) => { const c = { ...obj }; removedKeys.forEach(k => delete c[k]); return c; };
    setAppealCount(n - 1);
    setAppeals(prev => dropKeys(prev));
    setDeductions(prev => dropKeys(prev));
    setSectionDeadlines(prev => dropKeys(prev));
    setLevelDates(prev => dropKeys(prev));
    setDecisions(prev => dropKeys(prev));
    setDirtyLevels(prev => dropKeys(prev));
    const proposedHasValues = appeals.proposed && Object.keys(appeals.proposed).length > 0;
    const revertStatus = (n - 1 === 0)
      ? (proposedHasValues ? APPEAL_STATUS.under_review : APPEAL_STATUS.value_pending)
      : APPEAL_STATUS.appeal_open;
    await setAppealStatus(revertStatus);
    if (parcel?.id) { // clean up any persisted rows for the removed levels (no-op if they were never saved)
      for (const k of removedKeys) {
        await supabase.from('parcel_appeals').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', ORIGINAL_NOTICE).eq('appeal_level', k);
        await supabase.from('parcel_appeal_deductions').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', ORIGINAL_NOTICE).eq('appeal_level', k);
        await supabase.from('parcel_appeal_deadlines').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', ORIGINAL_NOTICE).eq('appeal_level', k);
        await supabase.from('parcel_appeal_decisions').delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', ORIGINAL_NOTICE).eq('appeal_level', k);
      }
      if (n === 1 && nonNtrg[ORIGINAL_NOTICE]) await writeNonNtrg(ORIGINAL_NOTICE, false);   // Appeal One gone → its Non-NTRG tick too
    }
  }

  // ── Additional notices (step 6) ───────────────────────────────
  // Add: numbered after the year's highest; its Proposed group takes the original Proposed's columns, blank. Stored
  // with Save All (parcel_notices first, then its sections).
  function addNotice(typeKey) {
    const no = Math.max(0, ...notices.map(n => n.notice_no)) + 1;
    const sk = sectionKey('proposed', no);
    setNotices(prev => [...prev, { notice_no: no, type_key: typeKey, notice_date: null, _new: true, _dirty: true }]);
    setNoticeDepth(d => ({ ...d, [no]: 0 }));
    setAppeals(prev => ({ ...prev, [sk]: levelColumnsLike(prev.proposed, levelBase(sk)) }));
    markLevelDirty(sk);
  }
  const setNoticeDate = (notice, date) =>
    setNotices(prev => prev.map(n => (n.notice_no === notice ? { ...n, notice_date: date || null, _dirty: true } : n)));
  // parcel_notices rows for new or edited notices. ⛔ Row-count checked — an RLS-refused write returns 0 rows, no error.
  async function saveNotices() {
    for (const n of notices.filter(x => x._dirty)) {
      const q = n._new
        ? supabase.from('parcel_notices').insert({ parcel_id: parcel.id, tax_year: String(valueYear), notice_no: n.notice_no,
            type_key: n.type_key, notice_date: n.notice_date || null, created_by: staffUser?.name || staffUser?.email || null })
        : supabase.from('parcel_notices').update({ notice_date: n.notice_date || null })
            .eq('parcel_id', parcel.id).eq('tax_year', String(valueYear)).eq('notice_no', n.notice_no);
      const { data, error } = await q.select('notice_no');
      if (error || !data?.length) {
        setLevelSaveErr(prev => ({ ...prev, [sectionKey('proposed', n.notice_no)]: error ? error.message : '0 rows saved' }));
        return false;
      }
      setNotices(prev => prev.map(x => (x.notice_no === n.notice_no ? { ...x, _new: false, _dirty: false } : x)));
    }
    return true;
  }
  // Drop every section of one notice from the per-section maps (or only `keys`).
  const dropSections = (keys) => {
    const drop = (obj) => { const c = { ...obj }; keys.forEach(k => delete c[k]); return c; };
    [setAppeals, setDeductions, setSectionDeadlines, setLevelDates, setDecisions, setDirtyLevels, setLevelSaveErr, setDecisionErr].forEach(set => set(drop));
  };
  const noticeSections = (notice) => [0, ...Array.from({ length: noticeDepth[notice] || 0 }, (_, i) => i + 1)]
    .map(i => sectionKey(i ? appealKeyFor(i) : 'proposed', notice));
  // Remove one notice — asks first, naming what goes. A saved notice goes through remove_parcel_notice (one
  // transaction; refused while documents are filed against it). The original notice is never removed here.
  async function removeNotice(notice) {
    const n = notices.find(x => x.notice_no === notice);
    if (!n) return;
    const secs = noticeSections(notice);
    const withValues = secs.filter(k => !levelIsEmpty(appeals[k], deductions[k]));
    const nDec = secs.filter(k => decisions[k]).length, nDl = secs.filter(k => sectionDeadlines[k]).length;
    const parts = [withValues.length && `its values on ${withValues.map(k => levelTitle(parseSection(k).level)).join(', ')}`,
      nDec && `${nDec} decision${nDec === 1 ? '' : 's'}`, nDl && `${nDl} deadline${nDl === 1 ? '' : 's'}`].filter(Boolean);
    const when = n.notice_date ? ` (${new Date(`${n.notice_date}T00:00:00`).toLocaleDateString('en-US')})` : '';
    if (!window.confirm(`Remove ${noticeTypeName(notice)}${when}?${parts.length ? ` Deletes ${parts.join(', ')}.` : ''}`)) return;
    if (!n._new) {
      const { error } = await supabase.rpc('remove_parcel_notice', { p_parcel: parcel.id, p_year: String(valueYear), p_notice: notice });
      if (error) { window.alert(`Not removed — ${error.message}`); return; }
    }
    dropSections(secs);
    setNonNtrg(prev => { const c = { ...prev }; delete c[notice]; return c; });
    setNotices(prev => prev.filter(x => x.notice_no !== notice));
    setNoticeDepth(d => { const c = { ...d }; delete c[notice]; return c; });
  }
  // Remove a notice's appeal level n and every later one (its ✕ on that level). The original's levels are untouched.
  async function removeNoticeLevels(notice, n) {
    const keys = Array.from({ length: Math.max(0, (noticeDepth[notice] || 0) - n + 1) }, (_, i) => sectionKey(appealKeyFor(n + i), notice));
    if (!window.confirm(`Remove ${levelTitle(keys[0])}${keys.length > 1 ? ' and the levels after it' : ''}?`)) return;
    dropSections(keys);
    setNoticeDepth(d => ({ ...d, [notice]: n - 1 }));
    if (!parcel?.id) return;
    for (const k of keys) {
      const lvl = parseSection(k).level;
      for (const t of ['parcel_appeals', 'parcel_appeal_deductions', 'parcel_appeal_deadlines', 'parcel_appeal_decisions']) {
        await supabase.from(t).delete().eq('parcel_id', parcel.id).eq('tax_year', valueYear).eq('notice_no', notice).eq('appeal_level', lvl);
      }
    }
    if (n === 1 && nonNtrg[notice]) await writeNonNtrg(notice, false);   // its Appeal One gone → its Non-NTRG tick too
  }

  // ── Pro-rata allocation line items ────────────────────────────
  // A line points to one of the client's properties (property_id — it counts in reports) or is a named line that is not
  // the client's (`other`: "No Rights", another occupant — left out of reports). utils/prorata has the rules.
  const toProLine = (r) => ({ key: ++proKey.current, id: r.id, property_id: r.property_id || null, other: !r.property_id,
    prop_tenant: r.prop_tenant || '', id_code: r.id_code || '', pro_rata_pct: r.pro_rata_pct ?? 100 });
  const blankProLine = (pct, own = false) => ({ key: ++proKey.current, id: null, property_id: own ? homePropId : null, other: false,
    prop_tenant: own ? propInfo.name || '' : '', id_code: own && propInfo.mpid != null ? String(propInfo.mpid) : '', pro_rata_pct: pct });
  const proSum = () => proLines.reduce((s, l) => s + pctOf(l.pro_rata_pct), 0);
  const proValid = () => Math.abs(proSum() - 100) < 0.01;
  const addProLine = () => {
    setProDirty(true);
    // a new line defaults to the remaining balance toward 100% (overridable)
    setProLines(prev => [...prev, blankProLine(Math.max(0, 100 - prev.reduce((s, l) => s + pctOf(l.pro_rata_pct), 0)))]);
  };
  // Prop/Tenant: one of the client's properties (its name and MPID fill in), or "No Rights" — not the client's; the name
  // fills in as "No Rights" and stays editable.
  const pickProProperty = (key, v) => {
    const p = clientProps.find(x => x.id === v);
    setProDirty(true);
    setProLines(prev => prev.map(l => l.key !== key ? l
      : p ? { ...l, property_id: p.id, other: false, prop_tenant: p.name || '', id_code: p.master_property_id != null ? String(p.master_property_id) : '' }
      : { ...l, property_id: null, other: v === '__other', prop_tenant: v !== '__other' ? '' : l.other ? l.prop_tenant : 'No Rights', id_code: v === '__other' && l.other ? l.id_code : '' }));
  };
  // ═══ THE CLIENT'S SHARE ON THE TAXES TAB (Philip 2026-10-28) ═══ When the year's SAVED pro-rata gives the client less
  // than the whole parcel, each bill and the Billed total carry a greyed, display-only line: "DTAG share (10%): $15,919.09".
  // The client's % is the sum of its lines (utils/prorata prorataShares — the reports' rule). Bills stay as billed.
  const clientPct = (() => {
    const sh = parcel?.id ? prorataShares(proRows, { id: parcel.id, property_id: homePropId }, valueYear) : null;
    const pct = sh ? sh.reduce((sum, x) => sum + x.pct, 0) : null;
    return pct != null && pct < 100 ? pct : null;
  })();
  const shareLine = (amt) => (clientPct == null || amt == null ? null : (
    <span style={{ flexBasis: '100%', textAlign: 'right', fontSize: '10px', fontWeight: 400, color: c.muted }}
      title="Display only — the client's pro-rata share. The bill stays as billed.">
      {proClientName || 'Client'} share ({clientPct}%): {fmtc(Math.round(Number(amt) * clientPct) / 100)}
    </span>));
  // Every year, or this year only (an override). Back to every year: the every-year lines, when the parcel has them.
  const setProScope = (yearOnly) => {
    setProDirty(true);
    setProYearOnly(yearOnly);
    const every = proRows.filter(r => r.tax_year == null);
    if (!yearOnly && every.length) setProLines(every.map(toProLine));
  };
  const removeProLine = (key) => { setProDirty(true); setProLines(prev => prev.length > 1 ? prev.filter(l => l.key !== key) : prev); };
  const setProLine = (key, field, value) => { setProDirty(true); setProLines(prev => prev.map(l => l.key === key ? { ...l, [field]: value } : l)); };
  // Close the pro-rata section: hide it and discard just-added/edited unsaved lines (revert to the loaded/saved
  // baseline). Saved DB rows are NOT deleted here — syncParcelProrata skips while hidden, so they persist.
  const revertProrata = () => {
    setProLines(savedProLines.length ? savedProLines : [blankProLine(100, true)]);
    setProYearOnly(prorataSet(proRows, valueYear).yearOnly);
    setProDelKey(null);
    setProDirty(false); // reverted to the saved baseline → no unsaved pro-rata changes
  };
  const closeProrata = () => { revertProrata(); setProShown(false); };

  // Anchor the floating ratio popover to the cell it was triggered from (fixed-position → no layout impact).
  // via: 'check' (checkbox — EDIT: reason input + Save, persists) | 'hover' (VIEW-only: audit history, 1.5s auto-dismiss).
  const openRatioPop = (level, jur, evt, via) => {
    const anchor = evt.currentTarget.closest('td') || evt.currentTarget;
    const r = anchor.getBoundingClientRect();
    setNewComment(''); // fresh empty box each time the popover opens
    setRatioPop({ level, jur, x: r.left, top: r.top, via });
  };
  // Snapshot the prior ratio state (override flag, %, comment, and the taxable values it drove) so an
  // uncommitted change can be undone. Capture only once per edit session → reverts to the last COMMITTED state.
  const RATIO_SNAP_FIELDS = ['assessment_ratio_override', 'assessment_ratio', 'assessment_ratio_comment', 'taxable_land', 'taxable_imp', 'taxable_pp', 'taxable_subtotal', 'taxable_total'];
  const ensureRatioSnapshot = (level, jur) => {
    if (ratioSnapshot) return; // a session is already in progress — keep its original baseline
    const row = (appeals[level] || {})[jur] || {};
    const snap = { level, jur };
    RATIO_SNAP_FIELDS.forEach(f => { snap[f] = row[f] ?? null; });
    setRatioSnapshot(snap);
  };
  const revertRatioSnapshot = () => {
    const s = ratioSnapshot; if (!s) return;
    setAppeals(prev => {
      const ld = { ...(prev[s.level] || {}) };
      const row = { ...(ld[s.jur] || {}) };
      RATIO_SNAP_FIELDS.forEach(f => { row[f] = s[f]; });
      ld[s.jur] = row;
      return { ...prev, [s.level]: ld };
    });
    setRatioSnapshot(null);
  };
  // Dismiss the popover: if an uncommitted ratio change is pending, auto-revert it (unjustified change can't stick).
  const dismissRatioPop = () => { revertRatioSnapshot(); setRatioPop(null); };
  const clearRatioTimer = () => { if (ratioTimer.current) { clearTimeout(ratioTimer.current); ratioTimer.current = null; } };
  // Hover (view) popover self-closes after 3s. Stays open while the pointer is inside it (see popover handlers).
  useEffect(() => {
    if (ratioPop && ratioPop.via === 'hover') {
      ratioTimer.current = setTimeout(dismissRatioPop, 3000);
      return clearRatioTimer;
    }
  }, [ratioPop]);
  useEffect(() => () => { clearRatioTimer(); }, []); // clear the pending hover timer on unmount
  // Initials for the audit trail — derived from the logged-in staff name (ntrg_staff has no initials column).
  const staffInitials = () => {
    const n = staffUser?.name || staffUser?.email || '';
    return n.split(/\s+/).filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase() || '?';
  };
  const fmtAuditTime = (iso) => { if (!iso) return ''; const d = new Date(iso); return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }); };
  // Append-only: Save inserts a NEW parcel_ratio_audit row (never edits past entries) with the % + new comment,
  // reflects the comment on the row, then resets the baseline and clears the box — the popover stays open.
  async function saveRatioReason() {
    const rp = ratioPop; if (!rp) return;
    const { level, jur } = rp;
    const row = (appeals[level] || {})[jur] || {};
    const ratioStr = row.assessment_ratio != null ? String(row.assessment_ratio) : '';
    const reason = newComment.trim();
    if (!reason) return; // every append requires a comment (also gated at the Save button)
    // Audit the ratio in effect: the override value when override is ON, else null (override removed).
    const ratio_value = (row.assessment_ratio_override && ratioStr !== '') ? (parseFloat(ratioStr.replace(/[^0-9.]/g, '')) || null) : null;
    if (parcel?.id) {
      const entry = { parcel_id: parcel.id, tax_year: valueYear, notice_no: parseSection(level).notice, appeal_level: parseSection(level).level, jurisdiction: jur, staff_id: staffUser?.id || null, staff_name: staffUser?.name || null, initials: staffInitials(), ratio_value, reason };
      const { data, error } = await supabase.from('parcel_ratio_audit').insert(entry).select().single();
      if (error) console.error('[ratio audit] insert error:', error);
      else if (data) setRatioAudit(prev => { const k = `${level}|${jur}`; return { ...prev, [k]: [data, ...(prev[k] || [])] }; });
    }
    setAppealRaw(level, jur, 'assessment_ratio_comment', reason); // row reflects the latest comment
    setRatioSnapshot(null); // change is now justified + audited → committed
    setNewComment(''); // clear the box
    setRatioPop(null); // committed → close IMMEDIATELY (no post-save timer → nothing can fire late and revert)
  }
  // Close on a genuine mousedown OUTSIDE the popover. Using mousedown (not click) + containment means
  // drag-selecting text inside the input never closes it (the drag STARTS inside → target is contained).
  useEffect(() => {
    if (!ratioPop) return;
    const onDown = (e) => { if (ratioPopRef.current && !ratioPopRef.current.contains(e.target)) dismissRatioPop(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ratioPop]);

  // ── Deduction line helpers ────────────────────────────────────
  // taxable_total feeds off both the value grid AND deductions, so amount/remove edits recompute it here.
  const taxableSubtotal = (row) => gNum(row, 'taxable_subtotal') || (gNum(row, 'taxable_land') + gNum(row, 'taxable_imp') + gNum(row, 'taxable_pp'));
  const applyDedTotal = (level, jur, dedSum) => setAppeals(prev => {
    const ld = prev[level]; if (!ld || !ld[jur]) return prev;
    const row = { ...ld[jur] };
    // Cap first, then deductions — the one formula (utils/appealRows taxableTotalOf); it used to leave the cap out here.
    row.taxable_total = taxableTotalOf({ ...row, taxable_subtotal: taxableSubtotal(row) || null }, dedSum);
    return { ...prev, [level]: { ...ld, [jur]: row } };
  });
  const addDedLine = (level) => { markLevelDirty(level); setDeductions(prev => ({ ...prev, [level]: [...(prev[level] || []), { key: ++dedKey.current, type: '', amounts: {} }] })); };
  const setDedType = (level, key, type) => { markLevelDirty(level); setDeductions(prev => ({ ...prev, [level]: (prev[level] || []).map(l => l.key === key ? { ...l, type } : l) })); };
  const setDedAmount = (level, key, jur, amount) => {
    markLevelDirty(level);
    setDeductions(prev => ({ ...prev, [level]: (prev[level] || []).map(l => l.key === key ? { ...l, amounts: { ...l.amounts, [jur]: amount } } : l) }));
    const newSum = dedLines(level).reduce((s, l) => s + (l.key === key ? (parseFloat(String(amount || '').replace(/[^0-9.]/g, '')) || 0) : gNum(l.amounts, jur)), 0);
    applyDedTotal(level, jur, newSum);
  };
  const removeDedLine = (level, key) => {
    markLevelDirty(level);
    const remaining = dedLines(level).filter(l => l.key !== key);
    setDeductions(prev => ({ ...prev, [level]: remaining }));
    setAppeals(prev => {
      const ld = prev[level]; if (!ld) return prev;
      const nld = {};
      Object.keys(ld).forEach(jur => {
        const sum = remaining.reduce((s, l) => s + gNum(l.amounts, jur), 0);
        const row = { ...ld[jur] };
        row.taxable_total = taxableTotalOf({ ...row, taxable_subtotal: taxableSubtotal(row) || null }, sum);
        nld[jur] = row;
      });
      return { ...prev, [level]: nld };
    });
  };

  // Clean-replace the shown set of allocation lines: this year's override, or the every-year set (utils/prorata — one
  // database function, all or nothing, counts checked). Only when changed (or on create): an untouched set is not rewritten.
  // → an error message, or null.
  async function syncParcelProrata(parcelId, created = false) {
    if (!parcelId || !proShown || (!proDirty && !created)) return null; // never revealed / unchanged → nothing to write
    const savedCount = (yr) => proRows.filter(r => (yr == null ? r.tax_year == null : String(r.tax_year) === String(yr))).length;
    // This year had its own override and now follows the every-year split: the override goes, in the same transaction.
    const dropYear = !proYearOnly && prorataSet(proRows, valueYear).yearOnly ? valueYear : null;
    const year = proYearOnly ? valueYear : null;
    const e = await saveProrataSet({ parcelId, year, lines: proLines, savedCount: savedCount(year),
      dropYear, dropCount: dropYear == null ? 0 : savedCount(dropYear) });
    if (!e) setProReload(n => n + 1);   // read the saved lines back
    return e;
  }

  // ── Tax savings ──────────────────────────────────────────────
  // The FINAL level for this year (utils/finalLevel): the bottom-most level with values, a withdrawn level skipped, and
  // the year's Appeal Status saying whether that is final or still pending. Shared with every report.
  // ⛔ THE ORIGINAL NOTICE ONLY. Final level, savings and tax totals read notice 0 until reports learn notices (step 8),
  // so an additional notice on the card never moves a figure the reports print.
  const levelRows = () => Object.fromEntries(Object.entries(originalOnly(appeals)).map(([k, v]) => [k, Object.values(v || {})]));
  function cardFinal() {
    return resolveFinalLevel({ levels: levelRows(), decisions: originalOnly(decisions), status: parcelData.appeal_status });
  }
  // The level a Proposed column can be the savings baseline for — the final level, when it is an APPEAL level.
  const savingsLevel = () => { const { level } = cardFinal(); return level && level !== 'proposed' ? level : null; };

  // ⛔ THE REPORTS' OWN CALCULATION, NOT A COPY (Philip, 2026-10-08: the card and the reports must never show different
  // savings for the same parcel-year). parcelYearFigures is what every report row is built from; the card hands it
  // its live state, so it shows what the reports will show once saved: this year's bill rate when there is a bill,
  // the latest prior year's only when there is not, marked baselines, Tax Rate row rates, hand-entered taxes.
  // Pre-appeal reference bills (components/ReferenceBillScan) — read ONLY by the savings calculation. Until the
  // tax_bill_references migration has run the table is missing; that reads as "none on file", anything else warns.
  async function loadSavingsRefs() {
    const { data, error } = await supabase.from('tax_bill_references')
      .select('id,parcel_id,tax_year,file_exchange_id,jurisdiction_rates,total_ad_valorem,source,note,scanned_at,file_exchange(file_name)')
      .eq('parcel_id', parcel.id);
    if (error && error.code !== '42P01' && error.code !== 'PGRST205') console.warn('[tax_bill_references] read:', error.message);
    const byYear = new Map();
    (data || []).forEach((r) => { const y = String(r.tax_year); byYear.set(y, [...(byYear.get(y) || []), r]); });
    setSavingsRefs(byYear);
  }
  async function removeSavingsRef(id) {
    if (!window.confirm('Remove this pre-appeal reference? Savings go back to the method used without it.')) return;
    const { data, error } = await supabase.from('tax_bill_references').delete().eq('id', id).select('id');
    if (error || !data?.length) { window.alert(`Not removed: ${error ? error.message : 'no row deleted'}`); return; }
    await loadSavingsRefs();
  }

  function cardFigures() {
    const manual = { proposed: null, final: null };
    Object.values(parcelTaxes).forEach((t) => {   // summed across collectors, exactly as the reports sum parcel_taxes
      const p = toNumber(t?.proposed_tax), f = toNumber(t?.final_tax);
      if (p != null) manual.proposed = (manual.proposed ?? 0) + p;
      if (f != null) manual.final = (manual.final ?? 0) + f;
    });
    return parcelYearFigures({
      lvlRows: levelRows(), decisions: originalOnly(decisions), status: parcelData.appeal_status, savedRates: jurRates, manual,
      nonNtrg: !!nonNtrg[ORIGINAL_NOTICE],
      billsByYear: savingsBills, referencesByYear: savingsRefs, year: String(valueYear), state: parcel?.state || parcelData.state,
      label: `${parcel?.account_number || parcel?.id || 'parcel'} / ${valueYear}`,
    });
  }

  // ── Deadline badge ───────────────────────────────────────────
  function deadlineBadge(deadline) {
    if (!deadline) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(deadline); d.setHours(0, 0, 0, 0);
    const days = Math.ceil((d - today) / 86400000);
    const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (days < 0) return <span style={s.badge('248,113,113')}>⚠ Expired · {label}</span>;
    if (days <= 30) return <span style={s.badge('251,191,36')}>🔥 {days}d · {label}</span>;
    return <span style={s.badge('52,211,153')}>✓ {label}</span>;
  }

  // E1a: field-level color from the same deadline logic — neutral >30d, amber <=30d, red past.
  // ── Appeal value block ───────────────────────────────────────
  // NOTE: this is a FUNCTION invoked as {renderAppealBlock(...)}, NOT a <Component/>. Defining a
  // component inside render gives it a new identity every render → React remounts the whole grid and
  // every input loses focus after one keystroke. As a plain function call its JSX is part of the parent
  // tree and reconciles in place. (Its former internal add-jurisdiction useState is lifted to ParcelModal.)
  const renderAppealBlock = (level, title) => {
    const levelData = appeals[level] || {};          // the ORIGINAL notice's section (empty when it stopped above)
    const jurisdictions = Object.keys(levelData);
    const isCollapsed = !!collapsed[level];
    const decIdx = level === 'proposed' ? 0 : appealIndexOf(level); // for the collapsed-header decision indicator
    // ⛔ COLUMN GROUPS (step 6): the original notice's, when it reaches this level, then each additional notice's that
    // does — to its right. One label column; each cell reads its own section (col.sk). With no additional notice
    // there is one group and the section is exactly what it was.
    const groups = [
      ...(level === 'proposed' || decIdx <= appealCount ? [{ sk: level, notice: ORIGINAL_NOTICE }] : []),
      ...notices.filter(n => decIdx <= (noticeDepth[n.notice_no] || 0))
        .map(n => ({ sk: sectionKey(level, n.notice_no), notice: n.notice_no, meta: n })),
    ];
    const hasOriginal = groups[0]?.notice === ORIGINAL_NOTICE;
    const grouped = groups.some(g => g.notice);
    // ⛔ ONE WHITE CARD PER NOTICE once the level has an added notice's group (see NOTICE_BOX_CSS and the helpers below).
    const boxed = grouped && !isCollapsed;
    const PAD_X = 18, PAD_Y = 16;   // each card's inner padding — s.card's body padding (16px 18px), as on Collectors / Services
    const cols = groups.flatMap((g, gi) => Object.keys(appeals[g.sk] || {})
      .map((j, ji) => ({ sk: g.sk, j, g, first: ji === 0, edge: gi > 0 && ji === 0 })));
    const groupDepth = (g) => (g.notice ? (noticeDepth[g.notice] || 0) : appealCount);
    const showAddNotice = level === 'proposed' && mode !== 'create' && canEditYear && jurisdictions.length > 0;
    const saveErrs = groups.map(g => levelSaveErr[g.sk] && `${levelTitle(g.sk)}: ${levelSaveErr[g.sk]}`).filter(Boolean);

    // ⛔ COMPUTED FROM THIS LEVEL'S ROWS, and the rate row only renders on proposed, so this is the proposed
    // bases. Suggestion only — nothing here writes, and every uncertain case returns a note instead of a figure.
    // ⛔ BILL LINES WIN, NOTICE LINES FALL BACK — the same precedence the report side uses (a billed figure
    // outranks a predicted one). A notice's PROPOSED rate is what the authority intends to levy and can still
    // change; a bill's is what was levied. Only the fallback is new: a year with a bill behaves as before.
    const finalCols = Object.keys(appeals[savingsLevel()] || {});   // what a Proposed column can be the savings baseline for: the final level's columns
    const rateHint = level === 'proposed'
      ? (billLines.length
          ? suggestJurisdictionRates(billLines, Object.values(levelData), { year: valueYear, source: 'bill' })
          : suggestJurisdictionRates(noticeLines, Object.values(levelData), { year: valueYear, source: 'notice' }))
      : null;

    const ROWS = [
      { key: 'market_land', label: 'Market Land', computed: false },
      { key: 'market_imp', label: 'Market Imp.', computed: false },
      { key: 'market_pp', label: 'Market PP', computed: false },
      { key: 'market_total', label: 'Market Total', computed: true, bold: true, highlight: true },
      { key: '_assessment_ratio', label: 'Assessment Ratio', isRatio: true },
      { key: 'taxable_land', label: 'Taxable Land', computed: false },
      { key: 'taxable_imp', label: 'Taxable Imp.', computed: false },
      { key: 'taxable_pp', label: 'Taxable PP', computed: false },
      { key: 'taxable_subtotal', label: 'Taxable Subtotal', computed: false, bold: true },
      // ⛔ BETWEEN SUBTOTAL AND DEDUCTIONS, AND NOT INSIDE THEM. An assessment cap is not an exemption
      // type: it reduces assessed value, and exemptions apply to what remains. The column has existed
      // since 2026-06-30, when it was "RETAINED in schema for legacy rows but dropped from the new UI
      // layout" — the consolidation of Deductions did not anticipate a pre-exemption reduction.
      { key: 'cap_deduction', label: 'Assessment Cap', computed: false },
      { key: '_cap_note', isCapNote: true },
      { key: '_deductions', label: 'Deductions', isExemptionDed: true },
      { key: 'taxable_total', label: 'Taxable Total', computed: true, bold: true, highlight: true },
      // ⛔ PROPOSED ONLY, because a rate is not a property of an appeal level. Rendering it on every level
      // would invite proposed and appeal_one to carry different rates, and aggregateSavings values BOTH sides
      // of a saving at the same per-jurisdiction rates — divergence there would report the assessment cap as
      // a reduction we had won. One input, one stored row per (parcel, year, jurisdiction).
      // ⛔ ON EVERY YEAR (Philip 2026-10-27): a single-base year's entry is the same rate the Taxes tab's cell shows.
      ...(level === 'proposed' ? [{ key: '_jur_rate', isJurRate: true, label: 'Tax Rate' },
                                  { key: '_jur_rate_note', isJurRateNote: true }] : []),
    ];

    // Shared grid geometry so every number right-aligns in the same column under each jurisdiction.
    // Number cells pad 10px right; input cells pad 0 and the input carries the same 10px right pad →
    // input text and computed text share the exact right edge. Label column shrinks to its content.
    const JW = 100;                                                                       // jurisdiction column width (fixed-layout; snug to a currency figure like 348,780)
    const LABEL_W = 132;                                                                  // Value Type column width (fits "Assessment Ratio" / "Taxable Subtotal" + a small gap)
    // ⛔ COLUMN SIZES ARE THE LIVE CARD'S AND DO NOT CHANGE (Philip 2026-10-21): notice groups add columns of the same
    // JW; when they no longer fit, the table scrolls sideways with Value Type pinned on the left. The pinned label
    // cells need an OPAQUE background or the scrolled columns show through; dark mode's card is translucent, so it
    // is laid over the page colour. `tint` = the row's own highlight, laid over that.
    const stickyBg = (tint) => [tint && `linear-gradient(${tint}, ${tint})`, `linear-gradient(${c.card}, ${c.card})`, c.bg].filter(Boolean).join(', ');
    const stickyCell = (tint) => ({ position: 'sticky', left: boxed ? PAD_X : 0, zIndex: 1, background: stickyBg(tint) });   // boxed: right of box 1's padding
    const numTd = { padding: '4px 10px', textAlign: 'right', borderLeft: `1px solid ${c.border}` };
    const inTd = { padding: 0, borderLeft: `1px solid ${c.border}` };
    const valInput = { ...s.inputMono, width: '100%', maxWidth: 'none', padding: '4px 10px', fontSize: '12px' };
    const labelTd = { padding: '4px 12px', fontSize: '12px', whiteSpace: 'nowrap', ...stickyCell() }; // column width set by the colgroup (table-layout:fixed); nowrap keeps titles on one line
    // Between groups: a GAP_W spacer column with one 1px line in the table's border grey down its middle — 12px clear
    // on each side, so the line never touches a value box. The value columns keep JW; the group's first column
    // drops its own left border (the spacer's line is the divider).
    // ⛔ ONE WHITE CARD PER NOTICE (Philip 2026-10-23, docs/notice-boxes-plan.md): with an added notice's group on the
    // level, each group is its own card — s.card's background, border and 12px corners, padded 16px 18px — with the
    // grey page showing only in the 16px gaps between, and the level's title line above them on the page. STILL ONE
    // TABLE, so every row stays level across the cards and Value Type stays pinned (box 1 carries the labels). A card's
    // padding is cells of the table too: a PAD_X column at each side of the group and a PAD_Y row at the top and bottom.
    // NOTICE_BOX_CSS draws the cards from data-* marks: data-b (in a card), data-p (padding), data-l / data-r (a card's
    // left / right edge); a row's separator line and tint ride on its --line / --tint. With no added notice none of
    // this applies and the level renders exactly as before.
    const GAP_W = boxed ? 16 : 25;
    const nGaps = cols.filter(col => col.edge).length;
    const gapStyle = boxed ? { padding: 0 } : { padding: 0, background: `linear-gradient(${c.border}, ${c.border}) no-repeat center / 1px 100%` };
    const groupCols = (g) => Object.keys(appeals[g.sk] || {}).length;
    const cards = groups.map((g, gi) => gi).filter(gi => groupCols(groups[gi]));   // the groups drawn, in order
    const prevCard = (gi) => cards[cards.indexOf(gi) - 1];
    const lastCard = cards[cards.length - 1];
    const bx = boxed ? { 'data-b': '' } : {};
    // ⛔ A CARD IS AT LEAST TWO JURISDICTIONS WIDE (Philip 2026-10-23): a one-jurisdiction card gets an empty filler
    // column after its value column, so its title and ✕ sit on one line and its date inputs show whole. The value
    // columns stay JW; a card with two or more jurisdictions is unchanged.
    const fillW = (gi) => (boxed ? Math.max(0, 2 * JW - groupCols(groups[gi]) * JW) : 0);
    const fill = (gi, Tag = 'td', style = {}) => (fillW(gi) ? <Tag key={`fill|${gi}`} data-b="" style={{ padding: 0, ...style }} /> : null);
    // A card's side padding in one row. Card 1's left padding stays pinned with its labels when the cards scroll sideways.
    const padCell = (side, gi, Tag = 'td') => (
      <Tag key={`pad${side}|${gi}`} data-b="" data-p="" {...(side === 'l' ? { 'data-l': '' } : { 'data-r': '' })}
        style={{ padding: 0, ...(side === 'l' && gi === cards[0] ? { position: 'sticky', left: 0, zIndex: 1, background: stickyBg() } : {}) }} />
    );
    // ⛔ EVERY CARD HAS ITS OWN LABEL COLUMN (Philip 2026-10-23: "each card reads on its own"). Card 1's is the row's own
    // label cell, pinned when the cards scroll sideways; each other card's comes from the row's `label(gi)` — the same
    // label unpinned (freeLabel) — or is left empty when the row passes none. `label === false`: the row's next cell
    // spans the label column itself.
    const freeLabel = ({ position, left, zIndex, background, ...rest }) => rest;
    const rowStart = (Tag = 'td') => (boxed ? padCell('l', cards[0], Tag) : null);
    // filled: the row's last cell already spans the filler column.
    const rowEnd = (Tag = 'td', filled = false) => (boxed ? <>{!filled && fill(lastCard, Tag)}{padCell('r', lastCard, Tag)}</> : null);
    // Between two cards: the left card's filler and right padding, the gap, the right card's left padding and label.
    const between = (gi, Tag = 'td', label = null, filled = false) => (boxed
      ? <React.Fragment key={`between|${gi}`}>
          {!filled && fill(prevCard(gi), Tag)}{padCell('r', prevCard(gi), Tag)}<Tag style={gapStyle} />{padCell('l', gi, Tag)}
          {label === false ? null : label ? label(gi) : <Tag data-b="" style={{ padding: 0 }} />}
        </React.Fragment>
      : <Tag key={`between|${gi}`} style={gapStyle} />);
    // A card's top or bottom padding row: one cell per card across its padding, label, columns and filler.
    const padRow = (Tag) => (
      <tr>{cards.map((gi, ci) => (
        <React.Fragment key={gi}>
          {ci > 0 && <Tag style={gapStyle} />}
          <Tag colSpan={groupCols(groups[gi]) + 3 + (fillW(gi) ? 1 : 0)} data-b="" data-p="" data-l="" data-r="" style={{ padding: 0, height: `${PAD_Y}px` }} />
        </React.Fragment>
      ))}</tr>
    );
    const bxLabel = bx;
    const rowBg = (bg) => (boxed ? { '--tint': bg } : { background: bg });
    const rowLine = (color) => (boxed ? { style: { '--line': color }, 'data-line': '' } : { style: { borderBottom: `1px solid ${color}` } });
    // A row spanning the whole table (a note, Pro-Rata's title and inputs): boxed, its content goes in card 1 and each
    // other card gets one cell of its own width, holding `others` (or nothing).
    const spanRow = (style, content, others = null) => (boxed
      ? <>{rowStart()}{cards.map((gi, ci) => (
          <React.Fragment key={gi}>
            {ci > 0 && between(gi, 'td', false, true)}
            <td colSpan={groupCols(groups[gi]) + 1 + (fillW(gi) ? 1 : 0)} style={style} data-b="">{ci === 0 ? content : others}</td>
          </React.Fragment>))}{rowEnd('td', true)}</>
      : <td colSpan={cols.length + nGaps + 1} style={style}>{content}</td>);
    const gapCell = (col, Tag = 'td', label = null) => (col.edge ? between(groups.indexOf(col.g), Tag, label) : null);
    const tdAt = (col, base) => (col.edge && !boxed ? { ...base, borderLeft: 'none' } : base);   // boxed: the card's label column is to its left
    const groupTh = { padding: '6px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: c.text, borderLeft: `1px solid ${c.border}` };
    const groupLabelTd = { padding: '4px 12px', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', ...stickyCell() };
    const groupCellTd = (g) => ({ padding: boxed ? '3px 2px' : '3px 10px', borderLeft: boxed || g.sk === groups[0].sk ? `1px solid ${c.border}` : 'none' });
    // Boxed, a header input fills its cell instead of its natural width, so it never runs past its card.
    const fitInput = boxed ? { width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '3px 4px' } : { width: 'auto', padding: '3px 6px' };
    // A group-wide header cell (title / Notice / Deadline / Decision) — after the first group, the spacer comes first.
    // Boxed, it also spans its card's filler column, and `label(gi)` gives the card's own label cell.
    const groupCell = (g, Tag, props, children, label = null) => (
      <React.Fragment key={g.sk}>
        {g.sk !== groups[0].sk && between(groups.indexOf(g), Tag, label, true)}
        <Tag colSpan={groupCols(g) + (fillW(groups.indexOf(g)) ? 1 : 0)} {...props} {...bx}>{children}</Tag>
      </React.Fragment>
    );
    const valueTypeTh = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: c.muted, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.border}`, width: `${LABEL_W}px` };
    const groupLabel = (text) => (gi) => <td key={`lab|${gi}`} data-b="" style={freeLabel(groupLabelTd)}>{text}</td>;

    return (
      <div style={boxed ? { marginBottom: s.card.marginBottom } : s.card}>
        <div style={boxed ? { ...s.cardHeader, borderBottom: 'none', padding: '4px 2px 10px' } : s.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button type="button" onClick={() => setCollapsed(prev => ({ ...prev, [level]: !prev[level] }))} title={isCollapsed ? 'Expand section' : 'Collapse section'} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '11px', padding: '0 2px', lineHeight: 1 }}>{isCollapsed ? '▶' : '▼'}</button>
            <div style={s.cardTitle}>{title}</div>
            {/* Collapsed sections still show their decision at a glance */}
            {isCollapsed && decisions[level] === 'accepted' && <span style={{ fontSize: '11px', fontWeight: '700', color: c.text }}>✓ Accepted</span>}
            {isCollapsed && decisions[level] === 'withdrawn' && <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted }}>↩ Withdrawn</span>}
            {isCollapsed && decisions[level] === 'denied' && <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted }}>✗ Denied</span>}
            {isCollapsed && !decisions[level] && decIdx < appealCount && <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--red)' }}>↑ Appealed</span>}
            {/* Proposed only — the appeal levels follow its columns. A choice already on Proposed is greyed out. */}
            {!isCollapsed && level === 'proposed' && jurisdictions.length > 0 && (
              <select value="" onChange={e => { if (e.target.value) addJurisdiction(e.target.value); }}
                style={{ ...s.select, width: 'auto', fontSize: '11px', padding: '4px 8px', fontWeight: 600, cursor: 'pointer' }}>
                <option value="">+ Add Jurisdiction</option>
                {ADD_JURISDICTIONS.map(name => <option key={name} value={name} disabled={!!levelData[name]}>{jurisdictionLabel(name)}</option>)}
              </select>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {saveErrs.length > 0 && <NotSaved title={saveErrs.join('\n')} />}
            {appealSaveMsg && <span style={{ fontSize: '11px', color: '#34d399', fontWeight: '600' }}>✓ {appealSaveMsg}</span>}
            {/* Remove-appeal-level control (appeal sections only, not Proposed) — confirm-gated, discards its data */}
            {level !== 'proposed' && mode !== 'create' && hasOriginal && (() => {
              const n = appealIndexOf(level);
              return removeLevelConfirm === n ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: c.subtext, whiteSpace: 'nowrap' }}>
                  Remove {appealTitleFor(n)}?
                  <button type="button" onClick={() => { setRemoveLevelConfirm(null); removeAppealLevel(n); }} style={{ ...s.btn, fontSize: '11px', padding: '4px 10px' }}>Confirm</button>
                  <button type="button" onClick={() => setRemoveLevelConfirm(null)} style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px' }}>Cancel</button>
                </span>
              ) : (
                <button type="button" onClick={() => setRemoveLevelConfirm(n)} title="Remove this appeal level" style={{ background: 'none', border: `1px solid ${c.border}`, color: '#f87171', cursor: 'pointer', fontSize: '11px', padding: '4px 9px', borderRadius: '6px', fontFamily: "'Public Sans',system-ui,sans-serif" }}>✕ Remove</button>
              );
            })()}
          </div>
        </div>

        {/* Collapsed → hide everything below the header (deadline, decision, grid, pro-rata) */}
        {!isCollapsed && (<>
        {/* DERIVED, NOT SAVED. Figures the screen computed rather than loaded (taxable = market × ratio, a Proposed
            level built from the scanned notice) are display-only: no Save and no year switch stores them. This
            button is the one way to store them without editing the level (editing it stores them too). */}
        {mode !== 'create' && !dirtyLevels[level] && levelHasDerived(levelData) && (() => {
          const rows = Object.values(levelData);
          const what = [rows.some(r => r._seeded_from_scanner) && 'values from the scanned notice',
                        rows.some(r => r._derived_taxable) && 'taxable filled from market × ratio'].filter(Boolean).join(' and ');
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '8px 18px', background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.08)',
              ...(boxed ? { border: `1px solid ${c.border}`, borderRadius: '12px', marginBottom: '10px' } : { borderTop: `1px solid ${c.border}` }) }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: isDark ? '#fbbf24' : '#b45309' }}>
                Derived, not saved — this section shows {what}.
              </span>
              <button type="button" onClick={() => saveAppealLevel(level)} disabled={appealSaving || !canEditYear}
                style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px' }}>Use derived values</button>
            </div>
          );
        })()}
        {/* COPY VALUES FROM THE LEVEL ABOVE — a new, empty appeal level only (a withdrawn or no-change outcome).
            Refused while the level above shows derived, unsaved figures: copying them would store a derived value
            through the back door, which "Use derived values" exists to prevent. */}
        {level !== 'proposed' && mode !== 'create' && canEditYear && hasOriginal && levelIsEmpty(levelData, deductions[level]) && (() => {
          const above = levelAbove(level);
          const aboveRows = appeals[above] || {};
          if (!Object.keys(aboveRows).length) return null;
          const aboveTitle = above === 'proposed' ? 'Proposed' : appealTitleFor(appealIndexOf(above));
          const blocked = !dirtyLevels[above] && levelHasDerived(aboveRows);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', ...(boxed ? { padding: '0 2px 10px' } : { padding: '8px 18px', borderTop: `1px solid ${c.border}` }) }}>
              <button type="button" onClick={() => copyValuesFromAbove(level)} disabled={blocked}
                title={blocked ? `${aboveTitle} shows derived figures that are not saved — use "Use derived values" there first.`
                  : `Copies every column of ${aboveTitle} — values, assessment cap, exemptions and deductions. Saved with Save All.`}
                style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px', opacity: blocked ? 0.5 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}>
                Copy values from {aboveTitle}
              </button>
              <span style={{ fontSize: '11px', color: c.muted }}>For a withdrawn or no-change outcome.</span>
            </div>
          );
        })()}
        {/* Per-section deadline — under the header, above the value grid. Last-write-wins onto the top field.
            With additional notices each group carries its own Deadline and Decision in the table header instead. */}
        {hasOriginal && !grouped && <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 18px', borderTop: `1px solid ${c.border}` }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deadline</span>
          <input type="date" value={sectionDeadlines[level] || ''} onChange={e => setSectionDeadline(level, e.target.value)}
            style={{ ...s.input, width: 'auto', fontSize: '12px', padding: '5px 8px' }} />
        </div>}

        {/* Decision — per section, under the deadline row. Appealed is DERIVED (next level exists); accepted is stored. */}
        {mode !== 'create' && jurisdictions.length > 0 && !grouped && (() => {
          const idx = level === 'proposed' ? 0 : appealIndexOf(level);
          return (
            <DecisionBar
              value={decisions[level] || (idx < appealCount ? 'appealed' : '')}
              locked={!decisions[level] && idx < appealCount}
              lockTitle={`Remove ${appealTitleFor(idx + 1)} to change`}
              onChange={(v) => changeDecision(level, idx, v)}
              err={!!decisionErr[level]}
              s={s} c={c}
            />
          );
        })()}

        {/* Non-NTRG appeal (step 7) — Appeal One only, saved at once. With notices, each group has its own in the header. */}
        {level === 'appeal_one' && mode !== 'create' && hasOriginal && !grouped && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 18px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!nonNtrg[ORIGINAL_NOTICE]} disabled={!canEditYear} onChange={e => writeNonNtrg(ORIGINAL_NOTICE, e.target.checked)} />
              Non-NTRG appeal
            </label>
            {nonNtrgErr[ORIGINAL_NOTICE] && <NotSaved />}
          </div>
        )}
        {cols.length === 0 ? null : (
          <ScrollX style={{ overflowX: 'auto' }}>
            {/* The add-notice "+" sits BESIDE the table, level with its header — not a column, so no row lines run under it. */}
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {boxed && <style>{NOTICE_BOX_CSS(c)}</style>}
            <table className={boxed ? 'pm-nb' : undefined}
              style={{ tableLayout: 'fixed', width: `${LABEL_W + cols.length * JW + nGaps * GAP_W
                + (boxed ? cards.reduce((w, gi, ci) => w + 2 * PAD_X + fillW(gi) + (ci ? LABEL_W : 0), 0) : 0)}px`, borderCollapse: boxed ? 'separate' : 'collapse', borderSpacing: 0, flex: '0 0 auto' }}>
              {/* Explicit widths: with notice groups the first header row is the group titles (colSpan), which would
                  otherwise set the column widths under table-layout:fixed. Same widths as the live card. */}
              <colgroup>
                {boxed && <col style={{ width: `${PAD_X}px` }} />}
                <col style={{ width: `${LABEL_W}px` }} />
                {cols.map(col => <React.Fragment key={`${col.sk}|${col.j}`}>
                  {col.edge && boxed && fillW(prevCard(groups.indexOf(col.g))) > 0 && <col style={{ width: `${fillW(prevCard(groups.indexOf(col.g)))}px` }} />}
                  {col.edge && boxed && <col style={{ width: `${PAD_X}px` }} />}
                  {col.edge && <col style={{ width: `${GAP_W}px` }} />}
                  {col.edge && boxed && <col style={{ width: `${PAD_X}px` }} />}
                  {col.edge && boxed && <col style={{ width: `${LABEL_W}px` }} />}
                  <col style={{ width: `${JW}px` }} /></React.Fragment>)}
                {boxed && fillW(lastCard) > 0 && <col style={{ width: `${fillW(lastCard)}px` }} />}
                {boxed && <col style={{ width: `${PAD_X}px` }} />}
              </colgroup>
              <thead>
                {boxed && padRow('th')}
                {/* Additional notices: each group's title, notice date (Proposed), Deadline and Decision above its columns. */}
                {grouped && (<>
                  <tr style={rowBg(c.th)}>
                    {rowStart('th')}<th style={stickyCell(c.th)} {...bxLabel} />
                    {groups.map(g => {
                      const n = Object.keys(appeals[g.sk] || {}).length;
                      if (!n) return null;
                      const above = levelAbove(g.sk);
                      const canCopy = g.notice > 0 && level !== 'proposed' && canEditYear && levelIsEmpty(appeals[g.sk], deductions[g.sk])
                        && Object.keys(appeals[above] || {}).length > 0;
                      return groupCell(g, 'th', { style: { ...groupTh, ...(boxed || g.sk === groups[0].sk ? {} : { borderLeft: 'none' }) } }, (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                            <span>{g.notice ? noticeTypeName(g.notice) : 'Proposed'}</span>
                            <span style={{ display: 'flex', gap: '4px' }}>
                              {canCopy && <button type="button" onClick={() => copyValuesFromAbove(g.sk)} title={`Copies ${levelTitle(above)}`}
                                style={{ ...s.btnGhost, fontSize: '10px', padding: '1px 6px' }}>Copy from above</button>}
                              {g.notice > 0 && canEditYear && (
                                <button type="button" onClick={() => (level === 'proposed' ? removeNotice(g.notice) : removeNoticeLevels(g.notice, decIdx))}
                                  title={level === 'proposed' ? 'Remove this notice' : 'Remove this level'}
                                  style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px', padding: '0 2px', lineHeight: 1 }}>✕</button>
                              )}
                            </span>
                          </div>
                      ));
                    })}
                    {rowEnd('th', true)}
                  </tr>
                  {level === 'proposed' && (
                    <tr>
                      {rowStart()}<td style={groupLabelTd} {...bxLabel}>Notice</td>
                      {groups.map(g => Object.keys(appeals[g.sk] || {}).length ? groupCell(g, 'td', { style: groupCellTd(g) },
                        g.notice ? <input type="date" value={g.meta.notice_date || ''} onChange={e => setNoticeDate(g.notice, e.target.value)}
                          style={{ ...s.input, fontSize: '12px', ...fitInput }} /> : null, groupLabel('Notice')) : null)}
                      {rowEnd('td', true)}
                    </tr>
                  )}
                  <tr>
                    {rowStart()}<td style={groupLabelTd} {...bxLabel}>Deadline</td>
                    {groups.map(g => Object.keys(appeals[g.sk] || {}).length ? groupCell(g, 'td', { style: groupCellTd(g) },
                      <input type="date" value={sectionDeadlines[g.sk] || ''} onChange={e => setSectionDeadline(g.sk, e.target.value)}
                        style={{ ...s.input, fontSize: '12px', ...fitInput }} />, groupLabel('Deadline')) : null)}
                    {rowEnd('td', true)}
                  </tr>
                  {mode !== 'create' && (
                    <tr>
                      {rowStart()}<td style={groupLabelTd} {...bxLabel}>Decision</td>
                      {groups.map(g => {
                        const n = Object.keys(appeals[g.sk] || {}).length;
                        if (!n) return null;
                        const appealed = decIdx < groupDepth(g);
                        return groupCell(g, 'td', { style: groupCellTd(g) }, (
                            <span style={{ display: boxed ? 'flex' : 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <DecisionSelect fit={boxed} value={decisions[g.sk] || (appealed ? 'appealed' : '')}
                                locked={g.meta?._new || (!decisions[g.sk] && appealed)}
                                lockTitle={g.meta?._new ? 'Save All first' : `Remove ${appealTitleFor(decIdx + 1)} to change`}
                                onChange={(v) => changeDecision(g.sk, decIdx, v)} s={s} c={c} />
                              {decisionErr[g.sk] && <NotSaved />}
                            </span>
                        ), groupLabel('Decision'));
                      })}
                      {rowEnd('td', true)}
                    </tr>
                  )}
                  {mode !== 'create' && level === 'appeal_one' && (
                    <tr>
                      {rowStart()}<td style={groupLabelTd} {...bxLabel}>Non-NTRG</td>
                      {groups.map(g => Object.keys(appeals[g.sk] || {}).length ? groupCell(g, 'td', { style: groupCellTd(g) }, (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <input type="checkbox" checked={!!nonNtrg[g.notice]} disabled={!!g.meta?._new || !canEditYear}
                            title={g.meta?._new ? 'Save All first' : 'Non-NTRG appeal'} onChange={e => writeNonNtrg(g.notice, e.target.checked)} />
                          {nonNtrgErr[g.notice] && <NotSaved />}
                        </span>
                      ), groupLabel('Non-NTRG')) : null)}
                      {rowEnd('td', true)}
                    </tr>
                  )}
                </>)}
                {/* boxed, the row's line also runs under each card's own label and filler (data-line) */}
                <tr style={{ ...rowBg(c.th), ...(boxed ? { '--line': c.border } : {}) }} {...(boxed ? { 'data-line': '' } : {})}>
                  {rowStart('th')}<th {...bxLabel} style={{ ...valueTypeTh, ...stickyCell(c.th) }}>Value Type</th>
                  {cols.map((col) => { const { sk, j, edge } = col; return (
                    <React.Fragment key={`${sk}|${j}`}>{gapCell(col, 'th', (gi) => <th key={`lab|${gi}`} data-b="" style={valueTypeTh}>Value Type</th>)}
                    <th {...bx} style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: 'var(--red)', borderBottom: `1px solid ${c.border}`, borderLeft: edge && !boxed ? 'none' : `1px solid ${c.border}`, width: `${JW}px` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <span>{jurisdictionLabel(j)}</span>
                        {sk === 'proposed' && j !== 'County' && levelData[j]?.is_primary !== true && (
                          <button onClick={() => removeJurisdiction(j)}
                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px', padding: '0 2px', lineHeight: 1 }}
                            title={`Remove ${jurisdictionLabel(j)} from every level`}>✕</button>
                        )}
                      </div>
                      {/* ⛔ THE SAVINGS BASELINE IS PICKED HERE, NEVER INFERRED FROM A NAME (utils/baselineSavings). Proposed,
                          two-base, and only once Appeal One has columns to measure against. A final column already
                          claimed by another Proposed column is not offered (the database allows one per final column). */}
                      {sk === 'proposed' && twoBase && finalCols.length > 0 && (() => {
                        const mine = levelData[j]?.savings_baseline_for || '';
                        const taken = new Set(Object.entries(levelData).filter(([k, r]) => k !== j && r?.savings_baseline_for).map(([, r]) => r.savings_baseline_for));
                        return (
                          <select value={mine} title="The Appeal One column whose saving is measured from this column (e.g. a revised notice)"
                            onChange={e => setAppealRaw('proposed', j, 'savings_baseline_for', e.target.value || null)}
                            style={{ ...s.select, marginTop: '4px', width: '100%', padding: '1px 2px', fontSize: '9px', fontWeight: 400, color: mine ? c.text : c.muted }}>
                            <option value="">Savings baseline for: —</option>
                            {finalCols.filter(f => f === mine || !taken.has(f)).map(f => <option key={f} value={f}>Baseline for {jurisdictionLabel(f)}</option>)}
                          </select>
                        );
                      })()}
                    </th>
                    </React.Fragment>
                  ); })}
                  {rowEnd('th')}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => {
                  // ⛔ A CAP AT OR ABOVE THE SUBTOTAL MAKES THE TAXABLE TOTAL MEANINGLESS: it computes to 0 or to a
                  // negative taxable value. Neither is a figure, and neither says why.
                  // A cap cannot remove the whole base: it limits how much assessed value may RISE, so it is
                  // always a fraction of it. This states the problem beside the number instead of dropping the
                  // input — a field that silently discards what was typed is the shape being removed, not added.
                  // Names the jurisdictions because the cap is per-column and only one may be wrong.
                  if (row.isCapNote) {
                    const bad = cols.filter(({ sk, j }) => {
                      const r = (appeals[sk] || {})[j] || {};
                      const cap = gNum(r, 'cap_deduction');
                      if (!(cap > 0)) return false;
                      const sub = gNum(r, 'taxable_subtotal');
                      return !(sub > 0) || cap >= sub;
                    }).map(({ g, j }) => (g.notice ? `${j} (${noticeTypeName(g.notice)})` : j));
                    if (!bad.length) return null;
                    return (
                      <tr key={row.key}>
                        {spanRow({ padding: '2px 12px 8px' }, (
                          <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 600 }}>
                            ⚠ The Assessment Cap is not smaller than the Taxable Subtotal in {bad.join(', ')} — so the
                            Taxable Total is not a real figure (it computes to zero or to a negative).
                            A cap limits how much assessed value may RISE; it cannot remove the whole base, and it is
                            the amount taken off, not the capped value itself. Check which figure the notice states.
                          </div>
                        ))}
                      </tr>
                    );
                  }
                  // ⛔ A NOTE, NOT SILENCE. "No suggestion" and "no scanned bill" look identical otherwise, which is
                  // the state someone is in when they are adding millage lines up by hand not knowing whether the
                  // feature exists. ONE row, spanning every column, because the reason is a property of the bill and
                  // not of a column. (It used to be built INSIDE the per-column loop, so it printed once per column
                  // and the browser stacked the copies under the first one.)
                  if (row.isJurRateNote) {
                    if (!rateHint || !rateHint.note) return null;
                    return (
                      <tr key={row.key}>
                        {spanRow({ padding: '0 12px 8px' }, <div style={{ fontSize: '10px', color: c.muted }}>Tax Rate — {rateHint.note}</div>)}
                      </tr>
                    );
                  }
                  // Deductions: a title row (with [+]) then one row per line — the line's type dropdown sits in the
                  // label column, its amounts in each jurisdiction column, so alignment matches the value rows for free.
                  // ⛔ Lines belong to ONE group: the original's first, then each notice's, filled only under that
                  // group's columns. A notice group adds its lines from the [+] in its first column.
                  if (row.isExemptionDed) {
                    const rowBorderColor = isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6';
                    const addBtn = (sk) => <button type="button" onClick={() => addDedLine(sk)} title="Add deduction" style={{ ...s.btnGhost, fontSize: '11px', lineHeight: 1, padding: '2px 7px', flex: '0 0 auto' }}>+</button>;
                    // The title and its [+]. Boxed, each card's label adds lines to its own notice; otherwise the one
                    // label adds to the original, and a notice group's [+] sits in its first column.
                    const dedTitle = (gi) => (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <span>{row.label}</span>
                        {boxed ? addBtn(groups[gi].sk) : hasOriginal && addBtn(level)}
                      </div>
                    );
                    return (
                      <React.Fragment key={row.key}>
                        <tr {...rowLine(rowBorderColor)}>
                          {rowStart()}<td style={{ ...labelTd, color: c.subtext }} {...bxLabel}>{dedTitle(cards[0])}</td>
                          {cols.map(col => <React.Fragment key={`${col.sk}|${col.j}`}>{gapCell(col, 'td', (gi) => <td key={`lab|${gi}`} data-b="" style={{ ...freeLabel(labelTd), color: c.subtext }}>{dedTitle(gi)}</td>)}
                            <td {...bx} style={tdAt(col, numTd)}>{!boxed && col.g.notice > 0 && col.first ? addBtn(col.sk) : null}</td></React.Fragment>)}
                          {rowEnd()}
                        </tr>
                        {groups.flatMap(g => dedLines(g.sk).map((line) => { const lineType = (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingLeft: '8px' }}>
                                <select value={line.type} onChange={e => setDedType(g.sk, line.key, e.target.value)}
                                  style={{ ...s.select, fontSize: '12px', padding: '3px 4px', flex: '1 1 auto', minWidth: 0, color: c.text }}>
                                  <option value="">— type —</option>
                                  {exemptionOpts.map(et => <option key={et} value={et}>{et}</option>)}
                                </select>
                                <button type="button" onClick={() => removeDedLine(g.sk, line.key)} title="Remove deduction" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px', flex: '0 0 auto' }}>✕</button>
                              </div>);
                          return (
                          <tr key={`${g.sk}|${line.key}`} {...rowLine(rowBorderColor)}>
                            {rowStart()}<td style={labelTd} {...bxLabel}>{!boxed || groups[cards[0]].sk === g.sk ? lineType : null}</td>
                            {cols.map(col => <React.Fragment key={`${col.sk}|${col.j}`}>{gapCell(col, 'td', (gi) => <td key={`lab|${gi}`} data-b="" style={freeLabel(labelTd)}>{groups[gi].sk === g.sk ? lineType : null}</td>)}{col.sk !== g.sk ? <td {...bx} style={tdAt(col, numTd)} /> : (
                              <td {...bx} style={tdAt(col, inTd)}>
                                <ValueCell level={g.sk} j={col.j} rowKey={`ded_${line.key}`} initialValue={line.amounts[col.j] != null ? line.amounts[col.j] : ''} inputStyle={valInput} onCommit={v => setDedAmount(g.sk, line.key, col.j, v)} />
                              </td>
                            )}</React.Fragment>)}
                            {rowEnd()}
                          </tr>
                        ); }))}
                      </React.Fragment>
                    );
                  }
                  return (
                    <tr key={row.key} {...(boxed ? {
                        style: { '--line': isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6', ...(row.highlight ? { '--tint': isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)' } : {}) },
                        'data-line': '', ...(row.highlight ? {} : { 'data-hov': '' }),
                      } : {
                        style: { background: row.highlight ? (isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)') : 'transparent', borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}` },
                        onMouseEnter: e => { if (!row.highlight) e.currentTarget.style.background = c.rowHover; },
                        onMouseLeave: e => { if (!row.highlight) e.currentTarget.style.background = 'transparent'; },
                      })}>
                      {rowStart()}<td {...bxLabel} style={{ ...labelTd, ...(row.highlight ? stickyCell(isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)') : {}), fontWeight: row.bold ? '700' : '400', color: row.bold ? c.text : c.subtext }}>
                        {row.label}
                      </td>
                      {cols.map((col) => <React.Fragment key={`${col.sk}|${col.j}`}>{gapCell(col, 'td', (gi) => (
                        <td key={`lab|${gi}`} data-b="" style={{ ...freeLabel(labelTd), fontWeight: row.bold ? '700' : '400', color: row.bold ? c.text : c.subtext }}>{row.label}</td>
                      ))}{(() => {
                        const { sk, j } = col;
                        const rowData = (appeals[sk] || {})[j] || {};
                        // The Tax Rate is the year's, one per jurisdiction — entered under the original group only.
                        if (row.isJurRate && sk !== 'proposed') return <td {...bx} key={`${sk}|${j}`} style={tdAt(col, numTd)} />;
                        if (row.isJurRate) {
                          const rr = jurRates[j] || {};
                          const unit = rr.rate_unit === 'mills' ? 'mills' : 'percent';
                          const pct = ratePercentOf(rr);
                          return (
                            <td {...bx} key={`${sk}|${j}`} style={tdAt(col, numTd)}>
                              {/* Inside the column: the rate box on its own line, the unit and "= x%" under it (wrapping again if
                                  they don't fit side by side). */}
                              <input type="text" value={rr.tax_rate ?? ''} placeholder="—"
                                onChange={e => setJurRate(j, { tax_rate: e.target.value })}
                                style={{ ...valInput, padding: '4px 6px', textAlign: 'right', boxSizing: 'border-box' }} />
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', columnGap: '4px', rowGap: '2px', marginTop: '3px', whiteSpace: 'nowrap' }}>
                                {/* ⛔ A TOGGLE, NOT AN INFERENCE. Florida quotes millage, California percent, and a
                                    factor-of-ten on a rate is this codebase's most frequent error — three times in two
                                    weeks. detectRateUnit infers from a ratio and, on a two-base bill with a large cap,
                                    infers WRONG (its mills window is +/-5% and a 2:1 base split pushes it to ~6.7).
                                    Two clicks beats a silent 10x. */}
                                <select value={unit} onChange={e => setJurRate(j, { rate_unit: e.target.value })}
                                  title="The unit the notice states this rate in"
                                  style={{ ...s.select, width: 'auto', padding: '2px 4px', fontSize: '10px' }}>
                                  <option value="percent">%</option>
                                  <option value="mills">mills</option>
                                </select>
                                {/* The converted figure, so a mis-set toggle is visible as an implausible rate. */}
                                <span style={{ fontSize: '9px', color: c.muted, marginLeft: 'auto' }}>
                                  {pct != null ? `= ${pct.toFixed(4)}%` : ''}
                                </span>
                              </div>
                              {/* ⛔ A SUGGESTION, NEVER A FILL. Clicking writes the value AND the unit; nothing
                                  happens on its own. The TS reading this figure back against the notice is the
                                  check, which is why the label says WHAT was added up and from how many bills
                                  rather than just offering a number. */}
                              {(() => {
                                const lbl = suggestionLabel(rateHint, j);
                                if (!lbl) return null;
                                const sug = rateHint.byJurisdiction[j];
                                const already = ratePercentOf(rr) != null
                                  && Math.abs(ratePercentOf(rr) - (sug.unit === 'mills' ? sug.rate / 10 : sug.rate)) < 1e-9;
                                return (
                                  <div style={{ marginTop: '3px', textAlign: 'right' }}>
                                    {already
                                      ? <span style={{ fontSize: '9px', color: c.muted }}>✓ {lbl}</span>
                                      : <button type="button" onClick={() => setJurRate(j, { tax_rate: String(sug.rate), rate_unit: sug.unit })}
                                          title={`Totals the scanned bill's ${sug.lineCount} rate-bearing lines for this base and sets the unit. Check it against the notice.`}
                                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: '9px', color: 'var(--red)', cursor: 'pointer', textDecoration: 'underline' }}>
                                          {lbl} — use this
                                        </button>}
                                  </div>
                                );
                              })()}
                            </td>
                          );
                        }
                        if (row.isRatio) {
                          const overridden = !!rowData.assessment_ratio_override;
                          return (
                            <td {...bx} key={`${sk}|${j}`} style={tdAt(col, numTd)}>
                              {/* Override at cell start (just right of the divider); ratio value right-aligned like every other number. */}
                              {/* Wraps inside the column: the percentage drops under "Override" (right-aligned) when both don't fit. */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', columnGap: '8px', rowGap: '2px', whiteSpace: 'nowrap' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: c.muted, cursor: 'pointer', flex: '0 0 auto' }}>
                                  <input type="checkbox" checked={overridden} onChange={e => {
                                    // Checking OR unchecking is a ratio-state change → snapshot prior state, apply, then
                                    // open the gated popover. Dismiss without a comment auto-reverts (checkbox can't bypass).
                                    ensureRatioSnapshot(sk, j);
                                    setAppealRaw(sk, j, 'assessment_ratio_override', e.target.checked);
                                    openRatioPop(sk, j, e, 'check');
                                  }} /> Override
                                </label>
                                {/* value + reason indicator. Read-only %: CLICK opens the edit popover (the only place the
                                    % can change — so the "comment required" rule can't be bypassed); HOVER shows history. */}
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flex: '0 0 auto', marginLeft: 'auto', cursor: overridden ? 'pointer' : 'default' }}
                                  onMouseEnter={overridden ? (e => openRatioPop(sk, j, e, 'hover')) : undefined}
                                  onClick={overridden ? (e => { ensureRatioSnapshot(sk, j); openRatioPop(sk, j, e, 'check'); }) : undefined}
                                  title={overridden ? 'Click to edit ratio / add comment' : undefined}>
                                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '12px', color: c.text }}>
                                    {overridden
                                      ? (rowData.assessment_ratio != null && String(rowData.assessment_ratio) !== '' ? pctToInput(rowData.assessment_ratio) : '—')
                                      : <>{assessorRatio != null && assessorRatio !== '' ? pctToInput(assessorRatio) : '—'}{assessorRatio != null && <span style={{ fontSize: '9px', color: c.muted }}> (a)</span>}</>}
                                  </span>
                                  {overridden && (rowData.assessment_ratio_comment || '').trim() && <span title="Comment on file — hover to view, click to edit" style={{ color: 'var(--red)', fontSize: '12px', lineHeight: 1 }}>●</span>}
                                </span>
                              </div>
                            </td>
                          );
                        }
                        if (row.computed) {
                          // ⛔ Market Total is DERIVED here, not read. The stored column is only ever written
                          // by recomputeTotals() on a user EDIT — nothing populates it on load, import or
                          // scan — so a row with Market Land and Market Imp could still show "—" (and blank
                          // Val/SF downstream). Summing at render makes the total agree with the components
                          // above it however the row was created. Nothing else in the appeal flow changes:
                          // the stored column and the save path are untouched, and the next edit writes the
                          // same number recomputeTotals already computes.
                          // ⛔ A STATED MARKET TOTAL IS TYPED HERE while Land, Imp and PP are all blank — a board decision
                          // or bill can give only a total (30-3129-029-0010 / 2024: 24,985,750). Once any component is
                          // filled it is their sum again and reads only (Philip, 2026-10-09).
                          if (row.key === 'market_total' && marketTotalIsStated(rowData)) {
                            return (
                              <td {...bx} key={`${sk}|${j}`} style={tdAt(col, inTd)} title="No Land / Imp / PP: type the stated total. Filling any component makes it their sum again.">
                                <ValueCell level={sk} j={j} rowKey={row.key} initialValue={rowData.market_total ?? ''} inputStyle={{ ...valInput, fontWeight: '700' }} onCommit={v => updateAppealField(sk, j, 'market_total', v)} />
                              </td>
                            );
                          }
                          const shown = row.key === 'market_total' ? marketTotalOf(rowData) : rowData[row.key];
                          return (
                            <td {...bx} key={`${sk}|${j}`} style={{ ...tdAt(col, numTd), fontFamily: "'DM Mono',monospace", fontSize: '12px', fontWeight: '700', color: row.highlight ? 'var(--red)' : c.text }}>
                              {shown != null ? fmt(shown) : '—'}
                            </td>
                          );
                        }
                        return (
                          <td {...bx} key={`${sk}|${j}`} style={tdAt(col, inTd)}>
                            <ValueCell level={sk} j={j} rowKey={row.key} initialValue={rowData[row.key] || ''} inputStyle={valInput} onCommit={v => updateAppealField(sk, j, row.key, v)} />
                          </td>
                        );
                      })()}</React.Fragment>)}
                      {rowEnd()}
                    </tr>
                  );
                })}
                {/* Pro-rata renders in EVERY level (Stage E). Line items (%s) are shared per-parcel; each level's
                    Market/Taxable amounts compute from each column's own totals × the shared %s (every group). */}
                {(() => {
                  const num = v => (typeof v === 'number' ? v : parseFloat(String(v || '').replace(/[^0-9.]/g, '')) || 0);
                  const amtTd = { ...numTd, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: c.text };
                  const proLabel = (text) => (gi) => <td key={`lab|${gi}`} data-b="" style={{ ...freeLabel(labelTd), color: c.subtext }}>{text}</td>;
                  const proLbl = { display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }; // label stacked ABOVE its field
                  return (
                    <>
                      <tr>{spanRow({ padding: '10px 12px 4px', borderTop: `2px solid ${c.border}` }, (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: c.text }}>Pro-Rata Allocation</span>
                          {/* bare [+]: reveals the section (first click) or adds another allocation line (when shown) */}
                          <button type="button" onClick={() => { if (!proShown) setProShown(true); else addProLine(); }} title={proShown ? 'Add allocation' : 'Add pro-rata allocation'} style={{ ...s.btnGhost, fontSize: '13px', lineHeight: 1, padding: '2px 8px' }}>+</button>
                          {proShown && <span style={{ fontSize: '11px', fontWeight: '600', color: proValid() ? '#34d399' : '#f87171' }}>Σ {proSum()}%{proValid() ? '' : ' — must equal 100%'}</span>}
                          {/* The split is the parcel's for every year, or this year's own override (utils/prorata). */}
                          {proShown && (
                            <select value={proYearOnly ? 'year' : 'all'} onChange={e => setProScope(e.target.value === 'year')} title="Which years this split applies to"
                              style={{ ...s.input, fontSize: '11px', padding: '2px 6px', width: 'auto', textTransform: 'none', letterSpacing: 0 }}>
                              <option value="all">Every year</option>
                              <option value="year">{valueYear} only</option>
                            </select>
                          )}
                          {proShown && <button type="button" onClick={closeProrata} title="Close pro-rata (discard unsaved allocations)" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '2px 4px' }}>✕</button>}
                        </div>
                      ), <span style={{ fontSize: '12px', fontWeight: '700', color: c.text }}>Pro-Rata Allocation</span>)}</tr>
                      {proShown && proLines.map((line, li) => {
                        const pct = num(line.pro_rata_pct);
                        return (
                          <React.Fragment key={line.key}>
                            {/* horizontal input row: Prop/Tenant · ID Code · Pro Rata % */}
                            <tr>
                              {spanRow({ padding: '5px 12px' }, (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                                  {proLines.length > 1 && <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted, alignSelf: 'center' }}>#{li + 1}</span>}
                                  {/* One of the client's properties under this parcel's assessor (it counts in reports), or No
                                      Rights: a line that is not the client's, left out of reports — its name editable. */}
                                  <label style={proLbl}>Prop/Tenant
                                    <select value={line.property_id || (line.other ? '__other' : '')} onChange={e => pickProProperty(line.key, e.target.value)}
                                      style={{ ...s.input, fontSize: '11px', padding: '4px 6px', width: '190px', textTransform: 'none', letterSpacing: 0 }}>
                                      <option value="">— pick a property —</option>
                                      {clientProps.map(p => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
                                      {line.property_id && !clientProps.some(p => p.id === line.property_id) && <option value={line.property_id}>{line.prop_tenant || line.property_id}</option>}
                                      <option value="__other">No Rights</option>
                                    </select></label>
                                  {line.other && <label style={proLbl}>Name
                                    <input value={line.prop_tenant} onChange={e => setProLine(line.key, 'prop_tenant', e.target.value)} placeholder="No Rights" style={{ ...s.input, fontSize: '11px', padding: '4px 8px', width: '150px', textTransform: 'none', letterSpacing: 0 }} /></label>}
                                  <label style={proLbl}>ID Code
                                    <input value={line.id_code} onChange={e => setProLine(line.key, 'id_code', e.target.value)} placeholder="—" style={{ ...s.input, fontFamily: "'DM Mono',monospace", fontSize: '11px', padding: '4px 6px', width: '55px', textTransform: 'none', letterSpacing: 0 }} /></label>
                                  <label style={proLbl}>Pro Rata %
                                    <input value={line.pro_rata_pct} onChange={e => setProLine(line.key, 'pro_rata_pct', e.target.value.replace(/[^0-9.]/g, ''))} placeholder="%" style={{ ...s.inputMono, width: '46px', padding: '4px 5px' }} /></label>
                                  {proLines.length > 1 && (proDelKey === line.key ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', alignSelf: 'center', fontSize: '10px', color: c.subtext, whiteSpace: 'nowrap' }}>
                                      Remove this allocation?
                                      <button type="button" onClick={() => { removeProLine(line.key); setProDelKey(null); }} style={{ ...s.btn, fontSize: '10px', padding: '2px 7px' }}>Confirm</button>
                                      <button type="button" onClick={() => setProDelKey(null)} style={{ ...s.btnGhost, fontSize: '10px', padding: '2px 7px' }}>Cancel</button>
                                    </span>
                                  ) : (
                                    <button type="button" onClick={() => setProDelKey(line.key)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '13px', alignSelf: 'center' }} title="Remove allocation">✕</button>
                                  ))}
                                </div>
                              ))}
                            </tr>
                            {/* aligned amount rows */}
                            <tr>
                              {rowStart()}<td style={{ ...labelTd, color: c.subtext }} {...bxLabel}>Market Value</td>
                              {cols.map(col => <React.Fragment key={`${col.sk}|${col.j}`}>{gapCell(col, 'td', proLabel('Market Value'))}<td {...bx} style={tdAt(col, amtTd)}>{fmt(pct / 100 * num(((appeals[col.sk] || {})[col.j] || {}).market_total))}</td></React.Fragment>)}
                              {rowEnd()}
                            </tr>
                            <tr {...rowLine(c.border)}>
                              {rowStart()}<td style={{ ...labelTd, color: c.subtext }} {...bxLabel}>Taxable Value</td>
                              {cols.map(col => <React.Fragment key={`${col.sk}|${col.j}`}>{gapCell(col, 'td', proLabel('Taxable Value'))}<td {...bx} style={tdAt(col, amtTd)}>{fmt(pct / 100 * num(((appeals[col.sk] || {})[col.j] || {}).taxable_total))}</td></React.Fragment>)}
                              {rowEnd()}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
                {boxed && padRow('td')}
              </tbody>
            </table>
            {/* Add a notice (Supplemental, Corrected, …) — opens the Notice Type window. Right of the last group. */}
            {showAddNotice && (
              <button type="button" onClick={() => setNoticePicker(true)} title="Add a notice"
                style={{ ...s.btnGhost, fontSize: '13px', lineHeight: 1, padding: '2px 8px', margin: '6px 8px', flex: '0 0 auto' }}>+</button>
            )}
            </div>
          </ScrollX>
        )}
        </>)}
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────
  const finalInfo = cardFinal();   // { level, outcome } — the savings box sits under the final level's section
  const figs = cardFigures();      // the reports' own figures for this parcel-year
  const baseSavings = figs.card.marked;   // per-base breakdown shown under the total (null = no markers)
  // The savings box. `levelTitle` = the final appeal level it sits under; null = Proposed stands ("$0 — value accepted").
  const renderSavingsBox = (levelTitle) => {
    const sv = figs.card;
    return (
      <div style={{ background: isDark ? 'rgba(52,211,153,0.06)' : 'rgba(52,211,153,0.04)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '12px', padding: '18px 24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
            Total Tax Savings
            {figs.outcome === 'pending' && <span style={{ color: '#f59e0b', marginLeft: '8px' }}>Pending — not final</span>}
            {sv.viaPriorRate && <span style={{ color: c.muted, marginLeft: '8px', textTransform: 'none', letterSpacing: 0 }}>Estimated (prior-year rate)</span>}
          </div>
          {!levelTitle ? (
            <div style={{ fontSize: '11px', color: c.muted }}>The Proposed value stands, so nothing came off it.</div>
          ) : sv.nonNtrg ? (
            // Step 7: Appeal One was someone else's appeal — NTRG's savings start at its result.
            <div style={{ fontSize: '11px', color: c.muted }}>Non-NTRG Appeal One — savings from Appeal One's result.</div>
          ) : sv.fromEntered ? (
            // The Taxes tab's Appeal Tax $ (report) Proposed − Final won over the bill / rate methods.
            <div style={{ fontSize: '11px', color: c.muted }}>From the entered Appeal Tax $</div>
          ) : sv.fromBills ? (
            // Read off the bills, to the cent, as the county billed it (utils/billSavings) — say which way.
            <div style={{ fontSize: '11px', color: c.muted, lineHeight: 1.6 }}>
              {sv.fromBills.method === 'bills'
                ? <>From the original and revised bills: ad valorem {fmtc(sv.fromBills.baselineAdValorem)} − {fmtc(sv.fromBills.finalAdValorem)}</>
                : sv.fromBills.method === 'entered'
                ? <>From the original bill total (entered) and the revised bill: ad valorem {fmtc(sv.fromBills.baselineAdValorem)} − {fmtc(sv.fromBills.finalAdValorem)}</>
                : <>From the {valueYear} {sv.fromBills.side === 'final' ? 'final' : 'original'} bill, line by line ({sv.fromBills.lineCount} lines, each rounded to the cent as the county bills)</>}
            </div>
          ) : baseSavings ? (
            // One line per base: baseline column → final-level column = reduction × that base's rate (and where
            // the rate came from), so the figure can be checked against the notices and the bill.
            <div style={{ fontSize: '11px', color: c.muted, lineHeight: 1.6 }}>
              {baseSavings.bases.map(b => (
                <div key={b.final}>
                  <b style={{ color: c.text }}>{jurisdictionLabel(b.final)}</b>{' '}
                  {b.baselineColumn ? `${jurisdictionLabel(b.baselineColumn)} ${fmtInt(b.baseline)} → ${fmtInt(b.finalValue)}` : ''}
                  {b.reduction != null && ` = ${fmtInt(b.reduction)}`}
                  {b.reduction ? (b.pct != null ? ` × ${b.pct.toFixed(4)}% (${b.rateSource}) = ${fmtc(b.savings)}` : '') : ''}
                  {b.note && <span style={{ color: '#f59e0b' }}> — {b.note}</span>}
                </div>
              ))}
              <div>Measured from each base's marked baseline (Savings baseline for …), at that base's own rate.</div>
            </div>
          ) : (
            <div style={{ fontSize: '11px', color: c.muted }}>
              {sv.ratePct != null
                ? `Proposed Taxable Total − ${levelTitle} × ${sv.ratePct.toFixed(4)}% (${sv.rateYear} bill)`
                : sv.savings != null ? 'Each jurisdiction at its own Tax Rate row rate'
                : 'No figure: no usable bill rate for this year or any earlier year'}
            </div>
          )}
          {/* The pre-appeal reference bill: the filed original's lines, so savings can be original − revised to the cent. */}
          {levelTitle && (() => {
            const refs = savingsRefs.get(String(valueYear)) || [];
            return (
              <div style={{ fontSize: '11px', color: c.muted, marginTop: '6px' }}>
                {refs.length ? refs.map(r => (
                  <div key={r.id}>
                    {r.source === 'entered'
                      ? <>Pre-appeal reference: original bill total entered — ad valorem {fmtc(r.total_ad_valorem)}{r.note ? ` (${r.note})` : ''}</>
                      : <>Pre-appeal reference: {r.file_exchange?.file_name || 'filed original'} — ad valorem {fmtc(r.total_ad_valorem)}</>}{' '}
                    <span onClick={() => removeSavingsRef(r.id)} style={{ color: 'var(--red)', cursor: 'pointer', borderBottom: '1px dotted var(--red)' }}>Remove</span>
                  </div>
                )) : (
                  <span onClick={() => setRefScanOpen(true)} style={{ color: 'var(--red)', cursor: 'pointer', fontWeight: 600, borderBottom: '1px dotted var(--red)' }}>
                    Add the pre-appeal reference (scan the filed original, or type its total)
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '32px', fontWeight: '700', color: '#34d399', whiteSpace: 'nowrap' }}>
          {/* Savings are money, so cents always (fmtc) — fmt is whole dollars, for assessed values. */}
          {!levelTitle ? <>$0.00 <span style={{ fontSize: '14px' }}>— value accepted</span></> : sv.savings != null ? fmtc(sv.savings) : '—'}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 9998, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      {...backdropProps(requestClose)}>
      <div style={{ background: isDark ? 'var(--ink)' : 'var(--bg)', width: '100%', maxWidth: '1300px', height: `${modalHeight}vh`, maxHeight: '98vh', borderRadius: '18px 18px 0 0', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 48px rgba(0,0,0,0.45)', animation: 'slideUp 0.25s ease' }}>

        {/* ── DRAG HANDLE ── */}
        <div onMouseDown={onDragStart}
          style={{ flexShrink: 0, padding: '8px 0 4px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'ns-resize', borderRadius: '18px 18px 0 0', background: isDark ? 'rgba(26,34,56,0.8)' : '#ffffff' }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: isDark ? '#434C68' : '#c8ccd8' }} />
        </div>

        {/* ── MODAL HEADER ── */}
        <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${c.border}`, flexShrink: 0, background: isDark ? 'rgba(26,34,56,0.8)' : '#ffffff', borderRadius: '18px 18px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '3px' }}>
                <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>{mode === 'create' ? 'Add Parcel' : (parcelData.parcel_name || 'Unnamed Parcel')}</div>
                <span style={s.badge(isRE ? '96,165,250' : '167,139,250')}>{isRE ? 'Real Estate' : 'Personal Property'}</span>
                <span style={s.badge(
                  parcelData.appeal_status === APPEAL_STATUS.value_pending ? '180,0,0' :
                  parcelData.appeal_status === APPEAL_STATUS.under_review ? '96,165,250' :
                  parcelData.appeal_status === APPEAL_STATUS.value_accepted ? '52,211,153' :
                  parcelData.appeal_status === APPEAL_STATUS.appeal_open ? '251,191,36' : '167,139,250'
                )}>{parcelData.appeal_status || APPEAL_STATUS.value_pending}</span>
                {mode !== 'create' && (() => {
                  const rl = retirementLabel(parcelData, propRetire);
                  if (!rl) return null;
                  // ⛔ CLEAR sits HERE, beside the badge, because the badge is the only retirement surface that
                  // renders on EVERY year. The Parcel Status dropdown shows 'Retired' only from inside the retired
                  // range, and since the 2026-08-23 exclusive-end flip that range is no longer represented — so the
                  // land-once effect (:1581) drops you on the last represented year on re-open, where the dropdown
                  // reads Active and the retirement is unreachable. This makes undo reachable from anywhere without
                  // touching the auto-navigate.
                  // PARCEL's own retirement only: a property retirement cascades to every sibling and is not this
                  // modal's to undo. Deliberately does NOT write parcel_status — the dropdown's own undo path
                  // (:2856) does, which is how a stored 'Final Year' gets silently replaced with 'Active'.
                  // Local edit like every other field: the review panel lists retired_year, Save All commits.
                  const own = parcelData.retired_year != null;
                  return (
                    <>
                      <span style={s.badge('148,163,184')}>{rl}</span>
                      {own && editMode && (
                        <button type="button"
                          onClick={() => setParcelData(p => ({ ...p, retired_year: null, reactivated_year: null }))}
                          title="Clear this parcel's retirement — it becomes ongoing again. Save All to commit."
                          style={{ marginLeft: '2px', padding: '2px 8px', background: 'transparent', border: '1px solid rgba(148,163,184,0.5)', borderRadius: '10px', color: c.muted, fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', cursor: 'pointer' }}>
                          CLEAR
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* Editable in create AND edit. The title above is a read-only heading in both modes, so this
                  input is the only way to set or change the name; it tracks parcelData, so the heading
                  updates as you type. autoFocus on create only — stealing focus on every Edit click is not. */}
              {editMode && (
                <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <div style={{ ...s.label, color: 'var(--red)' }}>Parcel Name *</div>
                  <input
                    value={parcelData.parcel_name || ''}
                    onChange={e => setParcelData(p => ({ ...p, parcel_name: e.target.value }))}
                    placeholder="Enter parcel name…"
                    autoFocus={mode === 'create'}
                    style={{ ...s.input, fontSize: '16px', fontWeight: '600', padding: '9px 12px' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: c.subtext }}>
                {/* ⛔ SAY WHICH ACCOUNT. This read "Acct: 4268001052" on The Pen Factory — the viewed year's (2022) AIN —
                    and was taken for an overwrite of the base, which still held -053. The year's own value is labelled
                    with its year, and the base is shown beside it whenever the two differ. */}
                {parcelData.account_number && (() => {
                  const digits = (v) => String(v || '').replace(/\D/g, '');
                  const fromYear = !!acctSource.year;
                  const showBase = fromYear && acctSource.base && digits(acctSource.base) !== digits(parcelData.account_number);
                  return (<>
                    <span style={{ fontFamily: 'monospace' }}>Acct{fromYear ? ` (${valueYear})` : ''}: {parcelData.account_number}</span>
                    {showBase && <span style={{ fontFamily: 'monospace' }} title="The parcel's base account — what scans and bills are matched against">Base: {acctSource.base}</span>}
                  </>);
                })()}
                {parcelData.assessor && <span>🏛 {parcelData.assessor}</span>}
                {parcelData.client_name && <span>👤 {parcelData.client_name}</span>}
                {parcelData.state && <span>📍 {parcelData.state}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {/* E2a: Documents + Key Contacts depend on a saved parcel — hidden in create mode */}
              {mode !== 'create' && (<>
              <button onClick={() => setDocsOpen(true)} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid rgba(96,165,250,0.35)', borderRadius: '8px', color: '#60a5fa', fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: '600' }}>📁 Documents</button>
              {/* Key Contacts toggle */}
              <button onClick={() => setContactsOpen(o => !o)}
                style={{ padding: '6px 12px', background: contactsOpen ? 'rgba(180,0,0,0.15)' : 'transparent', border: `1px solid ${contactsOpen ? 'var(--red)' : c.inputBorder}`, borderRadius: '8px', color: contactsOpen ? 'var(--red)' : c.subtext, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: '600' }}>
                👥 Key Contacts {contactsOpen ? '▲' : '▼'}
              </button>
              </>)}
              <button onClick={requestClose} style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', color: '#f87171', fontSize: '12px', padding: '6px 14px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: '600' }}>✕ Close</button>
            </div>
          </div>

          {/* ⛔ TWO REASONS A YEAR IS READ-ONLY, and they need different sentences and different advice.
              This said only "set Parcel Status to Active to reactivate" — correct for a RETIRED parcel, and
              actively WRONG before a first_year: the parcel is not retired, so retirementLabel() returns ''
              and the sentence read as a fragment, while the advice did nothing. That is what turned a
              correctly-disabled save button into an hour of "it's dead and there's no error".
              Name the cause, point at the control that fixes it. */}
          {/* ⛔ THIS BANNER WAS GATED ON THE SAVE GATE, AND THE TWO HAVE NOW SEPARATED. canEditYear
              (now yearAcceptsData) admits a BASELINE year, so keying the banner off it would have left a
              pre-engagement year silently unlabelled — enterable, editable, and looking exactly like a year
              we managed. That is the one thing this change must not do. A baseline year is labelled MORE
              carefully than a closed one, not less: it is the only state where data can be entered for a
              year we were not engaged. */}
          {mode !== 'create' && yearIsBaseline() && (
            <div style={{ padding: '8px 12px', marginTop: '10px', borderRadius: '8px',
              border: '1px solid rgba(251,191,36,0.55)', background: isDark ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.12)',
              fontSize: '12px', fontWeight: 600, color: isDark ? '#fbbf24' : '#8A5B00' }}>
              ⚠ Baseline year — we were not engaged in {valueYear}. Values and taxes can be entered here as the
              client's figures from before we took this on, for comparison. No appeal work was performed, and this
              year generates no deadlines, worklist rows or bills.
            </div>
          )}
          {mode !== 'create' && !canEditYear && (() => {
            const y = String(valueYear);
            const pFirst = parcelData.first_year, prFirst = propRetire?.first_year;
            const beforeParcel = pFirst != null && pFirst !== '' && y < String(pFirst);
            const beforeProp   = prFirst != null && prFirst !== '' && y < String(prFirst);
            const box = { padding: '8px 12px', marginTop: '10px', borderRadius: '8px', border: '1px solid var(--red)', background: 'rgba(180,0,0,0.06)', color: 'var(--red)', fontSize: '12px', fontWeight: 600 };
            if (beforeParcel || beforeProp) return (
              <div style={box}>
                ⚠ Not represented in {valueYear} — this {beforeParcel ? 'parcel' : 'property'} begins in {beforeParcel ? pFirst : prFirst}.{' '}
                Viewing history only; change <strong>First Year</strong> on the {beforeParcel ? 'parcel' : 'property'} if that start is wrong.
              </div>
            );
            return (
              <div style={box}>
                ⚠ Not represented in {valueYear} — {retirementLabel(parcelData, propRetire)}. Viewing history only; set Parcel Status to Active to reactivate.
              </div>
            );
          })()}

          {/* E1a: anchor nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginTop: '10px', flexWrap: 'wrap' }}>
            {[
              { label: 'Parcel Details', onClick: () => scrollToSection(parcelDetailsRef) },
              { label: 'Appeals', onClick: () => scrollToSection(appealsRef) },
              { label: 'Taxes', onClick: () => scrollToSection(taxesRef) },
              { label: 'Compliance', onClick: () => scrollToSection(complianceRef) },
              ...(mode !== 'create' ? [{ label: `Notes (${notesCount})`, onClick: () => scrollToSection(notesRef) }] : []),
            ].map(item => (
              <button key={item.label} onClick={item.onClick} style={{ background: 'none', border: 'none', padding: 0, fontSize: '12px', fontWeight: '600', color: c.subtext, cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", letterSpacing: '0.02em' }}>{item.label}</button>
            ))}
          </div>

          {/* Value Year (top) — synced with the selector above the value section (no compare here) */}
          {mode !== 'create' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Value Year</div>
              {valueYears.map(yr => {
                const isActive = valueYear === yr;
                // ⛔ TAG THE REASON, NOT JUST THE FACT. This said '·retired' for every non-represented year, so a
                // parcel whose first_year is 2025 read "2021 ·retired" — asserting we retired something we never
                // held, while the banner two inches away correctly said it had not begun. One helper for both.
                // ⛔ ONE CALL, NOT A COMBINATION. yearChip returns the state, the tag and the tooltip
                // together, so a tab cannot disagree with the gate or with the banner. '·not yet' is now
                // reserved for a year that genuinely cannot hold data; a pre-engagement year inside the
                // baseline window says '·baseline'. Two different facts no longer share a word.
                const chip = yearChip(parcelData, propRetire, yr);
                const rep = chip.state === 'represented';   // dim the rest; all still navigable
                return (
                  <button key={yr} onClick={() => switchValueYear(yr)} title={chip.title}
                    style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", border: isActive ? '1px solid #B40000' : `1px solid ${c.border}`, background: isActive ? 'rgba(180,0,0,0.15)' : 'transparent', color: isActive ? 'var(--red)' : c.muted, opacity: rep ? 1 : 0.4 }}>
                    {yr}{chip.tag ? ` ${chip.tag}` : ''}
                  </button>
                );
              })}
            </div>
          )}

          {/* Key Contacts panel — NTRG = staff (client_role_assignments). Expert/Client/Delivery = contact_properties
              resolved for THIS parcel, SCOPE-AWARE by re_pp (resolveContactsForParcel). N people per role, stacked.
              Delivery is a CONDITIONAL column: shown only when ≥1 delivery role is populated (and then only the populated
              roles), so delivery-less parcels stay 3 full-width columns. Read-only — assign/edit on the property card. */}
          {contactsOpen && (() => {
            const asgClientId = parcel?.client_id || property?.client_id || null;
            const ntrgValue = (label) => {
              if (!asgClientId) return <span style={{ color: c.muted }}>—</span>;
              if (label === 'Client Manager') { const t = roleNames(asg.assignments, asg.staff, asgClientId, 'CM'); return t ? <span style={{ color: c.text }}>{t}</span> : <span style={{ color: c.muted }}>—</span>; }
              if (label === 'Client Specialist') { const t = roleNames(asg.assignments, asg.staff, asgClientId, 'CS'); return t ? <span style={{ color: c.text }}>{t}</span> : <span style={{ color: c.muted }}>—</span>; }
              if (label === 'Tax Specialist') return <ParcelTsDisplay assignments={asg.assignments} clientId={asgClientId} parcel={{ state: parcelData.state, ts_id: parcel?.ts_id }} staffName={asg.staffName} darkMode={darkMode} />;
              return <span style={{ color: c.muted }}>—</span>;
            };
            // The four firm-personnel EXPERT roles read parcel_contact_roles (per parcel, per value year).
            // Attorney Contact is NOT firm personnel and keeps reading contact_properties via kcContacts,
            // exactly as before — as do all client and delivery roles.
            const PCR_ROLES = ['consultant', 're_appeal_agent', 're_compliance', 'bpp_compliance'];
            const contactValue = (code) => {
              if (PCR_ROLES.includes(code)) {
                const entry = roleRoster[code];
                // ⛔ CONSULTANT = THE PARCEL'S FIRM (Philip 2026-11-03). Parcel Details shows the firm from
                // consultant_assignments; this cell read only parcel_contact_roles, which holds 2 consultant rows in all
                // of Wigley, so it was blank on 519 of the 521 parcels with a firm. It now shows that same firm for the
                // value year, with the parcel's own consultant people when any are set, else the firm's Primary contact.
                const firmCell = code === 'consultant' && roleFirm;
                const ppl = entry ? entry.people
                  : (firmCell && roleFirm.primary ? [{ id: 'firm-primary', ...roleFirm.primary }] : []);
                return (
                  <div>
                    {firmCell && (
                      <div style={{ color: c.text, fontWeight: 600, marginBottom: '2px' }}>
                        {roleFirm.company || '(firm)'}
                        {roleFirm.year !== Number(valueYear) && <span style={{ fontSize: '9px', color: c.muted, fontWeight: 400 }}> · since {roleFirm.year}</span>}
                      </div>
                    )}
                    {ppl.length === 0 ? <span style={{ color: c.muted }}>{firmCell ? 'no Primary contact on file' : '—'}</span> : ppl.map((p, i) => (
                      <div key={p.id} style={{ marginBottom: i < ppl.length - 1 ? '6px' : 0 }}>
                        <div style={{ color: c.text }}>{`${p.first_name || ''} ${p.last_name || ''}`.trim() || '(no name)'}</div>
                        {p.email && <a href={`mailto:${p.email}`} onClick={e => e.stopPropagation()} style={{ fontSize: '10px', color: '#60a5fa', textDecoration: 'none' }}>{p.email}</a>}
                      </div>
                    ))}
                    {/* A roster resolved from an earlier year is carried forward, not set for this year —
                        say so, or "why does 2027 show 2021's people" reads as a bug. */}
                    {entry && entry.year !== Number(valueYear) && (
                      <div style={{ fontSize: '9px', color: c.muted, marginTop: '2px' }}>since {entry.year}</div>
                    )}
                    <button onClick={() => setEditingRole(editingRole === code ? null : code)}
                      style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '10px', padding: '2px 0 0' }}>
                      {editingRole === code ? 'close' : 'edit'}
                    </button>
                  </div>
                );
              }
              const ppl = kcContacts.filter(p => p.role === code);
              if (!ppl.length) return <span style={{ color: c.muted }}>—</span>;
              return ppl.map((p, i) => (
                <div key={i} style={{ marginBottom: i < ppl.length - 1 ? '6px' : 0 }}>
                  <div style={{ color: c.text }}>{`${p.first_name || ''} ${p.last_name || ''}`.trim() || '(no name)'}</div>
                  {p.email && <a href={`mailto:${p.email}`} onClick={e => e.stopPropagation()} style={{ fontSize: '10px', color: '#60a5fa', textDecoration: 'none' }}>{p.email}</a>}
                </div>
              ));
            };
            const populatedDelivery = kcRoleOpts.filter(o => o.category === 'delivery' && kcContacts.some(p => p.role === o.code));
            const cols = [
              { header: 'NTRG',   staff: true, items: ['Client Manager', 'Client Specialist', 'Tax Specialist'].map(l => ({ label: l })) },
              { header: 'Expert', items: kcRoleOpts.filter(o => o.category === 'expert').map(o => ({ label: o.label, code: o.code })) },
              { header: 'Client', items: kcRoleOpts.filter(o => o.category === 'client').map(o => ({ label: o.label, code: o.code })) },
              ...(populatedDelivery.length ? [{ header: 'Delivery', items: populatedDelivery.map(o => ({ label: o.label, code: o.code })) }] : []),
            ];
            return (
              <div style={{ marginTop: '12px', padding: '14px 16px', background: isDark ? 'rgba(26,34,56,0.6)' : '#f8f9fc', borderRadius: '10px', border: `1px solid ${c.border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, 1fr)`, gap: '16px' }}>
                  {cols.map(col => (
                    <div key={col.header}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>{col.header}</div>
                      {col.items.map(it => (
                        <div key={it.label} style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '10px', color: c.muted, marginBottom: '3px' }}>{it.label}</div>
                          <div style={{ fontSize: '12px' }}>{col.staff ? ntrgValue(it.label) : contactValue(it.code)}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {/* The role editor renders BELOW the grid, full width — the four columns are too narrow for
                    a multi-picker, and a per-year roster needs room to explain itself. */}
                {editingRole && (
                  <ParcelRoleEditor
                    parcelId={parcel?.id}
                    role={editingRole}
                    roleLabel={(kcRoleOpts.find(o => o.code === editingRole) || {}).label || editingRole}
                    valueYear={valueYear}
                    firmConsultantId={roleFirm?.id || null}
                    staffId={staffUser?.id}
                    onClose={() => setEditingRole(null)}
                    onSaved={async () => { if (parcel?.id) setRoleRoster(await resolveRoleRoster(parcel.id, valueYear)); }}
                    s={s} c={c}
                  />
                )}
                <div style={{ fontSize: '10px', color: c.muted, marginTop: '4px' }}>Expert roles are per parcel and per value year — edit them here. Client &amp; delivery contacts are assigned on the property's Contacts card.</div>
              </div>
            );
          })()}
        </div>

        {/* ── MODAL BODY ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── HEADER FIELDS ── */}
          <div ref={parcelDetailsRef} style={s.card}>
            <div style={s.cardHeader}>
              <div style={s.cardTitle}>Parcel Details</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {createError && <span style={{ fontSize: '11px', color: '#f87171', fontWeight: '600' }}>{createError}</span>}
                {saveMsg && <span style={{ fontSize: '11px', color: '#34d399', fontWeight: '600' }}>✓ {saveMsg}</span>}
                {mode === 'create' ? (
                  <>
                    <button onClick={requestClose} style={s.btnGhost}>✕ Cancel</button>
                    <button onClick={createParcel} disabled={saving} style={s.btn}>{saving ? 'Creating...' : '✓ Create Parcel'}</button>
                  </>
                ) : (
                  <>
                    {!editMode && !reviewMode && (
                      <button onClick={() => setEditMode(true)} style={s.btnGhost}>✏️ Edit</button>
                    )}
                    {editMode && !reviewMode && (
                      <>
                        <button onClick={() => { setParcelData({ ...savedParcelData }); setEditMode(false); }} style={s.btnGhost}>✕ Cancel</button>
                        <button onClick={() => setReviewMode(true)} style={s.btn}>Review Changes →</button>
                      </>
                    )}
                    {reviewMode && (
                      <>
                        <button onClick={() => setReviewMode(false)} style={s.btnGhost}>← Back to Edit</button>
                        <button onClick={saveAll} disabled={saving || appealSaving || !canEditYear} style={s.btn}>{(saving || appealSaving) ? 'Saving...' : '✓ Save All Changes'}</button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <React.Fragment>
            {reviewMode && (() => {
              const FIELD_LABELS = { parcel_status:'Parcel Status', appeal_status:'Appeal Status',
                // ⛔ The panel iterates THESE KEYS (:2559), not the fields that changed — so a field absent
                // here saves silently, unreviewed. first_year decides which YEARS a parcel counts in, a
                // larger consequence than most entries below. retired_year/reactivated_year were missing
                // too, so retiring a parcel has always been invisible in this panel (retirement is stored
                // ONLY in retired_year, never in parcel_status). Adding a saved field? Add it here.
                parcel_name:'Parcel Name', first_year:'First Year', retired_year:'Retired Year', reactivated_year:'Reactivated Year', appeal_deadline:'Appeal Deadline', reappraisal_year:'Reappraisal Year', year_built:'Year Built', owner_name:'Owner Name', address:'Parcel Address', account_number:'Assessor ID', sigertax_prop_id:'Sigertax PropID', sigertax_parcel_id:'Sigertax ParcelID', land_area_sqft:'Land Area (sq ft)', assessor_bldg_sqft:'Assessor Bldg Sqft', client_bldg_sqft:'Client Bldg Sqft', pro_rata_pct:'Pro-Rata %', assessor:'Assessor', consultant_firm:'Consultant', re_pp:'RE/PP', parcel_type_detail:'Parcel Type', exemption_type:'Exemption', exemption_exp_year:'Exemption Exp. Year' };
              const changed = Object.keys(FIELD_LABELS).filter(k => String(parcelData[k]||'') !== String(savedParcelData[k]||''));
              const collDiffs = collectorChangeList();
              const collName = (id) => (cascadeCollectors.find(x => x.id === id) || taxCollectors.find(x => x.id === id) || {}).name || id;
              // Revert one collector change back to the loaded baseline (selection AND its Collector ID).
              const revertColl = (d) => {
                if (d.type === 'added') { setSelCollectorIds(prev => { const n = new Set(prev); n.delete(d.id); return n; }); setCollectorAccts(m => { const n = { ...m }; delete n[d.id]; return n; }); }
                else if (d.type === 'removed') { setSelCollectorIds(prev => { const n = new Set(prev); n.add(d.id); return n; }); setCollectorAccts(m => ({ ...m, [d.id]: savedCollectorAccts[d.id] || '' })); }
                else { setCollectorAccts(m => ({ ...m, [d.id]: savedCollectorAccts[d.id] || '' })); }
              };
              const rowStyle = { display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px', marginBottom:'6px', background:isDark?'rgba(180,0,0,0.06)':'rgba(180,0,0,0.04)', border:`1px solid ${c.border}`, borderRadius:'8px' };
              const oldCell = { fontSize:'12px', color:'#f87171', flex:1, textDecoration:'line-through', fontFamily:"'DM Mono',monospace" };
              const newCell = { fontSize:'12px', color:'#34d399', flex:1, fontFamily:"'DM Mono',monospace" };
              const revertBtn = { ...s.btnGhost, fontSize:'10px', padding:'3px 8px', color:'#f87171', borderColor:'rgba(248,113,113,0.3)' };
              return (changed.length === 0 && collDiffs.length === 0) ? (
                <div style={{ padding:'12px 18px', fontSize:'13px', color:c.muted }}>No changes detected.</div>
              ) : (
                <div style={{ padding:'12px 18px' }}>
                  <div style={{ fontSize:'12px', fontWeight:'700', color:c.text, marginBottom:'10px' }}>Changes to save:</div>
                  {changed.map(k => (
                    <div key={k} style={rowStyle}>
                      <div style={{ fontSize:'11px', color:c.muted, width:'140px', flexShrink:0 }}>{FIELD_LABELS[k]}</div>
                      <div style={oldCell}>{String(savedParcelData[k]||'—')}</div>
                      <div style={newCell}>{String(parcelData[k]||'—')}</div>
                      <button onClick={() => setParcelData(p => ({ ...p, [k]: savedParcelData[k] }))} style={revertBtn}>↩ Revert</button>
                    </div>
                  ))}
                  {collDiffs.map(d => (
                    <div key={`coll-${d.id}`} style={rowStyle}>
                      <div style={{ fontSize:'11px', color:c.muted, width:'140px', flexShrink:0 }}>{collName(d.id)}</div>
                      <div style={oldCell}>{d.type==='added' ? '—' : d.type==='removed' ? `Present${d.old?` · Account Number ${d.old}`:''}` : `Account Number ${d.old||'—'}`}</div>
                      <div style={newCell}>{d.type==='removed' ? 'Removed' : d.type==='added' ? `Added${d.new?` · Account Number ${d.new}`:''}` : `Account Number ${d.new||'—'}`}</div>
                      <button onClick={() => revertColl(d)} style={revertBtn}>↩ Revert</button>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ padding: '16px 18px', overflow: 'visible', pointerEvents: editMode ? 'all' : reviewMode ? 'none' : 'none' }}>
              {/* Dense, content-sized fields — flex rows; narrow boxes for short content, wide only for long text. */}
              {/* Row 1 (identification) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', marginBottom: '10px', alignItems: 'flex-start' }}>
                <div style={{ width: '150px' }}>
                  <div style={s.label}>Assessor ID</div>
                  <input value={parcelData.account_number || ''} onChange={e => setParcelData(p => ({ ...p, account_number: e.target.value }))} style={{ ...s.input, fontFamily: "'DM Mono',monospace" }} placeholder="on notice" />
                </div>
                <div style={{ width: '240px' }}>
                  <div style={s.label}>Assessor</div>
                  {stateAssessors.length > 0 ? (
                    <>
                      <select style={s.select} value={parcelData.assessor_ref_id || ''} onChange={e => {
                        const id = e.target.value || null;
                        const a = stateAssessors.find(x => x.entity_id === id);
                        setParcelData(p => ({ ...p, assessor_ref_id: id, assessor: a ? a.name : p.assessor }));
                        setSelCollectorIds(new Set()); // assessor changed → drop the prior assessor's collectors (only fires on a genuine change, not on load)
                      }}>
                        <option value="">— Select assessor —</option>
                        {assessorGroups.matched.length > 0 ? (
                          <>
                            <optgroup label={`${propCounty} County`}>
                              {assessorGroups.matched.map(a => <option key={`m-${a.entity_id}`} value={a.entity_id}>{a.name}</option>)}
                            </optgroup>
                            <optgroup label={`All ${parcelData.state} assessors`}>
                              {assessorGroups.rest.map(a => <option key={`a-${a.entity_id}`} value={a.entity_id}>{a.name}</option>)}
                            </optgroup>
                          </>
                        ) : (
                          stateAssessors.map(a => <option key={a.entity_id} value={a.entity_id}>{a.name}</option>)
                        )}
                      </select>
                      {!parcelData.assessor_ref_id && parcelData.assessor && (
                        <div style={{ fontSize: '11px', color: c.muted, marginTop: '3px' }}>current: {parcelData.assessor}</div>
                      )}
                    </>
                  ) : (
                    <input value={parcelData.assessor || ''} onChange={e => setParcelData(p => ({ ...p, assessor: e.target.value }))} style={s.input} placeholder="Assessor / CAD" />
                  )}
                </div>
                <div style={{ width: '120px' }}>
                  <div style={s.label}>Reappraisal Year</div>
                  <input value={parcelData.reappraisal_year || parcelData.sys_year || ''} onChange={e => setParcelData(p => ({ ...p, reappraisal_year: e.target.value }))} style={s.input} placeholder={String(new Date().getFullYear())} />
                </div>
                <div style={{ width: '150px' }}>
                  <div style={s.label}>Appeal Deadline <span style={{ color: c.muted, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(auto)</span></div>
                  <input type="date" value={parcelData.appeal_deadline || ''} disabled readOnly title="Set from the value-section deadlines" style={{ ...s.input, background: isDark ? 'rgba(26,34,56,0.5)' : '#f1f2f6', color: c.subtext, cursor: 'default' }} />
                </div>
                {/* SigerTax — retiring after Jan 1: pushed to the right edge (marginLeft:auto), set apart */}
                <div style={{ width: '120px', marginLeft: 'auto' }}>
                  <div style={s.label}>SigerTax Prop ID</div>
                  <input value={parcelData.sigertax_prop_id || ''} onChange={e => setParcelData(p => ({ ...p, sigertax_prop_id: e.target.value.replace(/[^0-9]/g,'') }))} style={{ ...s.input, fontFamily: "'DM Mono',monospace" }} placeholder="77013" inputMode="numeric" />
                </div>
                <div style={{ width: '120px' }}>
                  <div style={s.label}>SigerTax Parcel ID</div>
                  <input value={parcelData.sigertax_parcel_id || ''} onChange={e => setParcelData(p => ({ ...p, sigertax_parcel_id: e.target.value.replace(/[^0-9]/g,'') }))} style={{ ...s.input, fontFamily: "'DM Mono',monospace" }} placeholder="125492" inputMode="numeric" />
                </div>
              </div>

              {/* Row 3 (property / location) — wide text fields */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', marginBottom: '10px', alignItems: 'flex-start' }}>
                <div style={{ width: '280px' }}>
                  <div style={s.label}>Owner Name</div>
                  <input value={parcelData.owner_name || ''} onChange={e => setParcelData(p => ({ ...p, owner_name: e.target.value }))} style={s.input} placeholder="Owner name" />
                </div>
                <div style={{ width: '280px' }}>
                  {/* Was a free-text write to parcels.consultant_firm. That column still exists (the Siger
                      importer lands strings in it) but is no longer written here: the assignment is a real
                      row in consultant_assignments, per value year, FK'd to consultants.id. */}
                  {/* ⛔ NOT IN CREATE MODE. consultant_assignments.parcel_id is NOT NULL, and this panel
                      writes IMMEDIATELY on its own Save — it is not part of the parcel form's submit. With
                      no parcel row yet, parcelId is undefined, JSON.stringify drops the key, and the insert
                      fails on the constraint. Same guard the ten child-record loaders already use
                      (:544, :557, :569, :590, :601, :612, :735, :779, :796). It SAYS so rather than
                      disappearing: a field that vanishes reads as missing, not as deferred. */}
                  {mode === 'create' ? (
                    <div>
                      <div style={s.label}>Consultant</div>
                      <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic', lineHeight: 1.4 }}>
                        Consultant can be assigned once the parcel is saved.
                      </div>
                    </div>
                  ) : (
                    <ParcelFirmAssignment parcelId={parcel?.id} valueYear={valueYear} staffId={staffUser?.id} s={s} c={c} />
                  )}
                </div>
                <div style={{ width: '300px' }}>
                  <div style={s.label}>Parcel Address</div>
                  <input value={parcelData.address || ''} onChange={e => setParcelData(p => ({ ...p, address: e.target.value }))} style={s.input} placeholder="Address line 1" />
                  <input value={parcelData.address2 || ''} onChange={e => setParcelData(p => ({ ...p, address2: e.target.value }))} style={{ ...s.input, marginTop: '6px' }} placeholder="Address line 2 (suite, unit)" />
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    <input value={parcelData.city || ''} onChange={e => setParcelData(p => ({ ...p, city: e.target.value }))} style={{ ...s.input, flex: 2 }} placeholder="City" />
                    <input value={parcelData.state || ''} onChange={e => setParcelData(p => ({ ...p, state: e.target.value.toUpperCase().slice(0, 2) }))} style={{ ...s.input, width: '52px' }} placeholder="ST" maxLength={2} />
                    <input value={parcelData.postal_code || ''} onChange={e => setParcelData(p => ({ ...p, postal_code: e.target.value }))} style={{ ...s.input, flex: 1 }} placeholder="Zip" />
                  </div>
                </div>
                <div style={{ width: '300px' }}>
                  <div style={s.label}>Legal Description</div>
                  <textarea
                    value={parcelData.legal_description || ''}
                    onChange={e => setParcelData(p => ({ ...p, legal_description: e.target.value }))}
                    placeholder="from scan / manual"
                    style={{ ...s.input, minHeight: '54px', resize: 'vertical', fontFamily: "'DM Mono',monospace", fontSize: '11px', lineHeight: 1.4 }}
                  />
                </div>
              </div>

              {/* Row 4 (physical — all narrow numbers) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', marginBottom: '10px', alignItems: 'flex-start' }}>
                {/* 132px so "LAND AREA (SQ FT)" fits on ONE line at 10px/700/0.07em — at 100px it wrapped
                    and pushed this row taller than its neighbours. */}
                <div style={{ flex: '0 0 200px' }}>
                  <div style={{ ...s.label, whiteSpace: 'nowrap' }}>Land Area</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input value={landDisplay} onChange={e => setLandFromInput(e.target.value)} style={{ ...s.input, flex: 1, minWidth: 0 }} placeholder={landUnit === 'acres' ? 'acres' : 'sq ft'} />
                    <select value={landUnit} onChange={e => { setLandInput(null); setLandUnit(e.target.value); }}
                      style={{ ...s.select, width: '78px', flexShrink: 0 }} title="The unit you are typing in. Stored as square feet either way.">
                      <option value="sqft">sq ft</option>
                      <option value="acres">acres</option>
                    </select>
                  </div>
                  {/* Show the conversion BEFORE the save, not after. An acres entry that silently became a
                      sq-ft number is unrecoverable once stored — nothing records which unit was typed. */}
                  {landUnit === 'acres' && landNum(landDisplay) != null && (
                    <div style={{ fontSize: '10px', color: c.muted, marginTop: '3px', lineHeight: 1.3 }}>
                      {landNum(landDisplay)} acres = {Math.round(landNum(landDisplay) * SQFT_PER_ACRE).toLocaleString()} sq ft
                    </div>
                  )}
                </div>
                <div style={{ flex: '0 0 185px' }}>
                  <div style={s.label}>Assessor Bldg <span style={{ color: c.muted, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(sq ft or acres)</span></div>
                  <input
                    value={(() => { const s = String(parcelData.assessor_bldg_sqft ?? '').replace(/,/g, ''); return (s === '' || s.includes('.') || isNaN(Number(s))) ? String(parcelData.assessor_bldg_sqft ?? '') : Number(s).toLocaleString(); })()}
                    onChange={e => {
                      const raw = e.target.value.replace(/,/g, '');
                      const num = parseFloat(raw);
                      // Auto-convert acres to sq ft if value looks like acres (< 100, has decimal)
                      const isAcres = !isNaN(num) && num < 100 && raw.includes('.');
                      setParcelData(p => ({
                        ...p,
                        assessor_bldg_sqft: isAcres ? Math.round(num * 43560) : raw,
                        _assessor_bldg_raw: raw,
                        _assessor_bldg_was_acres: isAcres,
                      }));
                    }}
                    style={s.input}
                    placeholder="sq ft or acres"
                  />
                  {parcelData._assessor_bldg_was_acres && parcelData._assessor_bldg_raw && (
                    <div style={{ fontSize: '10px', color: 'var(--red)', marginTop: '3px', fontFamily: "'DM Mono',monospace" }}>
                      {parcelData._assessor_bldg_raw} acres → {Number(parcelData.assessor_bldg_sqft).toLocaleString()} sq ft
                    </div>
                  )}
                </div>
                <div style={{ flex: '0 0 185px' }}>
                  <div style={s.label}>Client Bldg (sq ft) <span style={{ color: c.muted, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>rent roll</span></div>
                  <input value={parcelData.client_bldg_sqft || ''} onChange={e => setParcelData(p => ({ ...p, client_bldg_sqft: e.target.value }))} style={s.input} placeholder="sq ft" />
                  {parcelData.assessor_bldg_sqft && parcelData.client_bldg_sqft && (() => {
                    const diff = parseFloat(parcelData.client_bldg_sqft) - parseFloat(parcelData.assessor_bldg_sqft);
                    if (isNaN(diff) || diff === 0) return null;
                    return (
                      <div style={{ fontSize: '10px', marginTop: '3px', fontFamily: "'DM Mono',monospace", color: diff > 0 ? '#f87171' : '#34d399' }}>
                        {diff > 0 ? '▲' : '▼'} {Math.abs(diff).toLocaleString()} sq ft vs assessor
                      </div>
                    );
                  })()}
                </div>
                {/* NLA and Units — the divisors for Imp/SF, Val/SF and Val/Unit. YEAR-GRAINED (they live
                    on parcel_year_data, not parcels): a building can be remeasured or units added, and a
                    static column would rewrite every year's figures at once. Siblings of the other five —
                    they were previously nested INSIDE the Land Area div (a missing closing tag), which is
                    why they rendered wrapped underneath it in a 100px-wide box. */}
                <div style={{ flex: '0 0 104px' }}>
                  <div style={{ ...s.label, whiteSpace: 'nowrap' }}>NLA (sq ft)</div>
                  <input value={parcelData.nla_sqft || ''} onChange={e => setParcelData(p => ({ ...p, nla_sqft: e.target.value }))} style={s.input} placeholder="net leasable" />
                </div>
                <div style={{ flex: '0 0 68px' }}>
                  <div style={{ ...s.label, whiteSpace: 'nowrap' }}>Units</div>
                  <input value={parcelData.units || ''} onChange={e => setParcelData(p => ({ ...p, units: e.target.value }))} style={s.input} placeholder="—" />
                </div>
                <div style={{ flex: '0 0 84px' }}>
                  <div style={{ ...s.label, whiteSpace: 'nowrap' }}>Year Built</div>
                  <input value={parcelData.year_built || ''} onChange={e => setParcelData(p => ({ ...p, year_built: e.target.value }))} style={s.input} placeholder="1998" />
                </div>
              </div>

              {/* Row 5 (type / status — packed) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', overflow: 'visible', alignItems: 'flex-start' }}>
                <div style={{ width: '140px' }}>
                  <div style={s.label}>RE / PP</div>
                  <select
                    value={parcelData.re_pp || 'Real Estate'}
                    onChange={e => setParcelData(p => ({ ...p, re_pp: e.target.value, parcel_type_detail: '' }))}
                    style={s.select}>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Personal Property">Personal Property</option>
                  </select>
                </div>
                {/* Parcel type searchable dropdown — switches on RE/PP */}
                <div style={{ width: '200px', position: 'relative' }} ref={parcelTypeRef}>
                  <div style={s.label}>Parcel Type <span style={{ color: 'var(--red)' }}>({rePp === 'Real Estate' ? 'RE' : 'PP'})</span></div>
                  <div style={{ position: 'relative' }}>
                    <input
                      value={parcelTypeOpen ? parcelTypeSearch : (parcelData.parcel_type_detail || parcelData.parcel_type || '')}
                      onFocus={() => { setParcelTypeOpen(true); setParcelTypeSearch(''); }}
                      onChange={e => setParcelTypeSearch(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') { setParcelTypeOpen(false); setParcelTypeSearch(''); e.currentTarget.blur(); } }}
                      placeholder="Click to select..."
                      style={{ ...s.input, cursor: 'pointer', paddingRight: '28px' }}
                    />
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: c.muted, fontSize: '11px', pointerEvents: 'none' }}>▾</span>
                  </div>
                  {parcelTypeOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: isDark ? '#2F3854' : '#ffffff', border: `1px solid ${c.border}`, borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', marginTop: '2px' }}>
                      {filteredParcelTypes.length === 0 && <div style={{ padding: '12px', fontSize: '12px', color: c.muted, textAlign: 'center' }}>No matches</div>}
                      {filteredParcelTypes.map(t => (
                        <div key={t} onClick={() => { setParcelData(p => ({ ...p, parcel_type_detail: t })); setParcelTypeOpen(false); setParcelTypeSearch(''); }}
                          style={{ padding: '9px 14px', fontSize: '13px', color: c.text, cursor: 'pointer', background: parcelData.parcel_type_detail === t ? (isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.08)') : 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(180,0,0,0.1)' : 'var(--line-soft)'}
                          onMouseLeave={e => e.currentTarget.style.background = parcelData.parcel_type_detail === t ? (isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.08)') : 'transparent'}>
                          {t}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ width: '160px' }}>
                  <div style={s.label}>Exemption</div>
                  <select value={parcelData.exemption_type || ''} onChange={e => setParcelData(p => ({ ...p, exemption_type: e.target.value }))} style={s.select}>
                    <option value="">— None —</option>
                    {exemptionOpts.map(et => <option key={et} value={et}>{et}</option>)}
                  </select>
                </div>
                <div style={{ width: '105px' }}>
                  <div style={s.label}>Exempt. Exp. Yr</div>
                  <input value={parcelData.exemption_exp_year || ''} onChange={e => setParcelData(p => ({ ...p, exemption_exp_year: e.target.value }))} style={s.input} placeholder="2028" />
                </div>
                <div style={{ width: '150px' }}>
                  <div style={s.label}>Appeal Status</div>
                  <select value={parcelData.appeal_status || APPEAL_STATUS.value_pending} onChange={e => setParcelData(p => ({ ...p, appeal_status: e.target.value }))} style={s.select}>
                    {appealStatusOpts.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div style={{ width: '150px' }}>
                  <div style={s.label}>Parcel Status</div>
                  <select
                    value={retiredAsOf(parcelData.retired_year, parcelData.reactivated_year, valueYear) ? PARCEL_STATUS.retired : (parcelData.parcel_status || PARCEL_STATUS.active)}
                    onChange={e => {
                      const v = e.target.value;
                      // 'Retired' is VIRTUAL — stored only in retired_year (base), never in the per-year parcel_status column.
                      if (v === PARCEL_STATUS.retired) setParcelData(p => ({ ...p, retired_year: valueYear, reactivated_year: null }));
                      else if (retiredAsOf(parcelData.retired_year, parcelData.reactivated_year, valueYear)) {
                        // Coming OFF Retired: at the retire year → clear (ongoing); at a later year → reactivate here (gave it back).
                        if (String(valueYear) === String(parcelData.retired_year)) setParcelData(p => ({ ...p, parcel_status: v, retired_year: null, reactivated_year: null }));
                        else setParcelData(p => ({ ...p, parcel_status: v, reactivated_year: valueYear }));
                      } else setParcelData(p => ({ ...p, parcel_status: v }));
                    }} style={s.select}>
                    {parcelStatusOpts.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                {/* ⛔ FIRST YEAR — the year this parcel came into our representation. The MIRROR of retired_year,
                    and parcel-wide (NOT year-scoped): a parcel that began in 2024 began in 2024 whichever year
                    you are viewing. Blank = always ours, which is every existing parcel until someone sets one.
                    WHY IT IS A PLAIN PICKER AND retired_year IS NOT: retirement is a transition you perform
                    while viewing the year it happens in, so it rides the Parcel Status dropdown. A start year
                    is a FACT about the parcel, stated from any year — it cannot be expressed as a status change.
                    18 East is the case that needs parcel grain: the building has been ours since 2021, but its
                    parcels come and go as tenants move and walls change, so a parcel created in 2024 must not
                    be counted back to 2021. properties.first_year covers the building; this covers the parcel.
                    ⛔ NOTHING GATES ON THIS YET — the is_represented start clause and its 7 callers ship next.
                    Setting a value today changes no count. */}
                <div style={{ width: '150px' }}>
                  <div style={s.label}>First Year</div>
                  <select value={parcelData.first_year || ''} onChange={e => setParcelData(p => ({ ...p, first_year: e.target.value || null }))} style={s.select}
                    title="The year this parcel came into our representation. Blank = we have always had it.">
                    <option value="">— Always ours —</option>
                    {valueYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
                        </div>
            </React.Fragment>

            {/* ── PER-SF FIGURES ── computed from the FINAL level for the selected year (utils/finalLevel): the
                bottom-most level with values, so an opened appeal still waiting on its figures shows the level
                above rather than "-" (Philip, 2026-10-08).
                Deliberately BELOW the inputs and not mixed into them: with two jurisdictions this is eight
                figures, and folding them into the input row would change its height and column count per
                parcel — broken-looking on the single-jurisdiction majority.
                ⛔ READ-ONLY. This touches nothing in the appeal flow. */}
            {(() => {
              const lvl = finalInfo.level;
              if (!lvl) return null;
              const jurs = Object.keys(appeals[lvl] || {});
              if (!jurs.length) return null;
              const multi = jurs.length > 1;
              const divisors = {
                landAreaSqft: parseNumericField(parcelData.land_area_sqft).value,
                nlaSqft: parseNumericField(parcelData.nla_sqft).value,
                units: parseNumericField(parcelData.units).value,
              };
              const title = lvl === 'proposed' ? 'Proposed' : appealTitleFor(appealIndexOf(lvl));
              return (
                <div style={{ marginTop: '4px', paddingTop: '10px', borderTop: `1px solid ${c.border}` }}>
                  <div style={{ ...s.label, marginBottom: '6px' }}>
                    Per SF / Unit <span style={{ color: c.muted, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>({title})</span>
                  </div>
                  {jurs.map(j => (
                    <div key={j} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '18px', marginBottom: '4px' }}>
                      {multi && <span style={{ fontSize: '11px', color: c.muted, minWidth: '90px' }}>{jurisdictionLabel(j)}</span>}
                      {figuresFor(appeals[lvl][j], divisors).map(f => (
                        <span key={f.key} style={{ fontSize: '12px', color: c.text }}>
                          <span style={{ fontSize: '10px', color: c.muted, marginRight: '4px' }}>{f.label}</span>
                          {fmtPerSf(f.value)}
                          {/* An overridden figure is not the computed one — say so, or the numbers stop
                              reconciling with the values above and nobody can tell why. */}
                          {f.overridden && <span title="Manual override" style={{ color: '#d4b078', marginLeft: '3px' }}>*</span>}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Box 2 — Collector info (own card) */}
          <div ref={collectorsRef} style={s.card}>
            <div style={s.cardHeader}><div style={s.cardTitle}>Collectors</div></div>
            {/* Gap A — non-blocking: a linked assessor with NO collectors won't generate tax bills. Always visible while
                true (view or edit); never gates the save. The pull screen's coverage banner is the downstream backstop. */}
            {parcelData.assessor_ref_id && selCollectorIds.size === 0 && (
              <div style={{ margin: '10px 18px 0', padding: '8px 12px', borderRadius: '8px', background: isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.06)', border: '1px solid var(--red)', fontSize: '12px', fontWeight: '600', color: isDark ? '#fca5a5' : 'var(--red)' }}>
                No collector assigned — this parcel will not generate tax bills for {valueYear}.
              </div>
            )}
            <div style={{ padding: '16px 18px', overflow: 'visible', pointerEvents: editMode ? 'all' : 'none' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 260px', minWidth: '260px' }}>
                  <div style={s.label}>Collectors <span style={{ color: c.muted, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(tax bills expected — enter each collector's Account Number)</span></div>
                  {!parcelData.assessor_ref_id ? (
                    <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic', padding: '7px 2px' }}>Pick an assessor first.</div>
                  ) : cascadeCollectors.length === 0 ? (
                    <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic', padding: '7px 2px' }}>No collectors linked.</div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <select value="" onChange={e => { const id = e.target.value; if (id) setSelCollectorIds(prev => { const n = new Set(prev); n.add(id); return n; }); }} style={{ ...s.select, width: 'auto' }}>
                        <option value="">+ Add…</option>
                        {cascadeCollectors.filter(col => !selCollectorIds.has(col.id)).map(col => <option key={col.id} value={col.id}>{col.name}</option>)}
                      </select>
                      {cascadeCollectors.filter(col => selCollectorIds.has(col.id)).map(col => (
                        <span key={col.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, background: isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.06)', border: '1px solid var(--red)', borderRadius: '7px', padding: '4px 8px' }}>
                          <span style={{ fontWeight: '600' }}>{col.name}</span>
                          <span style={{ fontSize: '10px', color: c.muted }}>Account Number</span>
                          <input value={collectorAccts[col.id] || ''} onChange={e => setCollectorAccts(m => ({ ...m, [col.id]: e.target.value }))}
                            placeholder="on bill" title={`${col.name}'s Account Number for this parcel (${valueYear}) — shows as Account Number on the tax-bill worklist`}
                            style={{ width: '96px', padding: '2px 6px', fontSize: '11px', fontFamily: "'DM Mono',monospace", color: c.text, background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '5px', outline: 'none' }} />
                          <button type="button" onClick={() => { setSelCollectorIds(prev => { const n = new Set(prev); n.delete(col.id); return n; }); setCollectorAccts(m => { const n = { ...m }; delete n[col.id]; return n; }); }}
                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }} title={`Remove ${col.name}`}>✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Box 3 — Services (own card) */}
          <div style={s.card}>
            <div style={s.cardHeader}><div style={s.cardTitle}>Services</div></div>
            <div style={{ padding: '16px 18px', overflow: 'visible', pointerEvents: editMode ? 'all' : 'none' }}>
              <div style={s.label}>Services</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <select value="" onChange={e => {
                  const v = e.target.value;
                  if (v && !selectedServices.includes(v)) setParcelData(p => ({ ...p, services: [...selectedServices, v].join(',') }));
                }} style={{ ...s.select, width: 'auto' }}>
                  <option value="">+ Add…</option>
                  {serviceOpts.filter(svc => !selectedServices.includes(svc)).map(svc => <option key={svc} value={svc}>{svc}</option>)}
                </select>
                {selectedServices.map(svc => (
                  <span key={svc} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, background: isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.06)', border: '1px solid var(--red)', borderRadius: '7px', padding: '4px 8px' }}>
                    {svc}
                    <button type="button" onClick={() => setParcelData(p => ({ ...p, services: selectedServices.filter(s => s !== svc).join(',') }))}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }} title={`Remove ${svc}`}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* E2a: appeal values require a saved parcel.id — gated out of create; multi-year entry lands in E2b */}
          {mode !== 'create' ? (<>
          <div ref={appealsRef} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Value Year</div>
            {valueYears.map(yr => {
              const isActive = compareMode ? compareYears.includes(yr) : valueYear === yr;
              // Same one call as the other tab row — see the note there.
              const chip = yearChip(parcelData, propRetire, yr);
              const rep = chip.state === 'represented';
              return (
                <button key={yr} onClick={async () => {
                  if (compareMode) {
                    setCompareYears(prev => prev.includes(yr) ? prev.filter(y => y !== yr) : [...prev, yr].sort());
                  } else {
                    await switchValueYear(yr);
                  }
                }} title={chip.title}
                  style={{ padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", border: isActive ? '1px solid #B40000' : `1px solid ${c.border}`, background: isActive ? 'rgba(180,0,0,0.15)' : 'transparent', color: isActive ? 'var(--red)' : c.muted, opacity: rep ? 1 : 0.4 }}>
                  {yr}{chip.tag ? ` ${chip.tag}` : ''}
                </button>
              );
            })}
            <button onClick={() => { setCompareMode(m => !m); setCompareYears([]); }}
              style={{ padding: '5px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", border: compareMode ? '1px solid #60a5fa' : `1px solid ${c.border}`, background: compareMode ? 'rgba(96,165,250,0.15)' : 'transparent', color: compareMode ? '#60a5fa' : c.muted }}>
              {compareMode ? '✓ Compare Mode' : '⇄ Compare Years'}
            </button>
            {compareMode && compareYears.length >= 2 && (
              <button onClick={async () => {
                const result = {};
                for (const yr of compareYears) {
                  const { data: ap } = await supabase.from('parcel_appeals').select('*').eq('parcel_id', parcel.id).eq('tax_year', yr).eq('notice_no', ORIGINAL_NOTICE).eq('appeal_level', 'proposed');
                  if (ap && ap.length > 0) {
                    result[yr] = {};
                    ap.forEach(row => { result[yr][row.jurisdiction] = row; });
                  } else {
                    const { data: pv } = await supabase.from('parcel_values').select('*').eq('parcel_id', parcel.id).eq('tax_year', yr).eq('notice_no', ORIGINAL_NOTICE);
                    if (pv && pv.length > 0) {
                      result[yr] = {};
                      pv.forEach(pv2 => { result[yr][pv2.jurisdiction || 'County'] = { market_land: pv2.land_value, market_imp: pv2.improvement_value, market_total: pv2.market_value, taxable_total: pv2.taxable_value }; });
                    } else {
                      result[yr] = {}; // empty — show dashes
                    }
                  }
                }
                setCompareData(result);
              }} style={{ padding: '5px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '20px', color: 'white', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
                Load Comparison
              </button>
            )}
          </div>

          {/* ── YEAR COMPARISON TABLE ── */}
          {compareMode && compareYears.length >= 2 && (
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ padding: '12px 18px', borderBottom: `1px solid ${c.border}`, fontSize: '13px', fontWeight: '700', color: c.text }}>
                📊 Year-over-Year Comparison — Proposed Values
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: 'auto', minWidth: '400px' }}>
                  <thead>
                    <tr style={{ background: c.th }}>
                      <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: c.muted, width: '180px', borderBottom: `1px solid ${c.border}` }}>Value Type</th>
                      {compareYears.map(yr => (
                        <th key={yr} style={{ padding: '8px 16px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: compareData[yr] ? 'var(--red)' : c.muted, borderBottom: `1px solid ${c.border}`, borderLeft: `1px solid ${c.border}`, width: '140px' }}>
                          {yr}{!compareData[yr] && <div style={{ fontSize:'10px', fontWeight:'400', color:c.muted }}>no data</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'market_land', label: 'Market Land' },
                      { key: 'market_imp', label: 'Market Imp.' },
                      { key: 'market_total', label: 'Market Total', bold: true },
                      { key: 'taxable_total', label: 'Taxable Total', bold: true, highlight: true },
                    ].map(row => (
                      <tr key={row.key} style={{ borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, background: row.highlight ? (isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)') : 'transparent' }}>
                        <td style={{ padding: '8px 16px', fontSize: '12px', fontWeight: row.bold ? '700' : '400', color: row.bold ? c.text : c.subtext }}>{row.label}</td>
                        {compareYears.map((yr, i) => {
                          // ⛔ WAS `?.County || first row` — the literal name, then an ARBITRARY fallback.
                          // On a two-base parcel that silently compared whichever row the query happened to
                          // return first, so a year-over-year % could be measured between different bases.
                          const jurisData = primaryAppealRow(Object.values(compareData[yr] || {}), `compare ${yr}`);
                          const val = jurisData?.[row.key];
                          const prevYr = compareYears[i - 1];
                          const prevJurisData = prevYr ? primaryAppealRow(Object.values(compareData[prevYr] || {}), `compare ${prevYr}`) : null;
                          const prevVal = prevJurisData?.[row.key];
                          const pct = val && prevVal ? ((val - prevVal) / Math.abs(prevVal) * 100).toFixed(1) : null;
                          return (
                            <td key={yr} style={{ padding: '8px 16px', textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '12px', fontWeight: row.bold ? '700' : '400', color: row.highlight ? 'var(--red)' : c.text, borderLeft: `1px solid ${c.border}` }}>
                              {val != null ? fmt(val) : <span style={{ color: c.muted }}>—</span>}
                              {pct !== null && <span style={{ fontSize: '10px', color: parseFloat(pct) > 0 ? '#f87171' : '#34d399', marginLeft: '4px' }}>{parseFloat(pct) > 0 ? '▲' : '▼'}{Math.abs(parseFloat(pct))}%</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── PROPOSED block ── */}
          {appealsLoading ? (
            <div style={{ border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', textAlign: 'center', color: c.muted, marginBottom: '16px', fontSize: '13px' }}>Loading values...</div>
          ) : (appeals.proposed && Object.keys(appeals.proposed).length > 0) ? (
            <>
              {renderAppealBlock('proposed', '📋 Proposed Notice')}
            </>
          ) : !proposedOpen ? (
            <div onClick={() => { setProposedOpen(true); initAppealLevel('proposed'); }}
              style={{ border: `2px dashed ${c.border}`, borderRadius: '12px', padding: '24px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.background = 'rgba(180,0,0,0.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>+</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: c.text, marginBottom: '4px' }}>Add Proposed Notice</div>
              <div style={{ fontSize: '12px', color: c.muted }}>Click to enter values manually, or scan an assessment notice to auto-populate</div>
            </div>
          ) : (
            <>
              {renderAppealBlock('proposed', '📋 Proposed Notice')}
            </>
          )}

          {/* ── APPEAL LEVELS (dynamic, uncapped) — one block per open level, each with its Supporting Documents; Tax Savings under the final level ── */}
          {(proposedOpen || (appeals.proposed && Object.keys(appeals.proposed).length > 0)) &&
            Array.from({ length: Math.max(appealCount, 0, ...Object.values(noticeDepth)) }, (_, i) => i + 1).map(n => {
              const key = appealKeyFor(n);
              return (
                <React.Fragment key={key}>
                  {renderAppealBlock(key, appealTitleFor(n))}
                  {!collapsed[key] && n <= appealCount && <AppealSupportingDocs parcel={parcel} year={valueYear} level={key} staffUser={staffUser} s={s} c={c} />}
                  {/* A saved notice's appeal at this level has its own documents (file_exchange.notice_no). */}
                  {!collapsed[key] && notices.filter(x => !x._new && n <= (noticeDepth[x.notice_no] || 0)).map(x => (
                    <AppealSupportingDocs key={x.notice_no} parcel={parcel} year={valueYear} level={key} notice={x.notice_no}
                      title={noticeTypeName(x.notice_no)} staffUser={staffUser} s={s} c={c} />
                  ))}
                  {key === finalInfo.level && !collapsed[key] && (figs.card.savings != null || baseSavings) && renderSavingsBox(appealTitleFor(n))}
                </React.Fragment>
              );
            })}

          {/* Proposed stands (Value Accepted with no appeal, or withdrawn back to Proposed): $0, as the reports print. */}
          {figs.unchanged && (proposedOpen || (appeals.proposed && Object.keys(appeals.proposed).length > 0)) && renderSavingsBox(null)}

          {/* Lower unified Save — same saveAll() as the top button, so no scroll-to-top needed */}
          {mode !== 'create' && (proposedOpen || (appeals.proposed && Object.keys(appeals.proposed).length > 0)) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              {appealSaveMsg && <span style={{ fontSize: '11px', color: '#34d399', fontWeight: '600' }}>✓ {appealSaveMsg}</span>}
              <button onClick={saveAll} disabled={saving || appealSaving || !canEditYear} style={s.btn}>{(saving || appealSaving) ? 'Saving…' : '💾 Save All'}</button>
            </div>
          )}
          </>) : (
            <>
              {/* E2b: create-mode Proposed Values per year — accumulate in pendingByYear, persisted on Create Parcel */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Value Year</div>
                {['2023', '2024', '2025', '2026'].map(yr => {
                  const isActive = valueYear === yr;
                  return (
                    <button key={yr} onClick={() => {
                      setPendingByYear(prev => ({ ...prev, [valueYear]: appeals }));
                      setAppeals(pendingByYear[yr] || {});
                      setValueYear(yr);
                    }}
                      style={{ padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", border: '1px solid ' + (isActive ? '#B40000' : c.border), background: isActive ? 'rgba(180,0,0,0.15)' : 'transparent', color: isActive ? 'var(--red)' : c.muted }}>
                      {yr}
                    </button>
                  );
                })}
              </div>
              {(appeals.proposed && Object.keys(appeals.proposed).length > 0) ? (
                renderAppealBlock('proposed', '📋 Proposed Notice')
              ) : (
                <div onClick={() => initAppealLevel('proposed')}
                  style={{ border: '2px dashed ' + c.border, borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: c.text, marginBottom: '4px' }}>+ Add Proposed Notice for {valueYear}</div>
                  <div style={{ fontSize: '12px', color: c.muted }}>Enter values per jurisdiction; switch years to add more. Saved when you click Create Parcel.</div>
                </div>
              )}
            </>
          )}

          <div ref={taxesRef} style={s.card}>
            <div style={s.cardHeader}><div style={s.cardTitle}>Taxes</div></div>
            <div style={{ padding: '14px 18px' }}>
              {taxCollectors.length === 0 ? (
                <div style={{ fontSize: '13px', color: c.muted, fontStyle: 'italic' }}>No collectors on this parcel yet — add collectors in Parcel Details to bill taxes.</div>
              ) : (() => {
                const isREp = rePp === 'Real Estate';
                const fmtDue = (d) => { if (!d) return ''; const [y, m, day] = String(d).split('-').map(Number); return `${m}/${day}/${String(y).slice(2)}`; };
                // Column table: Jurisdiction | Taxable | Rate | Amount | Bill% | Adj | Sub-Total | Due.
                // rate 74→92: the input holds six decimals AND reserves 16px on the right for the "%"
                //   suffix, leaving ~47px of text room — "1.178467" needs ~58px at 12px DM Mono, so it
                //   clipped to "1.1784(%". The Installment 2 row renders the same value as static text
                //   with no suffix reserve, which is why only the input truncated.
                // due 62→112: a NATIVE date input has its own internal mm/dd/yyyy layout and cannot be
                //   squeezed — at 62px Chrome showed "12/1" for 12/10/2021.
                // The grid's wrapper is overflowX:'auto', so the extra 68px scrolls rather than overflowing.
                // ⛔ ONE COLUMN DEFINITION — TRACK WIDTH, HEADER LABEL AND ALIGNMENT TOGETHER. These were three
                // separate things: a COL width map, a literal array of header labels, and a POSITIONAL alignment
                // rule (`(i >= 1 && i <= 4) || i >= 6 ? 'right' : 'left'`) that had no idea which body cells are
                // deliberately left-aligned. Due and Status render their content LEFT — a date input and a status
                // chip — while that rule right-aligned both headers, so the header sat away from the value beneath
                // it. Removing the Δ column shifted every index and made it obvious. Alignment now comes from the
                // column, so a header cannot disagree with its own body cell, and removing a column is one edit.
                //
                // `billed` is BILLED again (2026-10-08): the full tax, before discount — never the bill's Amount Due, which
                // is $0 on a paid bill and the discounted tier on a Florida bill. Amount Due lives on the card.
                const TAX_COLS = [
                  { key: 'jur',    label: 'Jurisdiction',  w: 132, align: 'left'  },
                  { key: 'tax',    label: 'Taxable Value', w:  96, align: 'right' },
                  { key: 'rate',   label: 'Tax Rate',      w:  92, align: 'right' },
                  { key: 'adj',    label: 'Adj Type',      w: 184, align: 'left'  },
                  { key: 'billed', label: 'Billed',        w: 120, align: 'right' },
                  { key: 'due',    label: 'Due',           w: 112, align: 'left'  },   // body renders a date input, left
                  { key: 'stat',   label: 'Status',        w: 124, align: 'center' }, // body renders a PILL: centred, so left
                ];
                const GRID_TRACKS = TAX_COLS.map(x => `${x.w}px`).join(' ');
                // Body cells that DON'T inherit `cell`'s right-align read their alignment from here, so the
                // header and the thing under it cannot disagree — that disagreement is what put the Status
                // chip and the Due date away from their own headers.
                const colAlign = Object.fromEntries(TAX_COLS.map(x => [x.key, x.align]));
                const GRID_MIN = TAX_COLS.reduce((a, x) => a + x.w, 0) + (TAX_COLS.length - 1) * 8;   // + the 8px column gap
                const th = { fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 2px' };
                const cell = { fontSize: '12px', color: c.text, fontFamily: "'DM Mono',monospace", padding: '4px 2px', textAlign: 'right' };
                const cellL = { ...cell, fontFamily: "'Public Sans',system-ui,sans-serif", textAlign: 'left' };
                const smIn = { ...s.input, padding: '3px 7px', fontSize: '12px', fontFamily: "'DM Mono',monospace", textAlign: 'right' };
                const selIn = { ...s.input, padding: '3px 6px', fontSize: '11px' };
                const ADJ_TYPES = ['', 'Discount', 'Equalization Factor', 'Last years total tax amount', 'Abatement', 'Market Adjustment', 'Taxable Adjustment', 'Other'];
                // ⛔ THESE ARE OVERRIDE SETTERS NOW (the rate cell writes the Tax Rate row instead: setEnteredRate). They are the ONLY way a value lands in
                // parcelTaxes for a field tax_bills also holds — nothing snapshots a scan into them any
                // more. That is what makes `pt ?? scan` mean 'a TS's figure, else the bill'.
                const setAdvOv = (cid, inst, val) => setParcelTaxes(prev => {
                  const cur = prev[cid] || {}; const advOv = { ...(cur.advOv || {}) };
                  if (val === '' || val == null) delete advOv[inst]; else advOv[inst] = val;
                  return { ...prev, [cid]: { ...cur, advOv } };
                });
                const setNav = (cid, val) => setParcelTaxes(prev => ({ ...prev, [cid]: { ...(prev[cid] || {}), nav: val } }));
                const setPTax = (cid, field, val) => setParcelTaxes(prev => ({ ...prev, [cid]: { ...(prev[cid] || {}), [field]: val } }));
                const setDue = (cid, inst, val) => setParcelTaxes(prev => ({ ...prev, [cid]: { ...(prev[cid] || {}), dueOv: { ...((prev[cid] || {}).dueOv || {}), [inst]: val } } }));
                const setAdj = (cid, inst, patch) => setParcelTaxes(prev => {
                  const cur = prev[cid] || {}; const adj = { ...(cur.adj || {}) };
                  adj[inst] = { ...(adj[inst] || {}), ...patch };
                  return { ...prev, [cid]: { ...cur, adj } };
                });
                // Billed = the authoritative amount (→ TBT). Manual entry (no scan) or override (correct a scan)
                // both live in parcelTaxes[cid].billed[inst]; presence of a key there takes precedence over the scan.
                const setBilled = (cid, inst, val) => setParcelTaxes(prev => {
                  const cur = prev[cid] || {}; const billed = { ...(cur.billed || {}) };
                  billed[inst] = val; return { ...prev, [cid]: { ...cur, billed } };
                });
                const clearBilled = (cid, inst) => setParcelTaxes(prev => {
                  const cur = prev[cid] || {}; const billed = { ...(cur.billed || {}) };
                  delete billed[inst]; return { ...prev, [cid]: { ...cur, billed } };
                });
                return taxCollectors.map(col => {
                  const model = collectorTaxModel(col.id, isREp);
                  const firstRateIdx = model.rows.findIndex(x => x.amount != null); // one editable rate input, on the first rate-row
                  const scan = scanBill[col.id] || {};
                  const pt = parcelTaxes[col.id] || {};
                  const rateIn = taxInputs(col.id);
                  // ⛔ THE BILL'S RATE IS SHOWN, NOT EDITED (Philip 2026-10-27: a bill's rate always wins — fix a wrong one on
                  // the bill). Otherwise the cell is the Tax Rate row's entry for the year's one column, typed as a percent.
                  const fromBill = rateIn.rateSource === 'bill' || rateIn.rateSource === 'lines' || rateIn.rateSource === 'effective';
                  const rateEditable = !fromBill && soleColumn() != null;
                  const soleRow = jurRates[soleColumn()] || {};
                  const rateVal = rateEditable ? (soleRow.rate_unit === 'mills' ? (ratePercentOf(soleRow) ?? '') : (soleRow.tax_rate ?? ''))
                    : (rateIn.rate != null ? Number(Number(rateIn.rate).toFixed(6)) : '');
                  const rateTitle = rateIn.rateSource === 'bill' ? 'The bill\'s printed rate'
                    : rateIn.rateSource === 'lines' ? 'From the bill\'s jurisdiction lines'
                    : rateIn.rateSource === 'effective' ? 'Effective rate: the bill prints no rate, so this is its ad valorem ÷ taxable value'
                    : rateEditable ? 'Type the rate from the notice — the same rate as the Tax Rate row on the values'
                    : twoBase ? 'Two or more bases: enter each column\'s rate in the Tax Rate row on the values'
                    : 'Enter the year\'s values first — the rate is entered against its value column';
                  const navVal = pt.nav != null && pt.nav !== '' ? pt.nav : (scan.nav ?? '');
                  // The annual non-ad valorem placed on the installments for DISPLAY — split, or all on installment 1 when the
                  // collector says so (collectors.nav_billed_on). One array for the Non-Ad-Valorem rows and the per-row calc check.
                  const navAnnual = Math.round((Number(navVal !== '' ? navVal : model.navTotal) || 0) * 100) / 100;
                  const navOn1 = col.nav_billed_on === 'installment_1';
                  const navShare = navShares(navAnnual, model.rows.length, col.nav_billed_on);
                  // Billed (authoritative) per installment: manual/override in pt.billed[inst] wins; else the scan.
                  const bScan = billedScan[col.id] || {};
                  const bMan = pt.billed || {};
                  const effBilled = (inst) => (bMan[inst] !== undefined && bMan[inst] !== '') ? Number(bMan[inst])
                    : (bScan[inst]?.total != null ? Number(bScan[inst].total) : null);
                  // ⛔ EVERY CELL CARRIES THE OPEN HANDLER, AND EVERY INTERACTIVE CELL STOPS IT. A CSS grid has no
                  // row element to hang one onClick on — the cells are siblings — so the two have to be applied
                  // per cell. stopRow goes on the cells that own a control (Tax Rate, Adj Type); without it,
                  // typing a rate would also open the card over the field being typed into.
                  const openRow = (r) => ({ onClick: () => setBillModal({ colId: col.id, inst: r.installmentNumber }) });
                  const stopRow = (e) => e.stopPropagation();
                  const instByNum = new Map((taxInstallments[col.id] || []).filter(i => i.kind === (isREp ? 'RE' : 'PP')).map(i => [i.installment_number, i]));
                  return (
                    <div key={col.id} style={{ marginBottom: '18px' }}>
                      {/* Collector heading + Jurisdiction Breakdown + View Tax Bill */}
                      <div style={{ background: isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.06)', border: `1px solid ${c.border}`, borderRadius: '10px 10px 0 0', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: c.text }}>{col.name || 'Unnamed Collector'}
                          {/* The CALCULATION METHOD (utils/taxBill), not a state. 'CA' is the plain taxable × rate split every
                              collector gets unless it is Cook / FL / IL / multi — so it reads "Standard" (County of Maui, HI,
                              showed "CA"). Label only: the key stays 'CA' in parcel_taxes meta and tests. */}
                          <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: c.muted, background: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.1)', borderRadius: '6px', padding: '2px 7px' }}>{model.path === 'CA' ? 'Standard' : model.path === 'refused' ? 'Not computed' : model.path}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => openTaxBillPdf(col.id)} disabled={!scan.fileExchangeId}
                            title={scan.fileExchangeId ? 'Open the scanned tax bill PDF' : 'No scanned tax bill on file for this collector/year'}
                            style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px', whiteSpace: 'nowrap', opacity: scan.fileExchangeId ? 1 : 0.5, cursor: scan.fileExchangeId ? 'pointer' : 'not-allowed' }}>📄 View Tax Bill</button>
                          <button type="button" onClick={() => openJurisdictionBreakdown(col.id)} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}>{jbCollector === col.id ? '▾' : '▸'} Jurisdiction Breakdown</button>
                          {/* The collector-level "Process bill → Approval" button was REMOVED here. It looped every
                              installment with a computed amount, so the TS could not tell WHICH bill was being
                              processed, and each call overwrote the single procMsg — installment 1 failing and
                              installment 2 succeeding reported only the success. Processing is now per installment,
                              on that installment's card in "Bill fields → Approval" below. */}
                        </div>
                      </div>
                      {/* MARK ALL INSTALLMENTS PAID — backfill years: every live, unpaid, not-Returned bill of this
                          collector-year, each at its own billed amount (utils/paymentRecord). Offered for two or more. */}
                      {(() => {
                        const unpaid = Object.entries(bScan)
                          .filter(([, b]) => b && b.taxBillId && !b.paid && b.status !== TAX_BILL_STATUS.REJECTED)
                          .map(([k, b]) => ({ id: b.taxBillId, status: b.status, billed: b.total ?? null,
                                              label: isOffScheduleKey(k) ? k.replace('#', ' #') : `Inst ${k}` }));
                        return (
                          <MarkAllPaid key={`${col.id}:${valueYear}`} bills={unpaid} entityName={`${parcelData.parcel_name || parcel?.id} · ${col.name} · ${valueYear}`}
                            staffName={staffUser?.name} onChanged={() => setBillsReload(n => n + 1)} s={s} c={c}
                            wrapStyle={{ border: `1px solid ${c.border}`, borderTop: 'none', padding: '8px 14px' }} />
                        );
                      })()}
                      {jbCollector === col.id && (
                        <div style={{ border: `1px solid ${c.border}`, borderTop: 'none', padding: '10px 14px', background: isDark ? 'rgba(0,0,0,0.12)' : '#faf9f7' }}>
                          {jbBill === undefined ? <div style={{ fontSize: '12px', color: c.muted }}>Loading…</div>
                            : !jbBill ? <div style={{ fontSize: '12px', color: c.muted }}>No bill entered for {valueYear} on this collector. Enter the bill on an installment first (Bill fields), then add jurisdiction lines here.</div>
                            : jbEdit ? (
                              <>
                                <JurisdictionBreakdown rates={jbEdit} unit={detectRateUnit(jbEdit, jbBill.total_ad_valorem, jbBill.taxable_value)} darkMode={isDark}
                                  onChange={editJurisdictionLine} onRemove={(i) => setJbEdit(prev => prev.filter((_, j) => j !== i))} />
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                                  <button type="button" onClick={() => setJbEdit(prev => [...prev, { name: '', tax_rate: '', taxable_value: '', amount: '' }])} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px' }}>+ Add line</button>
                                  <span style={{ flex: 1 }} />
                                  <button type="button" onClick={() => { setJbEdit(null); setJbMsg(''); }} disabled={jbSaving} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px' }}>Cancel</button>
                                  <button type="button" onClick={() => saveJurisdictionLines(col.id)} disabled={jbSaving} style={{ ...s.btn, padding: '4px 12px', fontSize: '11px' }}>{jbSaving ? 'Saving…' : 'Save lines'}</button>
                                </div>
                                <div style={{ fontSize: '10px', color: c.muted, marginTop: '4px' }}>Saved on every installment of this {valueYear} bill. A line with no rate, taxable value or amount is dropped. The bill's totals are not changed.</div>
                              </>
                            ) : (
                              <>
                                {(jbBill.jurisdiction_rates || []).length
                                  ? <JurisdictionBreakdown rates={jbBill.jurisdiction_rates} unit={detectRateUnit(jbBill.jurisdiction_rates, jbBill.total_ad_valorem, jbBill.taxable_value)} darkMode={isDark} />
                                  : <div style={{ fontSize: '12px', color: c.muted }}>No jurisdiction breakdown stored for {valueYear} on this collector.</div>}
                                <div style={{ marginTop: '8px' }}>
                                  <button type="button" onClick={startJurisdictionEdit} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px' }}>{(jbBill.jurisdiction_rates || []).length ? '✎ Edit lines' : '+ Add lines'}</button>
                                </div>
                              </>
                            )}
                          {jbMsg && <div style={{ fontSize: '11px', color: '#f87171', marginTop: '6px' }}>{jbMsg}</div>}
                        </div>
                      )}
                      <div style={{ border: `1px solid ${c.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '10px 14px', overflowX: 'auto' }}>
                        {billOpenErr && <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '6px' }}>{billOpenErr}</div>}
                        <div style={{ display: 'grid', gridTemplateColumns: GRID_TRACKS, gap: '6px 8px', alignItems: 'center', minWidth: `${GRID_MIN}px` }}>
                          {/* header — one cell per TAX_COLS entry, aligned by the column, never by its index. */}
                          {TAX_COLS.map(hc => (
                            <div key={hc.key} style={{ ...th, textAlign: hc.align }}>{hc.label}</div>
                          ))}
                          {/* ad-valorem / prepay installment rows. Rate is one value per collector, so the
                              editable input renders on the FIRST rate-row only (multiple inputs bound to the
                              same state fight for focus); other rate-rows show it read-only. */}
                          {model.rows.map((r, idx) => {
                            const inst = instByNum.get(r.installmentNumber) || {};
                            const ov = (pt.adj || {})[r.installmentNumber] || {};
                            const effType = ov.type != null ? ov.type : (inst.adjustment_type || '');
                            const effPct = ov.pct != null ? ov.pct : (inst.adjustment_pct ?? '');
                            const rateRow = r.amount != null; // prepay row has no rate/amount
                            return (
                              <React.Fragment key={r.installmentNumber}>
                                {/* ⛔ THE ROW IS THE TRIGGER. Its fields live in a modal now, so every cell carries the
                                    same onClick — and every cell that is ITSELF interactive stops the event, or editing
                                    a rate would also open the card behind it. Interactive cells today: Tax Rate and Adj
                                    Type. Amount Due and Due are read-only here on purpose (requirement 4) — their twin
                                    controls are the modal's fields, writing to the same store via OWNED. */}
                                <div {...openRow(r)} style={{ ...cellL, cursor: 'pointer' }}>
                                  <span style={{ color: 'var(--red)', fontWeight: 600 }}>{r.jurisdiction}</span>
                                  <span style={{ display: 'block', fontSize: '9px', color: c.muted }}>{r.billType || `Installment ${r.installmentNumber}`}</span>
                                </div>
                                <div {...openRow(r)} style={{ ...cell, cursor: 'pointer' }}>{rateRow ? fmtc(r.taxableValue) : '—'}</div>
                                <div style={cell} onClick={stopRow}>
                                  {!rateRow ? '—' : idx === firstRateIdx ? (
                                    <div style={{ position: 'relative' }}>
                                      {/* ⛔ THE PLACEHOLDER WAS "0.0000", BESIDE A PERMANENT "%" — so an EMPTY rate
                                          cell read as "0.0000%", a rate of zero. A zero rate is a claim (this
                                          property is taxed at nothing); blank is a question (nobody has entered
                                          one). The two must not look alike, and the % is hidden when there is no
                                          value so the cell reads "—" rather than "— %". */}
                                      {rateEditable ? <>
                                        <input type="text" inputMode="decimal" value={rateVal} placeholder="—" onChange={e => setEnteredRate(e.target.value.replace(/[^0-9.]/g, ''))} style={{ ...smIn, paddingRight: '16px', color: c.text }} title={rateTitle} />
                                        {String(rateVal) !== '' && <span style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: c.muted }}>%</span>}
                                      </> : <span title={rateTitle} style={{ color: c.muted }}>
                                        {rateVal !== '' ? `${rateVal}%` : '—'}
                                        {rateIn.rateSource === 'effective' && <span style={{ display: 'block', fontSize: '9px' }}>effective</span>}
                                      </span>}
                                    </div>
                                  ) : <span style={{ color: c.subtext }}>{rateVal !== '' ? `${rateVal}%` : '—'}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: '3px' }} onClick={stopRow}>
                                  {/* A new type clears the value: the number means something different per type, so a Discount's 4 must
                                      not become a $4 abatement. */}
                                  {/* ⛔ A STORED TYPE NOT IN ADJ_TYPES IS ADDED TO THIS ROW'S LIST. The schedule's adjustment_type is free
                                      text from the collector setup / template import; without its own option the select showed "—" while
                                      the amount box beside it showed the stored value (Clark County installments 2–4: "0.08", no type). */}
                                  <select value={effType} onChange={e => setAdj(col.id, r.installmentNumber, { type: e.target.value, pct: '' })} style={{ ...selIn, flex: 1, minWidth: 0 }}>
                                    {(ADJ_TYPES.includes(effType) ? ADJ_TYPES : [...ADJ_TYPES, effType]).map(t => <option key={t} value={t}>{t || '—'}</option>)}
                                  </select>
                                  {/* Abatement is DOLLARS off this installment (cents allowed); the other types are a % or a multiplier
                                      (utils/taxBill ADJUSTMENT ENCODING). Both are saved with the parcel's taxes. */}
                                  {effType === 'Abatement'
                                    ? <MoneyInput value={fmtMoneyIn(effPct)} placeholder="$0.00" onChange={v => setAdj(col.id, r.installmentNumber, { pct: v })} title="Abatement — dollars off this installment" style={{ ...smIn, width: '84px' }} />
                                    : effType ? <input type="text" inputMode="decimal" value={effPct} placeholder="0" onChange={e => setAdj(col.id, r.installmentNumber, { pct: e.target.value.replace(/[^0-9.]/g, '') })} style={{ ...smIn, width: '46px' }} /> : null}
                                </div>
                                {/* Billed — READ-ONLY. The scanned figure is authoritative; an override is entered
                                    on the installment card, not here. `manual` only changes the tooltip. A paid bill
                                    carries a Paid tag; a bill that couldn't be fully read carries a review tag.
                                    The tags WRAP above the amount when the track is too narrow for both — on one
                                    nowrap line, review + PAID + the amount overflowed the 120px track and the amount
                                    ran into the Due date (12-08-100-006-8531, 2021). */}
                                <div {...openRow(r)} style={{ ...cell, cursor: 'pointer', fontWeight: 700, minWidth: 0 }}>{(() => {
                                  const v = effBilled(r.installmentNumber);
                                  if (v == null) return <span style={{ color: c.muted, fontWeight: 400, fontSize: '11px' }}>not entered</span>;
                                  const manual = bMan[r.installmentNumber] !== undefined;
                                  const sbr = bScan[r.installmentNumber] || {};
                                  return (
                                    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 5px', justifyContent: 'flex-end' }}>
                                      {sbr.needsReview && <span title={sbr.reviewNote || 'Needs review'} style={{ fontSize: '9px', fontWeight: 800, color: '#b45309', border: '1px solid rgba(180,83,9,0.45)', borderRadius: '4px', padding: '0 4px' }}>⚠ review</span>}
                                      {sbr.paid && <span title={paidTitle(sbr.paid)} style={{ fontSize: '9px', fontWeight: 800, color: '#059669', background: 'rgba(52,211,153,0.14)', borderRadius: '4px', padding: '1px 5px' }}>PAID</span>}
                                      <span style={{ whiteSpace: 'nowrap' }} title={manual ? 'Entered by hand on the installment card' : 'From the scanned bill — the full tax, before any discount'}>{fmtc(v)}</span>
                                      {/* THE PER-INSTALLMENT CHECK: this installment's computed share (taxable × rate × Bill %, less its
                                          abatement) plus the non-ad valorem shown on this installment (navShare: split, or all on installment 1),
                                          beside what was billed. A cross-check only — billed stays authoritative — so nothing shows unless the
                                          difference is over $1.00, and then only an amber ⚠ with the figures on hover (Philip, 2026-10-19). */}
                                      {r.subTotal != null && (() => {
                                        const calc = Math.round((r.subTotal + (navShare[idx] || 0)) * 100) / 100;
                                        const d = Math.round((v - calc) * 100) / 100;
                                        return deltaMatters(d) && <span style={deltaWarn} title={deltaTitle(calc, d)}>⚠</span>;
                                      })()}
                                      {shareLine(v)}
                                    </span>
                                  );
                                })()}</div>
                                <div {...openRow(r)} style={{ ...cell, cursor: 'pointer', textAlign: colAlign.due }}>
                                  {r.dueDate ? fmtDue(r.dueDate) : <span style={{ color: c.muted }}>—</span>}
                                </div>
                                {/* PER-INSTALLMENT workflow chip. READ-ONLY — the modal SHOWS where this bill is; it
                                    does not move it. Processing happens on the Tax Bill Processing worklist.
                                    Derived from THIS installment's tax_bills.status via billWorkflowStatus (the one
                                    fold) — never from the per-collector scanBill, which held an arbitrary
                                    installment's status and is why installment 1 'filed' made installment 2 look done. */}
                                <div {...openRow(r)} style={{ ...cell, cursor: 'pointer', textAlign: colAlign.stat }}>{(() => {
                                  const st = (bScan[r.installmentNumber] || {}).status;
                                  if (!st) return <span style={{ fontSize: '11px', color: c.muted }}>—</span>;
                                  const key = billWorkflowStatus(st);
                                  const tone = key === 'rejected' ? '248,113,113'
                                    : key === 'filed' || key === 'transmitted' ? '52,211,153'
                                    : key === 'approved' ? '96,165,250' : '148,163,184';
                                  return <span title={`tax_bills.status = '${st}' for installment ${r.installmentNumber}`}
                                    style={{ fontSize: '10px', fontWeight: 700, fontFamily: "'Public Sans',system-ui,sans-serif", color: `rgb(${tone})`, background: `rgba(${tone},0.12)`, border: `1px solid rgba(${tone},0.35)`, borderRadius: '20px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                                    {TAX_BILL_CHIP_LABEL[key] || key}</span>;
                                })()}</div>
                              </React.Fragment>
                            );
                          })}
                          {/* Non-Ad-Valorem — ONE ROW PER INSTALLMENT, pairing with the rows above. Only when the annual
                              figure is above $0: a $0 / blank Non-Ad (Cook has none) drew rows of $0.00. The annual
                              field on the installment card is where it is entered, so hiding the rows hides nothing editable. */}
                          {navAnnual > 0 && model.rows.length > 0 && (
                            (() => {
                              // ⛔ DISPLAY-ONLY SPLIT. The STORED figure stays ANNUAL everywhere it matters:
                              // parcelTaxes.nav (an override only), tax_bills.total_non_ad_valorem,
                              // and tax_bills.total_non_ad_valorem — whose column comment says ANNUAL in so many
                              // words. Nothing below reads these rendered halves; they exist so the Amount Due
                              // column beside them is explicable, since the county's installment figure already
                              // contains this share. Writing a half into the annual column is the exact
                              // two-grains-one-column bug the ad-valorem backfill was written to repair.
                              //
                              // ⛔ WHERE IT FALLS IS navShares' CALL (utils/taxBill): split with the last row absorbing the
                              // remainder (1,539.19 over two rows is 769.59 + 769.60), or ALL on installment 1 when the collector
                              // bills it there (Clark County's artesian basin fee) — then only installment 1 carries a row.
                              const n = model.rows.length;
                              // Rendered FROM TAX_COLS and keyed by column — a track with no entry is empty by
                              // construction, so a placeholder cannot land under the wrong header.
                              return model.rows.map((r, i) => {
                                if (navOn1 && i > 0) return null;
                                const navContent = {
                                  jur: (<>
                                    <span>Non-Ad-Valorem</span>
                                    <span style={{ display: 'block', fontSize: '9px', color: c.muted }}>{navOn1 ? (n > 1 ? `on ${r.billType || 'installment 1'}` : 'annual') : n > 1 ? `${i + 1} of ${n}` : 'annual'}</span>
                                  </>),
                                  tax: '—', rate: '—',
                                  adj: '',
                                  billed: <span title={`Display only — ${fmtc(navAnnual)} annual, ${navOn1 ? 'billed on installment 1 (collector setting)' : `split across ${n} installment${n > 1 ? 's' : ''}`}. Stored and submitted as the annual figure.`}>{fmtc(navShare[i])}</span>,
                                };
                                return (
                                  <React.Fragment key={`nav-${r.installmentNumber ?? i}`}>
                                    {TAX_COLS.map(cd => (
                                      <div key={cd.key} style={{
                                        ...(cd.key === 'jur' || cd.key === 'adj' ? cellL : cell),
                                        textAlign: cd.align,
                                        ...(cd.key === 'adj' ? { fontSize: '10px', color: c.muted } : {}),
                                      }}>{navContent[cd.key] ?? null}</div>
                                    ))}
                                  </React.Fragment>
                                );
                              });
                            })()
                          )}
                        </div>
                        {/* Appeal tax dollars (Executive Summary report) — entered directly, not computed from rate */}
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginTop: '10px', paddingTop: '8px', borderTop: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Appeal Tax $ (report)</span>
                          <label style={{ fontSize: '11px', color: c.subtext, display: 'flex', alignItems: 'center', gap: '5px' }}>Proposed Tax $
                            <input type="text" inputMode="decimal" value={pt.proposed_tax ?? ''} placeholder="0.00" onChange={e => setPTax(col.id, 'proposed_tax', e.target.value.replace(/[^0-9.]/g, ''))} style={{ ...smIn, width: '110px' }} /></label>
                          <label style={{ fontSize: '11px', color: c.subtext, display: 'flex', alignItems: 'center', gap: '5px' }}>Final Tax $
                            <input type="text" inputMode="decimal" value={pt.final_tax ?? ''} placeholder="0.00" onChange={e => setPTax(col.id, 'final_tax', e.target.value.replace(/[^0-9.]/g, ''))} style={{ ...smIn, width: '110px' }} /></label>
                          <span style={{ fontSize: '10px', color: c.muted }}>Tax Saved = Proposed − Final (both required)</span>
                        </div>
                        {/* Footer: Transmittal Status · Computed total (verification) vs Billed total (authoritative → TBT) · Δ */}
                        {(() => {
                          const instNums = model.rows.map(r => r.installmentNumber);
                          // ⛔ "inst 1" IS THE WRONG WORD ON AN ANNUAL BILL, AND IT IS EVERY FLORIDA PARCEL.
                          // A Florida annual bill is ONE row with one due date and a discount — there is no
                          // installment 1 to not have entered. The grid already labels that row from its own
                          // bill_type ("Annual Tax Bill", :3989); the footer invented a different vocabulary for
                          // the same row. Reuse the label, and drop "total incomplete" when there is only one
                          // row: nothing is partial about a single missing amount.
                          // Computed vs Billed, the Δ, discount, Paid / To pay — utils/taxesFooter (unit-tested; the
                          // like-with-like and older-bill rules are stated there).
                          const { missing: missingNums, billedTotal, billedFull, computedShown, totalDelta, billDisc, discRow, allPaid, paidAmt, toPay } =
                            taxesFooter({ instNums, effBilled, bScan, bMan, model });
                          const missingRows = model.rows.filter(r => missingNums.includes(r.installmentNumber));
                          const missing = missingRows;
                          const missingLabel = model.rows.length === 1
                            ? `${model.rows[0].billType || 'billed amount'} not entered`
                            : `billed total incomplete — ${missingRows.map(r => r.billType || `inst ${r.installmentNumber}`).join(', ')} not entered`;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginTop: '10px', paddingTop: '8px', borderTop: `1px solid ${c.border}` }}>
                              {/* The collector-level "Transmittal Status" badge was REMOVED here. It read scan.status —
                                  one status per COLLECTOR, taken from an arbitrary tax_bills row — so a collector with
                                  installment 1 'filed' and installment 2 not yet processed displayed "Filed" for the
                                  whole collector. Status is per INSTALLMENT and now lives in the grid's Status column. */}
                              <span />
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                {/* The computed figure is only a cross-check on the billed one. Once every installment's
                                    billed amount is entered, what the estimate lacks is not a warning: it shows muted as
                                    "estimate only", saying why there is no Computed / Δ. */}
                                {!missing.length && (model.priorYearMissing || model.computedUnavailable)
                                  ? <span style={{ fontSize: '10px', color: c.muted }} title="Billed is complete; the computed estimate is a cross-check only">
                                      estimate only — {[model.priorYearMissing && 'prior year not entered (55% prepay $0)',
                                        model.computedUnavailable && (model.computedUnavailableReason || 'equalization factor not entered')].filter(Boolean).join(' · ')}
                                    </span>
                                  : model.priorYearMissing && <span style={{ fontSize: '10px', fontWeight: 700, color: c.subtext }}>prior year not entered — 55% prepay is $0</span>}
                                {model.computedUnavailable
                                  ? (missing.length ? <span style={{ fontSize: '11px', fontWeight: 700, color: c.subtext }}>computed unavailable — {model.computedUnavailableReason || 'equalization factor not entered'}</span> : null)
                                  : <span style={{ fontSize: '12px', color: c.subtext }} title={model.discount > 0 ? (billedFull ? `Before the computed discount of ${fmtc(model.discount)}` : `After the computed discount of ${fmtc(model.discount)}`) : undefined}>Computed <b style={{ color: c.text }}>{fmtc(computedShown)}</b></span>}
                                {missing.length
                                  ? <span style={{ fontSize: '11px', fontWeight: 700, color: c.text }}>{missingLabel}</span>
                                  : <>
                                      <span style={{ fontSize: '13px', fontWeight: 800, color: c.text }}>Billed {fmtc(billedTotal)}</span>
                                      {deltaMatters(totalDelta) && <span style={deltaWarn} title={deltaTitle(computedShown, -totalDelta)}>⚠</span>}
                                      <span style={{ fontSize: '11px', color: c.muted }} title="billed is authoritative → TBT">→ TBT</span>
                                      {shareLine(billedTotal)}
                                    </>}
                                {/* The bill's own discount and what it comes to: Paid (a catch-up bill) or To pay (a
                                    current one). Falls back to the computed discount when no bill row carries one. */}
                                {!missing.length && (billDisc > 0 || model.discount > 0 || allPaid) && (
                                  <span style={{ flexBasis: '100%', textAlign: 'right', fontSize: '11px', color: c.subtext }}>
                                    {billDisc > 0
                                      ? <>Discount −{fmtc(billDisc)}{discRow?.discountDate ? ` by ${fmtYmd(discRow.discountDate)}` : ''}{discRow?.discountDerived ? (discRow.paid ? ' (derived from the paid date)' : ' (derived from the printed tiers)') : ''}</>
                                      : model.discount > 0 ? <>Discount −{fmtc(model.discount)} (computed)</> : null}
                                    {allPaid
                                      ? <span style={{ marginLeft: '10px', fontWeight: 700, color: '#059669' }}>Paid{paidAmt != null ? ` ${fmtc(paidAmt)}` : ''}</span>
                                      : billDisc > 0 && toPay != null
                                        ? <span style={{ marginLeft: '10px', fontWeight: 700, color: c.text }}>To pay {fmtc(toPay)}</span>
                                        : null}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {/* SUPPLEMENTAL / OFF-SCHEDULE BILLS — one line each, under the collector's installments and its
                            annual totals (they are not part of either); a line opens the bill's own window
                            (renderOffScheduleCard). "+ Supplemental bill" types a new one from the county's bill. */}
                        {(() => {
                          const offKeys = Object.keys(bScan).filter(isOffScheduleKey);
                          const canAdd = mode !== 'create' && canEditYear;
                          if (!offKeys.length && !canAdd) return null;
                          const seqs = offKeys.filter(kk => kk.split('#')[0] === 'Supplemental')
                            .map(kk => Number(kk.split('#')[1])).filter(nn => Number.isFinite(nn));
                          const nextSeq = seqs.length ? Math.max(...seqs) + 1 : 1;
                          const formKey = `${col.id}|new-supplemental`;
                          const cell = { fontSize: '12px', color: c.text };
                          return (
                            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px solid ${c.border}` }}>
                              {offKeys.length > 0 && <>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                                  Supplemental bills <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— not part of the annual billed total</span>
                                </div>
                                {offKeys.map(k => {
                                  const sb = bScan[k];
                                  const seq = k.split('#')[1];
                                  const chipKey = sb.status ? billWorkflowStatus(sb.status) : null;
                                  return (
                                    <div key={k} onClick={() => setBillModal({ colId: col.id, off: k })} title="Open this bill"
                                      style={{ display: 'grid', gridTemplateColumns: 'minmax(170px,1.4fr) 1fr 1fr auto', gap: '10px', alignItems: 'center', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer' }}
                                      onMouseEnter={e => { e.currentTarget.style.background = c.rowHover; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                      <span style={{ ...cell, fontWeight: 700 }}>{k.split('#')[0]}{/^\d+$/.test(seq) ? ` — installment ${seq}` : ''}</span>
                                      <span style={cell}>Due {sb.dueDate ? fmtYmd(sb.dueDate) : '—'}</span>
                                      <span style={{ ...cell, fontFamily: "'DM Mono',monospace" }}>{sb.amountDue != null ? fmtc(sb.amountDue) : '—'}</span>
                                      {chipKey ? <span style={{ fontSize: '10px', fontWeight: 700, color: c.muted, background: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.1)', borderRadius: '6px', padding: '2px 7px' }}>
                                        {TAX_BILL_CHIP_LABEL[chipKey] || sb.status}</span> : <span />}
                                    </div>
                                  );
                                })}
                              </>}
                              {canAdd && (!(addOff && addOff.key === formKey)
                                ? <button type="button" onClick={() => setAddOff({ key: formKey, seq: String(nextSeq), accountNumber: '', dueDate: '', amountDue: '', adValorem: '', nonAdValorem: '', taxRate: '', taxable: '', busy: false, msg: '' })}
                                    style={{ ...s.btnGhost, marginTop: '8px', padding: '4px 10px', fontSize: '11px' }}>+ Supplemental bill</button>
                                : <div style={{ border: `1px solid ${c.border}`, borderRadius: '8px', padding: '10px 14px', marginTop: '8px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 800, color: c.text }}>Supplemental</span>
                                    {renderAddOffForm(col, 'Supplemental', null, nextSeq)}
                                  </div>)}
                            </div>
                          );
                        })()}
                      </div>
                      {/* A supplemental's own bill window — the line above opens it. */}
                      {billModal && billModal.colId === col.id && billModal.off && bScan[billModal.off] && (
                        <div onClick={() => setBillModal(null)}
                          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                          <div onClick={e => e.stopPropagation()}
                            style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '14px 16px', width: 'min(1000px, 100%)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '16px', fontWeight: 700, color: c.text, marginBottom: '2px' }}>
                                {col.name} · {billModal.off.replace('#', ' #')} · {valueYear}
                              </div>
                              <button type="button" onClick={() => setBillModal(null)}
                                style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>✕</button>
                            </div>
                            {renderOffScheduleCard(col, bScan, billModal.off, bScan[billModal.off])}
                          </div>
                        </div>
                      )}
                      {billModal && billModal.colId === col.id && (() => {
                        const mr = model.rows.find(x => x.installmentNumber === billModal.inst);
                        if (!mr) return null;
                        return (
                      <div onClick={() => setBillModal(null)}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <div onClick={e => e.stopPropagation()}
                        style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '14px 16px', width: 'min(1000px, 100%)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '16px', fontWeight: 700, color: c.text, marginBottom: '2px' }}>
                              {col.name} · {mr.billType || `Installment ${mr.installmentNumber}`} · {valueYear}
                            </div>
                            <button type="button" onClick={() => setBillModal(null)}
                              style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>✕</button>
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: c.text, marginBottom: '4px' }}>Bill fields → Approval (editable — pre-filled from the scan)</div>
                          <div style={{ fontSize: '11px', color: c.muted, marginBottom: '10px' }}>Edit any field; edits flow to Approval and the TBT. Market {fmtc(currentTaxTotals().marketTotal)} · Taxable {fmtc(currentTaxTotals().taxableTotal)} reused from the tax section. Each installment is submitted on its own card.</div>
                          {/* ⛔ NO `r.amount != null` FILTER — do not put one back. r.amount is the COMPUTED ad-valorem
                              (Value × Rate), a verification figure; it is null for every Cook PREPAY row by
                              construction (taxBill.js:108), for every row when the equalization factor is unknown,
                              and for all rows in the multi-jurisdiction guard. Filtering on it rendered NO CARD for
                              those installments, which left Cook's prepay reachable only by the deleted
                              collector-level button. Submittability is not "was it computed" — it is "is there an
                              Amount Due", which processBill enforces at submit (:1490) with a readable message. */}
                          {[mr].map(r => {
                            const v = procValues(col, r);
                            // ⛔ FOUR FIELDS DO NOT WRITE TO procForm — they write to the store that OWNS the value,
                            // so the card and its twin control can never disagree:
                            //   amountDue     → setBilled  (grid "Billed", authoritative → TBT / Δ / Cook base)
                            //   dueDate       → setDue     (grid DUE column → parcel_taxes.installments.due_date)
                            //   nonAdValorem  → setNav     (per-collector-year OVERRIDE; the bill is the default)
                            //   accountNumber → setCollectorAccts (parcel_collectors.account_number — one per parcel)
                            // Everything else is card-only and rides the draft that saveParcelTaxes persists.
                            const OWNED = {
                              amountDue:     (val) => setBilled(col.id, r.installmentNumber, cleanMoney(val)),
                              dueDate:       (val) => setDue(col.id, r.installmentNumber, val),
                              nonAdValorem:  (val) => setNav(col.id, cleanMoney(val)),
                              // Typing here records an OVERRIDE of the bill's annual ad valorem. Clearing the
                              // field removes the override and the bill's own figure returns.
                              adValorem:     (val) => setAdvOv(col.id, r.installmentNumber, cleanMoney(val)),
                              accountNumber: (val) => setCollectorAccts(m => ({ ...m, [col.id]: val })),
                            };
                            const fld = (label, field, opts = {}) => (
                              <div>
                                <div style={{ fontSize: '9px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</div>
                                {opts.money ? (
                                  // MoneyInput shows the typed text while focused and formats only on blur — the
                                  // owned stores get the cleaned number; card-only fields keep the typed text, formatted on blur.
                                  <MoneyInput value={v[field]}
                                    onChange={(clean, raw) => (OWNED[field] ? OWNED[field](clean) : setProc(v.key, field, raw))}
                                    onBlur={!OWNED[field] ? () => setProc(v.key, field, fmtMoneyIn(v[field])) : undefined}
                                    placeholder={opts.ph || ''} style={{ ...smIn, width: '100%' }} />
                                ) : (
                                  <input type={opts.date ? 'date' : 'text'} value={v[field]}
                                    onChange={e => (OWNED[field] ? OWNED[field](e.target.value) : setProc(v.key, field, e.target.value))}
                                    placeholder={opts.ph || ''} style={{ ...smIn, width: '100%' }} />
                                )}
                              </div>
                            );
                            return (
                              <div key={r.installmentNumber}
                                ref={el => { billCardRefs.current[`${col.id}:${r.installmentNumber}`] = el; }}
                                style={{ border: `1px solid ${focusBill && focusBill.collectorId === col.id && focusBill.installmentNumber === r.installmentNumber ? 'var(--red)' : c.border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: c.text, marginBottom: '8px' }}>{v.billType}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                                  <div>
                                    {fld('Amount Due', 'amountDue', { ph: '0.00', money: true })}
                                    {/* The override's revert lived beside the grid's Amount Due cell, which is now
                                        read-only — so it moves here rather than disappearing with the cell. */}
                                    {bMan[r.installmentNumber] !== undefined && bScan[r.installmentNumber]?.amountDue != null && (
                                      <button type="button" onClick={() => clearBilled(col.id, r.installmentNumber)}
                                        style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '10px', padding: '3px 0 0' }}>
                                        ↩ revert to the scanned {fmtc(bScan[r.installmentNumber].amountDue)}
                                      </button>
                                    )}
                                  </div>
                                  <div>
                                    {fld('Ad Valorem (annual)', 'adValorem', { ph: '0.00', money: true })}
                                    {/* The computed tax is a HINT, never a value: the field is the bill's figure (procValues). */}
                                    {model.subTotal != null && (
                                      <div style={{ fontSize: '10px', color: c.muted, marginTop: '3px' }}>computed: {fmtc(model.subTotal)}</div>
                                    )}
                                  </div>
                                  <div>
                                    {fld('Non-Ad Valorem (annual)', 'nonAdValorem', { ph: '0.00', money: true })}
                                    {/* ⛔ DERIVED, READ-ONLY, AND NEVER THE INPUT. The field above is the ANNUAL figure —
                                        it is what reaches tax_bills.total_non_ad_valorem, whose column comment says
                                        ANNUAL. This line only restates what the grid shows split, so the two agree on
                                        screen without the split ever becoming something a person can type into. Making
                                        the input the share is the two-grains-one-column bug the backfill repaired. */}
                                    {(() => {
                                      const n = model.rows.length;
                                      const annual = Number(String(v.nonAdValorem ?? '').replace(/[^0-9.]/g, ''));
                                      if (!Number.isFinite(annual) || annual === 0 || n < 2) return null;
                                      const idx = model.rows.findIndex(x => x.installmentNumber === r.installmentNumber);
                                      const share = navShares(annual, n, col.nav_billed_on)[idx] ?? 0;
                                      return <div style={{ fontSize: '10px', color: c.muted, paddingTop: '3px' }}>
                                        derived · this installment's share {fmtc(share)} of {fmtc(annual)}{col.nav_billed_on === 'installment_1' ? ' (billed on installment 1)' : ''}
                                      </div>;
                                    })()}
                                  </div>
                                  {fld('Penalty', 'penalty', { ph: '0.00', money: true })}
                                  {fld(col.discount_offered === 'Yes' ? 'Discount · collector offers early-payment discount' : 'Discount', 'discount', { ph: '0.00', money: true })}
                                  {/* Three date inputs on one row. Their content width is fixed by the browser's
                                      date control, so the labels are abbreviated rather than the fields shrunk. */}
                                  {fld('Disc. Date', 'discountDate', { date: true })}
                                  {fld('Due Date', 'dueDate', { date: true })}
                                  {/* MANUAL ONLY — the extractor returns no delinquent date (its prompt excludes
                                      late-tier dates to protect the TBT first-tier invariant), so this is typed in.
                                      Display-and-store: nothing downstream reads it. */}
                                  {fld('Delinq. Date', 'delinquentDate', { date: true })}
                                  {fld('Bill Type', 'billType')}
                                  {/* PAID — catch-up bills only (a current bill is never paid when we get it). Ticked, the
                                      bill is filed after approval and never transmitted; Billed is the full tax. Paid date /
                                      amount only if the bill shows them. utils/billPayment.
                                      ⛔ ONLY WHILE PROCESS CAN WRITE IT (no bill yet, Returned, or a baseline Recorded bill).
                                      Once a bill is At Approval or beyond, its payment is recorded on the payment panel below
                                      (utils/paymentRecord) — never by re-processing. */}
                                  {(() => { const st0 = (bScan[r.installmentNumber] || {}).status;
                                    return !st0 || st0 === TAX_BILL_STATUS.REJECTED || st0 === TAX_BILL_STATUS.RECORDED; })() && (<>
                                  <div>
                                    <div style={{ fontSize: '9px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>Paid</div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: c.text, height: '26px', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={!!v.paid} onChange={e => setProc(v.key, 'paid', e.target.checked)} />
                                      already paid
                                    </label>
                                  </div>
                                  {v.paid && fld('Paid Date', 'paidDate', { date: true })}
                                  {v.paid && fld('Amount Paid', 'amountPaid', { ph: 'if shown', money: true })}
                                  {v.paid && (
                                    <div>
                                      <div style={{ fontSize: '9px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>Proof</div>
                                      <select value={v.paidProof} onChange={e => setProc(v.key, 'paidProof', e.target.value)} style={{ ...smIn, width: '100%' }}>
                                        <option value="">— pick —</option>
                                        {PAID_PROOFS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                                      </select>
                                    </div>
                                  )}
                                  {v.paid && fld('Payment Note', 'paidNote', { ph: 'optional' })}
                                  </>)}
                                  {fld('Collector # (Account)', 'accountNumber', { ph: 'parcel account #' })}
                                </div>
                                <div style={{ fontSize: '9px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 4px' }}>Make Checks Payable To</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 0.55fr 0.8fr', gap: '8px 10px' }}>
                                  <input type="text" value={v.payToName} onChange={e => setProc(v.key, 'payToName', e.target.value)} placeholder="Payee" style={{ ...smIn, width: '100%' }} />
                                  <input type="text" value={v.payToAddr1} onChange={e => setProc(v.key, 'payToAddr1', e.target.value)} placeholder="Address" style={{ ...smIn, width: '100%' }} />
                                  <input type="text" value={v.payToCity} onChange={e => setProc(v.key, 'payToCity', e.target.value)} placeholder="City" style={{ ...smIn, width: '100%' }} />
                                  <input type="text" value={v.payToState} onChange={e => setProc(v.key, 'payToState', e.target.value)} placeholder="ST" style={{ ...smIn, width: '100%' }} />
                                  <input type="text" value={v.payToZip} onChange={e => setProc(v.key, 'payToZip', e.target.value)} placeholder="ZIP" style={{ ...smIn, width: '100%' }} />
                                </div>
                                {/* PER-CARD action + outcome. The button's state comes from THIS installment's chip:
                                    submitTaxBill:47 throws on approved/transmitted/filed, so those are disabled with
                                    the reason rather than left live to produce a thrown error the TS has to read.
                                    'rejected' (Returned) stays LIVE — a redo is exactly what it is asking for. */}
                                {(() => {
                                  const st = (bScan[r.installmentNumber] || {}).status;
                                  const key = st ? billWorkflowStatus(st) : null;
                                  const terminal = st ? [TAX_BILL_STATUS.APPROVED, TAX_BILL_STATUS.TRANSMITTED, TAX_BILL_STATUS.FILED].includes(st) : false;
                                  const atApproval = key === 'processed';
                                  const blocked = terminal || atApproval;
                                  // ⛔ "RESOLVE THAT BILL FIRST" NAMED NO WAY TO RESOLVE IT. Sixteen bills were
                                  // reopened by hand this week because of that sentence. Filed and approved bills
                                  // now carry a Reopen control; transmitted ones name the path instead of refusing.
                                  const sent = st === TAX_BILL_STATUS.TRANSMITTED;
                                  const canReopen = st === TAX_BILL_STATUS.FILED || st === TAX_BILL_STATUS.APPROVED;
                                  // ⛔ RECORDED IS NOT BLOCKED AND GETS NO REOPEN. It is absent from `terminal`
                                  // above because submitTaxBill's guard is absent it too — so Process STAYS LIVE
                                  // and re-processing supersedes the recorded bill and inserts a fresh one. That
                                  // IS the correction path for a baseline bill, which is why there is no Reopen
                                  // here: reopenBill writes status 'rejected', and a baseline parcel-year returns
                                  // no expected_bills row, so a 'rejected' baseline bill would appear on no
                                  // worklist at all — reopening it would hide it. The notice says what pressing
                                  // Process will do, since replacing a stored figure silently is the thing this
                                  // grid has been audited for all week.
                                  const recorded = st === TAX_BILL_STATUS.RECORDED;
                                  const baselineYear = yearIsBaseline();
                                  const why = sent ? 'Already sent to the client — correct the bill, re-approve, then regenerate the transmittal.'
                                    : canReopen ? `Already ${TAX_BILL_CHIP_LABEL[key]} — reopen it to correct.`
                                    : atApproval ? 'Already at Approval — waiting on the CS.'
                                    : recorded ? `Recorded as a baseline bill for ${valueYear} — not sent for approval. Processing again replaces it (the current figures are superseded, not deleted).` : '';
                                  const tbId = (bScan[r.installmentNumber] || {}).taxBillId;
                                  const busy = procBusy === v.key;
                                  const msg = procMsg[v.key];
                                  // ⛔ SAY IT BEFORE THEY PROCESS. market_value on this path comes from
                                  // currentTaxTotals() — the parcel's own parcel_values for the value year — and
                                  // when the parcel has none it submits null, which prints as an em-dash in the
                                  // TBT's Market Value column. That went out blank on 33 transmittals and nobody
                                  // knew, because nothing said so. NOT A BLOCK: a bill with no market value is
                                  // still a valid bill, and the amount due is unaffected. It is a statement, made
                                  // where the parcel's value section is one scroll away, so the fix is to record
                                  // the value on the PARCEL — which then feeds every installment, the Properties
                                  // roll-up and the modal alike. ⛔ DO NOT "fix" this by inheriting the value from
                                  // installment 1: that hides a parcel missing its market value everywhere and
                                  // makes a bill row more trustworthy than the parcel it belongs to.
                                  const noMarket = !(currentTaxTotals().marketTotal > 0);
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                      {/* ⛔ THE BUTTON MUST NOT PROMISE APPROVAL ON A BASELINE YEAR. It is the
                                          only thing a TS reads before clicking, and "→ Approval" would be a
                                          claim the write deliberately does not honour. */}
                                      <button type="button" disabled={blocked || !!procBusy} title={why || (baselineYear ? `Record ${v.billType} for ${valueYear} — not sent for approval` : `Submit ${v.billType} to Approval`)}
                                        onClick={() => processBill(col, r)}
                                        style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', whiteSpace: 'nowrap', opacity: (blocked || procBusy) ? 0.5 : 1, cursor: (blocked || procBusy) ? 'not-allowed' : 'pointer' }}>
                                        {busy ? 'Processing…' : baselineYear ? `Record ${v.billType} (baseline)` : `Process ${v.billType} → Approval`}</button>
                                      {why && <span style={{ fontSize: '11px', color: c.muted }}>{why}</span>}
                                      {canReopen && tbId && reopenFor !== tbId && (
                                        <button type="button" onClick={() => { setReopenFor(tbId); setReopenWhy(''); }}
                                          style={{ ...s.btnGhost, padding: '4px 12px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                          Reopen bill
                                        </button>
                                      )}
                                      {noMarket && !blocked && (
                                        <span style={{ fontSize: '11px', fontWeight: 600, color: isDark ? '#fbbf24' : '#8A5B00', flexBasis: '100%' }}>
                                          No market value recorded for this parcel in {valueYear} — the transmittal's Market Value column will be blank.
                                        </span>
                                      )}
                                      {msg && <span style={{ fontSize: '11px', fontWeight: 600, color: msg.startsWith('\u2713') ? '#16a34a' : (msg.startsWith('Process failed') || msg.startsWith('Process blocked') ? '#dc2626' : c.muted) }}>{msg}</span>}
                                      {/* PAYMENT — recorded on the bill as it stands, never by re-processing (utils/paymentRecord).
                                          Any live bill except a Returned one, which is re-processed with its paid tick above. */}
                                      {tbId && st !== TAX_BILL_STATUS.REJECTED && (
                                        <BillPaymentPanel
                                          bill={{ id: tbId, status: st, billed: (bScan[r.installmentNumber] || {}).total ?? null, paid: (bScan[r.installmentNumber] || {}).paid || null }}
                                          entityName={`${parcelData.parcel_name || parcel?.id} · ${col.name} · ${valueYear} inst ${r.installmentNumber ?? '—'}`}
                                          staffName={staffUser?.name} onChanged={() => setBillsReload(n => n + 1)} s={s} c={c} />
                                      )}
                                      {canReopen && tbId && reopenFor === tbId && (
                                        <div style={{ width: '100%', marginTop: '8px', padding: '10px 12px', borderRadius: '8px',
                                          background: isDark ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.55)' }}>
                                          {/* ⛔ SAY THE COST BEFORE THE CLICK, NOT AFTER. 'rejected' leaves Approvals and
                                              lands on the TS Processing worklist, so even a one-character correction has to
                                              go TS -> CS again. Discovering that afterwards is how a quick fix becomes a
                                              surprise; the whole point of replacing the dead-end was to tell people what
                                              happens next. */}
                                          <div style={{ fontSize: '11px', fontWeight: 700, color: isDark ? '#fbbf24' : '#8A5B00', marginBottom: '4px' }}>
                                            Reopen {v.billType} for correction
                                          </div>
                                          <div style={{ fontSize: '11px', color: c.muted, marginBottom: '8px', lineHeight: 1.5 }}>
                                            This sends the bill back through TS Processing — it leaves Approvals, reappears on the
                                            Tax Bill Processing worklist, and has to be re-Processed and re-approved before it can be
                                            filed or transmitted again. Even a one-character fix takes the full round trip.
                                            The reason below is stored as the rejection note.
                                          </div>
                                          <input type="text" value={reopenWhy} autoFocus
                                            onChange={e => setReopenWhy(e.target.value)}
                                            placeholder="Why is this being reopened? (required)"
                                            style={{ ...s.input, fontSize: '12px', marginBottom: '8px' }} />
                                          <div style={{ display: 'flex', gap: '8px' }}>
                                            <button type="button" disabled={!reopenWhy.trim() || reopenBusy}
                                              onClick={() => reopenBill(col, r, tbId)}
                                              style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', opacity: (!reopenWhy.trim() || reopenBusy) ? 0.5 : 1 }}>
                                              {reopenBusy ? 'Reopening…' : 'Reopen bill'}
                                            </button>
                                            <button type="button" onClick={() => { setReopenFor(null); setReopenWhy(''); }}
                                              style={{ ...s.btnGhost, padding: '4px 12px', fontSize: '11px' }}>Cancel</button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          <div ref={complianceRef} style={s.card}>
            <div style={s.cardHeader}><div style={s.cardTitle}>Compliance</div></div>
            <div style={{ padding: '20px 18px', fontSize: '13px', color: c.muted, fontStyle: 'italic' }}>Coming soon</div>
          </div>
          {mode !== 'create' && (
            <div ref={notesRef} style={s.card}>
              <div style={{ padding: '14px 18px' }}>
                <EntityNotes
                  level="parcel"
                  entityId={parcel?.id}
                  rollup={{ propertyId: parcel?.property_id || parcelData?.property_id || null }}
                  selfLabel={parcelData?.account_number || parcelData?.parcel_name || ''}
                  rollupLabels={{ [`property:${parcel?.property_id || parcelData?.property_id}`]: notesPropName }}
                  viewingYear={valueYear}
                  staff={{ id: staffUser?.id, name: staffUser?.name || staffUser?.email }}
                  years={valueYears}
                  onCount={setNotesCount}
                  isDark={isDark}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {refScanOpen && (
        <ReferenceBillScan parcel={parcel} year={valueYear} staffUser={staffUser} isDark={isDark}
          onClose={() => setRefScanOpen(false)} onSaved={loadSavingsRefs} />
      )}
      {/* E1a: Documents shortcut — sibling of the animated sheet (outside its transform) */}
      {docsOpen && (
        <DocumentsBubble
          property={parcel.property_id ? { id: parcel.property_id } : null}
          parcel={parcel}
          isDark={isDark}
          onClose={() => setDocsOpen(false)}
        />
      )}
      {/* The Notice Type window (Proposed Notice's "+"): active types only, in Table Management order. */}
      {noticePicker && (
        <NoticeTypePicker types={noticeTypes.filter(t => t.active)} s={s} c={c}
          onCancel={() => setNoticePicker(false)}
          onAdd={(key) => { setNoticePicker(false); addNotice(key); }} />
      )}
      {/* Unsaved-changes-on-exit dialog */}
      {showExitWarn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          {...backdropProps(() => setShowExitWarn(false))}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.4)', padding: '20px 22px', width: '360px', maxWidth: '90vw' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>You have unsaved changes</div>
            <div style={{ fontSize: '13px', color: c.subtext, marginBottom: '18px', lineHeight: 1.4 }}>Save your changes before leaving, or discard them?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={() => setShowExitWarn(false)} style={{ ...s.btnGhost }}>Cancel</button>
              <button type="button" onClick={() => { setShowExitWarn(false); onClose(); }} style={{ ...s.btnGhost, color: '#f87171', borderColor: 'rgba(248,113,113,0.4)' }}>Discard</button>
              <button type="button" onClick={saveAllAndExit} disabled={saving || appealSaving || !canEditYear} style={s.btn}>{(saving || appealSaving) ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      {pendingYearSwitch !== null && (
        <YearSwitchWindow billCardPending={procCardsChanged()} saving={saving || appealSaving} canSave={canEditYear} s={s} c={c}
          onCancel={() => setPendingYearSwitch(null)}
          onDiscard={async () => { const yr = pendingYearSwitch; setPendingYearSwitch(null); discardUnsaved(); await doSwitchYear(yr); }}
          onSave={async () => { const yr = pendingYearSwitch; const ok = await saveAll(); if (ok === false) return; setPendingYearSwitch(null); await doSwitchYear(yr); }} />
      )}
      {/* Assessment-Ratio popover — fixed overlay ABOVE the cell (never covers the % field). History (read-only)
          on top; EDIT adds a new-comment box + % input, Save APPENDS an immutable audit row (never edits past ones).
          Rule: every append requires a comment (Save disabled until the box is non-empty). VIEW (hover) = history only, 3s. */}
      {ratioPop && (() => {
        const level = ratioPop.level, jur = ratioPop.jur, editing = ratioPop.via !== 'hover';
        const row = (appeals[level] || {})[jur] || {};
        const saveBlocked = !newComment.trim(); // every append requires a comment (blocks blank/no-op entries)
        const history = ratioAudit[`${level}|${jur}`] || [];
        return (
          <div ref={ratioPopRef}
            onMouseEnter={editing ? undefined : clearRatioTimer}
            onMouseLeave={editing ? undefined : dismissRatioPop}
            style={{ position: 'fixed', left: Math.max(8, Math.min(ratioPop.x, window.innerWidth - 296)), top: ratioPop.top - 6, transform: 'translateY(-100%)', zIndex: 10001, background: c.card, border: `1px solid ${c.border}`, borderRadius: '8px', boxShadow: '0 8px 28px rgba(0,0,0,0.35)', padding: '10px', width: '280px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '5px' }}>{editing ? 'Assessment Ratio' : 'Ratio History'} — {jurisdictionLabel(jur)}</div>
            {/* read-only history (newest first) */}
            {history.length > 0
              ? <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {history.map(h => (
                    <div key={h.id} style={{ fontSize: '11px', color: c.subtext, marginBottom: '3px', lineHeight: 1.35 }}>
                      <span style={{ fontWeight: '700', color: c.text }}>{h.initials || '—'}</span> · {fmtAuditTime(h.created_at)} · {h.ratio_value != null ? pctToInput(h.ratio_value) : '—'}{h.reason ? ` · ${h.reason}` : ''}
                    </div>
                  ))}
                </div>
              : <div style={{ fontSize: '11px', color: c.muted, fontStyle: 'italic' }}>No history yet.</div>}
            {editing && (
              <div style={{ marginTop: '10px', borderTop: `1px solid ${c.border}`, paddingTop: '8px' }}>
                {/* % input only when override is on; unchecking = removing the override (comment-only confirm) */}
                {row.assessment_ratio_override
                  ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '9px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>New %</span>
                      <input value={pctToInput(row.assessment_ratio)} onChange={e => { ensureRatioSnapshot(level, jur); setAppealRaw(level, jur, 'assessment_ratio', pctParse(e.target.value)); }}
                        placeholder="20%" style={{ ...s.inputMono, width: '64px', fontSize: '12px', padding: '4px 6px' }} />
                    </div>
                  : <div style={{ fontSize: '11px', color: c.subtext, marginBottom: '6px' }}>Removing override — reverts to the assessor ratio. Comment required.</div>}
                <input autoFocus value={newComment} onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !saveBlocked) saveRatioReason(); }}
                  placeholder="New comment…" style={{ ...s.input, fontSize: '12px', padding: '6px 8px' }} />
                {saveBlocked && <div style={{ fontSize: '10px', color: '#f87171', marginTop: '4px' }}>A comment is required.</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <button type="button" onClick={dismissRatioPop} style={{ ...s.btnGhost, fontSize: '11px', padding: '5px 12px' }}>Cancel</button>
                  <button type="button" onClick={saveRatioReason} disabled={saveBlocked}
                    style={{ ...s.btn, fontSize: '11px', padding: '5px 14px', ...(saveBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>Save</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      <style>{`@keyframes slideUp { from { transform: translateY(40px); opacity:0; } to { transform: translateY(0); opacity:1; } }`}</style>
    </div>
  );
}
